/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Spanish. Mirrors the key structure of en.ts exactly.
 *
 * Process vocabulary follows industry usage rather than literal translation:
 * "grabado" for etch, "oblea" for wafer, "equipo" for a process tool, "receta"
 * for a recipe. Asset tags and unit symbols are left alone.
 */

export const i18n_es = {
  translation: {
    mission: {
      whereThePhotonsWent: {
        Title: 'Adónde fueron los fotones',
        Briefing: 'EUV-22 va una cuarta parte lenta y el turno de noche tiene apuntado para mañana un cambio de colector de treinta horas. Un escáner EUV puede perder su luz en tres sitios, y solo uno de ellos es el colector. Recorre el camino del haz antes de que nadie abra la vasija de la fuente.',
        Debrief: 'La luz nunca se generó. El pulso principal del láser llegaba tarde a la gota, así que el plasma daba cerca del 60% de su energía de pulso cualificada, y la potencia bajaba en la misma fracción en todo lo que venía después. Un colector está DESPUÉS del plasma: no puede bajar la energía de pulso medida en el plasma, y esa única lectura es la que ahorró treinta horas y un colector que estaba haciendo su trabajo.',
        objective: {
          timing: 'Ejecuta el barrido de sincronía gota-pulso',
          call: 'Toma la decisión, correctamente',
        },
      },
      theRulerIsWide: {
        Title: 'La regla es ancha',
        Briefing: 'Todas las líneas que SEM-23 ha medido desde el fin de semana leen cerca de un nanómetro de más, y litografía ya ha subido la dosis una vez para recuperar la CD. Antes de que lo hagan otra vez: averigua si se movieron las líneas o el instrumento.',
        Debrief: 'Fue el instrumento. La punta fría de emisión de campo llevaba días sin un flash, su dispersión de energía casi se había triplicado, y a 500 eV eso es casi todo el punto: el desenfoque cromático va como ΔE sobre E. Un punto más ancho lee cada borde un poco más afuera. Se sacó el escáner de su valor para corregir una impresión que estaba bien, que es lo que cuesta un fallo de metrología: no una oblea desechada, sino una decisión equivocada en otro sitio.',
        objective: {
          spread: 'Mide la dispersión de energía del cañón',
          call: 'Toma la decisión, correctamente',
        },
      },
      coldPlateWarmQubit: {
        Title: 'Placa fría, cúbit caliente',
        Briefing: 'QDT-24 llegó a 10 mK a tiempo y todos sus termómetros son normales. El chip que lleva dentro midió bien en otro refrigerador el mes pasado y aquí lee fatal. Alguien propondrá calentar para cambiar el chip. Averigua primero qué puede oír el cúbit.',
        Debrief: 'Faltaba un atenuador de 20 dB en la línea de excitación, así que la línea llevaba cien veces el ruido de temperatura ambiente que debería: un octavo de fotón en el modo del cúbit, un cúbit a 109 mK sobre una placa a 10. El chip estaba bien y el refrigerador también. Un cúbit no está a la temperatura del metal al que está atornillado, está a la temperatura de su cableado, y el único termómetro que lee eso es el propio cúbit.',
        objective: {
          line: 'Mide la pérdida de inserción de la línea de excitación',
          call: 'Toma la decisión, correctamente',
        },
      },
      fiftyDegreesHot: {
        Title: 'Cincuenta grados que nadie fijó',
        Briefing: 'La resistencia de hoja que sale de THRM-04 viene baja y las uniones salen profundas. Se ha pedido al grupo del implantador que revise su dosis, y no les ha hecho gracia. Antes de que IMP-10 pierda un turno en una auditoría de dosis: una dosis baja no hace esto.',
        Debrief: 'El pirómetro del RTP leía de menos, así que las lámparas se llevaban cincuenta grados por encima de la consigna que mostraba la pantalla. La difusividad es de Arrhenius: cincuenta grados casi duplican el Dt, el dopante se extiende, la unión se hace más profunda y la resistencia de hoja BAJA. Una dosis corta mueve la resistencia de hoja en el otro sentido y deja la unión donde estaba. El sentido del error señaló la máquina antes de que nadie midiera nada.',
        objective: {
          pyrometer: 'Comprueba el pirómetro con una oblea de calibración',
          call: 'Toma la decisión, correctamente',
        },
      },
      oxygenClimb: {
        Title: 'La subida de oxígeno',
        Briefing: 'Ingeniería de dispositivo está rechazando obleas de los últimos tres lingotes por oxígeno intersticial, y el tirador no ha cambiado de receta en un mes. Misma velocidad de tiro, misma rotación, mismo caudal de argón. Averigua qué es distinto.',
        Debrief: 'El crisol de sílice estaba al final de su vida. Un crisol se disuelve en el fundido durante todo el tiempo que está caliente, y uno gastado cede oxígeno más deprisa que uno nuevo con exactamente la receta que funcionaba el mes pasado. El caudal de argón era nominal, y eso es lo que descarta el barrido —la otra vía por la que sube el oxígeno—; subir el argón lo habría enmascarado un lingote más.',
        objective: {
          oxygen: 'Mide el oxígeno intersticial en el último lingote',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      flatAndSlow: {
        Title: 'Plana, y lenta',
        Briefing: 'El pulido incumple su tiempo de ciclo en todos los lotes y los operarios llevan añadiendo minutos para alcanzar el espesor. La planitud sigue pasando. Nadie ha cambiado la suspensión ni la fuerza de apriete.',
        Debrief: 'El paño estaba vitrificado: su superficie se había cargado y dejó de cortar, así que la tasa de remoción cayó y el remedio que todos aplicaron —más tiempo— compraba el número gastando la herramienta. El tamaño de partícula estaba en especificación, lo que descarta el abrasivo; un paño vitrificado y una suspensión agotada frenan igual la tasa, y sólo uno de los dos se arregla con un acondicionado.',
        objective: {
          rate: 'Mide la tasa de remoción en una oblea testigo',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      sharpEnoughToPass: {
        Title: 'Bastante nítido para pasar',
        Briefing: 'El CD está derivando en LITH-03 y la respuesta obvia es la dosis. Antes de tocar la fuente: un escáner que ha perdido el foco imprime el mismo error de CD que una deriva de dosis, y las dos piden correcciones opuestas.',
        Debrief: 'Fue la platina, que había perdido el foco, no la fuente. El desenfoque cuesta CD y cuesta CONTRASTE y NILS con él; un error de dosis mueve el tamaño y deja el borde intacto, y eso es lo que separan los números de la imagen aérea. Corregir esto con dosis habría sostenido el número mientras la ventana de proceso seguía cerrándose por debajo.',
        objective: {
          focus: 'Ejecuta la sonda de foco',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      theSlowLeak: {
        Title: 'La fuga lenta',
        Briefing: 'ETCH-01 no alcanza la presión base y el turno anterior lo anotó como bomba cansada. Cambiar una bomba son cuatro horas y sólo hay una de repuesto en el edificio, así que asegúrate antes de gastarla.',
        Debrief: 'Una fuga real, no una bomba cansada. Ambas suben la presión base y ambas son idénticas en el medidor; lo que las separa es de dónde viene el gas: una fuga admite atmósfera a ritmo constante, y la velocidad de bombeo estaba bien. La bomba de repuesto sigue en la estantería, que es exactamente el valor de haberlo comprobado.',
        objective: {
          seal: 'Ejecuta la comprobación de estanqueidad',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      theCupThatLies: {
        Title: 'La copa que miente',
        Briefing: 'La resistencia laminar vuelve baja en los lotes implantados y el implantador dice que todas las dosis estuvieron en objetivo. Una de esas dos afirmaciones es falsa, y la herramienta es la que tiene una opinión sobre sí misma.',
        Debrief: 'La copa de Faraday había derivado, así que la herramienta contaba menos iones de los que entregaba y sobre-implantaba para cuadrar su número. Un monitor de dosis que se mide a sí mismo no puede detectar esto: sólo una oblea puede, y por eso la resistencia laminar es la lectura que lo zanja. Todos los demás números de banco estaban nominales, y todos ellos eran inocentes.',
        objective: {
          rho: 'Implanta un testigo y mide la resistencia laminar',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      thePitchThatWalks: {
        Title: 'El paso que camina',
        Briefing: 'PAT-21 pasa el CD medio y falla en test eléctrico, con los fallos alternando línea a línea. Una sola exposición hizo estas líneas, así que el solapamiento no puede ser la respuesta.',
        Debrief: 'Caminado del paso. La película espaciadora vino delgada, y en una celda de cuádruple patrón autoalineado el espesor del espaciador ES el semipaso, así que las cuatro líneas que el módulo hace a partir de un mandril dejan de estar equiespaciadas. La media queda intacta, y por eso el CD pasaba; ningún trabajo sobre el escáner habría ayudado: esto es un problema de deposición con quejas de litografía.',
        objective: {
          walk: 'Mide el paso, línea a línea',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      dishingOut: {
        Title: 'Ahuecando',
        Briefing: 'CMP-11 está sobre-puliendo las estructuras anchas de cobre y el paño se cambió el martes. El sospechoso obvio tiene coartada.',
        Debrief: 'La suspensión había envejecido y su inhibidor de corrosión estaba agotado. El inhibidor es lo que impide que la química ataque el cobre donde el paño no presiona, así que sin él las estructuras anchas se ahuecan mientras las estrechas parecen correctas: una firma de perfil a la que ninguna corrección de fuerza llega. Un paño nuevo con suspensión vieja habría pulido más rápido hacia el mismo defecto.',
        objective: {
          inhibitor: 'Analiza el inhibidor de la suspensión',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      voidsInTheVia: {
        Title: 'Huecos en la vía',
        Briefing: 'ECD-12 está depositando hasta espesor y las vías vuelven con una costura por el centro. Densidad de corriente, temperatura del baño y resistencia de contacto leen todas nominales.',
        Debrief: 'Los aditivos orgánicos estaban agotados. El llenado de abajo hacia arriba no es un ajuste de corriente: es el acelerador y el supresor decidiendo dónde va el cobre, y un baño que los ha perdido deposita de forma conforme y estrangula la vía antes de llenarla. El espesor fue correcto todo el tiempo, y por eso sólo una sección transversal podía mostrarlo.',
        objective: {
          xsection: 'Haz una sección transversal de una vía',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      theToolThatCriesWolf: {
        Title: 'La herramienta que grita lobo',
        Briefing: 'INSP-13 marca tantos defectos que ingeniería ha empezado a ignorar sus mapas. O la línea empeoró dramáticamente esta semana, o lo hizo el inspector.',
        Debrief: 'La óptica de colección se había velado. La luz dispersada por una lente enturbiada eleva el fondo contra el que trabaja el detector, así que la herramienta reporta defectos que no están en la oblea; y una tasa de falsos tan alta hace más daño que una herramienta ciega, porque la gente deja de leer los mapas reales. La rugosidad de entrada era nominal, lo que descarta las propias obleas.',
        objective: {
          nuisance: 'Verifica la tasa de falsos con una oblea conocida',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      goodDieCalledBad: {
        Title: 'Dado bueno, declarado malo',
        Briefing: 'El rendimiento en PRB-14 ha caído cuatro puntos y la pérdida está toda en el borde de la oblea. Las puntas se cambiaron la semana pasada y están holgadamente dentro de su vida.',
        Debrief: 'La tarjeta de puntas estaba fuera de plano. Una tarjeta que no es paralela al mandril apoya más fuerte en un lado que en el otro, así que el borde opuesto nunca hace contacto adecuado y dados buenos se clasifican como abiertos. El desgaste de puntas era nominal —el parecido al que todos acuden primero— y cambiarlas habría costado un cambio de tarjeta sin arreglar nada.',
        objective: {
          contact: 'Mide la resistencia de contacto a lo ancho de la oblea',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      wideKerf: {
        Title: 'La ranura que creció',
        Briefing: 'Ensamblaje está perdiendo dados por astillado de borde y corte reporta todos los lotes en especificación. El ancho de calle no se ha cambiado y el lote de cinta es el vigente.',
        Debrief: 'La cuchilla había pasado su límite de cortes. Una cuchilla desgastada corta una ranura más ancha y astilla más en los bordes, y los dados que sobreviven hasta la prensa de moldeo son los que no venían ya agrietados. El despegue de la cinta era nominal, lo que descarta el movimiento del dado durante el corte —la otra vía por la que sube el astillado, y la que te habría mandado al proveedor equivocado.',
        objective: {
          kerf: 'Mide el ancho de la ranura de corte',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      theQuietDispenser: {
        Title: 'El dosificador silencioso',
        Briefing: 'Los encapsulados de BOND-16 fallan en ciclado térmico y todos los hilos superan la prueba de tracción. Los fallos están bajo el dado, no en los lazos.',
        Debrief: 'La dosificación de epoxi venía escasa. Poco adhesivo deja huecos bajo el dado, y un hueco es un sitio por donde el calor no puede salir y donde la tensión no tiene a dónde ir, así que la pieza pasa todas las pruebas de hilo y falla el primer ciclado. Tracción y cizalla de bola eran ambas nominales, y eso es lo que declara inocente a la soldadora de hilo.',
        objective: {
          void: 'Toma imagen de los huecos bajo el dado',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      popcorn: {
        Title: 'Palomita',
        Briefing: 'Las piezas de PKG-17 se agrietan durante el reflujo de soldadura en el cliente, no aquí. La presión de transferencia es nominal y el compuesto está en fecha.',
        Debrief: 'Las piezas habían absorbido humedad en almacén. El agua atrapada en un compuesto de moldeo se convierte en vapor a temperatura de reflujo y parte el encapsulado desde dentro, y por eso esto falla en el cliente y pasa todas las pruebas de este edificio. El hueco de moldeo era normal, lo que descarta a la vez la prensa y el compuesto, y ninguna de esas dos reparaciones habría tocado el proceso de secado que realmente falló.',
        objective: {
          popcorn: 'Ejecuta el reflujo de sensibilidad a la humedad',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      theEarlyFailures: {
        Title: 'Los fallos tempranos',
        Briefing: 'FT-18 rechaza más piezas después del burn-in que antes, y el probador pasó su estudio de repetibilidad esta mañana. La temperatura del manipulador está en consigna.',
        Debrief: 'La propia población traía mortalidad infantil: una distribución de defectos de aguas arriba, no un problema de test. El burn-in existe para encontrarlas; un descarte que sube significa que el lote llegó con ellas, y la respuesta está aguas arriba y no en el probador. La sigma del calibre y la temperatura del manipulador eran ambas nominales, y eso es lo que convierte «nuestro test está roto» en «nuestras obleas lo están», y esas dos van a personas distintas.',
        objective: {
          fallout: 'Analiza el descarte de burn-in',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      theValveThatWont: {
        Title: 'La válvula que no quiere',
        Briefing: 'Verificación rutinaria de seguridad en GAS-19. El búnker funciona con normalidad y nada está en alarma, que es exactamente cuándo merece la pena probar un dispositivo de protección, porque es la única vez que puedes.',
        Debrief: 'La válvula de exceso de flujo estaba agarrotada en abierto. No había nada mal en el gas, el lavador ni la extracción, y no lo habría habido hasta que se rompiera una línea, momento en el cual el único dispositivo destinado a cerrar no habría cerrado. Un dispositivo de protección que nunca se ejercita es un dispositivo que estás suponiendo, y el modelo de fuga es lo que convierte esa suposición en un número.',
        objective: {
          release: 'Ejecuta el modelo de fuga contra el ensayo de la válvula',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      leakOrOutgas: {
        Title: 'Fuga o desgasificación',
        Briefing: 'FAC-06 no mantiene la base y el registro dice que la cámara se abrió a atmósfera el viernes. El agua en las paredes y una fuga real hacen ambas esto. Una se hornea y la otra no, y no puedes distinguirlas por la lectura del medidor.',
        Debrief: 'Una fuga real. Lo que las separa es la FORMA de la subida de presión: la atmósfera que entra por un sello sube linealmente y no para nunca, mientras que el agua que sale de las paredes sube y luego se dobla a medida que la superficie se agota. Misma presión base, dos curvas distintas, y un horneado habría costado un turno sin cambiar nada.',
        objective: {
          rise: 'Toma la curva de subida de presión, no sólo el número',
          call: 'Emite el diagnóstico, correctamente',
        },
      },
      innocentInstrument: {
        Title: 'El instrumento inocente',
        Briefing: 'MET-05 lee el óxido un 4% alto y media fábrica quiere recalibrarlo. Antes de parar un medidor que funciona: una medición repetible de la suposición equivocada se ve exactamente igual que un medidor roto, y solo una lectura puede distinguirlas.',
        Debrief: 'El reflectómetro era inocente: la PELÍCULA cambió. La herramienta de deposición derivó y la receta de medición siguió asumiendo el material antiguo, así que un ajuste perfecto del modelo equivocado reportó una película más gruesa, repetiblemente. El elipsómetro, que resuelve el índice en vez de asumirlo, es lo que separó \'medidor roto\' de \'mundo cambiado\' — y una recalibración habría enterrado la deriva real bajo una mentira corregida.',
        objective: {
          ellipsometer: 'Verifica en el elipsómetro',
          call: 'Haz el diagnóstico, correctamente',
        },
      },
      atomsToFabs: {
        Title: 'De los átomos a las fábricas',
        Briefing: 'Un lote, toda la línea de sustrato. Saca un cristal utilizable en CZ-07 y entrega el lingote; córtalo en WPR-08; pasa la limpieza de entrada en CLN-09; crece la capa de dispositivo en EPI-20, dentro de especificación, y pasa la oblea al front end. Cada decisión que tomes viaja en la tarjeta de ruta hasta la siguiente herramienta — incluidas las equivocadas.',
        Debrief: 'Del fundido a la capa de dispositivo: el título de esta fábrica, recorrido a pie. La oblea que sale de EPI-20 lleva en su tarjeta tu dopado del fundido, tu corte, tu limpieza y tu epitaxia, y cada herramienta aguas abajo vivirá dentro de los presupuestos que acabas de fijar. Eso significa \'de los átomos a las fábricas\' — no un lema: la historia de un lote.',
        objective: {
          grow: 'Crece un lingote utilizable en CZ-07',
          handCz: 'Entrega el lingote a preparación de obleas',
          slice: 'Corta, lapea y pule en WPR-08',
          clean: 'Pasa la limpieza de entrada en CLN-09',
          epi: 'Crece la capa de dispositivo en especificación en EPI-20',
          handEpi: 'Pasa el sustrato terminado adelante',
        },
      },
      holdTheClass: {
        Title: 'Mantén la clase',
        Briefing: 'Certificación de rutina. Demuestra que la bahía mantiene su clase ISO donde importa: en el producto, no en el monitor de pared. El contador portátil cuesta una hora; para eso existe.',
        Debrief: 'El monitor de pared está atornillado donde está la pared, y lo que importa es el aire un metro por encima del producto abierto. Pagaste una hora por saber la diferencia. Ese hábito es la sala.',
        objective: {
          enter: 'Entra en la bahía y abre GOWN-00',
          workzone: 'Pasa el contador portátil por la zona de trabajo',
        },
      },
      theWallLies: {
        Title: 'La pared miente',
        Briefing: 'La densidad de defectos sube en los lotes que pasan por esta bahía, y el monitor de pared lee cómodamente en clase. Alguien se equivoca. Averigua quién, y dilo.',
        Debrief: 'Las batas habían superado su vida de lavado: una fuente que la ventilación nunca alcanza a diluir antes de llegar a la oblea. La pared lee la sala; la oblea lee a la persona inclinada sobre ella.',
        objective: {
          workzone: 'Mide donde está el producto',
          call: 'Haz el diagnóstico, correctamente',
        },
      },
      twoRoomsAway: {
        Title: 'A dos salas del síntoma',
        Briefing: 'Son las 02:00. ETCH-01 está fuera de especificación en selectividad y uniformidad, la temperatura de su chuck va caliente, y la misma alarma aparece en todos los grabadores del edificio. Los lotes se amontonan en grabado. En el grabador no hay nada roto. Averigua qué lo está, decide qué hace la línea mientras esperas y deja el edificio sano —en ese orden, o la línea pagará cada hora que pases en la sala equivocada.',
        Debrief: 'La avería era el chiller del subfab; el grabador era el síntoma, a dos salas de distancia. Las cámaras volvieron pocas horas después de la llamada; la cola, no. Cada lote que esperó en grabado llegó tarde a su siguiente equipo, y la línea gastó su capacidad sobrante el resto del día en absorberlo. Abre la Línea para ver lo que costó tu decisión frente a las dos que no tomaste: por fecha pierde menos fechas a esta carga; retener arranques no hizo nada por la cola y quitó margen a los lotes retenidos. WIP, producción y tiempo de ciclo están atados por la ley de Little, y una línea al 95 % seguiría drenando mañana.',
        objective: {
          etch: 'Ve al grabador y mira la alarma',
          call: 'Haz la escalación correcta: no es este equipo',
          decide: 'Decide qué hace la línea mientras esperas: abre la Línea y elige',
          descend: 'Baja por las escaleras al subfab',
          repair: 'Restablece el suministro de agua fría',
          green: 'Deja el edificio sano',
        },
      },
      theResistClock: {
        Title: 'El reloj de la resina',
        Briefing: 'La misma noche, otra ruta: la resina de este producto caduca a las ocho horas entre el escáner y el grabador, y dos de las tres cámaras de cada grabador acaban de caer por control de temperatura. Cada lote que el escáner exponga a partir de ahora es un lote con el reloj corriendo hacia una cámara que no está. Decide qué hace el escáner y luego deja el edificio sano.',
        Debrief: 'La ventana nunca fue el problema de la avería. Los lotes que la avería devolvió ya estaban expuestos cuando cayeron las cámaras; nada podía salvarlos. Lo que hace una ventana es convertir cada hora de cola en grabado en retrabajo, todo el día, todos los días —y al 95 % de carga eso es casi toda la línea—. La puerta es lo que hace viable una ventana: retener la exposición hasta que el grabador pueda absorberla a tiempo, en el único paso cuya cola solo cuesta tiempo. Es la única retención correcta en esta planta, y solo porque retiene el lote antes de que arranque el reloj.',
        objective: {
          scanner: 'Ve al escáner y mira lo que espera para exponerse',
          decide: 'Decide qué hace el escáner: abre la Línea, la ventana de cola, y elige',
          call: 'Haz la escalación correcta en el grabador',
          descend: 'Baja por las escaleras al subfab',
          repair: 'Restablece el suministro de agua fría',
          green: 'Deja el edificio sano',
        },
      },
      theSilentTool: {
        Title: 'El equipo silencioso',
        Briefing: 'DEP-02 lleva horas depositando grueso y su propia pantalla dice que todo va bien, porque el termopar en el que confía es justo lo que falla. Nadie en la planta lo sabe aún. Metrología muestrea una fracción de sus visitas; una de ellas será el lote que te lo diga. Decide cuánto muestrear, encuentra el equipo y llámalo.',
        Debrief: 'Cada lote que pasó por ese equipo hasta que la inspección lo detectó está en riesgo, y ese número es la producción que pasa por el equipo por las horas que funcionó sin verse: la ley de Little desde el lado del rendimiento. Muestrear más compra horas de ese retraso y las paga en el equipo de metrología, en cuya cola está cada lote del edificio. La tasa es una decisión sobre cuál de las dos prefiere cargar la fábrica, y la Línea muestra ambos precios.',
        objective: {
          metrology: 'Ve a metrología: es el único equipo que puede ver esto',
          decide: 'Decide la tasa de muestreo: abre la Línea, la excursión, y elige',
          call: 'Llama el equipo de deposición correctamente',
        },
      },
      theDueChamber: {
        Title: 'La cámara pendiente',
        Briefing: 'La cámara C de ETCH-01 tiene su mantenimiento esta noche: cuatro horas planificadas, en el cuello de botella, en un turno que ya va retrasado. El jefe de turno prefiere que aguante la semana. Mira el equipo y decide, y prepárate para decir lo que costaría seguir.',
        Debrief: 'Un mantenimiento planificado cuesta sus horas y nada más. Uno aplazado es una apuesta a una cámara desgastada, y la Línea dice que la apuesta se pierde cada semana: la cámara falla, la reparación imprevista tarda el doble y cae a una hora que nadie eligió, en el equipo cuya hora es la hora de la fábrica. El jefe no se equivocaba en que cuatro horas esta noche duelen. Se equivocaba en lo que costarían ocho horas el jueves.',
        objective: {
          etch: 'Ve al grabador',
          decide: 'Decide el mantenimiento: abre la Línea, el mantenimiento, y elige',
        },
      },
      theStopwatch: {
        Title: 'El cronómetro',
        Briefing: 'La bahía lee más sucia con el mismo personal y tarda visiblemente más en calmarse tras un evento de puerta. Los filtros de techo se cambiaron el mes pasado. Diagnostícalo, y prepárate para defender la lectura en la que confiaste.',
        Debrief: 'El tiempo de recuperación lo fijan los cambios de aire y nada más, así que una recuperación lenta con filtros limpios es el edificio, no la sala. El instrumento que lo cazó fue un cronómetro. La reparación no está en esta planta — pero esa es otra orden de trabajo.',
        objective: {
          recovery: 'Cronometra la recuperación',
          call: 'Escala, correctamente',
        },
      },
      firstShift: {
        Title: 'Primer turno en el banco',
        Briefing: 'La densidad de defectos en inspección se ha duplicado en todo lo que pasa por CLN-09. El banco no ha alarmado y se ve exactamente como siempre. Tu primer diagnóstico: gánate una lectura antes de decidir.',
        Debrief: 'Una química fallando y la otra bien: la firma de un baño agotado, y nada en un líquido transparente parece nunca agotado. Pagaste la oblea testigo y te lo devolvió.',
        objective: {
          visit: 'Toma el turno en CLN-09',
          reading: 'Gánate al menos una lectura',
          call: 'Haz el diagnóstico, correctamente',
        },
      },
      invisibleFault: {
        Title: 'El fallo invisible',
        Briefing: 'La integridad del óxido de puerta falla en un producto y nadie encuentra un defecto. Los conteos de partículas son perfectos. Sea lo que sea, las mediciones baratas no lo ven.',
        Debrief: 'Una monocapa de cobre es invisible para todo inspector óptico del edificio, y entra al horno sobre obleas que parecen perfectas. La medición cara y destructiva existe precisamente porque este fallo existe.',
        objective: {
          metals: 'Ordena la medición de metales superficiales',
          call: 'Haz el diagnóstico, correctamente',
        },
      },
      shipsAnyway: {
        Title: 'Sale igual',
        Briefing: 'No hay nada reportado contra EPI-20. Cada visita está en especificación. La orden existe porque los datos de calificación se ven una pizca fuera de tendencia, y alguien arriba confía más en tu criterio que en los límites.',
        Debrief: 'El turno más difícil de la planta: una herramienta rota embarcando obleas que pasan. Nada te obligó a la decisión que acabas de tomar — exactamente por eso valía la pena tomarla.',
        objective: {
          reading: 'Gánate una lectura en EPI-20',
          call: 'Decláralo igualmente, correctamente',
        },
      },
      notThisTool: {
        Title: 'No es esta herramienta',
        Briefing: 'CLN-09 falla en partículas Y metales en la misma visita, y ambos baños se rehicieron esta mañana. Antes de ordenar ocho horas de reparaciones: ¿es este banco siquiera el problema?',
        Debrief: 'Fallar en ambas químicas a la vez es una firma que ningún baño puede producir: la última agua que ve la oblea es el agua del edificio. La reparación que te negaste a ordenar habría costado ocho horas y no habría arreglado nada.',
        objective: {
          phone: 'Llama a instalaciones por el panel de servicios',
          call: 'Escala, correctamente',
        },
      },
      takeTheStairs: {
        Title: 'Baja por la escalera',
        Briefing: 'La escalación está hecha: el circuito de enjuague lee 11 MΩ·cm y el lecho mixto pulidor es el sospechoso. Ahora eres el lado de instalaciones de la historia. Baja y deja el edificio sano.',
        Debrief: 'El lecho sostuvo 18,2 casi toda su vida y luego falló en los últimos puntos porcentuales — por eso la planta regenera por caudal, nunca por el medidor. El panel está en verde; el banco de arriba se recupera en su próximo lote.',
        objective: {
          descend: 'Baja por la escalera detrás de FAC-06',
          repair: 'Regenera el lecho mixto pulidor',
          green: 'Deja el edificio sano',
        },
      },
      oneEventTwoSymptoms: {
        Title: 'Un evento, dos síntomas',
        Briefing: 'Dos quejas con una hora de diferencia: el crecimiento ALD va rápido y no uniforme, y el óxido del horno sale grueso. Dos herramientas, dos grupos, dos tickets. Visita ambas antes de creer a ninguna.',
        Debrief: 'El agua en el colector de gas seco arruina el ALD porque el agua es su co-reactivo, y engrosa el óxido del horno por un mecanismo completamente distinto. Los síntomas no tenían nada en común salvo la causa — y los dos grupos no tenían motivo para comparar notas hasta que tú caminaste entre ellos.',
        objective: {
          ald: 'Visita la herramienta ALD',
          furnace: 'Visita el horno',
          call: 'Escala en cualquiera de las dos',
        },
      },
      wrongRepair: {
        Title: 'Deja el edificio sano',
        Briefing: 'Algo en el sub-fab está enfermo. La orden solo dice eso. Cada reparación de abajo cobra sus horas fuera o no la correcta — los manómetros son cómo evitas pagar dos veces.',
        Debrief: 'Los manómetros descartaron las plantas inocentes antes de que gastaras una sola hora en ellas: que la mayor parte del sub-fab esté bien es la mayor parte del diagnóstico. Si reparaste primero una planta sana, el informe muestra exactamente lo que costó esa certeza.',
        objective: {
          descend: 'Baja y lee los manómetros',
          repair: 'Repara la planta que de verdad está enferma',
          green: 'Deja el edificio sano',
        },
      },
    },
    orders: {
      Button: 'Órdenes de trabajo',
      Title: 'Órdenes de trabajo',
      BoardNote:
        'Un encargo, un edificio en cierto estado y la expectativa de evidencia. Aceptar ' +
        'una orden arma el mundo; todo lo que hagas queda en el diario, y la orden se ' +
        'completa cuando la evidencia está en él. Sin puntuación: el informe final solo ' +
        'muestra lo que costó tu certeza.',
      TrackRoom: 'La sala',
      TrackTool: 'La herramienta que tienes delante',
      TrackBuilding: 'El edificio',
      TrackCapstone: 'La línea completa',
      ClassLink: 'Enlace de clase',
      LinkCopied: 'Enlace copiado',
      Accept: 'Aceptar',
      Again: 'Repetir',
      Abandon: 'Abandonar',
      Active: 'activa',
      Completed: 'completada',
      Done: 'Orden de trabajo completada',
      Cost: 'Lo que costó',
      TotalHours: 'Tiempo total de herramienta',
    },
    subfab: {
      mau: {
        Title: 'Unidad de aire de reposición MAU-1',
        Intro:
          'Aire exterior que entra, aire de sala limpia que sale. Deshumidificar es enfriar hasta el ' +
          'punto de rocío y recalentar; el invierno es vapor; la presión es lo que queda tras la extracción.',
        Readings: 'En la unidad',
        WeatherSummer: 'Día de diseño de verano',
        WeatherWinter: 'Día de diseño de invierno',
        WeatherMild: 'Día templado',
        Reset: 'Restablecer',
        KnobOutsideC: 'Temperatura del aire exterior',
        KnobOutsideRh: 'Humedad del aire exterior',
        KnobSupplyC: 'Consigna de impulsión',
        KnobSupplyRh: 'Consigna de humedad de impulsión',
        KnobFanPct: 'Ventilador de impulsión, % del diseño',
        KnobExhaustPct: 'Extracción de proceso, % del diseño',
        Flow: 'Impulsión / extracción',
        Moisture: 'Humedad entra / sale',
        DewPoint: 'Punto de rocío de impulsión',
        CoilLeaving: 'La batería de frío sale a',
        Dehumidifying: 'deshumidificando',
        Cooling: 'Batería de frío',
        Heating: 'Baterías de precalentamiento y recalentamiento',
        Steam: 'Vapor del humidificador',
        FanPower: 'Potencia del ventilador',
        Power: 'Potencia, enfriadora a su COP',
        Pressure: 'Presión de la sala limpia',
        WarnFreeze: 'Se pide a la batería de agua fría un punto de rocío cercano a la congelación: riesgo de escarcha.',
        WarnCooling: 'La batería de frío supera su capacidad nominal; la impulsión saldrá caliente y húmeda.',
        WarnHeating: 'Las baterías de agua caliente superan su capacidad nominal; la impulsión saldrá fría.',
        WarnSteam: 'El humidificador supera su capacidad nominal; la sala limpia se secará.',
        WarnPressureLow: 'Presurización perdida: el pasillo entra por las rendijas.',
        WarnPressureHigh: 'Sobrepresión: las puertas lucharán con sus cierres y el plénum de FFU se levantará.',
        Note:
          'Placa de características: 12 000 m³/h, 240 kW de frío, 130 kW de calor, 110 kg/h de vapor. ' +
          'Nada arriba siente aún esta unidad; los números son la psicrometría, no un modelo de fallo.',
      },
      BoardTitle: 'SERVICIOS DEL SUB-FAB',
      SkidNote:
        'La versión del edificio. Las reparaciones hechas aquí son reales: las horas se ' +
        'cobran igual, y solo la planta que de verdad está enferma recupera algo.',
      RepairCleared:
        'Reparación completa y circuito recalificado. El panel de servicios vuelve a estar ' +
        'en verde: sube y mira cómo se recuperan las herramientas.',
      RepairWasted:
        'Reparada, recertificada… y el síntoma de arriba no se ha movido. Esta planta ' +
        'estaba sana; las horas están gastadas.',
      RepairIdle: 'Nada estaba mal en esta planta. Trabajo preventivo, registrado y cobrado.',
      BeltsSlipping: 'patinando bajo carga',
      BeltsTight: 'tensas',
      reading: {
        headerLeakUp: 'Tasa de fuga del colector',
        headerAdder: 'Presión añadida en cada cámara',
        pcwSupply: 'Temperatura de suministro vs nominal',
        gasMoisture: 'Humedad del colector a la salida del secador',
        upwLoop: 'Resistividad del circuito, compensada a 25 °C',
        ahuFlow: 'Caudal de aire entregado vs diseño',
        ahuBelts: 'Inspección de correas',
        forelineMET05: 'Presión de línea previa, MET-05',
        forelineINSP13: 'Presión de línea previa, INSP-13',
        forelinePRB14: 'Presión de línea previa, PRB-14',
        trayCMP11: 'Bandeja de contención, línea CMP-11',
        trayECD12: 'Bandeja de contención, línea ECD-12',
        chemInterlock: 'Enclavamiento de suministro',
        mauFlow: 'Aire de reposición entregado frente al diseño',
        mauFilterDp: 'Pérdida de carga del prefiltro',
        roomPressure: 'Presión de la sala limpia frente al ambiente',
      },
      PumpsPulling: 'todas bombeando',
      TrayDry: 'seca',
      TraysDry: 'bandejas secas',
      InterlockShut: 'CERRADO: suministro a {{tag}} detenido',
      InterlockOpen: 'abierto, todas las líneas suministrando',
      Tight: 'estanco',
    },
    name: {
      SimFab: 'SimFab',
      Institute: 'Instituto para la Inteligencia Futura',
      Tagline: 'SimFab — De los átomos a las fábricas',
    },

    language: {
      Language: 'Idioma',
      Change: 'Cambiar idioma',
    },

    splash: {
      Eyebrow: 'De los átomos a las fábricas',
      Headline: 'Estás en la <em>esclusa de vestuario</em>.',
      Sub:
        '¡Te damos la bienvenida! Tu trabajo es operar los equipos del pasillo: abre un equipo, ' +
        'prepara un proceso, pide al técnico de la estación que te explique lo que está ' +
        'pasando, analiza los datos y genera informes. También puedes seguir las órdenes de ' +
        'trabajo.',
      KeyWalk: '<span>caminar · <kbd>Shift</kbd> para correr</span>',
      KeyLook: '<span>mirar alrededor · o <kbd>←</kbd><kbd>→</kbd> para girar</span>',
      KeyOpen: '<span>abrir un equipo</span>',
      KeyAsk: '<span>el asistente ejecuta el experimento por ti</span>',
      CapLook: 'arrastrar dcho.',
      CapOpen: 'clic',
      CapAsk: 'preguntar',
      KeyFind: '<span>busca cualquier equipo por nombre y salta a él</span>',
      EnterFab: 'Entrar en {{fab}}',
      CapTap: 'toca',
      CapDrag: 'arrastra',
      KeyWalkTouch: '<span>un equipo — caminas hasta él</span>',
      KeyLookTouch: '<span>mira alrededor</span>',
      KeyOpenTouch: '<span>abre el equipo que tienes delante</span>',
    },

    vehicle: {
      Hoist: 'Vehículo de transporte aéreo',
      Robot: 'Robot móvil',
      Carrying: 'Lleva el lote {{lot}} en el contenedor {{carrier}}',
      BoundFor: 'Con destino a {{tool}}, puerto de carga {{port}}',
      BoundEnd: 'Regresando por el carril',
      Cruising: 'circulando a {{speed}} m/s',
      Holding: 'detenido tras el vehículo de delante',
      Transferring: 'transfiriendo el contenedor en el puerto',
      Moving: 'avanzando a {{speed}} m/s',
      Delivering: 'entregando en el puerto de carga',
      Yielding: 'parado — hay una persona en su campo de seguridad',
      Dismiss: 'Pulsa <kbd>Esc</kbd>, o haz clic en el suelo, para cerrar',
      DismissTouch: 'Toca el suelo para cerrar',
    },
    talk: {
      Title: 'Hablando con {{name}}',
      Placeholder: 'Pregúntale algo a {{name}}…',
      Send: 'Enviar',
      Close: 'Terminar la conversación',
      Stop: 'Detener',
      Stopped: 'detenida',
      Thinking: '…',
      Local: 'respuesta local — modelo alojado inaccesible',
      VoiceOn: 'Leer las respuestas en voz alta',
      VoiceOff: 'Dejar de leer las respuestas en voz alta',
      MicStart: 'Di tu pregunta',
      MicStop: 'Dejar de escuchar',
      Listening: 'Escuchando…',
      MicWhere: 'El dictado lo hace tu navegador, que puede enviar el audio a su servicio de voz.',
      MicDenied: 'Sin acceso al micrófono. Permítelo en el navegador para hablar.',
      predict: {
        Waiting: 'Antes de que vuelva:',
        Placeholder: 'Di en qué sentido crees que va…',
        Skip: 'Muéstramelo sin más',
        SkipTitle: 'Ver el resultado sin arriesgar una respuesta',
      },
      knock: {
        Wants: '{{name}} quiere hablar contigo',
        Listen: 'Escuchar',
        Dismiss: 'Ahora no',
        OutOfSpec: 'sobre la ejecución en {{tag}} que se salió de especificación',
        WrongCall: 'sobre el diagnóstico en {{tag}}',
        Stalled: 'sobre la orden de trabajo que tienes abierta',
      },
      experiment: {
        Running: 'Lo estoy ejecutando…',
        Progress: 'condición {{done}} de {{total}}',
        Unavailable: 'No puedo llegar al equipo desde aquí: pulsa E en él y pregunta allí.',
        Failed: 'Esa ejecución no terminó. El equipo queda intacto.',
        LockedOut: '{{tag}} está bloqueado por mantenimiento preventivo: no se ejecuta nada hasta que se firmen las comprobaciones',
      },
      line: {
        ChambersDown: 'el agua de refrigeración de proceso está caliente y dos de las tres cámaras de cada grabador del edificio están paradas por ello: una cámara por grabador está cargando con todo el grabado. Los lotes se amontonan en grabado en toda la planta.',
        Scenario: {
          chiller: 'el chiller disparado',
          qtime: 'la ventana de cola',
          excursion: 'la excursión',
          pm: 'la cámara pendiente',
        },
        Decided: {
          chiller: {
            reroute: 'dejar que todo haga cola en las cámaras que quedan, por orden de llegada',
            hold: 'retener los arranques hasta que vuelvan las cámaras',
            'by-date': 'grabar por fecha de entrega',
          },
          qtime: {
            run: 'dejar correr el escáner',
            gate: 'cerrar la puerta del escáner hasta que el grabador pueda grabar a tiempo',
          },
          excursion: {
            '0.05': 'inspeccionar el 5 % de las visitas de metrología',
            '0.1': 'inspeccionar el 10 % de las visitas de metrología',
            '0.25': 'inspeccionar el 25 % de las visitas de metrología',
            '0.5': 'inspeccionar el 50 % de las visitas de metrología',
          },
          pm: {
            now: 'hacer el mantenimiento ahora',
            defer: 'seguir con la cámara toda la semana',
          },
        },
      },
      demo: {
        // The controls a scene puts under itself in a conversation, where
        // its panel would have wired them to a recipe (app/talk/demoScenes.tsx).
        knob: {
          RingWear: 'Desgaste del anillo focal',
          IonEnergy: 'Energía media de los iones',
          Downflow: 'Velocidad de flujo descendente',
          Tilt: 'Inclinación del haz respecto al eje del cristal',
        },
        Show: 'Volver a mostrarla',
        Loading: 'Preparándola…',
        Failed: 'Esa no se puede mostrar aquí.',
        'crystal-lattice': {
          Title: 'Qué es un cristal de silicio',
          About: 'Átomos de silicio en la red del diamante, vibrando con dinámica molecular real.',
          Watch: 'Todos los átomos vibran y ninguno se va. Eso es un sólido: cada uno sujeto sólo por sus vecinos.',
          Try: 'Sube "Temperatura del cristal" hacia los 1600 K y mira crecer la vibración; luego bájala otra vez. Ninguno abandona nunca su sitio.',
        },
        bands: {
          Title: 'Por qué el silicio es un semiconductor',
          About: 'La estructura de bandas del silicio y la brecha que decide todo lo demás.',
          Watch: 'Fíjate en la brecha entre la banda llena y la vacía. Demasiado ancha y nada conduce; demasiado estrecha y nada se apaga.',
          Try: 'Aquí no hay nada que ajustar: las bandas se resuelven una sola vez. Lee la separación vertical en cada punto del eje inferior y busca dónde es más estrecha.',
        },
        carriers: {
          Title: 'De dónde salen los portadores del silicio dopado',
          About: 'Congelación, extrínseco e intrínseco: cuántos portadores da realmente un nivel de dopado.',
          Watch: 'Mueve el control de dopado y mira cómo el número de portadores se niega a seguirlo. En el extremo frío la mayoría de los dopantes aún retienen su electrón.',
          Try: 'Arrastra el control de dopado de un extremo al otro y observa pasar las tres regiones: congelación, extrínseca y por fin intrínseca, donde manda el propio silicio.',
        },
        'si-surface': {
          Title: 'Una superficie de silicio, y por qué reacciona',
          About: 'La superficie (100) reconstruyéndose, y los enlaces libres sobre los que trabaja todo proceso de esta planta.',
          Watch: 'Observa cómo se emparejan los átomos de la capa superior. Esos dímeros inclinados son los enlaces libres con los que reacciona cada proceso de esta planta.',
          Try: 'Déjalo asentarse, luego sube el control de temperatura y mira cómo los dímeros conservan su inclinación: el calor no deshace la reconstrucción.',
        },
        'diffusion-hop': {
          Title: 'Cómo se mueve un átomo dentro de un sólido',
          About: 'Un intersticial saltando por el silicio, cada vez más caliente, y la recta de Arrhenius que traza.',
          Watch: 'Espera a que el átomo marcado intercambie sitio con un vecino: cada salto es un evento de difusión. Sube el horno y llegan más deprisa, y el cristal perfecto no salta nunca.',
          Try: 'Sube "Temperatura del horno" hasta unos 1600 K y pulsa Run otra vez: los saltos llegan varias veces más rápido. Luego cambia a "Cristal perfecto (control)" y mira cómo se queda en cero por caliente que lo pongas.',
        },
        'film-growth': {
          Title: 'Cómo crece una capa, átomo a átomo',
          About: 'Átomos de vapor que llegan a una oblea caliente y se quedan, con la curva de crecimiento que forman.',
          Watch: 'Llegan átomos, unos se quedan y otros vuelven a marcharse. La curva de al lado es la capa engrosándose, y donde se dobla es donde llegada y salida se igualan.',
          Try: 'Baja "Temperatura de la oblea" y vuelve a ejecutarlo: una oblea más fría retiene más de lo que llega, así que la curva sube más cerca de toda la carga de vapor.',
        },
        tunnelling: {
          Title: 'Electrones atravesando por efecto túnel un óxido de puerta de unos pocos átomos de espesor',
          About: 'Un paquete de ondas electrónico frente a un óxido de puerta hecho de unas pocas capas de átomos: casi todo rebota en franjas, y una copia tenue lo atraviesa por efecto túnel, ampliada para que se vea.',
          Watch: 'Mira cómo el paquete se ensancha al viajar y luego se apila contra los átomos en franjas brillantes: las ondas incidente y reflejada cruzándose. Del otro lado, ampliada, una copia tenue sigue a través de una pared que no tenía energía para escalar. Eso es el efecto túnel.',
          Try: 'Baja "Espesor del óxido" a 0,6 nm, dos capas de átomos, y mira cómo se ilumina la parte tras la pared; la etiqueta de ampliación te dice cuánto. Luego pulsa "Óxido como bloque" para cambiar los átomos por la barrera lisa que promedian, y verás pasar la misma fracción. Pulsa "Volumen (3D)" para la misma pared como capas de esferas, resuelta en un hilo de trabajo en unos segundos.',
        },
        'plasma-emission': {
          Title: 'Por qué el resplandor te dice que la película se acabó',
          About: 'El punto final del grabador es un espectrómetro sobre el plasma. Cada línea es un átomo cayendo entre dos niveles, excitado por electrones de la cola de la distribución.',
          Watch: 'Mire las dos trazas: la banda de SiF se apaga cuando se despeja el silicio, y la línea de flúor sube en ese mismo instante porque ya nada consume flúor.',
          Try: 'Baje T_e de 3 a 2 eV y vea cómo la línea de argón a 750 nm se desploma mientras la banda de 440 nm apenas cambia: un umbral de 13,5 eV frente a uno de 2,9 eV.',
        },
        'dopant-atom': {
          Title: 'Un dopante es un átomo de hidrógeno, hasta que hay demasiados',
          About: 'El electrón sobrante de un átomo de fósforo lo orbita como en el hidrógeno, escalado por el cristal: una órbita de 2,4 nm y decenas de meV. Resuelto con la ecuación de Schrödinger, no dibujado.',
          Watch: 'Mire la órbita sombreada de la izquierda con tres reglas: el radio de Bohr, el radio medio y la mitad del camino al siguiente dopante. A la derecha, la energía de enlace cae con el dopado y llega a cero en la transición de Mott.',
          Try: 'Suba "Concentración de dopante" por encima de 10¹⁸ y vea cómo la regla de la mitad avanza hacia la órbita mientras el apantallamiento la hincha; luego el estado desaparece y el electrón es libre. Cambie a GaAs para ver el modelo en su mejor caso.',
        },
        'photon-counting': {
          Title: 'De qué está hecha una imagen',
          About: 'La imagen aérea entregada como fotones contados, una extracción de Poisson por píxel, luego difuminada por la resina y revelada. La rugosidad del borde impreso se mide, no se supone.',
          Watch: 'Mire la imagen central: a 20 mJ/cm² de EUV la imagen es grano, unos 100 fotones por píxel, y el borde revelado de la derecha serpentea. La curva discontinua es la misma imagen hecha de fotones ArF, √14 más suave.',
          Try: 'Baje "Dosis" a 2 mJ/cm² y vea la rugosidad subir como 1/√dosis; cambie a "La imagen EUV, fotones ArF" y el mismo borde se alisa. Reduzca el alcance de la resina a 2 nm con 0,5 mJ/cm² y aparecen filas con defecto.',
        },
        'channel-quantisation': {
          Title: 'Por qué el umbral no está donde dice la fórmula',
          About: 'La compuerta dobla la banda en un pozo de un nanómetro de ancho, y un pozo tan estrecho solo permite niveles discretos. La carga del canal se aparta del óxido, y la tarjeta de puntas mide la diferencia.',
          Watch: 'Mire las formas sombreadas de la izquierda: cada electrón del canal es cero en el óxido y tiene su máximo a un nanómetro. A la derecha, ambas capacitancias se quedan cortas de la del óxido; la cuántica continua queda más abajo, porque la carga de cada voltio llega unos ångströms más lejos.',
          Try: 'Suba la "Tensión de compuerta" y vea cómo los niveles bajan y las formas se aprietan hacia el óxido: más cerca, pero nunca sobre él. Reduzca el "Espesor del óxido" a 1 nm y la brecha entre las dos curvas de capacitancia crece: unos ångströms de separación pesan más cuando el óxido solo tiene diez.',
        },
        'raman-strain': {
          Title: 'La línea que un equipo Raman ve moverse',
          About: 'Los fonones del silicio a partir de las constantes de fuerza reales de Stillinger–Weber, y el modo óptico del centro de zona del que un espectrómetro Raman lee la deformación.',
          Watch: 'Mire las seis ramas, tres acústicas desde cero y tres ópticas cerca de 593 cm⁻¹, y la línea Raman al lado con los 520 medidos marcados: el modelo se pasa, y lo dice.',
          Try: 'Lleve "Deformación biaxial" a −1 % y la línea sube y se desdobla en un singlete y un doblete; llévela a +1 % y baja. Cambie a Ge y todo cae: átomos más pesados sobre enlaces más blandos.',
        },
        'quantum-well': {
          Title: 'Lo que unos pocos nanómetros de aleación le hacen a un hueco',
          About: 'Una capa delgada de silicio-germanio entre silicio es una caja para un hueco, y el espesor crecido es el ancho de la caja. Los niveles permitidos se resuelven a partir del perfil crecido, no se dibujan a mano.',
          Watch: 'Mire la escalera de niveles discontinuos dentro del pozo, y la curva sombreada sobre el más bajo, que muestra dónde está realmente ese hueco. Cada nivel es una energía que la capa permite; entre ellos no hay nada que un hueco pueda tener.',
          Try: 'Baje "Espesor crecido" desde 8 nm y vea subir todos los niveles; luego vea cómo el más alto alcanza la pared y desaparece: el pozo que tenía cuatro pasa a tres, y después a dos. Suba "Fracción de germanio" para profundizar el pozo y volverán.',
        },
        'resist-absorption': {
          Title: 'Por qué una resina responde a un color de luz y no a otro',
          About: 'La banda de absorción de una molécula conjugada, calculada, con las longitudes de onda de exposición marcadas encima. Alargue la cadena y la banda recorre el espectro, pasando una a una por cada línea.',
          Watch: 'Mire qué líneas se vuelven ámbar. Una molécula solo responde a la línea sobre la que se apoya su banda, y la banda se mueve porque el electrón π está en una caja cuya longitud es la conjugación: una cadena más larga tiene niveles más juntos y absorbe luz más roja.',
          Try: 'Lleve "Longitud de conjugación" de un enlace doble hasta once. En cuatro la banda se apoya en la línea i de 365 nm; hacia seis ya la ha dejado atrás y se posa en la línea g, y la i vuelve a apagarse. Nada de lo que puede fijar aquí cae sobre el ArF de 193 nm, y esa es la razón de que los 193 nm exigieran otra familia de molécula.',
        },
        chemisorption: {
          Title: 'Un precursor reaccionando con la oblea, enlace a enlace',
          About: 'Silano sobre una oblea de silicio con un campo de fuerzas reactivo: un enlace Si-H se rompe contra un enlace libre, se forma un enlace Si-Si y ese silicio pasa a la película. Las varillas son el orden de enlace, no un guion.',
          Watch: 'Mire cómo una molécula baja y se apoya en la superficie, y luego le cede un hidrógeno: la varilla entre ambos desaparece y surge otra hacia la oblea. La curva verde cuenta el silicio ya incorporado; la azul, el hidrógeno que queda en la superficie y que bloquea a la molécula siguiente.',
          Try: 'Pulse "Radicales sililo (SiH₃, CVD por plasma)" y baje "Temperatura de la oblea" a 600 K: un radical se adhiere donde un silano entero no lo hace, y por eso la CVD por plasma funciona cientos de kelvin más fría. Luego pulse "Terminada en hidrógeno" y vea cómo el crecimiento espera a que el hidrógeno se vaya.',
        },
        'electron-diffraction': {
          Title: 'Una onda electrónica atravesando dos rendijas',
          About: 'Un paquete de ondas electrónico gaussiano encuentra dos rendijas, o una fila de átomos, y el borde de la caja cuenta por ángulo lo que llega. Los haces caen donde dice sen θ = mλ/p, y desaparecen cuando la longitud de onda supera el paso.',
          Watch: 'Mire cómo el paquete se ensancha al avanzar, se divide en las rendijas y sale como haces separados. El gráfico es la pantalla: cada pico se apoya en una línea discontinua, y esas líneas son los órdenes que predice el paso. Nadie puso ahí los picos: lo hizo la onda.',
          Try: 'Baje "Energía del electrón" hacia 4 eV y vea cómo los órdenes exteriores se abren y luego desaparecen cuando la longitud de onda supera el paso. Pulse "Una fila de átomos, a 0,5 nm" para el blanco más fino, donde hace falta una longitud de onda mucho menor para ver estructura alguna.',
        },
        'light-absorption': {
          Title: 'Por qué una oblea de silicio es transparente a la luz roja',
          About: 'El sustrato absorbiendo luz, calculado a partir de sus bandas de enlace fuerte: cada salto de una banda llena a una vacía, en toda la zona. De ahí sale el índice de refracción que una medida de espesor da por supuesto.',
          Watch: 'Mire dónde la curva despega del suelo. Para el silicio es cerca de 3 eV, su banda prohibida directa, muy por encima de los 1,1 eV que se citan de costumbre: un fotón por sí solo no puede aportar el momento que exige un salto indirecto. Las líneas ámbar son el barrido del reflectómetro, y casi todo él cae en la parte transparente.',
          Try: 'Pulse "Ge" y después "GaAs". Ambos empiezan a absorber mucho más abajo y se tragan todo el barrido visible, y por eso ninguno sirve de ventana, y por eso la oblea bajo una película es la cosa transparente que un ajuste de espesor necesita que sea.',
        },
        'doped-lattice': {
          Title: 'El átomo dopante, en el cristal donde aterrizó',
          About: 'Boro en un sitio de silicio, vibrando bajo dinámica molecular real: lo que «tipo p» es en realidad.',
          Watch: 'Los átomos con halo son boro en sitios de silicio. Esa sustitución ES el dopado: no una propiedad de la oblea, sino un átomo en el asiento equivocado, repetido. Mira la gráfica: el boro, ligero, oscila visiblemente más rápido que el silicio que lo rodea.',
          Try: 'Arrastra «Concentración de dopante (exagerada)» arriba y abajo, y luego pulsa «Resaltar dopantes» para quitar el halo y ver las proporciones reales. El dopado real es de partes por millón: invisible en 216 átomos, y por eso la vista hace trampa.',
        },
        channelling: {
          Title: 'Por qué una implantación se inclina siete grados',
          About: 'La profundidad que alcanza realmente una implantación, y la inclinación que la decide.',
          AxisDepth: 'profundidad en la oblea (nm)',
          AxisIons: 'iones detenidos',
          LabelQualified: 'inclinado 7° (cualificado)',
          LabelLive: 'esta inclinación',
          Status: 'Ángulo crítico ψ₁ = {{a}}° · canalizado el {{b}}% del haz · el 1% más profundo llega a {{c}} nm',
          Tracing: 'Trazando 3.000 iones…',
          Watch: 'La curva discontinua es la receta cualificada de 7°. Con inclinación cero, un cuarto del haz encuentra una fila abierta y la atraviesa: misma dosis, misma energía, el doble de profundidad.',
          Try: 'Arrastra «Inclinación del haz respecto al eje del cristal» de 0 a 12° y mira dónde muere la cola profunda. Sobrevive casi intacta a 2° y 4°, y luego se derrumba — y el ángulo al que se derrumba es el ψ₁ impreso bajo la gráfica.',
        },
        sheath: {
          Title: 'La vaina sobre el borde de la oblea',
          About: 'La caída de tensión que convierte iones a la deriva en un haz vertical, y cómo la inclina un anillo focal gastado.',
          Watch: 'La banda brillante sobre la oblea es la caída de tensión. Las curvas de nivel son el camino que sigue un ion: planas sobre la oblea, dobladas donde el anillo focal se ha desgastado.',
          Try: 'Lleva "Desgaste del anillo focal" de 0 a 100% y mira cómo las curvas de nivel sobre el borde de la oblea pasan de planas a dobladas. Esa curvatura es donde los iones dejan de llegar en vertical.',
        },
        'trench-arde': {
          Title: 'Por qué una zanja estrecha graba más despacio',
          About: 'Cuatro zanjas de distinta anchura bajo un mismo plasma, y las estrechas quedándose sin reactivo.',
          Watch: 'Cuatro zanjas, un solo plasma. Mira cómo el reactivo llega al fondo de la ancha y nunca acaba de llegar al fondo de la estrecha.',
          Try: 'Sube "Energía media de los iones" desde 93 eV y mira cómo la zanja estrecha empeora en vez de mejorar: iones más duros hacen que la superficie consuma reactivo más deprisa de lo que puede llegar por un hueco estrecho.',
        },
        'thermal-budget': {
          Title: 'Qué le hace el calor a un perfil de dopante',
          About: 'Cuánto se mueve el dopante en un proceso, la integral de D(T) en el tiempo: dos implantaciones iguales, una con un pico de diez minutos a 1200 °C dentro de un proceso a 900 °C.',
          Watch: 'Dos obleas con la misma implantación de boro. La de la izquierda se calienta a 1200 °C durante diez minutos a mitad del proceso; la de la derecha se queda a 900 °C todo el tiempo. Mira cuánto más se extiende el boro en la izquierda.',
          Try: '',
        },
        'drive-in': {
          Title: 'Un recocido de difusión, de principio a fin',
          About: 'Boro empujado hacia dentro del silicio en un horno, con la temperatura y el perfil durante la marcha.',
          Watch: 'Boro entrando en el silicio. Mira cómo baja el frente y cómo las curvas de la derecha suben una tras otra según cada sensor lo ve llegar.',
          Try: 'Usa la consigna del horno bajo el lienzo: cien grados valen mucho más que duplicar el tiempo, y se ve en lo rápido que suben las curvas de los sensores.',
        },
        'line-chiller': {
          Title: 'La línea: lo que el chiller disparado le hizo a la planta',
          About: 'El edificio como una cola: dos de tres cámaras de grabado paradas seis horas y tres respuestas posibles del turno, cada una medida lote a lote frente a la misma semana sin avería.',
          Watch: 'La línea azul es lo que pasa si nadie decide. Mira cómo los lotes en grabado se disparan cuando caen las cámaras, y cuánto tiempo después de que vuelvan sigue pagándolo la línea. Luego mira la tabla: la misma avería, tres decisiones, y lo que costó cada una en fechas.',
          Try: 'Cambia la carga al 95 % y mira cómo la misma avería de seis horas tarda días en drenar. Luego cambia la llamada a las 08:00 y mira lo que costaron seis horas en el equipo equivocado.',
        },
        'bay-airflow': {
          Title: 'El flujo descendente de una bahía',
          About: 'El aire laminar que arrastra las partículas hacia abajo, y lo que le hace una persona de pie dentro de él.',
          Watch: 'Mira cómo el aire baja recto y gira hacia fuera al llegar al suelo. Lo que lleva se va con él, salvo que alguien esté en medio.',
          Try: 'Baja "Velocidad de flujo descendente" hacia 0,10 m/s y mira cómo se rompe la columna recta. Esa es la diferencia entre un aire que barre las partículas y otro que las deja vagar.',
        },
      },
      cast: {
        sarah: { Name: 'Lucía García', Short: 'Lucía', Role: 'Técnica de grabado', Post: 'la HMI de ETCH-01 en la bahía 04' },
        jake: { Name: 'Javier Martínez', Short: 'Javier', Role: 'Técnico de litografía', Post: 'la HMI de la pista LITH-03 en la bahía 05' },
        emily: { Name: 'Carmen López', Short: 'Carmen', Role: 'Técnica de CMP', Post: 'la HMI de CMP-11 en la bahía 08' },
        nicole: { Name: 'Marta Ruiz', Short: 'Marta', Role: 'Técnica de implantación iónica', Post: 'la HMI de IMP-10 en la bahía 03' },
        tom: { Name: 'Antonio Fernández', Short: 'Antonio', Role: 'Técnico de mantenimiento', Post: 'el panel abierto de THRM-04 en la bahía 02, subido al taburete' },
        owen: { Name: 'Sergio Castillo', Short: 'Sergio', Role: 'Técnico de epitaxia', Post: 'la HMI de EPI-20 en la bahía 02' },
        grace: { Name: 'Isabel Moreno', Short: 'Isabel', Role: 'Técnica de prueba de obleas', Post: 'la HMI de PRB-14 en la bahía 09' },
        laura: { Name: 'Elena Vargas', Short: 'Elena', Role: 'Técnica de metrología', Post: 'la HMI de MET-05 en la bahía 07' },
        ryan: { Name: 'Pablo Navarro', Short: 'Pablo', Role: 'Técnico de deposición', Post: 'la HMI de DEP-02 en la bahía 04' },
        hannah: { Name: 'Beatriz Romero', Short: 'Beatriz', Role: 'Técnica del escáner EUV', Post: 'la HMI de EUV-22 en la bahía 06' },
        brian: { Name: 'Andrés Molina', Short: 'Andrés', Role: 'Técnico de metrología CD-SEM', Post: 'la HMI de SEM-23 en la bahía 06' },
        claire: { Name: 'Paula Serrano', Short: 'Paula', Role: 'Técnica de criogenia', Post: 'la HMI de QDT-24 en la bahía 12' },
        kevin: { Name: 'Diego Sánchez', Short: 'Diego', Role: 'Supervisor de turno', Post: 'el pasillo, recorriendo la planta' },
        marcus: { Name: 'Marcos Herrera', Short: 'Marcos', Role: 'Técnico de equipos de grabado', Post: 'la parte trasera de ETCH-01, en el pasillo de servicio detrás de la bahía 04, con un panel trasero quitado' },
        rachel: { Name: 'Raquel Domínguez', Short: 'Raquel', Role: 'Técnica de equipos de deposición', Post: 'el rack de soporte detrás de DEP-02, en el pasillo de servicio detrás de la bahía 04' },
        dave: { Name: 'David Salazar', Short: 'David', Role: 'Electricista de instalaciones', Post: 'el centro de control de motores (MCC) del pasillo de servicio, detrás de la bahía 08' },
        megan: { Name: 'Marina Ortega', Short: 'Marina', Role: 'Técnica de instalaciones', Post: 'el pasillo de servicio detrás de la fila de ETCH-01, haciendo la ronda' },
      },
      floor: {
        Walking: 'en el pasillo, a la altura de la bahía {{bay}} ({{tags}}), en dirección {{toward}}',
        TowardGas: 'este, hacia la granja de gases',
        TowardGowning: 'oeste, hacia el vestuario',
        AtTool: '{{tag}}, en la bahía {{bay}}',
        Down: '{{tag}} está parado por mantenimiento preventivo',
        LayoutBallroom:
          'Una sala abierta: los mismos equipos en una sola planta, sin muros. Dos filas dan al pasillo principal; detrás de cada una hay una segunda fila de copias (ETCH-02 detrás de ETCH-01) hacia un pasillo exterior, sin personal y sin abrir, así que las preguntas van a la primera fila. Bombas, enfriadoras, generadores y armarios de gas están en el subfab. El vestuario está al oeste y la granja de gases al este; las bahías se numeran desde la 01 junto al vestuario, igual desde cualquier pasillo.',
        Layout:
          'Esto es Fab A. Una sala de bahías y galerías: las caras de los equipos van en un muro a cada lado de un solo pasillo, la galería detrás de cada muro, un lazo de transporte aéreo y robots de piso que llevan contenedores, el vestuario en el extremo oeste y la granja de gases en el este. Las bahías se numeran a lo largo del pasillo; la 01 es la más cercana al vestuario.',
      },
      local: {
        Greeting: 'Hola, soy {{name}}. Estoy en {{post}}. ¿Qué quieres saber?',
        Doing: 'Estoy en {{tag}}, {{state}}. La pantalla dice {{headline}} ({{caption}}).',
        DoingNoTool: 'Ahora mismo voy entre bahías, pasillo abajo.',
        About: '{{tag}} es un {{name}}: hace {{process}}. {{blurb}}',
        Screen: 'Ahora marca {{headline}} — {{caption}}. {{rows}}',
        Order: 'La orden de trabajo abierta es «{{title}}»: {{done}} de {{total}} objetivos cumplidos.',
        NoOrder: 'Ahora mismo no hay ninguna orden de trabajo abierta.',
        Down: '{{tag}} está parado por PM: panel quitado, tarjeta de bloqueo puesta, la barca fuera en el elevador. Vuelve a producción cuando se firmen las comprobaciones.',
        Showing: 'Eso no lo puedo ejecutar desde aquí, pero te lo puedo poner: {{title}}.',
        CantSay: 'Eso no te lo puedo decir desde aquí. Pulsa E en {{tag}} y pregúntale al asistente del equipo: él sí puede ejecutarlo.',
        CantSayNoTool: 'Eso no te lo puedo decir desde aquí. El panel del equipo sí puede ejecutarlo: pulsa E en el equipo.',
        NotHuman: 'No soy una persona: estás hablando con el simulador. Y ahora mismo tampoco hay ningún modelo de IA respondiendo; estas frases son fijas, salen del propio simulador.',
        States: { idle: 'en reposo', running: 'en marcha', alarm: 'en alarma' },
      },
    },
    hud: {
      OpenLive: 'Haz clic en el equipo, o pulsa <kbd>E</kbd>, para abrirlo',
      OpenPending: 'Haz clic, o pulsa <kbd>E</kbd>, para ver el alcance de la estación — aún sin simular',
      OpenLiveTouch: 'Toca el equipo para abrirlo',
      OpenPendingTouch: 'Toca para ver el alcance de la estación — aún sin simular',
      Idle: 'en reposo',
      Open: 'Abrir',
      Running: 'ejecutando {{speed}}x',
      Rate: 'TASA',
      NonUniformity: 'NU',
      Cleared: 'DESPEJADO',
      Time: 't',
    },

    panel: {
      Close: 'Esc — volver a la planta',
      Failed: 'Este panel no se pudo dibujar ({{message}}). Pulsa Esc para volver a la planta.',
    },

    hmi: {
      Status: 'Estado',
      Idle: 'En reposo',
      LastStep: 'Último paso',
      Down: 'Fuera de servicio',
      PmInProgress: 'Mantenimiento en curso',
    },

    xr: {
      EnterVr: 'Entrar en RV',
      ExitVr: 'Salir de RV',
      Unavailable: 'RV no disponible',
      ViewInVr: 'RV',
      ViewInVrTitle: 'Míralo en un visor de teléfono',
      HolderHint: 'Toca para recentrar · mantén pulsado para salir',
      NoMotion: 'Este teléfono no informa de hacia dónde apunta.',
      MotionDenied: 'Se denegó el acceso al movimiento. Actívalo en los ajustes del navegador para este sitio.',
    },

    routes: {
      Button: 'Rutas',
      ButtonTitle: 'El orden en que la oblea recorre la sala',
    },

    line: {
      DecideLabelQtime: 'Tu decisión, en el escáner',
      DecideLabelExcursion: 'Tu decisión: cuántas visitas de metrología se inspeccionan',
      DecideLabelPm: 'Tu decisión, sobre la cámara pendiente',
      ProductsLabel: 'Productos',
      ProductsOne: 'uno',
      ProductsTwo: 'dos — 30 % con doble patrón',
      ChartCtTitleMix: 'Tiempo de ciclo por producto, con redirigir (discontinua: sin avería)',
      ProductsThree: 'tres — 25 % EUV, 3 % chips de cúbits',
      ProductEuv: 'pieza EUV',
      ProductQuantum: 'chip de cúbits',
      NewToolsTitle: 'El escáner, el CD-SEM y la granja de refrigeradores, junto al cuello de botella',
      ColTool: 'Máquina',
      ColInBuilding: 'En el edificio',
      ColUtilisation: 'Ocupada',
      ColDown: 'Parada',
      ColWait: 'Espera media',
      NewToolsNote: 'Una cuarta parte de los inicios son una pieza de vanguardia: EUV-22 imprime su compuerta y sus cuatro metales más bajos, y SEM-23 mide cada una de esas exposiciones antes de que ETCH-01 pueda grabarla. Un tres por ciento son chips de cúbits, que dejan el front-end tras ocho operaciones y luego pasan dos días en un refrigerador. Lee la última columna frente a la tercera. La granja de refrigeradores trabaja a cerca de la mitad de su capacidad, muy por debajo de la grabadora, y sus lotes son los que más esperan, por un orden de magnitud: lo que un lote espera es que quede libre un refrigerador, y un refrigerador está ocupado dos días cada vez. La espera escala con lo que tarda el trabajo que tienes delante, no solo con lo ocupada que está la máquina. El escáner es la otra lección. El edificio tiene uno, está parado una hora de cada ocho, y no hay otro sitio al que enviar el lote. El disparo del chiller detiene a la vez las tres familias refrigeradas por agua: dos de cada tres cámaras de grabado, el escáner y todos los refrigeradores.',
      ProductA: 'patrón simple',
      ProductB: 'doble patrón',
      ChartCtTitleProducts: 'Tiempo de ciclo por producto, con redirigir',
      TabQtime: 'La ventana de cola',
      TabExcursion: 'La excursión',
      TabPm: 'El mantenimiento',
      QtimeRun: 'Dejarlo correr',
      QtimeGate: 'Cerrar la puerta del escáner',
      QtimeNote: 'La misma avería, en una ruta donde la resina caduca: un lote no grabado en las ocho horas siguientes a salir del escáner vuelve a decapar y a exponerse. Cada lote expuesto y luego atascado tras una cámara parada es un lote con el reloj corriendo. Dos jugadas: dejarlo correr, o cerrar la puerta del escáner —no exponer un lote que el grabador al que va no pueda grabar dentro de la ventana con las cámaras que tiene en pie— para que espere antes de que arranque el reloj.',
      QtimeChartTitle: 'Lotes devueltos al escáner desde la avería',
      ColReworks: 'Lotes devueltos',
      ColReworksControl: 'Sin avería',
      ColReworkHours: 'Horas de escáner perdidas',
      QtimeFinding: 'Mira primero la columna sin avería: una ventana de cola es una máquina de retrabajos por sí sola allí donde la cola de grabado la supera, que al 85 % son unos pocos lotes al día y al 95 % son la mayoría. La puerta es lo que hace viable la ventana —retiene las exposiciones en el escáner, cuya cola solo cuesta tiempo, hasta que el grabador pueda absorberlas a tiempo— y cuesta unas pocas fechas al 85 % y ninguna al 95 %. La avería en sí añade poco: los lotes que devuelve son los que ya estaban expuestos cuando cayeron las cámaras, y a esos no los salva ninguna política. Es el único caso en que retener lotes es correcto, y solo porque los retiene antes de que arranque el reloj.',
      ExcursionNote: 'Esta vez sin avería. A las dos de la madrugada un equipo de deposición se descontrola en silencio y sigue funcionando. Nada en la planta lo dice. Metrología inspecciona una fracción de sus visitas; un lote inspeccionado que pasó por el equipo malo es lo que lo detecta. Cada lote que pasa por el equipo hasta entonces está expuesto. Promediado sobre seis semanas con distinta suerte, porque una detección es una tirada.',
      ExcursionChartTitle: 'Lo que la tasa de muestreo compra, y cuesta',
      ExcursionChartX: 'fracción de visitas de metrología inspeccionadas',
      ExcursionChartY: 'lotes · espera × 10 h',
      ExcursionSeriesExposed: 'lotes expuestos, media',
      ExcursionSeriesWait: 'espera en MET-05, × 10 h',
      ColRate: 'Visitas inspeccionadas',
      ColDetect: 'Detectado tras',
      ColExposed: 'Lotes expuestos, media',
      ColExposedWorst: 'Peor semana',
      ColInspections: 'Inspecciones al día',
      ColMetWait: 'Espera en MET-05',
      ColMetUtil: 'MET-05 ocupado',
      ExcursionFinding: 'Los lotes expuestos son la producción que pasa por el equipo malo por las horas hasta detectarlo: la ley de Little desde el lado del rendimiento. Muestrear diez veces más lo detecta en la mitad de tiempo y reduce a la mitad los lotes en riesgo, y llena el equipo de metrología: su cola crece con la tasa y cada lote del edificio lo paga en tiempo de ciclo. La tasa de muestreo es una decisión sobre cuál de esos dos costes prefiere cargar la fábrica.',
      PmNow: 'Hacerlo ahora',
      PmDefer: 'Seguir funcionando',
      PmNote: 'Una cámara de un grabador tiene su mantenimiento pendiente a las dos de la madrugada. Hacerlo ahora: cuatro horas planificadas, esta noche. O seguir: una cámara desgastada que va a fallar, y cuando falle, la reparación imprevista tardará el doble, a una hora que nadie eligió. Aplazar es una apuesta, así que ambas se juzgan sobre ocho semanas con distinta suerte, frente a las mismas semanas sin ninguna de las dos.',
      ColChamberDown: 'Cámara parada, semana media',
      ColBeyondControl: 'Más que una semana sin ninguna',
      ColWorstWeek: 'Peor semana',
      ColFailed: 'Falló antes del mantenimiento',
      ColDelayLotHours: 'Lote-horas netas de retraso',
      PmFinding: 'El mantenimiento planificado cuesta sus {{pm}} horas y nada más. La cámara desgastada, con su vida reducida a {{worn}} horas, falló dentro de la semana todas las veces, y su reparación imprevista tardó {{repair}} horas frente a las {{mttr}} de la tabla: el doble del mantenimiento planificado, a una hora que nadie eligió, en el cuello de botella. La hora del cuello de botella es la hora de la fábrica: cada lote-hora de retraso de las dos últimas columnas es esa diferencia, sentida a lo largo de la línea.',
      DecideLabel: 'Tu decisión, mientras las cámaras están paradas',
      Decided: 'Elegiste: {{policy}}. Está en el diario.',
      Undecided: 'Aún sin decidir. La orden necesita una; los números de abajo son los mismos en cualquier caso.',
      Button: 'Línea',
      ButtonTitle: 'La fábrica como línea: lotes, colas y lo que una avería en el subfab les hace',
      Title: 'La línea — un chiller que se dispara a las 02:00',
      Intro: 'Diez bahías, una ruta, una cola en cada equipo. A las dos de la madrugada se dispara un chiller del subfab: el agua de refrigeración de proceso se calienta y dos de las tres cámaras de cada grabador pierden el control de temperatura. La tercera sigue funcionando. Los lotes siguen llegando. Estás de turno.',
      Bottleneck: 'Cuello de botella: {{tag}} — {{n}} grabadores de tres cámaras cada uno, unos {{starts}} arranques de oblea al día',
      LoadLabel: 'Carga de la línea',
      Load85: '85 % — una línea sana',
      Load95: '95 % — una línea llena',
      CallLabel: 'La llamada a instalaciones',
      CallPrompt: 'hecha al momento — cámaras de vuelta en 6 h',
      CallLate: 'hecha a las 08:00 — cámaras de vuelta en 12 h',
      ViewLabel: 'Vista',
      TabScenario: 'El chiller disparado',
      TabCurve: 'La curva de operación',
      Running: 'Ejecutando la línea…',
      PolicyReroute: 'Redirigir',
      PolicyRerouteNote: 'Todo hace cola en la cámara que queda, por orden de llegada. Lo que pasa si nadie decide.',
      PolicyHold: 'Retener arranques',
      PolicyHoldNote: 'Dejar de arrancar lotes hasta que vuelvan las cámaras.',
      PolicyByDate: 'Por fecha',
      PolicyByDateNote: 'El grabado procesa a continuación al lote más cercano a su fecha, sea urgente o no.',
      Control: 'Sin avería',
      ControlNote: 'la misma semana, con el chiller funcionando.',
      ChartWipTitle: 'Lotes en grabado por encima de la línea sin avería',
      ChartWipY: 'lotes',
      ChartX: 'horas después de la avería',
      ChartCtTitle: 'Tiempo de ciclo de los lotes que terminan, día a día',
      ChartCtY: 'horas',
      MarkTrip: 'avería',
      MarkRestore: 'vuelve',
      CostTitle: 'Lo que costó la avería, frente a la misma semana sin ella',
      ColPolicy: 'Política',
      ColPeak: 'Lotes en grabado, pico',
      ColDrain: 'Cola de vuelta a lo normal',
      ColDelayed: 'Lotes retrasados',
      ColMeanDelay: 'Retraso medio',
      ColDates: 'Fechas perdidas',
      ColHot: 'Lotes urgentes perdidos',
      ColChamberHours: 'Horas-cámara perdidas',
      DrainNever: 'no en dos semanas',
      HoursAfter: '{{h}} h tras la reparación',
      Hours: '{{h}} h',
      CostNote: 'Medido en las dos semanas posteriores a la avería, lote a lote frente al control: un lote está retrasado si terminó más tarde que sin avería, y una fecha se pierde si el control la cumplió. Las cámaras estuvieron paradas {{h}} h.',
      LittleTitle: 'La ley de Little en la línea estable',
      LittleLine: 'Antes de la avería: {{wip}} lotes en la línea = {{thr}} lotes/h × {{ct}} h de tiempo de ciclo (razón {{ratio}}). WIP, producción y tiempo de ciclo no pueden moverse por separado.',
      FindingsTitle: 'Lo que muestra la línea',
      FindingDrain: 'Las cámaras vuelven en horas; la cola no. Cada lote que esperó en grabado llega tarde a su siguiente equipo, y los de detrás llegan tarde a los suyos, y la línea gasta su capacidad sobrante —lo que queda tras las llegadas que ya tiene— en absorberlo. Al 85 % hay capacidad sobrante. Al 95 % casi no la hay, y las mismas seis horas tardan días en despejarse.',
      FindingByDate: 'Procesar el grabado por fecha de entrega pierde menos fechas a cualquier carga, porque «el más cercano a su fecha» se parece a «el más antiguo primero», y eso empuja los lotes fuera de una línea reentrante en vez de darles otra vuelta. Su precio solo aparece al 95 %: los lotes urgentes dejan de saltarse una cola que ahora es larga, y la mayoría falla. La regla correcta al 85 % es la que pierde el lote urgente al 95 %.',
      FindingHold: 'Retener los arranques no cambia nada en grabado. Los lotes que se amontonarán allí en las próximas seis horas ya están en el edificio; los que no arrancaste estaban a ocho días del grabado. Lo único que hace la retención es quitar seis horas de margen a los lotes retenidos.',
      CurveNote: 'Tiempo de ciclo frente a carga en la línea sana, sin avería. Plano y cerca del tiempo de proceso cuando la línea está medio vacía, curvándose en los ochenta, empinado en los noventa. La avería al 85 % y al 95 % son dos puntos de esta curva vistos desde dentro: la pendiente es lo que tarda una cola en despejarse una vez formada.',
      CurveTitle: 'Tiempo de ciclo frente a carga',
      CurveX: 'carga en el cuello de botella',
      CurveY: '× t. proceso',
      CurveSeries: 'línea sana',
      CurveHere: 'esta línea',
      ColLoad: 'Carga',
      ColCt: 'Tiempo de ciclo',
      ColCtRaw: '× tiempo de proceso',
      ColWip: 'Lotes en la línea',
    },
    finder: {
      Button: 'Buscar equipo',
      Placeholder: 'Escribe una etiqueta, una máquina o un proceso…',
      Hint: '<kbd>↑</kbd><kbd>↓</kbd> elegir · <kbd>Enter</kbd> ir · <kbd>Esc</kbd> cerrar',
      Empty: 'Ningún equipo coincide con «{{q}}».',
    },
    place: {
      subfab: {
        Name: 'Subfab (nivel de planta)',
        Where: 'Sala de planta, escalera detrás de FAC-06',
      },
    },
    zone: {
      entry: 'Entrada y protocolo',
      substrate: 'Sustrato — del silicio a las obleas',
      frontend: 'Front end — construir el dispositivo',
      metrology: 'Metrología y servicios',
      interconnect: 'Planarización e interconexión',
      sort: 'Inspección y clasificación',
      assembly: 'Ensamblaje, encapsulado y prueba',
      quantum: 'Dispositivos cuánticos: la misma fab, otro producto',
      support: 'Subplanta y suministro químico',
    },

    station: {
      bonding: {
        Name: 'Pegado de chip y soldadora de hilo',
        Process: 'Ensamblaje',
        Blurb:
          'Fuerza, potencia ultrasónica y tiempo: una ventana que es una superficie. Alcance ' +
          'definido; aún no simulado.',
      },
      finaltest: {
        Name: 'Prueba final y burn-in',
        Process: 'Prueba final',
        Blurb:
          'Cuánta prueba es suficiente es un cálculo, no una preferencia. Alcance definido; aún ' +
          'no simulado.',
      },

      etch: {
        Name: 'Equipo de grabado por plasma acoplado inductivamente',
        Process: 'Grabado por plasma',
        Blurb:
          'Grabado de puerta de polisilicio. Totalmente simulado: transporte en el reactor, física ' +
          'de la vaina y cinética de superficie.',
      },
      deposition: {
        Name: 'Clúster de deposición ALD / CVD',
        Process: 'Deposición',
        Blurb:
          'Al2O3 por ALD. Totalmente simulado: transporte de precursores, química de superficie ' +
          'limitada por sitios, saturación y ventana de temperatura.',
      },
      litho: {
        Name: 'Escáner de inmersión y pista de recubrimiento/revelado',
        Process: 'Litografía',
        Blurb:
          'La dosis y el enfoque fijan la dimensión crítica. Totalmente simulado: imagen aérea a ' +
          'partir de los órdenes de difracción, química de la resina, ventana de proceso y ' +
          'estadística de fotones.',
      },
      patterning: {
        Name: 'Celda de cuádruple patronado autoalineado',
        Process: 'Multipatronado',
        Blurb:
          'Imprime grueso, divide dos veces, y el paso final acaba siendo un espesor de película ' +
          'en vez de algo que el escáner resolviera. Totalmente simulado: geometría del ' +
          'espaciador, pitch walking, y por qué una media perfecta esconde un patrón roto.',
      },
      thermal: {
        Name: 'Horno vertical y RTP',
        Process: 'Procesos térmicos',
        Blurb:
          'Oxidación, difusión de dopantes y activación. Totalmente simulado: crecimiento ' +
          'Deal-Grove, difusión fickiana y el presupuesto térmico como cuenta compartida.',
      },
      metrology: {
        Name: 'Elipsómetro y reflectómetro',
        Process: 'Metrología y SPC',
        Blurb:
          'Detección de deriva y gráficos de control. Totalmente simulado: ajuste de reflectancia, ' +
          'capacidad del instrumento y la decisión de retener un lote o parar un equipo.',
      },
      facilities: {
        Name: 'Suministro de gases y abatimiento',
        Process: 'Instalaciones y seguridad',
        Blurb:
          'Vacío, suministro de gases y abatimiento. Totalmente simulado: conductancia, bombeo y el ' +
          'ensayo de tasa de subida que distingue una fuga de una cámara húmeda.',
      },
      gowning: {
        Name: 'Esclusa de vestuario y sala de protocolo',
        Process: 'Protocolo de sala limpia',
        Blurb:
          'Hasta que te vistes, eres la mayor fuente de partículas del edificio. Alcance ' +
          'definido; aún no simulado.',
      },
      crystal: {
        Name: 'Tirador de cristal Czochralski',
        Process: 'Crecimiento de cristal',
        Blurb:
          'Polisilicio y una semilla se convierten en un lingote monocristalino. Alcance ' +
          'definido; aún no simulado.',
      },
      waferprep: {
        Name: 'Línea de corte, lapeado y pulido',
        Process: 'Preparación de obleas',
        Blurb:
          'Un lingote se convierte en obleas, planas hasta la micra. Alcance definido; aún no ' +
          'simulado.',
      },
      epi: {
        Name: 'Reactor de epitaxia a presión reducida',
        Process: 'Epitaxia de silicio',
        Blurb:
          'Crece la capa donde realmente viven los dispositivos. Totalmente simulado: las dos resistencias de crecimiento de Grove en serie, el autodopado desde el sustrato que dopó el crecedor de cristales, y el deslizamiento y la neblina que vigilan la receta.',
      },
      clean: {
        Name: 'Banco de limpieza húmeda RCA',
        Process: 'Preparación de superficie',
        Blurb:
          'El proceso que más se ejecuta en la fábrica y el menos vistoso. Alcance definido; aún ' +
          'no simulado.',
      },
      implant: {
        Name: 'Implantador iónico de alta corriente',
        Process: 'Implantación iónica',
        Blurb:
          'El único proceso de la fábrica que cuenta sus propios átomos. Alcance definido; aún ' +
          'no simulado.',
      },
      cmp: {
        Name: 'Planarizadora químico-mecánica',
        Process: 'Planarización',
        Blurb:
          'La planitud es lo que hace posible el metal multinivel. Alcance definido; aún no ' +
          'simulado.',
      },
      plating: {
        Name: 'Celda de electrodeposición de cobre',
        Process: 'Interconexión',
        Blurb:
          'El cobre se deposita en lugar de grabarse, y tres aditivos hacen que rellene. Alcance ' +
          'definido; aún no simulado.',
      },
      inspection: {
        Name: 'Inspección óptica y revisión de obleas con patrón',
        Process: 'Inspección de defectos',
        Blurb:
          'Sensibilidad frente a tasa de falsos positivos, y lo que cuesta una firma. Alcance ' +
          'definido; aún no simulado.',
      },
      probe: {
        Name: 'Sonda y clasificación de obleas',
        Process: 'Prueba en oblea',
        Blurb:
          'Donde la oblea se convierte en chips y el rendimiento se convierte en un número. ' +
          'Alcance definido; aún no simulado.',
      },
      dicing: {
        Name: 'Adelgazado y singulación',
        Process: 'Adelgazado y corte de obleas',
        Blurb:
          'Bastante fino para apilarse, bastante frágil para importar. Alcance definido; aún no ' +
          'simulado.',
      },
      packaging: {
        Name: 'Moldeo, underfill y encapsulado avanzado',
        Process: 'Encapsulado',
        Blurb:
          'Donde un chip bueno se convierte en un producto malo, mecánicamente. Alcance ' +
          'definido; aún no simulado.',
      },
      euv: {
        Name: 'Escáner EUV con fuente de estaño',
        Process: 'Litografía EUV',
        Blurb:
          'La herramienta más importante de una fab moderna, y cuántica de principio a fin. Totalmente simulado: la ionización del estaño resuelta con la ecuación de Schrödinger, el plasma que produce luz de 13,5 nm, un espejo Mo/Si por matriz de transferencia y las cascadas de fotoelectrones que imprimen bordes rugosos.',
      },
      ebeam: {
        Name: 'CD-SEM e inspección por haz de electrones',
        Process: 'Metrología por haz de electrones',
        Blurb:
          'Medir lo que la luz no puede resolver. Totalmente simulado: la emisión Fowler-Nordheim del cañón con el motor de efecto túnel de THRM-04, la sonda que permiten la difracción y las aberraciones, y un perfil de barrido Monte Carlo cuyos bordes tiene que decidir la herramienta.',
      },
      quantum: {
        Name: 'Prueba criogénica de dispositivos cuánticos',
        Process: 'Caracterización de cúbits',
        Blurb:
          'La misma fab, las mismas herramientas, otro producto. Totalmente simulado: el óxido de la unión Josephson por matriz de transferencia, el hamiltoniano del transmón resuelto exactamente, el rendimiento frente a colisiones de frecuencia y el desdoblamiento de valles en el pozo de silicio que crece EPI-20.',
      },
      gas: {
        Name: 'Búnker de gases y distribución de químicos',
        Process: 'Manejo de gases y químicos',
        Blurb:
          'Silano, arsina, HF. La estación donde equivocarse no es un problema de chatarra. ' +
          'Alcance definido; aún no simulado.',
      },
    },

    assistant: {
      // Suggestion chips, keyed by intent id. The English example stays
      // in the intent as the source of record and is what actually routes.
      topic: {
        'ion-enhanced etching': 'grabado asistido por iones',
        'sheath and ion energy': 'la vaina y la energía iónica',
        'ion energy distribution': 'distribución de energía iónica',
      },
      example: {
        'etch-qual': '¿Está el equipo calificado para procesar producto?',
        'cmp-qual': '¿Está la pulidora calificada para procesar producto?',
        'litho-qual': '¿Está el escáner calificado para imprimir producto?',
        'thermal-qual': '¿Está el horno calificado para procesar producto?',
        selectivity: '¿Puedo simplemente subir el bias para alcanzar mi objetivo de velocidad?',
        'cmp-overpolish': '¿Cuánto sobre-pulido debería correr?',
        'cmp-speeds': '¿Por qué el plato y el portador giran a la misma velocidad?',
        'cmp-density': '¿Por qué se ahueca el pad de conexión cuando el array denso no?',
        'dep-saturation': '¿Está mi dosis realmente saturada?',
        'dep-window': '¿A qué temperatura debería correr esto?',
        'dep-purge': '¿Puedo acortar la purga para ganar producción?',
        'dep-conformality': '¿Por qué el fondo de mi zanja es más delgado?',
        'dep-uniformity': '¿Por qué mi película es más gruesa en el borde?',
        'fac-conductance': 'El vaciado es lento — ¿necesitamos una bomba más grande?',
        'fac-leak': 'La presión base está alta — ¿fuga o desgasificación?',
        'edge-fast': '¿Por qué mi oblea graba más rápido en el borde?',
        loading: '¿Por qué la misma receta corre más rápido en otro producto?',
        sheath: '¿Qué fija realmente la energía de los iones en la oblea?',
        endpoint: '¿Cómo sé cuándo terminó el grabado?',
        'what-if': '¿Qué pasa si subo la presión?',
        diagnose: 'No se cambió nada — ¿por qué el grabado va lento?',
        'litho-dose-focus': 'Mi CD está fuera de objetivo — ¿es dosis o foco?',
        'litho-window': '¿Tiene esta estructura una ventana de proceso usable?',
        'litho-stochastics': '¿Por qué exposiciones idénticas imprimen distinto?',
        'metro-trust': '¿Puedo fiarme de esta lectura de espesor?',
        'metro-capability': '¿Es este gauge lo bastante bueno para el trabajo?',
        'metro-signal': 'La carta alarmó — ¿es real?',
        'thermal-budget': 'Nadie cambió el implante — ¿por qué se movió la unión?',
        'thermal-oxide': '¿Por qué el óxido deja de crecer?',
        'thermal-junction': '¿Cómo consigo una unión menos honda sin perder resistencia de hoja?',
        'thermal-lateral': '¿La redistribución solo empuja el dopante hacia abajo?',
        'bond-window': '¿Cuál es la ventana de unión, y qué pasa en cada extremo?',
        'bond-wires': '¿Por qué la misma receta rinde peor en una pieza con más hilos?',
        'bond-imc': '¿Qué le hace el intermetálico a mis uniones con el tiempo?',
        'clean-longer': '¿Corro el SC-1 más tiempo para dejar la oblea más limpia?',
        'clean-queue': '¿Importa cuánto espera la oblea tras el baño de HF?',
        'clean-metals': '¿Para qué sirve realmente el baño SC-2?',
        'gown-monitor': 'El monitor dice que estamos en clase — ¿está limpia mi oblea?',
        'gown-garments': '¿Vestirse bien marca de verdad una diferencia medible?',
        'gown-recovery': '¿Cuánto tarda la bahía en recuperarse tras una perturbación?',
        'cz-segregation': '¿Por qué la resistividad difiere en los dos extremos del lingote?',
        'cz-voronkov': '¿Qué tan rápido puedo tirar este cristal?',
        'cz-oxygen': '¿De dónde viene el oxígeno de la oblea?',
        'dice-thin': '¿Cuánto puedo adelgazar las obleas antes de que empiecen a romperse?',
        'dice-chipping': '¿Puedo cortar más rápido sin desconchar los die?',
        'dice-wheel': '¿Se paga sola una muela de rectificado más fina?',
        'euv-dose': '¿Qué cuesta subir la dosis en obleas por hora?',
        'euv-power': '¿Cuánto más rápido iría el escáner con el doble de potencia de fuente?',
        'sem-landing': '¿Por qué no usar el CD-SEM con más energía de aterrizaje para una imagen más nítida?',
        'sem-current': '¿Haría más corriente de haz que la medición de CD repita mejor?',
        'cryo-attenuation': '¿Cuánta atenuación necesita la línea de excitación?',
        'cryo-frequency': '¿Por qué los cúbits se construyen a 5 GHz y no a 1 GHz?',
        'epi-hotter': '¿Por qué subir la temperatura deja de acelerar el crecimiento?',
        'epi-bake': '¿Puedo acortar el horneado en H2 para ahorrar ciclo?',
        'epi-faster': '¿Puedo simplemente subir el TCS y crecer la capa más rápido?',
        'ft-guardband': '¿Qué me cuesta una banda de guarda?',
        'ft-burnin': '¿Vale el burn-in el tiempo que cuesta?',
        'ft-coverage': '¿Cuánto tiempo de prueba es suficiente?',
        'gas-purge': '¿Cuántos ciclos de purga antes de poder abrir el panel?',
        'gas-detector': '¿Me avisará el detector a tiempo de salir?',
        'gas-abatement': '¿Qué sale por la chimenea?',
        'imp-channelling': '¿Por qué mi unión es más honda de lo que dicen las tablas?',
        'imp-damage': '¿A qué dosis vuelve el implante amorfo al silicio?',
        'imp-profile': '¿Cómo hago la unión menos honda?',
        'insp-threshold': '¿Bajo el umbral para atrapar más defectos?',
        'insp-limit': '¿Por qué no vimos el defecto que mató al die?',
        'insp-coverage': '¿Necesito escanear la oblea entera?',
        'pkg-popcorn': '¿Por qué falló en reflujo un die bueno?',
        'pkg-warpage': '¿Por qué se alabea el encapsulado, y cuándo importa?',
        'pkg-mould': '¿Qué hace realmente el curado post-moldeo?',
        'saqp-walk': '¿Por qué mis espacios alternan ancho y estrecho?',
        'saqp-pitch': '¿Qué fija realmente el paso final?',
        'saqp-collapse': '¿Por qué colapsó esta receta?',
        'ecd-fill': '¿Por qué hay un hueco en mi zanja?',
        'ecd-terminal': '¿Por qué el cobre es más grueso en el borde de la oblea?',
        'ecd-rate': '¿Cuánto tiempo electrodeposito para el espesor que necesito?',
        'prb-area': '¿Por qué un die más grande rinde tanto peor?',
        'prb-clustering': '¿Por qué el modelo de Poisson sub-predice mi línea?',
        'prb-signature': '¿Esta pérdida de rendimiento es la oblea o la tarjeta de prueba?',
        'wpr-kerf': '¿Cuánto del lingote acaba en obleas?',
        'wpr-damage': '¿Cuánto silicio tengo que quitar para sacar el daño de la sierra?',
        'wpr-flatness': '¿Qué comba una oblea, y por qué importa?',
      },
      Who: 'Asistente de indagación',
      OffThread:
        'Los experimentos se ejecutan fuera del hilo principal, así que la fábrica sigue siendo ' +
        'transitable.',
      InlineThread:
        'Los experimentos se ejecutan en línea; la vista puede entrecortarse mientras corre uno.',
      Placeholder: 'Pregunta por lo que estás viendo…  (Enter para enviar)',
      Run: 'Ejecutar experimento',
      Designing: 'Diseñando el experimento…',
      RunningCondition: 'Ejecutando la condición {{done}} de {{total}} — {{label}}',
      Promise:
        'Cada respuesta que doy está construida con números que una ejecución produjo de verdad. ' +
        'Si no puedo convertir tu pregunta en un experimento, lo diré en lugar de inventarme uno.',
      Hypothesis: 'Hipótesis',
      Design: 'Diseño',
      Measured: 'Medido',
      Findings: 'Hallazgos',
      CanInvestigate: 'Lo que puedo investigar',
      ConditionsRun: 'Condiciones ejecutadas',
      Condition: 'Condición',
      Descend: '↓ Bajar a la escala microscópica — {{topic}}',
      NextQuestion: 'Tu siguiente pregunta',
      NoMatch: 'Ningún experimento coincide',
      CannotRun:
        'Todavía no sé convertir "{{question}}" en un experimento que pueda ejecutar, así que no ' +
        'voy a inventarme una respuesta. Todo lo que digo sale de ejecutar la simulación, y para ' +
        'eso no tengo nada que ejecutar. Esto es lo que sí puedo investigar en esta herramienta:',
      NoMatchFollowUp:
        'Prueba con uno de esos, o reformula en términos de lo que viste en la oblea.',

      NearestMatch: 'Sin coincidencia exacta — experimento más cercano',
      NearestLead:
        'No puedo ejecutar "{{question}}" tal como la planteaste. Lo más cercano que sé ejecutar ' +
        'en esta herramienta es "{{example}}" — {{summary}} No es la misma pregunta que la tuya, ' +
        'así que decide si te responde antes de quedarte con el resultado.',
      RunNearest: 'Ejecutar ese en su lugar',
      NearestFollowUp:
        'Si no era eso lo que querías decir, plantéalo en términos de lo que viste en la oblea y ' +
        'lo vuelvo a mirar.',
    },

    auth: {
      Title: 'Inicia sesión para usar el asistente',
      Why:
        'Iniciar sesión es lo que pone un nombre a la sesión: las preguntas que haces, los ' +
        'experimentos en que se convierten y el trabajo que podrás retomar la próxima vez. El ' +
        'resto de la fábrica no necesita cuenta: los equipos, la oblea y el solucionador están ' +
        'abiertos a quien esté en la planta.',
      Google: 'Continuar con Google',
      Guest: 'Continuar como invitado',
      SignIn: 'Iniciar sesión',
      AsMe: 'Iniciar sesión como yo',
      Anonymously: 'Iniciar sesión de forma anónima',
      MenuWhy: 'Una cuenta es lo que permitirá que tu trabajo te acompañe a la próxima sesión.',
      GuestScope: 'Anónima: solo en este navegador',
      ConfirmGuestTitle: '¿Iniciar sesión de forma anónima?',
      ConfirmGuestBody:
        'Una cuenta anónima desaparece en cuanto cierras la sesión y no se puede recuperar: se ' +
        'lleva consigo todo lo que hayas guardado. Tampoco existe en ningún otro navegador ni ' +
        'ordenador. Úsala para una sola sesión, en este ordenador.',
      ConfirmSignOutTitle: '¿Cerrar la sesión de esta cuenta anónima?',
      ConfirmSignOutBody:
        'Esta cuenta no se podrá recuperar una vez que cierres la sesión, y se perderá todo el ' +
        'trabajo guardado en ella. Iniciar sesión como tú mismo es lo que hace que el trabajo ' +
        'perdure.',
      Yes: 'Sí',
      No: 'No',
      GuestNote:
        'Una cuenta de invitado no lleva nombre ni correo. Funciona igual que una con sesión ' +
        'iniciada y dura lo que este navegador la conserve.',
      Checking: 'Comprobando quién ha iniciado sesión…',
      Retry: 'Reintentar',
      SignedInAs: 'Sesión iniciada como {{name}}',
      SignedInAsGuest: 'Sesión iniciada como invitado',
      SignOut: 'Cerrar sesión',
      GuestName: 'Invitado',
      SignedIn: 'Sesión iniciada',
      ErrNetwork: 'Sin red. Iniciar sesión necesita conexión — la fábrica y el solucionador no.',
      ErrNotEnabled: 'Ese método de inicio de sesión aún no está habilitado en este proyecto.',
      ErrDomain:
        'El inicio de sesión no está autorizado para {{host}}. Añádelo a los dominios autorizados de Firebase.',
      ErrTooMany: 'Demasiados intentos desde este equipo. Espera un minuto y vuelve a intentarlo.',
      ErrPopupBlocked:
        'El navegador bloqueó la ventana de inicio de sesión. Permite las ventanas emergentes para este sitio, o entra por la puerta de invitado.',
      ErrUnavailable: 'El inicio de sesión no está disponible — la aplicación no pudo contactar con Firebase.',
    },

    recipe: {
      // Preset display labels. Code-like ones keep their code everywhere.
      presetLabel: {
        // Etch (ETCH-01) recipe codes: rendered without a namespace.
        baseline: 'BASELINE-SI-01',
        lowPressure: 'LO-P HAMBRIENTA',
        highPressure: 'HI-P LIMITADA POR DIFUSIÓN',
        highBias: 'HI-BIAS ANISOTRÓPICA',
        smallOpenArea: 'PRODUCTO DE BAJA CARGA',
        starvedFlow: 'EXCURSIÓN DE BAJO FLUJO',
        facilities: {
          vacNominal: 'CÁMARA SANA',
          vacThrottled: 'LIMITADA POR CONDUCTANCIA',
          vacLeak: 'FUGA REAL',
          vacDirty: 'CÁMARA HÚMEDA',
          vacBiggerPump: 'BOMBA MAYOR, MISMA TUBERÍA',
        },
        ale: {
          baseline: 'BASELINE-ALE-SI',
          tooEnergetic: 'ENERGÍA IÓNICA DEMASIADO ALTA',
          tooGentle: 'ENERGÍA IÓNICA DEMASIADO BAJA',
          starvedDose: 'DOSIS BAJO SATURACIÓN',
          noPurge: 'SIN PURGA',
          highAspect: 'ALTA RELACIÓN DE ASPECTO',
          nitride: 'NITRURO',
        },
        bonding: {
          baseline: 'BASELINE-AUAL',
          coldBond: 'SUB-UNIDO',
          overDriven: 'SOBRE-FORZADO',
          longCure: 'CURADO EXTENDIDO',
          highPinCount: 'ALTO NÚMERO DE PINES',
        },
        dicing: {
          baseline: 'BASELINE-200UM',
          thinStack: 'DELGADO PARA APILAR',
          noRelief: 'SIN ALIVIO DE TENSIONES',
          fastFeed: 'AVANCE RÁPIDO',
          narrowStreet: 'CALLES ESTRECHAS',
        },
        packaging: {
          baseline: 'BASELINE-BGA',
          thinMould: 'TAPA DE MOLDEO FINA',
          bigBody: 'CUERPO GRANDE',
          floorLifeBlown: 'FUERA DEL EMPAQUE SECO',
          hotReflow: 'REFLUJO CALIENTE',
        },
        finaltest: {
          baseline: 'BASELINE-COMERCIAL',
          noBurnIn: 'SIN BURN-IN',
          automotive: 'AUTOMOCIÓN',
          noGuardBand: 'SIN BANDA DE GUARDA',
          shortTest: 'PRUEBA CORTA',
        },
        clean: {
          baseline: 'BASELINE-RCA',
          noHf: 'SIN HF FINAL',
          queueBlown: 'TIEMPO DE COLA EXCEDIDO',
          aggressive: 'SC-1 AGRESIVO',
          shortClean: 'LIMPIEZA CORTA',
        },
        gowning: {
          baseline: 'BASELINE-BAHÍA-ISO5',
          noHood: 'SIN CAPUCHA',
          wrongOrder: 'VESTIDO EN DESORDEN',
          crowded: 'BAHÍA ABARROTADA',
          straightThrough: 'ESCLUSA SIN PARADA',
          lowFlow: 'RENOVACIONES REDUCIDAS',
          longExposure: 'OBLEA DEJADA FUERA',
        },
        cmp: {
          baseline: 'BASELINE-CU-DAMASQUINADO',
          underPolished: 'SIN SOBRE-PULIDO',
          overPolished: 'SOBRE-PULIDO FUERTE',
          mismatchedSpeeds: 'VELOCIDADES DESIGUALES',
          hardPress: 'ALTA FUERZA DE APRIETE',
        },
        crystal: {
          baseline: 'BASELINE-CZ300-B',
          perfectSilicon: 'SILICIO PERFECTO',
          antimony: 'SUSTRATO DE ANTIMONIO',
          pulledTooFast: 'TIRADO DEMASIADO RÁPIDO',
          tightSpec: 'RESISTIVIDAD ESTRECHA',
          magneticCz: 'CZ MAGNÉTICO',
        },
        epi: {
          epiDevice: 'CAPA DE DISPOSITIVO',
          epiSlow: 'SONDA CINÉTICA',
          epiThin: 'CAPA FINA RÁPIDA',
        },
        gas: {
          baseline: 'BASELINE-PANEL-ARSINA',
          lazyPurge: 'PURGA PEREZOSA',
          silaneRelease: 'FUGA DE SILANO',
          chlorinePool: 'CLORO, DETECTOR EN TECHO',
          dilutedArsine: 'ARSINA DILUIDA',
          slowDetector: 'DETECTOR LENTO',
          ammoniaHabit: 'AMONIACO — EL MAL HÁBITO',
        },
        implant: {
          baseline: 'BASELINE-BORO-SD',
          zeroTilt: 'INCLINACIÓN CERO',
          shallow: 'EXTENSIÓN SUPERFICIAL',
          deepWell: 'POZO PROFUNDO',
          hotImplant: 'IMPLANTE CALIENTE',
        },
        patterning: {
          baseline: 'BASELINE-SAQP-40',
          pitchWalk: 'PITCH WALK',
          overTrimmed: 'MANDRIL SOBRE-RECORTADO',
          thickSpacer: 'ESPACIADOR DEMASIADO GRUESO',
          doubleOnly: 'SADP (UNA PASADA)',
          aggressive: 'AGRESIVO — PASO 32 nm',
        },
        plating: {
          baseline: 'BASELINE-CU-DAMASQUINADO',
          noAccelerator: 'SIN ACELERADOR',
          noSuppressor: 'SIN SUPRESOR',
          thinSeed: 'SEMILLA FINA',
          hotPlate: 'CORRIENTE ALTA',
          overPlated: 'SOBRE-ELECTRODEPOSITADO',
          highAspect: 'ALTA RELACIÓN DE ASPECTO',
        },
        inspection: {
          baseline: 'BASELINE-CAMPO-OSCURO',
          hotScan: 'ESCANEO CALIENTE',
          coldScan: 'ESCANEO FRÍO',
          shortWave: 'LONGITUD DE ONDA MÁS CORTA',
          sampled: 'ESCANEO MUESTREADO',
        },
        probe: {
          baseline: 'BASELINE-CLASIFICACIÓN',
          bigDie: 'DIE GRANDE',
          poissonForecast: 'PRONÓSTICO POISSON',
          heavyCluster: 'MUY AGRUPADO',
          wideEdge: 'EXCLUSIÓN DE BORDE ANCHA',
        },
        thermalBaseline: 'ÓXIDO DE PUERTA + RTA',
        thermalWetField: 'ÓXIDO DE CAMPO HÚMEDO',
        thermalFurnaceAnneal: 'RECOCIDO EN HORNO',
        thermalShallowJunction: 'UNIÓN SUPERFICIAL',
        thermalArsenicSd: 'FUENTE/DRENADOR DE ARSÉNICO',
        waferprep: {
          baseline: 'BASELINE-300MM',
          thinWire: 'HILO FINO',
          singleSided: 'PULIDO A UNA CARA',
          shortEtch: 'GRABADO CORTO',
          shortPolish: 'PULIDO CORTO',
          coarseSaw: 'SIERRA GRUESA',
          thinWafer: 'OBLEA FINA',
        },
      },
      knob: {
        bedLoadFraction: 'Vida del lecho mixto usada',
        uvIntensity: 'Salida del oxidador UV',
        pouTempC: 'Temperatura de la muestra',
        inletTocPpb: 'TOC desde RO',
        modifySeconds: 'Dosis de cloro',
        modifyPressureMtorr: 'Presión de modificación',
        purgeSeconds: 'Purga',
        ionEnergyEv: 'Energía iónica',
        removeSeconds: 'Paso de remoción',
        ionFlux: 'Flujo iónico',
        forceGf: 'Fuerza de unión',
        ultrasonicMw: 'Potencia ultrasónica',
        bondMs: 'Tiempo de unión',
        stageTempC: 'Temperatura de la platina',
        cureMinutes: 'Curado del epoxi',
        cureTempC: 'Temperatura de curado',
        wiresPerDie: 'Hilos por die',
        targetThicknessUm: 'Espesor de oblea',
        finalGrit: 'Grano de la muela final',
        feedRateMmS: 'Avance de corte',
        streetWidthUm: 'Ancho de calle',
        diePerWafer: 'Die por oblea',
        mouldThicknessUm: 'Tapa de moldeo',
        substrateThicknessUm: 'Sustrato',
        mouldTempC: 'Temperatura de moldeo',
        postCureMinutes: 'Curado post-moldeo',
        floorLifeHours: 'Vida en planta antes del reflujo',
        humidityPct: 'Humedad ambiente',
        reflowPeakC: 'Pico de reflujo',
        bodySizeMm: 'Tamaño del encapsulado',
        useTempC: 'Temperatura de uso especificada',
        burnInTempC: 'Temperatura de burn-in',
        burnInHours: 'Duración del burn-in',
        hotTestC: 'Prueba en caliente',
        coldTestC: 'Prueba en frío',
        guardBandFraction: 'Banda de guarda',
        testSeconds: 'Tiempo de prueba por unidad',
        lotSize: 'Tamaño de lote',
        sc1Minutes: 'Tiempo de SC-1',
        sc1TempC: 'Temperatura de SC-1',
        megasonicW: 'Potencia megasónica',
        sc2Minutes: 'Tiempo de SC-2',
        hfSeconds: 'Inmersión en HF',
        hfDilution: 'Dilución del HF',
        queueMinutes: 'Espera tras el HF',
        incomingParticlesCm2: 'Partículas de entrada',
        incomingMetal: 'Metal de entrada',
        occupants: 'Personas en la bahía',
        activity: 'Nivel de actividad',
        airChangesPerHour: 'Renovaciones de aire',
        filterCoverage: 'Cobertura de filtros',
        filterEfficiency: 'Eficiencia de filtrado',
        bayVolumeM3: 'Volumen de la sala',
        downflowMs: 'Velocidad de flujo descendente',
        airlockMinutes: 'Permanencia en la esclusa',
        waferExposureMinutes: 'Exposición de la oblea',
        particleSizeUm: 'Tamaño de partícula especificado',
        downForceKPa: 'Fuerza de apriete',
        platenRpm: 'Velocidad del plato',
        carrierRpm: 'Velocidad del portador',
        slurryFlowMlMin: 'Caudal de slurry',
        overpolishPct: 'Sobre-pulido',
        overburdenNm: 'Sobreespesor de entrada',
        incomingStepNm: 'Altura de escalón de entrada',
        meltDopingCm3: 'Dopado del fundido',
        chargeKg: 'Masa de carga',
        diameterMm: 'Diámetro del lingote',
        pullRateMmMin: 'Velocidad de tiro',
        gradientKCm: 'Gradiente axial',
        crucibleRpm: 'Rotación del crisol',
        crystalRpm: 'Rotación del cristal',
        magneticFieldT: 'Campo magnético',
        resistivityTolerance: 'Tolerancia de resistividad',
        targetResistivity: 'Resistividad objetivo',
        cylinderPercent: 'Concentración del cilindro',
        purgeCycles: 'Ciclos de purga',
        purgeRatio: 'Razón por ciclo',
        leakRateSccm: 'Tasa de fuga',
        detectorThreshold: 'Umbral de alarma',
        detectorResponseS: 'Respuesta del detector',
        energyKeV: 'Energía',
        dose: 'Dosis',
        tiltDeg: 'Inclinación',
        twistDeg: 'Giro',
        beamCurrentMa: 'Corriente de haz',
        waferTempC: 'Temperatura de oblea',
        mandrelPitchNm: 'Paso del mandril',
        mandrelCdNm: 'CD del mandril, impreso',
        mandrelTrimNm: 'Recorte del mandril',
        spacer1Nm: 'Espesor del espaciador 1',
        spacer2Nm: 'Espesor del espaciador 2',
        spacerOveretch: 'Sobre-grabado del espaciador',
        currentDensityMaCm2: 'Densidad de corriente',
        seconds: 'Tiempo de electrodeposición',
        seedThicknessNm: 'Espesor de la semilla',
        trenchWidthNm: 'Ancho de zanja',
        trenchDepthNm: 'Profundidad de zanja',
        suppressor: 'Supresor',
        accelerator: 'Acelerador',
        leveler: 'Nivelador',
        copperGL: 'Sulfato de cobre',
        acidGL: 'Ácido',
        wavelengthNm: 'Longitud de onda',
        thresholdSigma: 'Umbral de detección',
        pixelNm: 'Tamaño de píxel',
        scanCoverage: 'Cobertura de la oblea',
        reviewSample: 'Muestra para revisión SEM',
        designRuleNm: 'Regla de diseño',
        incomingDensityCm2: 'Defectos de entrada',
        dieAreaCm2: 'Área del die',
        edgeExclusionMm: 'Exclusión de borde',
        clusterAlpha: 'Agrupamiento',
        touchdownsPerClean: 'Contactos entre limpiezas',
        parametricGuardBand: 'Banda de guarda paramétrica',
        wireDiameterUm: 'Diámetro del hilo',
        sawGrit: 'Grano de la sierra',
        lapGrit: 'Grano del lapeado',
        lapRemovalUm: 'Remoción por cara, lapeado',
        etchRemovalUm: 'Remoción por cara, grabado',
        polishPressureKPa: 'Presión de pulido',
        polishRpm: 'Velocidad de pulido',
        polishMinutes: 'Tiempo de pulido',
        ingotLengthMm: 'Longitud útil del lingote',
        bakeMinutes: 'Tiempo de horneado en H2',
        epiTempC: 'Temperatura de crecimiento',
        tcsTorr: 'Presión parcial de TCS',
        epiMinutes: 'Tiempo de crecimiento',
        epiDopingCm3: 'Dopado de la capa',
        h2Slm: 'Portador de hidrógeno',
        pressureMtorr: 'Presión de cámara',
        sourcePowerW: 'Potencia RF de fuente',
        biasPowerW: 'Potencia RF de polarización',
        flowSccm: 'Caudal total de gas',
        cf4Fraction: 'Fracción de CF4',
        wafertempC: 'Temperatura de la oblea',
        openAreaFraction: 'Área abierta',

        // Litografía.
        doseMjCm2: 'Dosis de exposición',
        focusNm: 'Desplazamiento de foco',
        pitchNm: 'Paso',
        pebBlurNm: 'Longitud de difusión PEB',
        resistContrast: 'Contraste de la resina',
        developThreshold: 'Umbral de revelado',

        // Procesos térmicos.
        oxideTempC: 'Temperatura de oxidación',
        oxideMinutes: 'Tiempo de oxidación',
        implantDose: 'Dosis de implantación',
        implantKeV: 'Energía de implantación',
        annealTempC: 'Temperatura de recocido',
        annealSeconds: 'Tiempo de recocido',

        // Instalaciones.
        volumeL: 'Volumen de cámara',
        pumpSpeedLs: 'Velocidad de bombeo',
        lineDiameterCm: 'Diámetro de línea',
        lineLengthCm: 'Longitud de línea',
        leakThroughput: 'Caudal de fuga',
        outgassingInitial: 'Desgasificación en t=0',
        outgassingTau: 'Tiempo de decaimiento de desgasificación',
      },
      preset: {
        facilities: {
          vacNominal: 'Línea corta y gruesa, sellos apretados. La conductancia no es el límite y no hay fuga.',
          vacThrottled: 'La misma bomba tras una línea de 5 cm y dos metros de largo. La cámara nunca ve la velocidad de la bomba.',
          vacLeak: 'Un sello de puerta pellizcado en una cámara que ya bombeó toda la noche, así que las paredes están secas y solo queda la fuga. La presión base se atasca y la subida de presión nunca se curva. Nota la baja desgasificación: no puedes buscar fugas en una cámara que abriste esta mañana.',
          vacDirty: 'Abierta a la atmósfera y rearmada húmeda. Fuerte desgasificación que con el tiempo se bombea.',
          vacBiggerPump: 'El triple de bomba en la línea estrangulada — la respuesta equivocada y cara.',
        },
        ale: {
          baseline:
            'ALE cualificado: dosis de cloro saturada, argón a 28 eV — el centro de la ' +
            'ventana. Unos 0,3 nm por ciclo, dos órdenes de magnitud más lento que la ' +
            'receta RIE de la misma cámara.',
          tooEnergetic:
            'Sesenta electronvoltios para acelerarlo. Por encima del umbral de ' +
            'pulverización del sustrato, así que ahora se elimina silicio sin modificar ' +
            'de forma continua. Más rápido, y ya no es ALE — el número de sinergia lo ' +
            'dice antes que el perfil.',
          tooGentle:
            'Doce electronvoltios. Por debajo del umbral de la capa modificada, así que ' +
            'el cloro se adsorbe y nada sale. La cámara parece estar funcionando.',
          starvedDose:
            'Medio segundo de cloro. La cobertura es incompleta, así que el grabado por ' +
            'ciclo depende ahora de cuánta dosis llegó a cada parte de la oblea — que es ' +
            'exactamente el problema de uniformidad del que el ALE vino a escapar.',
          noPurge:
            'Los dos semiciclos se solapan, así que parte del grabado es continuo. Sigue ' +
            'grabando, y ya no es capa a capa.',
          highAspect:
            'Una estructura 50:1 con la misma receta. La mitad de remoción va bien — los ' +
            'iones son direccionales — pero la dosis neutra tiene que llegar al fondo, y ' +
            'a esta relación de aspecto no llega.',
          nitride:
            'Misma receta, otra película. La química del cloro no es universal — el ' +
            'nitruro se modifica peor, el grabado por ciclo cae y la receta hay que ' +
            'recualificarla.',
        },
        bonding: {
          baseline:
            'Unión termosónica cualificada de bola de oro sobre pads de aluminio.',
          coldBond:
            'Potencia ultrasónica a la mitad. El óxido sobrevive, así que la mayor parte ' +
            'de la interfaz nunca llega a soldarse.',
          overDriven:
            'Potencia y fuerza subidas para forzar la unión. Suelda, y luego sigue hacia ' +
            'dentro del silicio.',
          longCure:
            'Cuatro horas a 200 C para asegurar el epoxi. El intermetálico crece todo ese ' +
            'tiempo, y después no deja de ser grueso.',
          highPinCount:
            '480 hilos en un die. Un rendimiento por hilo que parecía excelente deja de ' +
            'parecerlo cuando lo elevas a la potencia 480.',
        },
        dicing: {
          baseline:
            'Flujo cualificado: muela fina, alivio de tensiones, corte con hoja a avance ' +
            'moderado.',
          thinStack:
            '50 um para un encapsulado apilado. Todo lo que viene después se vuelve más ' +
            'frágil por ello.',
          noRelief:
            'Rectificar y seguir. Ahorra 45 segundos por oblea y deja dentro el daño del ' +
            'rectificado, que es un problema de resistencia que nadie ve hasta la prensa ' +
            'de moldeo.',
          fastFeed:
            'Avance doblado por producción. El desconchado crece con su raíz cuadrada.',
          narrowStreet:
            'Calles reducidas a 40 um para meter más die en la oblea. El kerf y el ' +
            'desconchado no se han hecho más pequeños.',
        },
        packaging: {
          baseline:
            'BGA de hilo cualificado: moldeo y sustrato equilibrados, empacado en seco, ' +
            'reflujo estándar.',
          thinMould:
            'Tapa reducida a 250 um para un hueco con límite de altura. La pila queda muy ' +
            'desequilibrada y se alabea por ello.',
          bigBody:
            'Un cuerpo de 40 mm. El alabeo crece con el CUADRADO del vano, así que no es ' +
            'un cambio pequeño.',
          floorLifeBlown:
            'Tres semanas en planta al 70% de humedad antes del reflujo. El reloj impreso ' +
            'en la bolsa no era decoración.',
          hotReflow:
            'Pico subido a 268 C para garantizar el mojado. Todo lo relacionado con la ' +
            'humedad empeora.',
        },
        finaltest: {
          baseline:
            '48 h a 125 C, prueba en caliente y en frío, banda de guarda del 10%.',
          noBurnIn:
            'Saltárselo del todo por producción. Barato, rápido, y cada pieza con ' +
            'mortalidad infantil se envía a un cliente.',
          automotive:
            '168 h a 150 C con banda de guarda ancha. Aquí los escapes son retiradas de ' +
            'producto, así que el exceso sale barato.',
          noGuardBand:
            'Enviar todo lo que mida dentro del límite. Rendimiento máximo, y cada escape ' +
            'es una devolución.',
          shortTest:
            'Tiempo de prueba reducido a un segundo por capacidad. La cobertura cae con él.',
        },
        clean: {
          baseline:
            'RCA completa con HF-último corto y 20 minutos de cola hasta el horno.',
          noHf:
            'Deja el óxido químico puesto. Correcto justo antes de una deposición que lo ' +
            'quiere; incorrecto antes de un óxido de puerta.',
          queueBlown:
            'HF-último, y la oblea esperó cuatro horas. El óxido nativo ha vuelto y la ' +
            'limpieza fue para nada.',
          aggressive:
            'Treinta minutos a 80 C con los megasónicos a tope. Casi sin partículas, y ' +
            'una superficie a la que se le puede medir la rugosidad.',
          shortClean:
            'Dos minutos de SC-1 por producción. La mayoría de las partículas siguen ahí.',
        },
        gowning: {
          baseline:
            'Cuatro personas, totalmente vestidas, 500 renovaciones de aire por hora. El ' +
            'protocolo tal como está escrito, y el número que asume cada equipo aguas ' +
            'abajo.',
          noHood:
            'Falta una prenda. No es una quinta parte de la protección — es el factor ' +
            'entero que aportaba la capucha, porque las prendas multiplican.',
          wrongOrder:
            'Todas las prendas puestas, ninguna en el orden correcto. El traje está ' +
            'completo y ya se contaminó en el proceso de ponérselo.',
          crowded:
            'Doce personas dentro por la instalación de un equipo. La generación es ' +
            'lineal en la ocupación y nada de la ventilación cambió.',
          straightThrough:
            'Sin permanencia. La esclusa existe para barrer lo que suelta el traje recién ' +
            'puesto; cruzarla sin pararse se salta lo único que hace.',
          lowFlow:
            'Ventilación recortada a 150 ACH para ahorrar energía. La concentración es ' +
            'generación sobre ventilación, así que esto hace exactamente lo mismo que ' +
            'triplicar la ocupación.',
          longExposure:
            'Un lote abierto en una mesa durante media hora. La concentración no cambió y ' +
            'está perfectamente en clase; la oblea recolecta durante treinta minutos en ' +
            'vez de dos.',
        },
        cmp: {
          baseline:
            'Pulido de cobre cualificado: plato y portador a velocidad emparejada, 20% de ' +
            'sobre-pulido.',
          underPolished:
            'Se detiene en cuanto se mueve la traza de endpoint. En una máquina ' +
            'perfectamente uniforme eso casi se sobrevive; en cualquiera real, donde la ' +
            'oblea pulió lento sigue habiendo metal puenteando las líneas, que es un ' +
            'corto, que es un die muerto.',
          overPolished:
            'Doblar el sobre-pulido para asegurar el aclarado. Cada línea ancha se ahueca ' +
            'por ello.',
          mismatchedSpeeds:
            'Portador a la mitad de la velocidad del plato. La velocidad relativa deja de ' +
            'ser uniforme sobre la oblea y aparece una firma radial que el ajuste de ' +
            'presión no puede quitar.',
          hardPress:
            'Fuerza a 40 kPa por producción. Más rápido, y cada mecanismo de defecto ' +
            'escala con ella.',
        },
        crystal: {
          baseline:
            'Boro, 150 kg, 300 mm. Tirado a 1,0 mm/min contra un gradiente de 30 K/cm — ' +
            'el lado de vacantes de la ventana de Voronkov, que es donde vive casi todo ' +
            'el silicio de producción.',
          perfectSilicon:
            'Velocidad de tiro reducida hasta sentarse en la razón de Voronkov: ni ' +
            'vacantes ni intersticiales dominan. Cuesta velocidad de crecimiento y por ' +
            'tanto dinero, y es lo que paga un cliente de óxido de puerta.',
          antimony:
            'Sustrato n+ fuertemente dopado. k = 0,023, así que el fundido se enriquece ' +
            'casi cuarenta veces y la mayor parte del lingote falla cualquier ventana ' +
            'estrecha de resistividad. La segregación, en su versión más brutal.',
          pulledTooFast:
            'Dos milímetros y medio por minuto con el mismo gradiente. Pasado el techo de ' +
            'calor latente: la interfaz no puede mantenerse plana y el cristal pierde la ' +
            'estructura.',
          tightSpec:
            'El mismo cristal, tolerancia estrechada al cinco por ciento. Nada del ' +
            'crecimiento cambió y la mayor parte del lingote es ahora invendible — la ' +
            'especificación es el rendimiento.',
          magneticCz:
            'Un campo de 0,3 T amortigua la convección del fundido, así que llega mucho ' +
            'menos oxígeno del crisol a la interfaz. Silicio de bajo oxígeno — y con él, ' +
            'sin gettering interno al que recurrir.',
        },
        epi: {
          epiDevice:
            'El punto de producción: bien dentro de la meseta de transporte, donde el ' +
            'burbujeador fija la velocidad y a las lámparas se les perdona. Cuatro micras ' +
            'de silicio poco dopado sobre lo que haya crecido CZ-07.',
          epiSlow:
            'A 950 °C la reacción de superficie es el cuello de botella y la velocidad es ' +
            'un termómetro — esta es la receta que separa un problema de gas de uno de ' +
            'temperatura.',
          epiThin:
            'Una micra en menos de un minuto a TCS máximo. Se sienta cerca del límite de ' +
            'salpicadura a propósito — vigila el recuento de partículas.',
        },
        gas: {
          baseline:
            'Panel de arsina, diez ciclos a razón diez a uno antes de abrir. El ' +
            'procedimiento cualificado, y la aritmética que hay detrás.',
          lazyPurge:
            'Los mismos diez ciclos, pero bombeando solo a media presión cada vez. Diez ' +
            'ciclos a 0,5 son un factor de mil; diez a 0,1 son diez mil millones. Mismo ' +
            'procedimiento, mismo tiempo, siete órdenes de magnitud de diferencia.',
          silaneRelease:
            'Silano puro fugando a la bahía. Pirofórico, así que el incendio es el ' +
            'problema inmediato y el límite de exposición es el que importa si no llega a ' +
            'prender.',
          chlorinePool:
            'El cloro dobla la densidad del aire y se acumula a ras de suelo. El número ' +
            'de mezcla perfecta de abajo es optimista, y un detector de techo mide el ' +
            'sitio equivocado.',
          dilutedArsine:
            'Arsina al uno por ciento en hidrógeno, que es como suele comprarse. Dos ' +
            'órdenes de magnitud menos que purgar, y el mismo límite de exposición al ' +
            'final.',
          slowDetector:
            'Un detector fijado al doble del límite de exposición con dos minutos de ' +
            'respuesta. Alarmará. La pregunta de esta herramienta es si alarma a tiempo ' +
            'de que importe.',
          ammoniaHabit:
            'Hueles el amoniaco muy por debajo de su límite de exposición, y eso te ' +
            'enseña que tu nariz es un detector. La arsina no da aviso de olor a ninguna ' +
            'concentración peligrosa.',
        },
        implant: {
          baseline:
            'Boro de fuente/drenador: 30 keV, 3 × 10¹⁵, siete grados de inclinación para ' +
            'romper el canal.',
          zeroTilt:
            'Recto por el canal. El pico cae donde debe y una gran parte de la dosis ' +
            'llega tres veces más hondo de lo que nadie pretendía.',
          shallow:
            'BF2 a 5 keV. Molécula pesada, energía baja, muy superficial — y mucho más ' +
            'daño por ion.',
          deepWell:
            'Fósforo a 200 keV. Profundo, e inevitablemente difuminado — el straggle es ' +
            'el 38% del alcance.',
          hotImplant:
            'Oblea a 300 C para que la red se repare mientras entra el haz. Menos daño ' +
            'que recocer después, o sea menos presupuesto térmico gastado.',
        },
        patterning: {
          baseline:
            'Un paso de mandril de 160 nm dividido dos veces hasta 40 nm — un paso que ' +
            'ningún escáner de 193 nm resuelve. El recorte está puesto para que ambos ' +
            'espaciados salgan iguales.',
          pitchWalk:
            'El recorte del mandril está 4 nm desviado — 14 en vez de 10. El paso MEDIO ' +
            'sigue siendo exactamente 40 nm, el espaciado medio sigue siendo exactamente ' +
            '20 nm, y todos los resúmenes del CD-SEM están en objetivo. Una línea de cada ' +
            'dos está en el sitio equivocado.',
          overTrimmed:
            'Recortado hasta nada. El mandril desaparece antes de depositar el ' +
            'espaciador, así que no hay pared en la que apoyarse.',
          thickSpacer:
            'Treinta nanómetros de ALD sobre un paso de 160 nm. Los espaciadores se tocan ' +
            'y el patrón es un bloque macizo — geometría, no proceso.',
          doubleOnly:
            'Parar tras la primera pasada: paso de 80 nm, una sola forma de desviarse en ' +
            'vez de dos. La mitad de densidad y bastante menos que pueda salir mal.',
          aggressive:
            'Mandril de 128 nm y espaciadores de 16 nm, para un final de 32 nm. Todo es ' +
            'más estrecho y la ventana de recorte se estrecha con ello.',
        },
        plating: {
          baseline:
            'Baño cualificado, 20 mA/cm² durante un minuto en una zanja de 60 nm. Rellena ' +
            'de abajo arriba y deja sobreespesor para que lo quite CMP-11.',
          noAccelerator:
            'Sin acelerador no hay realimentación CEAC: la zanja se recubre conforme, la ' +
            'boca cierra primero y la estructura queda con huecos. El aditivo no es una ' +
            'optimización.',
          noSuppressor:
            'Nada frena al campo ni a la boca de la zanja, así que la ganancia se ' +
            'derrumba hacia uno y las paredes ganan la carrera.',
          thinSeed:
            'Una semilla de 10 nm deja más sitio en la zanja — y cuadruplica la ' +
            'resistencia de hoja, así que el efecto terminal mata de hambre al centro de ' +
            'la oblea.',
          hotPlate:
            'Cincuenta miliamperios por centímetro cuadrado para partir el tiempo de ' +
            'ciclo. Pasada la corriente límite el baño no puede suministrar cobre tan ' +
            'rápido y el depósito se quema.',
          overPlated:
            'Tres minutos en vez de uno. La zanja se llenó hace mucho; todo lo posterior ' +
            'es sobreespesor, y cada nanómetro es tiempo de ahuecado en CMP-11.',
          highAspect:
            'Una zanja de 20 nm, 300 de profundidad. El superrelleno se hace MÁS fuerte ' +
            'aquí — el área del fondo se contrae más deprisa — pero las paredes tienen ' +
            'mucha menos distancia que cerrar.',
        },
        inspection: {
          baseline:
            'Escaneo de producción cualificado: campo oscuro a 355 nm, 4 sigma, oblea ' +
            'completa.',
          hotScan:
            'Umbral bajado a 2 sigma para encontrarlo todo. Encuentra más, sí — y ' +
            'entierra la cola de revisión en rugosidad de superficie.',
          coldScan:
            'Umbral subido a 9 sigma para un informe limpio. El informe queda limpio; la ' +
            'oblea no.',
          shortWave:
            '266 nm en vez de 355 nm. La sensibilidad va como la potencia dos tercios de ' +
            'la longitud de onda, así que esto es una ganancia del 20%, no del 25%.',
          sampled:
            'Una quinta parte de la oblea, por producción. Todo escala, incluida la ' +
            'confianza.',
        },
        probe: {
          baseline:
            'Clasificación cualificada: die de 0,59 cm², 3 mm de exclusión de borde, ' +
            'binomial negativa con alfa 3.',
          bigDie:
            'Un die de 2 cm² en la misma línea. El rendimiento cae exponencialmente con ' +
            'el área, y por eso el tamaño del die es la decisión más cara que toma un ' +
            'diseñador.',
          poissonForecast:
            'La misma oblea predicha con Poisson. Sistemáticamente pesimista, porque los ' +
            'defectos reales se agrupan y los defectos agrupados se desperdician en die ' +
            'que ya estaban muertos.',
          heavyCluster:
            'Un rayón o un mal manejo: alfa baja a 0,8. Mismo número de defectos, mejor ' +
            'rendimiento.',
          wideEdge:
            '8 mm excluidos para evitar fallos de borde. También tira die buenos por el ' +
            'camino.',
        },
        waferprep: {
          baseline:
            'Línea cualificada: hilo de 120 µm, lapeado y pulido a dos caras, 775 µm de ' +
            'acabado. Una quinta parte del lingote se va igualmente en slurry.',
          thinWire:
            'Un hilo de 70 µm para ahorrar kerf. Lo ahorra — y también serpentea, porque ' +
            'la misma tensión en un hilo más fino es un corte más flojo. Más obleas, peor ' +
            'planitud.',
          singleSided:
            'Trabajar solo la cara frontal. La mitad del tiempo de proceso y una oblea ' +
            'que se comba, porque la tensión del daño en la cara trasera ya no la ' +
            'equilibra nada.',
          shortEtch:
            'El grabado químico reducido a una micra. Es el único paso que quita daño sin ' +
            'crear ninguno, así que recortarlo deja el daño del lapeado dentro de la ' +
            'oblea — y el pulido que sigue no llega tan hondo.',
          shortPolish:
            'Pulido reducido a dos minutos. El daño ya no estaba — lo que sufre es el ' +
            'acabado, y una oblea velada es una oblea que el escáner no puede enfocar.',
          coarseSaw:
            'Un hilo #250 corta más rápido y deja daño mucho más hondo de lo que alcanza ' +
            'el lapeado. Los pasos siguientes estaban dimensionados para una sierra más ' +
            'fina y ya no lo están.',
          thinWafer:
            '400 µm de acabado, para una pieza apilada. Más obleas por lingote y mucho ' +
            'menos silicio cargando con el mismo combado.',
        },
        baseline:
          'Receta cualificada en el centro de la ventana: 204 nm/min con 4.3% de no uniformidad, ' +
          '3.7:1. Empieza aquí.',
        lowPressure:
          'Presión bajada a 6 mTorr: 116 nm/min con 10.9%. Hay tan poco gas en la cámara para ' +
          'generar flúor que la demanda supera al suministro (Da 1.15), y el borde, que ve el gas ' +
          'primero, corre un 19% más rápido.',
        highPressure:
          'Presión subida a 90 mTorr: 201 nm/min con 8.6%. Flúor de sobra (Da 0.20), pero la ' +
          'difusión escala como 1/p y no llega al centro de la oblea con suficiente rapidez. Borde ' +
          'rápido otra vez, 13% - causa opuesta, misma firma.',
        highBias:
          'Polarización a 350 W: 227 nm/min pero 20% de no uniformidad y selectividad caída a ' +
          '1.3:1. La demanda extra lleva el grabado a régimen limitado por transporte (Da 1.22). ' +
          'La forma equivocada de perseguir la tasa.',
        smallOpenArea:
          'Solo 4% de área abierta: 308 nm/min, la mitad más rápido con una receta sin cambiar, ' +
          'porque hay mucho menos silicio compitiendo por el mismo flúor. La uniformidad mejora a ' +
          '1.8% por la misma razón. Este es el efecto de carga, y es la razón por la que una ' +
          'receta cualificada en un producto deriva en otro.',
        starvedFlow:
          'Controlador de flujo bajado a 20 sccm desde 200: se desploma a 72 nm/min con 28% de no ' +
          'uniformidad, con el 78% del gas disociado y Da por encima de 2. El presupuesto de flúor ' +
          'lo fija lo que el MFC deja entrar, y aquí se ha convertido en la restricción activa.',

        // Litografía.
        lithoBaseline:
          'Dosificada a medida con paso de 180 nm, mejor foco. Contraste 0,97, NILS 7,1, LER 2,4 nm.',
        lithoDefocused:
          '90 nm de desenfoque. El contraste cae a 0,42 y la línea se va muy fuera de medida.',
        lithoUnderdosed:
          '20 mJ/cm². La línea se imprime demasiado ancha — menos luz retira menos resina.',
        lithoTightPitch:
          'Más allá de lo que la iluminación convencional maneja bien: contraste 0,57, y la ' +
          'ventana se encoge.',
        lithoEuvFine:
          'Cambia también la fuente a EUV. Imprime, pero con una fracción de los fotones.',

        // Procesos térmicos.
        thermalBaseline:
          'Óxido de puerta seco a 1000 °C, y luego un recocido rápido de 30 s para activar una ' +
          'implantación superficial de boro.',
        thermalWetField:
          'Vapor a 1050 °C durante dos horas. Mucho más grueso, mucho más rápido — y gasta mucho ' +
          'presupuesto.',
        thermalFurnaceAnneal:
          'La misma implantación, pero recocida 30 minutos en horno en vez de 30 segundos en RTP.',
        thermalShallowJunction:
          'Implantación de baja energía con un recocido de pico corto y caliente — la receta ' +
          'moderna para una unión superficial.',
        thermalArsenicSd:
          'Arsénico en vez de boro. Un átomo mucho más pesado, así que apenas se mueve — que es ' +
          'justo el objetivo.',

        // Instalaciones: configuraciones del sistema de vacío.
        vacNominal:
          'Línea corta y gruesa, sellos estancos. La conductancia no es el límite y no hay fuga.',
        vacThrottled:
          'La misma bomba detrás de una línea de 5 cm y dos metros. La cámara nunca ve la ' +
          'velocidad de la bomba.',
        vacLeak:
          'Un sello de puerta pellizcado en una cámara que ya ha bombeado toda la noche, así que ' +
          'las paredes están secas y solo queda la fuga. La presión base se estanca y la tasa de ' +
          'subida nunca se dobla. Fíjate en la baja desgasificación: no puedes buscar fugas en ' +
          'una cámara que abriste esta mañana.',
        vacDirty:
          'Abierta a la atmósfera y montada de nuevo húmeda. Desgasificación fuerte que acaba ' +
          'bombeándose.',
        vacBiggerPump:
          'El triple de bomba en la línea estrangulada — la respuesta cara y equivocada.',
      },
    },

    chamber: {
      maint: {
        chamberClean: {
          Label: 'Limpieza y acondicionado de cámara',
          Detail:
            'Limpieza húmeda, remontaje y obleas de acondicionado. Reinicia el estado de las ' +
            'paredes y, con él, el desajuste del match que causaban los depósitos. Cuesta varias ' +
            'horas de disponibilidad del equipo.',
        },
        pumpService: {
          Label: 'Mantenimiento del grupo de bombeo',
          Detail:
            'Restaura la presión base y la tasa de subida. El equipo queda parado durante un turno.',
        },
        replaceFocusRing: {
          Label: 'Sustituir el anillo de enfoque',
          Detail:
            'Restaura el moldeado del campo en el borde. Suele hacerse dentro de una limpieza ' +
            'programada.',
        },
        matchTune: {
          Label: 'Reajustar el match de RF',
          Detail:
            'Recupera la potencia entregada sin abrir la cámara, pero si la causa son los ' +
            'depósitos volverá a derivar enseguida. Trata el síntoma, no el fallo.',
        },
      },
    },

    tool: {
      hmi: {
        Qubit: 'Cúbit',
        WafersPerHour: 'obleas/h',
        Yield: 'Rendimiento',
        WorkZone: 'Zona de trabajo',
        Wires: 'Hilos por dado',
        Welded: 'Soldado',
        WarpUm: 'µm de alabeo',
        Usable: 'Longitud útil',
        Uniformity: 'No uniformidad',
        Thickness: 'Espesor',
        Surface: 'Superficie',
        Stack: 'En la chimenea',
        Species: 'Especie',
        Screened: 'Cribado',
        Repeatability: 'Repetibilidad',
        Removal: 'Eliminación',
        Regime: 'Régimen',
        Recovery: 'Recuperación',
        Purge: 'Purga',
        PumpSpeed: 'Velocidad de bombeo',
        PitchWalk: 'Deriva de paso',
        PctYield: '% de rendimiento',
        OxideCapped: 'con óxido',
        Overkill: 'Sobrerrechazo',
        Nuisance: 'Falsos',
        NmProbe: 'nm de sonda',
        NmPitch: 'nm de paso',
        NmOxide: 'nm de óxido',
        Moisture: 'Humedad',
        LotHere: 'Lote dentro',
        Landing: 'Aterrizaje',
        HfLast: 'HF al final',
        Growth: 'Crecimiento',
        GoodWafers: 'Obleas buenas',
        GoodDie: 'Dados buenos',
        Gain: 'Ganancia ascendente',
        EscapesPpm: 'ppm de fugas',
        Dose: 'Dosis',
        Cycles: 'Ciclos',
        Contrast: 'Contraste',
        Channelled: 'Canalizado',
        Capture: 'Captura',
        Budget: 'Presupuesto',
        Bow: 'Arqueo',
        AcrossDie: 'En el dado',
      },
      history: {
        word: {
          map_clustered: 'el mapa muestra cúmulos',
          map_radial: 'el mapa es radial',
          map_centre: 'el mapa marca el centro',
          map_edge: 'el mapa marca el borde',
          map_uniform: 'el mapa es uniforme',
          attach_void: 'hueco en la adhesión',
          intermetallic: 'intermetálico',
          cratered_pad: 'pad craterizado',
          lifted_ball: 'bola levantada',
          trench_filled: 'zanja rellena',
          trench_seam: 'zanja con costura',
          trench_void: 'zanja con hueco',
          lattice_damaged_but_crystalline: 'red dañada pero cristalina',
          layer_amorphised: 'capa amorfizada',
          transport_limited: 'limitado por transporte',
          transition_limited: 'en régimen de transición',
          reaction_limited: 'limitado por reacción',
          interstitial_rich: 'rico en intersticiales',
          perfect: 'perfecto',
          vacancy_rich: 'rico en vacantes',
          arsenic: 'arsénico',
          antimony: 'antimonio',
          phosphorus: 'fósforo',
          boron: 'boro',
        },
        EtchedCleared: ', {{pct}}% de la oblea despejada hasta la capa de parada',
        Etched: 'Grabada {{s}} s a {{rate}} nm/min, {{nu}}% no uniforme, perfil de ' +
          '{{angle}}°: CD {{cd}} nm en la película',
        LithoFailed: ' — PARTE DEL CAMPO NO SE IMPRIMIÓ',
        LithoExposedEuv: 'Expuesta a 13,5 nm, {{dose}} mJ/cm², paso de {{pitch}} nm: CD {{cd}} nm, ' +
          'LER {{ler}} nm 3σ, {{range}} nm a lo largo del campo',
        LithoExposed: 'Expuesta a 193 nm, {{dose}} mJ/cm², paso de {{pitch}} nm: CD {{cd}} nm, ' +
          'LER {{ler}} nm 3σ, {{range}} nm a lo largo del campo',
        Cooled: 'Enfriado a 10 mK: R·A de la unión {{ra}} Ω·µm², E_J {{ej}} GHz sobre ' +
          '{{cap}} fF, f₀₁ del transmón {{f01}} GHz',
        SemMeasured: 'CD tras revelado {{cd}} nm con paso de {{pitch}} nm, medida a {{ev}} eV de ' +
          'aterrizaje con una sonda de {{probe}} nm',
        EuvExposedPlain: 'Expuesta a 13,5 nm',
        EuvExposed: 'Expuesta a {{dose}} mJ/cm², paso de {{pitch}} nm: CD {{cd}} nm, LER ' +
          '{{ler}} nm 3σ, {{failures}} fallos estocásticos',
        Tested: 'Envejecido {{hours}} h a {{temp}} °C y probado: {{shipped}} enviados, ' +
          '{{overkill}} piezas buenas desechadas, {{escaped}} piezas malas escaparon',
        MouldedCracked: ' ({{pct}}% agrietados por daño de rectificado)',
        MouldedPopcorned: ', EFECTO PALOMITA',
        Moulded: 'Moldeado y refundido: {{um}} µm de alabeo, entran {{die}} dados, salen ' +
          '{{packages}} encapsulados',
        BondedFailure: ' ({{mode}})',
        Bonded: '{{wires}} hilos por dado, {{gf}} gf de cizalla, {{welded}}% soldado — ' +
          'entran {{before}} dados, salen {{after}}',
        DicedChipping: ', ASTILLADO MÁS ALLÁ DE LA CALLE',
        Diced: 'Rebajada a {{um}} µm y singulada: {{die}} dados, {{mpa}} MPa con m = {{m}}',
        SaqpWalk: ' — separación media {{nm}} nm, y una línea de cada dos está mal',
        Saqp: 'SAQP {{mult}}×: paso {{from}} → {{to}} nm, líneas de {{cd}} nm, deriva de ' +
          'paso {{walk}} nm',
        SaqpCollapsed: 'SAQP colapsó: {{reason}}',
        Sorted: 'Clasificada: {{yield}}% de rendimiento, {{good}} dados buenos de ' +
          '{{probed}} probados, {{signature}}',
        Scanned: 'Inspeccionada a {{nm}} nm: reportó {{density}}/cm² ({{nuisance}}% falsos), ' +
          'capturó el {{capture}}% de lo que hay',
        RcaOxideCapped: ', con capa de óxido',
        RcaHfLast: ', HF al final con {{min}} min de espera ({{pct}}% del beneficio ya perdido)',
        Rca: 'Limpieza RCA: {{pct}}% de partículas eliminadas, quedan {{left}}/cm²',
        Exposure: '{{min}} min abierta en una zona de trabajo ISO {{iso}} ({{zone}}/m³ frente ' +
          'a una lectura de sala de {{room}}/m³): +{{adders}} partículas/cm²',
        PlatedOverburden: ', {{over}} nm de sobrecarga y un escalón de {{step}} nm para CMP-11, ' +
          '{{nu}}% no uniforme',
        PlatedVoid: ' ({{pct}}% de hueco)',
        Plated: 'Depositados {{nm}} nm con ganancia {{gain}}×: {{fill}}',
        PolishedWorst: ', lo peor en {{region}} ({{dishing}} nm de hundimiento, {{erosion}} nm de ' +
          'erosión)',
        PolishedBridging: ', EL METAL SIGUE PUENTEANDO',
        Polished: 'Pulida {{s}} s, {{nm}} nm a lo largo del dado',
        AldParasitic: ', {{pct}}% de ello CVD parásito',
        Ald: '{{cycles}} ciclos de ALD, {{nm}} nm con {{nu}}% de no uniformidad',
        AnnealOxide: ', {{nm}} nm de óxido crecido',
        AnnealFresh: ' (una oblea nueva quedaría en {{xj}} nm)',
        Anneal: 'Ciclo de recocido: xj {{xj}} nm',
        ImplantChannelled: ', {{pct}}% CANALIZADO hasta {{nm}} nm',
        Implant: '{{species}} a {{kev}} keV, {{dose}}/nm²: Rp {{rp}} nm, {{lattice}}',
        EpiSpitting: ', partículas de fase gaseosa añadidas',
        EpiSlip: ', DESLIZAMIENTO EN EL BORDE',
        EpiSeam: ', {{um}} µm de unión autodopada',
        Epi: 'Capa epitaxial: {{um}} µm a {{doping}}/nm³, {{regime}}',
        Sliced: 'Cortada, lapeada, grabada y pulida: {{um}} µm, TTV {{ttv}} µm, arqueo ' +
          '{{bow}} µm, {{damage}} µm de daño residual ({{mpa}} MPa). {{good}} obleas ' +
          'buenas del lingote.',
        IngotStructureLost: ' — ESTRUCTURA PERDIDA',
        Ingot: 'Lingote crecido: {{mm}} mm de silicio dopado con {{dopant}}, {{rho}} Ω·cm ' +
          'en la semilla, oxígeno {{oxygen}}/nm³, {{regime}}',
        StockBlank: 'Oblea de almacén: no hay lingote de CZ-07 en la sierra. ',
        BareWafer: 'Oblea pulida sin películas',
      },
      quantum: {
        Dd3Note: 'Todos los números de este panel por encima de aquí tratan los dos puntos ' +
          'como si estuvieran sobre una línea. No lo están: el electrón cruza por un ' +
          'hueco entre compuertas, y el hueco tiene anchura. Así que aquí se suelta ' +
          'un electrón en el punto izquierdo de un potencial tridimensional y se ' +
          'integra la ecuación de Schrödinger para toda la onda, con el mismo ' +
          'propagador que mueve el volumen de efecto túnel en THRM-04 y la difracción ' +
          'en MET-05. Nada le dice lo rápido que debe cruzar. La gráfica cuenta ' +
          'cuánta onda queda a la izquierda, y el acoplamiento se lee en el primer ' +
          'retorno: h/4t. Deja el estrechamiento a cero y la curva gris discontinua, ' +
          'dos niveles con la t del solucionador 1D, queda bajo la azul: dos cálculos ' +
          'que no comparten más que el potencial, de acuerdo. Ahora estrecha el ' +
          'hueco. Un hueco más estrecho sujeta más al electrón a lo ancho, eso cuesta ' +
          'energía de punto cero, y el electrón tiene que encontrarla al pasar: es ' +
          'altura de barrera con otro nombre, la mitad de ħω₀ por cada unidad de ' +
          'estrechamiento. La curva naranja suma exactamente eso a la barrera 1D, y ' +
          'cae a menos de una décima de lo que hace la onda de verdad. Una compuerta ' +
          'de barrera unos nanómetros más estrecha de lo dibujado cambia la velocidad ' +
          'del cúbit más que su voltaje, lo que convierte t en un número de ' +
          'litografía. El pozo se dibuja con sus diez nanómetros reales; en la malla ' +
          'es 2,2 veces más grueso, porque los electrones del silicio son cinco veces ' +
          'más pesados en la dirección de crecimiento y un cambio de variable pone ' +
          'ambas masas en una sola malla. Un electrón, un valle, sin espín, sin ' +
          'ruido, sin relajación.',
        Dd3KnobDetuning: 'Desintonía',
        Dd3KnobPinchValue: 'ħω en el hueco {{hw}} meV',
        Dd3KnobPinch: 'Cuánto estrecha la compuerta el hueco',
        Dd3KnobBarrier: 'Compuerta de barrera',
        Dd3Idle: 'Pulsa Ejecutar: se suelta el electrón en el punto izquierdo y se propaga ' +
          'en un hilo de trabajo, de diez a veinte segundos por ejecución.',
        Dd3Status: '{{ps}} de {{run}} ps · {{left}} % en el punto izquierdo · Σ|ψ|² = {{norm}}',
        Dd3TileNoPinch: 'el hueco es tan ancho como los puntos: nada que añadir',
        Dd3TileAdiabaticNote: '+{{mev}} meV de barrera: el hueco sujeta al electrón con ħω = {{hw}} meV',
        Dd3TileAdiabatic: 'Con la energía de punto cero del hueco',
        Dd3Tile1dNote: 'cruzaría en {{ps}} ps',
        Dd3Tile1d: 'El solucionador 1D, misma compuerta',
        Dd3TileWaiting: 'se lee en el primer retorno del electrón',
        Dd3TileMeasuredNote: 'primer retorno a {{ps}} ps, {{pct}} % al otro lado',
        Dd3TileMeasured: 'Acoplamiento túnel, medido en la ejecución',
        Dd3Crossed: 'primer retorno',
        Dd3ChartY: 'P(izquierda)',
        Dd3ChartX: 'tiempo (ps)',
        Dd3ChartTitle: 'Probabilidad de que el electrón esté en el punto izquierdo',
        Dd3LineAdiabatic: 'Dos niveles, con la energía de punto cero del hueco sumada a la barrera',
        Dd3Line1d: 'Dos niveles, con la t del solucionador 1D',
        Dd3Measured: 'Propagado en 3D',
        Dd3About: 'Qué muestra esto y qué deja fuera el modelo',
        SectionDd3: 'El cruce, propagado en tres dimensiones — y lo que cuesta un hueco más ' +
          'estrecho (Schrödinger, paso de Cayley dividido)',
        NextFrequency: 'Un cúbit de espín en el pozo de EPI-20 se desdobla con un campo magnético ' +
          'en lugar de una unión, a unos 28 GHz por tesla. ¿Qué campo lo haría tan ' +
          'frío como un transmón de 5 GHz en este refrigerador, y qué esperarías que ' +
          'fallara primero?',
        TitleFrequency: 'Población en |1⟩ frente a la frecuencia del cúbit',
        LabelP1High: 'En |1⟩, frecuencia más alta',
        LabelP1Low: 'En |1⟩, frecuencia más baja',
        HypFrequency: 'Un sistema de dos niveles está en su estado fundamental cuando la ' +
          'separación entre sus niveles es grande frente a la energía térmica que lo ' +
          'rodea: lo que importa es hf sobre kT, no T. Si es así, el mismo ' +
          'refrigerador y el mismo cableado deberían dejar un cúbit de baja ' +
          'frecuencia muy mezclado y uno de alta frecuencia limpio, sin que nada ' +
          'cambie en el refrigerador entre ambos.',
        AskedFrequency: '¿Cómo decide la frecuencia del cúbit si el refrigerador está lo bastante ' +
          'frío?',
        SumFrequency: 'Frecuencia del cúbit frente a población térmica: frío es hf frente a kT, ' +
          'no una cifra de milikelvin.',
        NextAttenuation: 'Un chip que midió bien el mes pasado lee hoy un diez por ciento en |1⟩, y ' +
          'todos los termómetros del refrigerador son normales. ¿Qué medirías con el ' +
          'refrigerador aún frío, antes de que nadie proponga calentarlo?',
        TitleAttenuation: 'Temperatura del cúbit frente a la atenuación de la línea',
        LabelPlate: 'La placa en todo momento',
        LabelQubitCold: 'Cúbit, máxima atenuación',
        LabelQubitWarm: 'Cúbit, mínima atenuación',
        HypAttenuation: 'El coaxial que lleva el pulso de excitación lleva también ruido de ' +
          'temperatura ambiente, unos mil fotones en el modo del cúbit. Cada 10 dB de ' +
          'atenuación fría quita nueve décimas de él. Si el cúbit se termaliza con lo ' +
          'que oye y no con la placa en la que se apoya, su temperatura debería caer ' +
          'con cada atenuador hasta que solo quede la emisión de la propia placa, y ' +
          'el termómetro de la placa no debería moverse nada.',
        AskedAttenuation: '¿Cuánta atenuación necesita la línea de excitación de un cúbit?',
        SumAttenuation: 'Atenuación de la línea frente a temperatura del cúbit: ruido de ' +
          'temperatura ambiente, diez veces menos por cada 10 dB.',
        knob: {
          circulation: 'Circulación de helio-3',
          attenuationDb: 'Atenuación de la línea de excitación',
        },
        AssistantBlurb: 'Pregunta cuánta atenuación necesita una línea de excitación, o por qué los ' +
          'cúbits se construyen a 5 GHz. Cada respuesta es el refrigerador ejecutado ' +
          'con esta receta.',
        AssistantLead: 'Asistente de indagación de QDT-24',
        TileExcitedNote: 'para el cúbit de {{f}} GHz diseñado arriba',
        TileExcited: 'Hallado en |1⟩ sin excitación',
        TileQubitNote: '{{n}} fotones térmicos en su modo',
        TileQubit: 'Temperatura del cúbit',
        TilePlateNote: '{{h}} h de 4 K a base',
        TilePlate: 'Placa de la cámara de mezcla',
        SectionTool: 'El refrigerador en este turno: qué tan fría está la placa y qué tan frío ' +
          'está el cúbit',
        fault: {
          missingAttenuator: {
            Evidence: [
              'El cúbit lee un diez por ciento en |1⟩ sin excitación, frente a un límite ' +
                'del uno por ciento, mientras la placa lee 10 mK.',
              'El flujo, la etapa de 4 K y la presión de la cámara son nominales: el ' +
                'refrigerador está sano.',
              'El analizador de redes encuentra 40 dB en una línea especificada a 60.',
            ],
            Truth: 'El atenuador de 20 dB del extremo de la cámara de mezcla de la línea de ' +
              'excitación se quedó fuera cuando se rehicieron las líneas. La línea deja ' +
              'pasar ahora cien veces el ruido de temperatura ambiente que debería: un ' +
              'octavo de fotón en el modo del cúbit en lugar de una milésima, que es un ' +
              'cúbit a 109 mK sobre una placa a 10. Un disparo de cada diez empieza en el ' +
              'estado equivocado. Todos los termómetros son correctos. Un cúbit no está a ' +
              'la temperatura del metal al que está atornillado; está a la temperatura de ' +
              'lo que puede oír.',
            Complaint: 'El refrigerador está perfecto: 10 mK, a tiempo. El cúbit no. La fidelidad ' +
              'de lectura es pésima y T1 se ve bien, y el chip midió bien en el otro ' +
              'refrigerador el mes pasado.',
            Label: 'Falta un atenuador en la línea de excitación',
          },
          canLeak: {
            Evidence: [
              'La presión de la cámara es ocho veces su valor cualificado.',
              'El flujo del destilador es nominal, así que la potencia de enfriamiento ' +
                'está intacta; la etapa de 4 K es nominal, así que el calor extra no baja ' +
                'por los soportes.',
              'El detector de fugas muestra helio en la brida de la cámara.',
            ],
            Truth: 'Un sello de indio de la cámara de vacío tiene una fuga y hay helio gaseoso ' +
              'en el espacio que debería estar vacío. El gas conduce: lleva calor de la ' +
              'pared de 4 K directamente a la placa, ocho veces la fuga cualificada, y la ' +
              'placa queda a raíz de ocho veces la base. Este es el caro, porque hay que ' +
              'llevar el refrigerador a temperatura ambiente para abrirlo, y justo por ' +
              'eso hay que descartar primero los dos parecidos baratos.',
            Complaint: 'En este enfriamiento la placa se estancó en 28 mK y no se mueve. El flujo ' +
              'es normal y la etapa de 4 K es normal.',
            Label: 'Helio en la cámara de vacío',
          },
          warmCompressor: {
            Evidence: [
              'La etapa de 4 K lee 5,2 K. Ni una trampa obstruida ni una cámara con fuga ' +
                'calientan la etapa de 4 K.',
              'El flujo del destilador y la presión de la cámara son nominales.',
              'El agua a la entrada del compresor está a 31 °C frente a un límite de 25 °C.',
            ],
            Truth: 'El compresor que mueve el tubo de pulsos recibe agua de refrigeración a 31 ' +
              '°C y está reduciendo su potencia, así que la etapa de 4 K está a 5,2 K. ' +
              'Todo lo más frío cuelga de esa etapa, y el calor conducido por los ' +
              'soportes y el cableado va aproximadamente como su temperatura al cuadrado: ' +
              '2,6 veces la fuga de calor, raíz de 2,6 veces la temperatura base. Lo de ' +
              'las tardes es el agua de refrigeración del edificio, no el refrigerador.',
            Complaint: 'La temperatura base ha ido subiendo de 10 a 16 mK en una semana, y es peor ' +
              'por las tardes. La circulación está exactamente donde se fijó.',
            Label: 'El compresor del tubo de pulsos va caliente',
          },
          pluggedTrap: {
            Evidence: [
              'El flujo en el destilador es el 20% de la consigna. Una fuga en la cámara ' +
                'añade calor; no quita flujo.',
              'La presión de la cámara y la etapa de 4 K son ambas nominales.',
              'La caída de presión en la trampa fría es cinco veces la nominal.',
            ],
            Truth: 'La trampa fría de nitrógeno que depura el helio-3 de retorno se ha cargado ' +
              'de aire congelado y estrangula la circulación a una quinta parte de su ' +
              'consigna. La potencia de enfriamiento va como la circulación por T al ' +
              'cuadrado, así que una quinta parte del flujo mantiene raíz de cinco veces ' +
              'la temperatura contra la misma fuga de calor: 22 mK. Dentro del ' +
              'refrigerador no pasa nada. El arreglo está en el armario de gestión de ' +
              'gases, lleva una tarde y no necesita calentar.',
            Complaint: 'La placa no baja de 22 mK. En el último enfriamiento llegó a 10. Alguien ' +
              'ha sugerido que la cámara tiene una fuga y quiere calentar y rehacer todos ' +
              'los sellos.',
            Label: 'La trampa fría se está obstruyendo',
          },
          cryoHealthy: {
            Evidence: [
              'La placa está a 10 mK y el cúbit lee un octavo de un uno por ciento en |1⟩.',
              'Todas las lecturas de banco son nominales.',
            ],
            Truth: 'No pasa nada. La decisión correcta es empezar a medir.',
            Complaint: 'Enfriamiento de rutina. La placa llegó a base durante la noche.',
            Label: 'Nada anda mal',
          },
        },
        reading: {
          lineInsertionLoss: {
            Label: 'Pérdida de inserción de la línea de excitación con un analizador de redes ' +
              '(2.0 h)',
          },
          leakCheck: {
            Limit: 'sin señal',
            Label: 'Prueba de fugas con helio en la cámara de vacío, con el refrigerador frío ' +
              '(3.0 h)',
          },
          compressorWater: {
            Limit: '< 25 °C de entrada',
            Label: 'Agua de refrigeración en el compresor del tubo de pulsos (30 min)',
          },
          trapPressure: {
            Label: 'Caída de presión en la trampa fría de nitrógeno (30 min)',
          },
          qubitPopulation: {
            Label: 'Población del cúbit en |1⟩ sin excitación',
          },
          canPressure: {
            Label: 'Presión de la cámara de vacío',
          },
          fourKelvin: {
            Label: 'Temperatura de la etapa de 4 K',
          },
          stillFlow: {
            Label: 'Flujo de helio-3 en el destilador',
          },
        },
        aspect: {
          wiring: {
            Action: 'Calentar, abrir el refrigerador y volver a montar el atenuador que falta ' +
              'en la línea de excitación',
            Label: 'Líneas de microondas',
          },
          vacuum: {
            Action: 'Calentar, encontrar la fuga, rehacer el sello de indio y volver a enfriar',
            Label: 'Cámara de vacío',
          },
          pulseTube: {
            Action: 'Restablecer el agua de refrigeración del compresor y dejar que la etapa de ' +
              '4 K se recupere',
            Label: 'Tubo de pulsos y su compresor',
          },
          circulation: {
            Action: 'Calentar y bombear la trampa fría de nitrógeno, y volver a subir la ' +
              'circulación',
            Label: 'Circulación de helio-3',
          },
        },
        SecondaryLabel: 'Población del cúbit en |1⟩',
        ToolLabel: 'QDT-24 Ensayo criogénico de dispositivos cuánticos',
        GuardWafer: 'Este lote sigue siendo una oblea entera. Córtalo primero en DICE-15: el ' +
          'criostato admite un chip en un portamuestras, y su placa fría es más ' +
          'pequeña que la oblea.',
        GuardPackaged: 'Estas piezas ya están moldeadas en encapsulados. Un chip de qubits entra ' +
          'al criostato desnudo, con hilos soldados a un portamuestras.',
        CoolLot: 'Enfriar a 10 mK y caracterizar los cúbits',
        NoteNoLot: 'No hay chip en el refrigerador. Trae aquí un chip cuántico terminado para ' +
          'enfriarlo y medirlo.',
        SectionSpin: 'El cúbit de espín en el pozo de EPI-20: desdoblamiento de valles (enlace ' +
          'fuerte)',
        SectionTransmon: 'El transmón: el hamiltoniano, resuelto (bisección de Sturm)',
        DriveRabiTitle: 'Un pulso resonante: las poblaciones oscilan',
        DriveRabiX: 'tiempo (ns)',
        DriveRabiY: 'población',
        DriveP0: '|0⟩',
        DriveP1: '|1⟩',
        DriveP2: '|2⟩ (fugado)',
        DriveLeakTitle: 'Un pulso π demasiado corto se fuga del cúbit',
        DriveLeakX: 'duración de la compuerta (ns)',
        DriveLeakY: 'log₁₀ de la población en |2⟩ y por encima',
        DriveLeakSeries: 'fuga tras un pulso π calibrado',
        DriveSpeedLimit: '1/|α| = {{nsec}} ns',
        DriveThisGate: 'esta compuerta',
        DriveRamseyTitle: 'Ramsey: la fase que se lleva el ruido de carga',
        DriveRamseyX: 'espera entre los dos pulsos π/2 ({{unit}})',
        DriveRamseyY: 'probabilidad de |0⟩ tras el segundo pulso',
        DriveFringe: 'franja',
        DriveEnvelope: 'envolvente',
        DriveT2: 'T₂*',
        DriveStatF01: 'Frecuencia del cúbit f₀₁',
        DriveStatRatio: 'E_J/E_C = {{ratio}}',
        DriveStatAlpha: 'Anarmonicidad α',
        DriveStatLimit: 'límite de velocidad 1/|α| = {{nsec}} ns',
        DriveStatRabi: 'Frecuencia de Rabi',
        DriveStatPi: 'un pulso π cuadrado dura {{nsec}} ns',
        DriveStatLeak: 'Fuga tras un pulso π de {{nsec}} ns',
        DriveStatP1: 'P(|1⟩) = {{p}}',
        DriveFrameRwa: 'aproximación de onda rotante',
        DriveFrameLab: 'marco del laboratorio, con los términos contrarrotantes',
        DriveStatT2: 'T₂* por ruido de carga',
        DriveStatAsymptotic: 'E_J/E_C supera lo que resuelve la base de carga; aquí la oscilación de f₀₁ es la forma asintótica de Koch, y el ruido de carga ya no limita este cúbit',
        DriveStatSigma: 'ruido de carga de desplazamiento σ = {{sigma}} e',
        DriveTransmon: 'Transmón (E_J de la unión)',
        DriveBox: 'Caja de pares de Cooper (E_J = E_C)',
        DriveShowLeak: 'Fuga frente a duración de la compuerta',
        DriveShowRamsey: 'Franja de Ramsey',
        DriveKnobCap: 'Capacitancia en paralelo',
        DriveKnobCapValue: '{{ff}} fF · E_C = {{ec}} MHz',
        DriveKnobAmp: 'Amplitud del pulso',
        DriveKnobGate: 'Duración del pulso π',
        DriveKnobSigma: 'Ruido de carga de desplazamiento σ',
        DriveKnobSigmaValue: '{{sigma}} e',
        SectionDrive: 'El transmón, excitado: Rabi, fuga y desfase (ecuación de Schrödinger, RK4)',
        DriveNote: 'El espectro de arriba, puesto a hacer algo. Un tono de microondas se acopla a la carga de la isla, y en la base propia de la escalera esa carga solo une niveles vecinos, así que un tono a f₀₁ hace oscilar el cúbit entre |0⟩ y |1⟩ a la frecuencia de Rabi de la izquierda. El problema es |2⟩. Está solo a la anarmonicidad α de donde caería el siguiente escalón regular, y un pulso más corto que unos pocos 1/|α| tiene ancho de banda suficiente para alcanzarlo. La curva de fuga es un pulso π calibrado a cada duración: el límite de velocidad bajo el que vive todo procesador superconductor. El otro gráfico es el otro reloj. La carga parásita del sustrato deriva, la dependencia de la escalera respecto a ella mueve f₀₁, y un conjunto de disparos pierde su fase en T₂*. Cambie a la caja de pares de Cooper y la fase desaparece en nanosegundos; el transmón, con los valores por defecto del panel, la conserva de decenas a cientos de microsegundos a lo largo del control de ruido, unas veinte mil veces más, y por eso existe. Cuatro niveles resueltos se excitan en la aproximación de onda rotante donde es válida, y en el marco del laboratorio, conservando los términos contrarrotantes, donde no lo es: la caja de pares de Cooper, o un pulso intenso frente a la frecuencia del cúbit. El ruido es cuasiestático, y se omiten la relajación de energía, los espectros de ruido 1/f y la conformación de pulsos como DRAG.',
        SectionChargeQubit: 'Un electrón, dos puntos: el cúbit de carga en movimiento (Schrödinger, pasos unitarios)',
        ChargeQubitNote: 'La sección anterior termina en un número: un acoplamiento túnel t de unas decenas de microelectronvoltios. Esto es lo que ese número significa. Deja el electrón en el punto izquierdo y suéltalo: no se queda - cruza al punto derecho en h/(4t), aquí unos cincuenta picosegundos, y vuelve, indefinidamente, porque nada en este modelo puede detenerlo.\n\nLa forma de la izquierda es |c_L φ_L + c_R φ_R|², los dos orbitales que el solucionador de canje ya encontró, con las amplitudes integradas paso a paso en lugar de tomadas de la fórmula de libro contra la que el arnés las compara. Inclina los puntos y ocurren dos cosas a la vez: cruza menos electrón, como 4t²/(4t² + ε²), y lo que cruza va más rápido.\n\nLa rampa es como se acciona una puerta real: barre la desintonía a través de cero y el electrón sigue el anticruce hasta el otro punto si el barrido es lento, o lo atraviesa de largo si es rápido, con la probabilidad de Landau-Zener.\n\nEl último mando es la razón por la que un cúbit de carga es una herramienta didáctica y no un producto: la carga parásita mueve la desintonía, la frecuencia se mueve con ella y la oscilación se difumina en nanosegundos - cientos de idas y vueltas, frente a los cientos de miles de un transmón. Con desintonía nula la frecuencia depende del ruido solo en segundo orden, y eso es un punto dulce. El modelo son dos niveles y ruido cuasiestático: sin valles, sin fonones, sin relajación, y el electrón nunca se mide.',
        CqDensityTitle: 'Dónde está el electrón: |ψ|² a lo largo de los dos puntos',
        CqDensityX: 'posición a lo largo de los puntos (nm)',
        CqDensityY: 'energía (meV)',
        CqPotential: 'Potencial de las puertas',
        CqDensitySeries: 'Densidad electrónica |ψ|²',
        CqTraceTitle: 'El electrón yendo y viniendo: P(izquierda) frente al tiempo',
        CqTraceX: 'tiempo (ns)',
        CqTraceY: 'probabilidad de que esté en el punto izquierdo',
        CqTraceSeries: 'P(izquierda), integrada',
        CqEnvelopeSeries: 'Visibilidad que deja el ruido de carga',
        CqNow: 'ahora',
        CqRampTitle: 'Una rampa de desintonía a través del anticruce',
        CqRampX: 'log₁₀ de la velocidad de rampa (µeV/ns)',
        CqRampY: 'probabilidad de que se quedara en su punto',
        CqRampSeries: 'Integrada',
        CqRampFormula: 'Fórmula de Landau–Zener',
        CqRampHere: 'esta rampa',
        CqCrossover: 'transición',
        CqToggleOscillation: 'Déjalo oscilar',
        CqToggleRamp: 'Rampa a través del anticruce',
        Cq3dCrossing: 'cruce',
        Cq3dSweep: 'el barrido dura',
        Dot3dInLeft: 'en el punto izquierdo',
        Dot3dInRight: 'en el punto derecho',
        CqAbout: 'Qué muestra esto y qué deja fuera el modelo',
        CqBenchKnobs: 'Mandos',
        CqBenchReadouts: 'Lecturas',
        Dot3dAbout: 'Sobre esta imagen',
        Dot3dScaleTailsShort: 'Colas (log)',
        Dot3dScaleDensityShort: 'Densidad (lineal)',
        Dot3dAnimCaption: 'Un electrón, en el dispositivo. La nube es la suma de los dos orbitales de los puntos con las amplitudes cambiantes del electrón, |c_L φ_L + c_R φ_R|², interferencia incluida: por eso se vacía de un punto al otro en lugar de ser dos nubes que intercambian brillo. A lo largo de los puntos la forma está resuelta; a través de ellos y del pozo de 10 nm es un estado fundamental supuesto.',
        CqStatCrossover: 'Ritmo de rampa de cruce',
        CqStatCrossoverNote: '2πt²/ħ: las rampas más lentas llevan el electrón al otro punto, las más rápidas lo dejan atrás',
        CqStatStays: 'Esta rampa deja atrás',
        CqStatStaysNote: 'la fórmula de Landau–Zener da {{formula}}%',
        YieldShort: 'El {{y}}% de los chips no tiene colisiones de frecuencia · cada cúbit cae a unos {{sigma}} MHz de su objetivo',
        CqRampAveraged: 'Lejos del cruce las dos ramas baten más rápido de lo que un fotograma puede mostrar, así que allí la imagen es el promedio de ese batido; cerca del cruce se dibuja el batido mismo.',
        CqStatT: 'Acoplamiento túnel t',
        CqStatTNote: 'los mismos {{freq}} que la sección de canje lee en este diseño',
        CqStatPeriod: 'Ida y vuelta',
        CqStatPeriodNote: '{{half}} para dejar el electrón al lado: medio periodo',
        CqStatAmplitude: 'Cuánto llega a cruzar',
        CqStatAmplitudeNote: '4t²/(4t² + ε²) con ε = {{eps}} µeV',
        CqStatT2: 'Coherencia T₂*',
        CqStatT2Note: '{{periods}} idas y vueltas antes de que la oscilación se difumine',
        CqStatT2Coherent: 'sin ruido: coherente',
        CqStatT2CoherentNote: 'sube el ruido de carga: es lo único que un cúbit de carga no sobrevive',
        CqSlowdown: 'ralentizado {{factor}}×: un segundo aquí es {{nsec}} ns en el criostato',
        CqRun: 'Ejecutar',
        CqPause: 'Pausa',
        CqReset: 'Volver al punto izquierdo',
        CqKnobBarrier: 'Puerta de barrera',
        CqKnobDetuning: 'Desintonía entre los puntos',
        CqKnobNoise: 'Ruido de carga sobre la desintonía',
        CqNoiseNone: 'ninguno',
        CqKnobRate: 'Velocidad de rampa',
        CqKnobRateValue: '{{rate}} µeV/ns · se queda el {{stays}}%',
        ExchOff: 'apagado',
        ExchPotentialTitle: 'Dos puntos, un electrón: los orbitales localizados',
        ExchPotentialX: 'posición a lo largo de los puntos (nm)',
        ExchPotentialY: 'energía (meV)',
        ExchPotentialSeries: 'Potencial de las compuertas',
        ExchLeft: 'Punto izquierdo |φ_L|²',
        ExchRight: 'Punto derecho |φ_R|²',
        ExchJTitleBarrier: 'Intercambio J frente a la compuerta de barrera',
        ExchJTitleDetuning: 'Intercambio J frente a la desintonía',
        ExchJXBarrier: 'altura de la compuerta de barrera (meV)',
        ExchJXDetuning: 'desintonía ε (meV)',
        ExchJY: 'log₁₀ J (µeV)',
        ExchJSeries: 'J, modelo de Hubbard',
        ExchJHere: 'este diseño',
        ExchJGate: '√SWAP de 10 ns',
        ExchStatT: 'Acoplamiento túnel t',
        ExchStatTNote: 'el elemento fuera de la diagonal entre los orbitales localizados; el punto de silla está {{saddle}} meV por encima del fondo del punto',
        ExchStatU: 'Costo de compartir un punto, U',
        ExchStatUNote: 'energía de Coulomb de ambos electrones en un punto de {{size}} nm',
        ExchStatJ: 'Intercambio J',
        ExchStatJNote: '{{mhz}} MHz; la fórmula de túnel débil 4t²U/(U² − ε²) da {{formula}}',
        ExchStatGate: 'Tiempo de la compuerta √SWAP',
        ExchStatGateNote: 'h/(4J): un cuarto de vuelta de la fase de intercambio',
        ExchStatMerged: 'el electrón ya no está retenido en dos puntos: baje el nivel fundamental por debajo del punto de silla o suba la barrera',
        ExchStatTilted: 'la inclinación ha llevado ambos orbitales a un mismo punto: no hay un electrón vecino con el que intercambiar; devuelva la desintonía hacia cero',
        ExchSweepBarrier: 'J frente a la compuerta de barrera',
        ExchSweepDetuning: 'J frente a la desintonía',
        ExchKnobBarrier: 'Compuerta de barrera',
        ExchKnobDetuning: 'Desintonía entre los puntos',
        ExchKnobSep: 'Separación de los puntos (medio paso)',
        ExchKnobHw: 'Confinamiento del punto ħω₀',
        SectionExchange: 'Dos qubits de espín y la compuerta entre ellos: acoplamiento de intercambio (Schrödinger, Hubbard)',
        ExchangeNote: 'Un punto cuántico contiene un qubit, y una computadora necesita que dos de ellos interactúen. En el silicio esa interacción es el intercambio, y nadie la cablea. Dos electrones en puntos vecinos pueden bajar su energía compartiendo brevemente un punto, pero el principio de Pauli solo lo permite cuando sus espines son opuestos, así que el singlete queda una energía J por debajo del triplete; mantenga J encendido durante h/(4J) y los espines forman una compuerta √SWAP. Cada paso se resuelve aquí: un electrón en el potencial de doble punto da el acoplamiento túnel t entre los orbitales localizados, la integral de Coulomb sobre el orbital izquierdo da U, el costo de compartir un punto, y el modelo de Hubbard de dos electrones da J, que con túnel débil es 4t²U/(U² − ε²). Suba la compuerta de barrera y t cae exponencialmente, y J con su cuadrado: diez milielectronvoltios en una sola compuerta cambian J en casi cinco décadas, y así es como un chip enciende y apaga una compuerta. El modelo es unidimensional a lo largo de los puntos, con el tamaño del punto suavizando el núcleo de Coulomb, y no incluye valles, acoplamiento espín-órbita ni ruido de carga.',
        Dot3dT: 'acoplamiento túnel t',
        Dot3dJ: 'intercambio J',
        Dot3dGate: '√SWAP',
        Dot3dCaption: 'A lo largo de los puntos: resuelto. A través de los puntos y del pozo de 10 nm: estados fundamentales supuestos. Una compuerta plunger sobre cada punto y la compuerta de barrera entre ellos; un color más cálido indica una barrera más alta. El blanco marca dónde se solapan los dos orbitales.',
        Dot3dScaleTails: 'Mostrar las colas (logarítmica, cuatro décadas)',
        Dot3dScaleDensity: 'Densidad real (lineal)',
        SectionJunction: 'La unión Josephson: un nanómetro de óxido (matriz de transferencia, ' +
          'Landauer)',
        SpinNote: 'La banda de conducción del silicio tiene su energía mínima en seis puntos ' +
          'del espacio de momentos; un pozo tensionado deja dos de ellos, los valles, ' +
          'casi al mismo nivel. La cadena de aquí es el modelo más pequeño que tiene ' +
          'dos valles —un sitio por plano atómico, con saltos elegidos para poner el ' +
          'mínimo donde lo tiene el silicio—, así que un escalón Si/SiGe abrupto los ' +
          'separa cientos de µeV. Difumina ese escalón medio nanómetro, como hace el ' +
          'germanio cuando el crecimiento va caliente, y el desdoblamiento suave se ' +
          'desploma; pero la aleación es aleatoria átomo a átomo, y el germanio ' +
          'aleatorio que toca el electrón vuelve a separar los valles por azar. El ' +
          'rendimiento de cúbits de espín de una fab es la fracción de puntos que ' +
          'ganan esa lotería con margen suficiente para mantener el espín fuera del ' +
          'valle superior al campo al que se opera.',
        KnobB: 'Campo magnético',
        KnobDot: 'Radio del punto',
        KnobField: 'Campo de puerta',
        KnobGe: 'Germanio en las barreras',
        KnobWell: 'Espesor del pozo de silicio',
        KnobInterface: 'Anchura de la intercara (crecimiento en EPI-20)',
        DotNext: 'Mirar otro punto',
        DotSmooth: 'Aleación suave, sin desorden',
        DotAlloy: 'Un punto real: aleación aleatoria',
        StatThermalNote: 'el desdoblamiento de valles tiene que ser muchas veces esto, o el valle ' +
          'superior se llena',
        StatThermal: 'Energía térmica a 100 mK',
        StatHotSpotNote: 'donde el desdoblamiento de espín iguala al de valles y el espín se relaja ' +
          'más rápido: hay que quedarse muy por debajo',
        StatHotSpot: 'Punto caliente',
        StatQubitNote: 'a {{b}} T: un desdoblamiento Zeeman de {{ez}} µeV',
        StatQubit: 'Frecuencia del cúbit de espín',
        StatWaferNote: 'de los puntos por encima de 100 µeV; mediana {{median}}, 10-90%: ' +
          '{{lo}}-{{hi}} µeV',
        StatWafer: 'En cincuenta puntos',
        StatThisDotNote: 'desdoblamiento de valles del punto dibujado; {{smooth}} µeV sin desorden',
        StatThisDot: 'Este punto',
        LevelsHot: 'punto caliente',
        LevelsHere: 'campo de trabajo',
        LevelsY: 'energía (µeV)',
        LevelsX: 'campo magnético (T)',
        LevelsTitle: 'Niveles de espín y de valle frente al campo magnético',
        LevelValley1Up: 'valle superior, espín arriba',
        LevelValley1Down: 'valle superior, espín abajo',
        LevelValley0Up: 'valle inferior, espín arriba',
        LevelValley0Down: 'valle inferior, espín abajo',
        ValleyTarget: '100 µeV',
        ValleyHere: 'este crecimiento',
        ValleyBand: '10% y 90% de los puntos',
        ValleyMedian: 'Aleación real, punto mediano',
        ValleySmooth: 'Aleación suave (sin desorden)',
        ValleyY: 'log₁₀ desdoblamiento de valles (µeV)',
        ValleyX: 'anchura de la intercara Si/SiGe (nm)',
        ValleyTitle: 'Desdoblamiento de valles frente a la anchura de la intercara',
        WellDensity: 'El electrón',
        WellSmooth: 'Perfil suave',
        WellAlloy: 'La aleación de este punto',
        WellY: 'borde de la banda de conducción (meV)',
        WellX: 'altura a través de la pila (nm)',
        WellTitle: 'El pozo, plano a plano, y dónde está el electrón',
        YieldNote: 'Con esta dispersión el cúbit de arriba cae a unos {{sigma}} MHz de su ' +
          'objetivo, una sigma. Dos vecinos colisionan cuando cualquiera cae a menos ' +
          'de {{window}} MHz de la línea 0-1 o 1-2 del otro, donde una puerta de dos ' +
          'cúbits excitaría la transición equivocada, y el {{y}}% de los chips no ' +
          'tiene ninguna colisión. Medir cada unión en caliente y ajustarla con un ' +
          'recocido láser es una forma en que las fabs reducen la dispersión.',
        KnobSpread: 'Dispersión de la resistencia de unión en la oblea',
        TransmonNote: 'El hamiltoniano del circuito es 4E_C(n − n_g)² − E_J cos φ, donde n cuenta ' +
          'pares de Cooper a través de la unión y φ es la diferencia de fase, ' +
          'conjugadas como posición y momento. Escrito en la base del número de pares ' +
          'es una matriz tridiagonal, y sus autovalores son la escalera. Reduce el ' +
          'condensador hasta que E_J se acerque a E_C y las bandas oscilan con cada ' +
          'carga parásita; hazlo grande y se aplanan exponencialmente mientras la ' +
          'anarmonicidad —la diferencia entre el primer y el segundo escalón que ' +
          'permite a un pulso excitar solo el cúbit— se reduce solo como una ' +
          'potencia. Ese compromiso es el transmón.',
        KnobNg: 'Carga de desplazamiento',
        KnobEj: 'Energía de Josephson E_J / h',
        KnobCapValue: '{{ff}} fF · E_C/h {{ec}} MHz',
        KnobCap: 'Capacidad en paralelo',
        StatShiftNote: 'cuánto mueve a este cúbit 0,01 nm más de AlOx',
        StatShift: 'Una décima de ångström de óxido',
        StatDispersionNote: 'fórmula asintótica de Koch et al.: {{koch}}',
        StatDispersion: 'Dispersión de carga de f₀₁',
        StatRatioNote: 'por encima de unos 20 es un transmón; cerca de 1, una caja de pares de ' +
          'Cooper',
        StatRatio: 'E_J / E_C',
        StatAnharmNote: 'cerca de −E_C, que son −{{ec}} MHz: lo que permite a un pulso excitar solo ' +
          '0→1',
        StatAnharm: 'Anarmonicidad',
        StatF01Note: 'la fórmula de libro √(8E_J E_C) − E_C da {{asym}} GHz',
        StatF01: 'Frecuencia del cúbit f₀₁',
        YieldHere: 'esta fab',
        YieldSeries: 'Monte Carlo sobre chips',
        YieldY: 'chips sin colisión de frecuencia (%)',
        YieldX: 'dispersión de la resistencia de unión, una sigma (%)',
        YieldTitle: 'Chips sin colisiones, {{n}} cúbits en cadena',
        BandsHere: 'n_g',
        BandsY: 'energía / h sobre la media del fundamental (GHz)',
        BandsX: 'carga de desplazamiento n_g (pares de Cooper)',
        BandsTitle: 'La escalera frente a la carga parásita en la isla',
        PotentialExcited: '|1⟩',
        PotentialGround: '|0⟩',
        PotentialSeries: 'Potencial de Josephson',
        PotentialY: 'energía / h (GHz)',
        PotentialX: 'fase a través de la unión φ (rad)',
        PotentialTitle: 'El péndulo cuántico: −E_J cos φ y sus dos estados más bajos',
        JunctionNote: 'La unión se forma dejando entrar oxígeno en el evaporador durante unos ' +
          'minutos entre dos depósitos de aluminio, y el óxido que queda es un ' +
          'nanómetro o así de AlOx amorfo. Los electrones lo cruzan por efecto túnel, ' +
          'así que su resistencia crece exponencialmente con el espesor: la lección ' +
          'del óxido de puerta de THRM-04, con el objetivo al revés, porque aquí la ' +
          'fab necesita una cantidad precisa de efecto túnel, no ninguna. La ' +
          'resistencia se mide en caliente, unión por unión, porque Ambegaokar y ' +
          'Baratoff demostraron que fija la corriente crítica una vez que el chip ' +
          'está frío. La altura de la barrera y la masa se ajustan a uniones reales; ' +
          'la pendiente de la curva no depende de ellas.',
        KnobMass: 'Masa efectiva en el óxido',
        KnobBarrier: 'Altura de la barrera',
        KnobArea: 'Área de la unión',
        KnobOxide: 'Espesor del óxido',
        StatEjNote: 'E_J/h: sobre lo que se construye el transmón de abajo',
        StatEj: 'Energía de Josephson',
        StatIcNote: 'Ambegaokar-Baratoff: I_c R_n = πΔ/2e, una vez que el aluminio es ' +
          'superconductor',
        StatIc: 'Corriente crítica',
        StatRnNote: 'para una unión de {{a}} µm², medida en caliente',
        StatRn: 'Resistencia en estado normal',
        StatRaNote: '×{{f}} por cada ångström más de óxido',
        StatRa: 'R·A',
        RaBand: 'uniones de cúbits',
        RaHere: 'este óxido',
        RaSeries: 'Landauer, con la matriz de transferencia',
        RaY: 'log₁₀ R·A (Ω·µm²)',
        RaX: 'espesor de AlOx (nm)',
        RaTitle: 'Resistencia frente al espesor del óxido',
        JunctionWave: 'la onda del electrón a través del óxido: sobrevive {{f}} de ella',
        JunctionAl: 'Al',
      },
      ebeam: {
        NextCurrent: 'El estudio de repetibilidad falló y los fotogramas sueltos se ven nítidos. ' +
          '¿Cuál mirarías primero entre la corriente, los fotogramas y el suelo bajo ' +
          'la máquina, y qué lectura te diría que no son los otros dos?',
        TitlePrecisionCurrent: 'Repetibilidad y punto frente a la corriente de sonda',
        LabelSpotThere: 'Punto con esa corriente',
        LabelAtCurrent: 'Con una corriente de',
        LabelBestPrecision: 'Mejor repetibilidad',
        HypCurrent: 'Un borde se localiza en un perfil hecho de electrones contados, así que su ' +
          'dispersión debería caer como uno sobre la raíz cuadrada de la corriente. ' +
          'Pero la corriente sale de una fuente de brillo fijo: pedir más significa ' +
          'proyectar un trozo mayor de la punta sobre la oblea. Si ambas cosas son ' +
          'ciertas, la repetibilidad debería mejorar deprisa al principio y luego ' +
          'estancarse: por el punto que crece, o por el ruido que la corriente no ' +
          'puede reducir.',
        AskedCurrent: '¿Da más corriente de haz una CD que repite mejor?',
        SumCurrent: 'Corriente de sonda frente a repetibilidad: el ruido de conteo cae hasta ' +
          'que solo queda el ruido que la corriente no puede tocar.',
        NextLanding: 'La sección de rendimiento de arriba muestra dónde una resina deja de ' +
          'cargarse. Si el punto más nítido está en 2000 eV y el punto de carga ' +
          'neutra está cerca de 500, ¿a cuál renunciarías, y cómo se vería la imagen ' +
          'si eligieras mal?',
        TitleSpotEnergy: 'Tamaño del punto frente a la energía de aterrizaje',
        LabelChromaticShare: 'Desenfoque cromático en el extremo bajo',
        LabelSpotHigh: 'Punto, energía más alta',
        LabelSpotLow: 'Punto, energía más baja',
        HypLanding: 'Dos desenfoques dependen de la energía del haz. La difracción va como la ' +
          'longitud de onda del electrón, que cae como uno sobre la raíz cuadrada de ' +
          'la energía. El desenfoque cromático va como la dispersión de energía del ' +
          'cañón dividida por la energía, que cae más deprisa. Si ambos son ' +
          'correctos, el punto debería encogerse de forma constante al endurecer el ' +
          'haz, y la parte cromática debería desplomarse primero.',
        AskedLanding: '¿Qué le hace la energía de aterrizaje al punto?',
        SumLanding: 'Energía de aterrizaje frente al tamaño del punto: la difracción y el ' +
          'desenfoque cromático se reducen ambos cuando el haz se endurece.',
        knob: {
          alphaMrad: 'Semiángulo de convergencia',
          frames: 'Fotogramas promediados',
          currentPa: 'Corriente de sonda',
          landingEv: 'Energía de aterrizaje',
        },
        AssistantBlurb: 'Pregunta qué le hace la energía de aterrizaje al punto, o si más corriente ' +
          'de haz hace que una CD repita mejor. Cada respuesta es la columna ' +
          'ejecutada con esta receta.',
        AssistantLead: 'Asistente de indagación de SEM-23',
        TileSignalNote: 'corriente × fotogramas × detector, frente a lo cualificado',
        TileSignal: 'Señal por medición',
        TilePrecisionNote: 'la misma línea, medida una y otra vez',
        TilePrecision: 'Repetibilidad',
        TileSpotNote: 'difracción {{d}} nm, cromático {{c}} nm',
        TileSpot: 'Punto sobre la oblea',
        SectionTool: 'El CD-SEM en este turno: qué tan pequeño ve, qué tan bien repite',
        fault: {
          tiredScintillator: {
            Evidence: [
              'La señal del detector en la referencia es el 25% de lo cualificado.',
              'El ruido está DENTRO de cada fotograma, no entre fotogramas: la dispersión ' +
                'del registro es nominal.',
              'El tamaño del punto y el espectro de potencia de la imagen son nominales.',
            ],
            Truth: 'El centelleador lleva años quemándose con electrones y da una cuarta parte ' +
              'de su luz original. Menos electrones contados por píxel es más ruido por ' +
              'píxel, y un borde hallado en un perfil ruidoso deambula: la precisión va ' +
              'como uno sobre la raíz cuadrada de la señal, así que una cuarta parte de ' +
              'la señal es el doble de dispersión. El punto no se ve afectado. Más ' +
              'fotogramas sí lo arreglan, a cuatro veces la dosis sobre una resina que ' +
              'encoge bajo el haz.',
            Complaint: 'La repetibilidad se ha ido saliendo de límite a lo largo de un mes. Las ' +
              'imágenes se ven granuladas, y los operadores han empezado a promediar más ' +
              'fotogramas para conseguir una limpia, lo que ha reducido la productividad ' +
              'a la mitad.',
            Label: 'Centelleador del detector agotado',
          },
          groundedMount: {
            Evidence: [
              'La dispersión del registro entre fotogramas es el triple de su límite ' +
                'mientras el punto de un solo fotograma es nominal.',
              'La señal del detector es nominal, así que no es ruido de conteo.',
              'El acelerómetro muestra que el bastidor se mueve con el suelo.',
            ],
            Truth: 'Un soporte neumático ha perdido presión y el bastidor descansa sobre su ' +
              'tope, así que la columna ahora sigue al suelo: 1,2 nm rms de movimiento ' +
              'entre haz y oblea. Apenas se nota en un fotograma porque un fotograma es ' +
              'rápido. Se nota entre fotogramas, porque cada uno cae en un sitio ' +
              'ligeramente distinto, y el borde promediado deambula con ellos. El ' +
              'escritorio de la consola de SEM-23, separado del pedestal, está ahí para ' +
              'evitar exactamente esto.',
            Complaint: 'El estudio de repetibilidad falló: la misma línea medida diez veces se ' +
              'dispersa el triple de lo que debería. Las imágenes sueltas se ven ' +
              'perfectamente nítidas.',
            Label: 'Un soporte de aislamiento no flota',
          },
          stigmatorDrift: {
            Evidence: [
              'El espectro de potencia de la imagen es elíptico. Un desenfoque cromático ' +
                'es redondo.',
              'El ruido de emisión es nominal, y el flash a la punta no cambió nada.',
              'A través del foco, el desenfoque se estira y gira noventa grados.',
            ],
            Truth: 'El estigmador ha derivado y hay 3,5 nm de astigmatismo en el punto. Se ' +
              'parece al cañón porque ambos dan una imagen blanda y una línea ancha, y no ' +
              'es el cañón porque el desenfoque tiene DIRECCIÓN: el punto se estira hacia ' +
              'un lado por debajo del foco y hacia el otro por encima. Las líneas que ' +
              'corren a lo largo del estiramiento siguen viéndose nítidas, y por eso un ' +
              'operador dice que la máquina está bien y el siguiente dice que no.',
            Complaint: 'Las imágenes están blandas y las líneas leen anchas. Esta mañana se dio un ' +
              'flash al cañón y no cambió absolutamente nada.',
            Label: 'Astigmatismo sin corregir',
          },
          gassyTip: {
            Evidence: [
              'El ruido de la corriente de emisión sube junto con el desenfoque. El ' +
                'astigmatismo no hace parpadear un cañón.',
              'El desenfoque es redondo: el espectro de potencia no está estirado.',
              'La serie de desenfoque da 0,7 eV de dispersión.',
            ],
            Truth: 'La punta fría de emisión de campo lleva días sin flash. El gas adsorbido ' +
              'en ella cambia la función de trabajo parche a parche, y la dispersión de ' +
              'energía ha pasado de 0,25 a 0,7 eV. Con una energía de aterrizaje de 500 ' +
              'eV esa dispersión es casi todo el punto: el desenfoque cromático va como ' +
              'ΔE/E, así que una columna de bajo voltaje es justo la que lo nota. Las ' +
              'líneas no son anchas. Lo es la regla.',
            Complaint: 'Las imágenes están blandas desde el fin de semana y todas las líneas leen ' +
              'cerca de un nanómetro de más. Litografía ya ha subido la dosis una vez ' +
              'para devolver la CD a su valor.',
            Label: 'La punta del cañón acumula gas',
          },
          semHealthy: {
            Evidence: [
              'El punto y la repetibilidad son ambos nominales.',
              'Todas las lecturas de banco están dentro de su límite.',
            ],
            Truth: 'No pasa nada. La decisión correcta es seguir midiendo.',
            Complaint: 'Turno de rutina. El patrón diario midió en su valor.',
            Label: 'Nada anda mal',
          },
        },
        reading: {
          grainCheck: {
            Label: 'Ruido dentro de un solo fotograma en una muestra plana (30 min)',
          },
          accelerometer: {
            Limit: 'aislando',
            Label: 'Acelerómetro en el bastidor de la columna frente al suelo (1.5 h)',
          },
          throughFocus: {
            Limit: 'redondo',
            Label: 'Serie a través del foco sobre el patrón de oro sobre carbono (1.0 h)',
          },
          energySpread: {
            Label: 'Dispersión de energía a partir de la serie de desenfoque cromático (2.0 h)',
          },
          detectorSignal: {
            Label: 'Señal del detector en la muestra de referencia',
          },
          frameJitter: {
            Label: 'Dispersión del registro entre fotogramas',
          },
          fftEllipticity: {
            Label: 'Elipticidad del espectro de potencia de la imagen',
          },
          emissionNoise: {
            Label: 'Ruido de la corriente de emisión',
          },
        },
        aspect: {
          detector: {
            Action: 'Sustituir el centelleador y reajustar la ganancia del fotomultiplicador',
            Label: 'Detector de electrones secundarios',
          },
          isolation: {
            Action: 'Volver a flotar y nivelar los soportes neumáticos, y encontrar qué está ' +
              'tocando el bastidor',
            Label: 'Aislamiento de vibraciones',
          },
          column: {
            Action: 'Realinear la columna y anular el astigmatismo con el patrón de oro sobre ' +
              'carbono',
            Label: 'Alineación de la columna y estigmadores',
          },
          gun: {
            Action: 'Dar un flash a la punta para expulsar el gas adsorbido y dejar que la ' +
              'emisión se estabilice',
            Label: 'Cañón de emisión de campo',
          },
        },
        SecondaryLabel: 'Repetibilidad',
        ToolLabel: 'SEM-23 CD-SEM e inspección por haz de electrones',
        GuardEtched: 'Este patrón ya se grabó en la película. La inspección tras el revelado es ' +
          'la mirada ANTES del grabado, cuando una mala impresión aún se puede ' +
          'retirar y rehacer.',
        GuardNoPattern: 'No hay nada impreso en este lote. Exponlo primero en EUV-22 o LITH-03: un ' +
          'CD-SEM mide un patrón, y esta oblea no lleva ninguno.',
        MeasureLot: 'Medir el patrón y liberarlo al grabado',
        NoteNoLot: 'No hay lote en la platina. Trae aquí una oblea expuesta para medirla.',
        SectionInspection: 'Inspección por haz de electrones: ver más, cubrir menos',
        SectionLinescan: 'Medir una línea (Monte Carlo)',
        WaveNote: 'La caja de MET-05 lanza electrones de unos pocos eV por rendijas separadas ' +
          'un nanómetro, y los haces se abren en abanico al ángulo que fija su ' +
          'longitud de onda. La columna de arriba trabaja a {{e}} eV, donde la ' +
          'longitud de onda es {{pm}} pm, y su lente acepta solo unos pocos ' +
          'milirradianes, así que el punto enfocado se ensancha 0,54 λ/α, unos ' +
          'nanómetros, exactamente por la misma razón por la que la rendija abre el ' +
          'haz. La caja de abajo es el propio solucionador de MET-05.',
        SectionWave: 'La onda detrás del término de difracción (la caja de MET-05)',
        SectionProbe: 'El punto: difracción frente a aberración',
        YieldBalance: 'σ = 1: salen tantos como llegan',
        YieldLandingMark: 'aterrizaje {{e}}',
        YieldChartTitle: 'Lo que sale de la muestra por cada electrón que llega',
        YieldChartX: 'energía de aterrizaje, log₁₀ eV (2 = 100 eV, 3 = 1 keV)',
        YieldChartY: 'electrones que salen por electrón que entra',
        YieldSeriesDelta: 'δ secundarios (< 50 eV)',
        YieldSeriesEta: 'η retrodispersados (≥ 50 eV)',
        YieldSeriesTotal: 'σ = δ + η',
        YieldChargeBalanced: 'equilibrio: un aislante se queda quieto aquí',
        YieldChargePositive: 'se carga positivo: salen más electrones de los que llegan',
        YieldChargePending: 'barriendo… el haz aún no ha llegado a esta energía',
        YieldChargeNegative: 'se carga negativo: los electrones se acumulan en la superficie',
        YieldStatMax: 'Máximo de secundarios',
        YieldStatMaxNote: 'a una energía de aterrizaje de {{e}}',
        YieldStatCross: 'Donde σ cruza uno',
        YieldStatCrossNote: 'E₁ y E₂: las energías de aterrizaje a las que se puede observar un aislante sin que se cargue',
        YieldStatLanding: 'A {{e}}',
        YieldProgress: 'Barriendo… {{pct}} % ({{n}} electrones del haz por energía)',
        YieldDone: '{{n}} electrones del haz en {{s}} s',
        YieldIdle: 'Pulse Ejecutar para disparar el haz sobre la película.',
        YieldMaterial: 'Bajo el haz',
        YieldCar: 'Resina químicamente amplificada',
        YieldMor: 'Resina de óxido metálico',
        YieldSi: 'Silicio',
        YieldPause: 'Pausa',
        YieldResume: 'Continuar',
        YieldRun: 'Ejecutar el barrido',
        YieldKnobLanding: 'Energía de aterrizaje a comprobar',
        SectionYield: 'Por qué la imagen tiene contraste: rendimiento de electrones secundarios frente a la energía de aterrizaje (Monte Carlo)',
        YieldNote: 'Cada imagen que toma un SEM está hecha de electrones que salieron de la muestra, y son de dos tipos. Los retrodispersados son electrones del haz que dieron la vuelta dentro del sólido y volvieron a salir rápidos; los secundarios son los electrones lentos que el haz arrancó, y solo salen los que nacen a pocos nanómetros de la superficie. Este barrido dispara electrones sobre una película gruesa y plana con la misma cascada Monte Carlo que usa el perfil de línea de arriba, y cuenta ambos. δ y η se dibujan desde 100 eV: por debajo, un electrón del haz que rebota directamente ya cae bajo el corte de 50 eV, así que allí solo se dibuja su suma. Observe cómo δ sube, llega a un máximo a unos cientos de eV y cae cuando un haz más rápido deposita su energía demasiado hondo para que un secundario alcance la superficie. Donde el total σ = δ + η supera uno, la superficie pierde carga; por debajo de uno, la gana. Una resina aislante solo se queda quieta en los dos cruces, y el superior, E₂, es la razón por la que un CD-SEM aterriza a unos cientos de voltios. Se omite: la carga no se realimenta al haz, la película es plana, y el silicio del modelo tiene su máximo más bajo y retrodispersa más que el silicio medido.',
        SectionGun: 'El cañón: electrones que salen de una punta por efecto túnel (matriz de ' +
          'transferencia de THRM-04)',
        KnobSnr: 'Relación señal-ruido requerida',
        KnobContrast: 'Contraste del defecto',
        KnobPixel: 'Tamaño de píxel',
        InspNote: 'Una sonda de electrones ve un contacto abierto o un hueco enterrado que ' +
          'ninguna herramienta óptica señalará nunca, porque no depende de que la luz ' +
          'se disperse en algo más pequeño que su longitud de onda. Pero un defecto ' +
          'solo se ve cuando destaca sobre el ruido del conteo, y eso fija los ' +
          'electrones que necesita cada píxel. Un píxel más pequeño necesita una ' +
          'sonda más pequeña, una sonda más pequeña lleva menos corriente, y por ' +
          'encima de cien nanoamperios el haz se difumina a sí mismo. Por eso un ' +
          'inspector de haz de electrones va a los pocos lugares que un diseño o un ' +
          'barrido óptico dicen que merecen el tiempo.',
        StatWaferTimeNote: 'INSP-13 barre una en {{min}} min, ciego por debajo de {{nm}} nm',
        Hours: '{{h}} horas',
        Years: '{{y}} años',
        StatWaferTime: 'Una oblea entera',
        StatRate: 'Área por hora',
        StatDwellNote: 'nunca menos de {{min}} ns: el detector tiene que estabilizarse',
        StatDwell: 'Permanencia por píxel',
        StatInspCurrentNote: 'lo que puede llevar una sonda de {{probe}} nm, con tope en {{cap}} nA',
        StatInspCurrent: 'Corriente del haz',
        StatElectronsPxNote: 'para ver un defecto de contraste {{c}}% con relación señal-ruido {{snr}}',
        StatElectronsPx: 'Electrones que necesita cada píxel',
        InspWafer: 'una oblea entera por hora',
        InspHere: 'este píxel',
        InspOptical: 'Barrido óptico de INSP-13 (ve hasta {{nm}} nm)',
        InspEbeam: 'Haz de electrones',
        InspY: 'log₁₀ mm² por hora',
        InspX: 'tamaño de píxel (nm)',
        InspTitle: 'Área inspeccionada por hora, frente al tamaño de píxel',
        ScanNote: 'Un CD-SEM nunca ve una anchura. Cuenta electrones lentos que escapan de la ' +
          'muestra mientras el haz avanza, y cerca de una pared escapan por la pared ' +
          'además de por arriba, así que los bordes brillan. El número que da es ' +
          'donde esa señal cruza una fracción elegida del camino entre el espacio y ' +
          'el pico: mueve el umbral y la misma línea mide más ancha o más estrecha, ' +
          'inclina la pared y la respuesta se mueve entre la anchura de arriba y la ' +
          'de abajo. Ese desfase se calibra frente a una sección transversal. Lo que ' +
          'no se puede calibrar es el conteo: promedia más fotogramas y la dispersión ' +
          'de medidas repetidas se reduce como su raíz cuadrada, mientras cada ' +
          'electrón adicional encoge la resina que se está midiendo.',
        KnobSidewall: 'Ángulo de la pared respecto a la vertical',
        KnobHeight: 'Altura de la resina',
        KnobLineCd: 'Anchura de la línea en la base',
        KnobFrames: 'Fotogramas promediados',
        KnobThreshold: 'Umbral de borde',
        ScanRepeat: 'Repetir cuarenta veces',
        ScanAgain: 'Medir otra vez',
        ScanRun: 'Barrer la línea',
        ScanStale: 'La muestra o el haz cambiaron: pulsa Barrer para medirla.',
        ScanStatus: 'Barrido con una sonda de {{probe}} nm a {{e}} eV, de la columna de arriba.',
        ScanProgress: 'Barriendo la línea… {{pct}}%',
        StatPrecisionNote: 'media {{mean}} nm',
        StatPrecision: 'Precisión, 40 repeticiones',
        StatDoseNote: '{{pa}} pA, {{dwell}} ns por píxel, {{frames}} fotogramas, {{lines}} líneas ' +
          'promediadas',
        StatDose: 'Electrones por píxel',
        StatBiasNote: 'lo que se calibra en la herramienta frente a una sección transversal',
        StatBias: 'Sesgo frente a la anchura a media altura',
        StatCdNote: 'con un umbral del {{threshold}}%; anchura real {{top}} nm arriba, ' +
          '{{bottom}} nm abajo',
        StatCd: 'Anchura medida',
        SignalCd: 'bordes · {{cd}} nm',
        SignalMeasured: 'Medido, {{frames}} fotogramas',
        SignalExpected: 'Esperado (Monte Carlo)',
        SignalY: 'secundarios por electrón del haz',
        SignalX: 'posición del haz (nm)',
        SignalTitle: 'Electrones secundarios a través de la línea',
        ScanSilicon: 'silicio',
        ScanResist: 'resina',
        KnobAlpha: 'Semiángulo de convergencia',
        KnobCurrent: 'Corriente de la sonda',
        KnobLanding: 'Energía de llegada',
        ProbeNote: 'Abre la lente y el difuminado por difracción se reduce —el electrón es una ' +
          'onda, y un cono más ancho enfoca más fino—, pero los rayos lejos del eje ' +
          'enfocan antes y los electrones de distinta energía enfocan a distinta ' +
          'profundidad, y ambas cosas crecen con el ángulo. El mejor punto está donde ' +
          'se cruzan. A unos cientos de eV suele ganar el término cromático, así que ' +
          'la dispersión de energía del cañón de la sección anterior decide la ' +
          'resolución; pedir más corriente agranda la imagen de la fuente y cambia ' +
          'resolución por señal.',
        StatGunUsedNote: 'dispersión de energía {{spread}} eV, del cañón de arriba',
        GunSchottky: 'Schottky',
        GunCold: 'Emisión de campo en frío',
        StatGunUsed: 'Cañón',
        StatBestNote: 'a {{a}} mrad',
        StatBest: 'El mejor posible',
        StatProbeNote: 'difracción {{d}}, cromática {{c}}, esférica {{s}}, fuente {{i}} nm',
        StatProbe: 'Punto con este ángulo',
        StatLambdaNote: 'a {{e}} eV: mucho menor que un átomo, y aun así limita el punto',
        StatLambda: 'Longitud de onda del electrón',
        ProbeHere: 'ajustado',
        ProbeBest: 'mejor ángulo',
        ProbeSpherical: 'Aberración esférica',
        ProbeChromatic: 'Cromática, por la dispersión del cañón',
        ProbeDiffraction: 'Difracción, 0,54 λ/α',
        ProbeTotal: 'El punto',
        ProbeY: 'diámetro que contiene la mitad de la corriente (nm)',
        ProbeX: 'semiángulo de convergencia (mrad)',
        ProbeTitle: 'Tamaño del punto frente al ángulo de convergencia',
        GunNote: 'A unos pocos voltios por nanómetro, el vacío fuera de una punta de ' +
          'wolframio es una barrera de menos de un nanómetro de ancho, y los ' +
          'electrones la atraviesan por efecto túnel: túnel de Fowler-Nordheim, el ' +
          'mismo régimen al que llega THRM-04 cuando un óxido de puerta se lleva más ' +
          'allá de su desnivel de bandas. La corriente sale de la misma matriz de ' +
          'transferencia, con vacío en lugar de óxido, integrada sobre los electrones ' +
          'del metal; coincide con la ecuación de Murphy-Good sin que nadie se la dé. ' +
          'Calienta la punta y recúbrela para bajar la función de trabajo, y el mismo ' +
          'cálculo se convierte en un emisor Schottky: electrones que trepan sobre ' +
          'una barrera que el campo ha rebajado. Es más estable y su dispersión de ' +
          'energía es mayor, y la sección siguiente convierte eso en un punto más ' +
          'grande.',
        KnobField: 'Campo de extracción en la punta',
        EmitterSchottky: 'Schottky, ZrO/W, 1800 K',
        EmitterCold: 'Emisión de campo en frío, W, 300 K',
        KnobEmitter: 'Emisor',
        StatBrightnessNote: 'A/(m²·sr·V): cuánta corriente puede llevar una sonda pequeña y bien colimada',
        StatBrightness: 'Brillo reducido',
        StatTopNote: 'sobre el nivel de Fermi, después de que el campo y la carga imagen la ' +
          'hayan bajado',
        StatTop: 'Cima de la barrera',
        StatSpreadNote: 'FWHM de los electrones emitidos: fija el difuminado cromático de abajo',
        StatSpread: 'Dispersión de energía',
        StatJNote: '{{i}} µA de una punta de {{r}} nm',
        StatJ: 'Densidad de corriente',
        FnSeries: 'Recta donde hay efecto túnel',
        FnY: 'ln(J / F²)',
        FnX: '1 / campo (nm/V)',
        FnTitle: 'El diagrama de Fowler-Nordheim',
        JfHere: 'este campo',
        JfMg: 'Ecuación de Murphy-Good',
        JfTmm: 'Matriz de transferencia (motor de THRM-04)',
        JfY: 'log₁₀ densidad de corriente (A/m²)',
        JfX: 'campo en la punta (V/nm)',
        JfTitle: 'Densidad de corriente frente al campo',
        GunWidth: '{{w}} nm que atravesar por efecto túnel',
        GunX: 'distancia hacia el vacío (nm)',
        GunOut: 'salida',
        GunMetal: 'punta',
      },
      euv: {
        NextPower: 'La máquina va una cuarta parte lenta esta semana y la fuente está a plena ' +
          'potencia. ¿Dónde, entre el plasma y la oblea, pondrías primero un medidor ' +
          'de potencia, y qué descartaría cada lectura?',
        TitlePowerWph: 'Obleas por hora frente a la potencia de la fuente',
        LabelMirrorHeat: 'Calor hacia los espejos',
        LabelWphHigh: 'Producción, potencia más alta',
        LabelWphLow: 'Producción, potencia más baja',
        HypPower: 'El tiempo de exposición es la dosis entre la potencia en la oblea, así que ' +
          'duplicar la fuente debería reducirlo a la mitad. Pero una oblea también ' +
          'pasa tiempo cargándose, alineándose y avanzando sin luz alguna. Si esa ' +
          'sobrecarga es real, la producción debería subir más despacio que la ' +
          'potencia y aplanarse hacia un techo que ninguna fuente puede levantar.',
        AskedPower: '¿Cuánta producción compra la potencia de la fuente?',
        SumPower: 'Potencia de fuente frente a producción: rendimientos decrecientes, pagados ' +
          'en calor en los espejos.',
        NextDose: 'Una resina de óxido metálico absorbe tres veces más de los fotones que le ' +
          'llegan. ¿Lo gastarías en una dosis más baja o en un borde más liso, y ' +
          'quién en la fábrica defendería cada opción?',
        AxisWph: 'obleas por hora',
        TitleDoseWph: 'Obleas por hora frente a la dosis',
        AxisLer: 'LER 3σ (nm)',
        TitleDoseTrade: 'Rugosidad y producción frente a la dosis',
        LabelWphLost: 'Producción cedida',
        LabelLerHigh: 'Rugosidad, dosis más alta',
        LabelLerLow: 'Rugosidad, dosis más baja',
        HypDose: 'Un borde es rugoso porque lo dibuja un número contable de fotones, y el ' +
          'ruido de conteo cae como uno sobre la raíz cuadrada de la cuenta. Si eso ' +
          'es todo, cuadruplicar la dosis debería reducir la rugosidad más o menos a ' +
          'la mitad, mientras que la exposición, que tiene que entregar esa dosis con ' +
          'un haz fijo, tarda cuatro veces más.',
        AskedDose: '¿Qué compra la dosis y qué cuesta?',
        SumDose: 'La dosis compra bordes lisos con producción: ruido de disparo contra ' +
          'tiempo de exposición.',
        knob: {
          sourceW: 'Potencia de la fuente en el foco intermedio',
          doseMjCm2: 'Dosis de exposición',
        },
        AssistantBlurb: 'Pregunta qué cuesta la dosis en obleas por hora, o qué compra de verdad ' +
          'más potencia de fuente. Cada respuesta es la cadena entera, del plasma a ' +
          'la oblea, ejecutada con esta receta.',
        AssistantLead: 'Asistente de indagación de EUV-22',
        TileIfNote: '{{heat}} W de ella acaban calentando espejos',
        TileIf: 'Potencia en el foco intermedio',
        TileLerNote: 'con {{dose}} mJ/cm² realmente entregados',
        TileLer: 'Rugosidad de borde de línea',
        TileWphNote: '{{w}} W llegan a la oblea',
        TileWph: 'Producción',
        SectionTool: 'El escáner en este turno: fotones que entran, obleas que salen',
        fault: {
          doseSensorOverReads: {
            Evidence: [
              'La rugosidad en el monitor supera su límite mientras la potencia es ' +
                'nominal en todo el camino del haz. Ninguna pérdida de luz hace eso.',
              'La producción está POR ENCIMA de la nominal. Cualquier otro fallo de esta ' +
                'máquina la hace más lenta.',
              'El fotodiodo de referencia en la platina lee el 80% de lo que informa el ' +
                'sensor de dosis.',
            ],
            Truth: 'El sensor de energía de la rendija ha derivado e informa una quinta parte ' +
              'más de dosis de la que entrega, así que la máquina termina cada exposición ' +
              'antes de tiempo. La oblea recibe 32 mJ/cm² con una receta de 40. La ' +
              'rugosidad es ruido de disparo de fotones, va como uno sobre la raíz ' +
              'cuadrada de la dosis, así que los bordes son cerca de una décima parte más ' +
              'rugosos, y la máquina es más rápida exactamente por la misma razón. La ' +
              'buena noticia y la mala son un solo fallo.',
            Complaint: 'Nadie se ha quejado de esta máquina. Va un diez por ciento por encima de ' +
              'su producción nominal, y el jefe de turno lo ha puesto en el informe ' +
              'semanal como buena noticia. SEM-23 ha marcado los tres últimos lotes por ' +
              'rugosidad de borde.',
            Label: 'El sensor de dosis lee de más',
          },
          carbonOnOptics: {
            Evidence: [
              'La potencia en el foco intermedio está en su valor; la de la oblea está ' +
                'una cuarta parte baja respecto a ella. La pérdida está dentro del escáner.',
              'La comprobación espejo a espejo da 68% donde debería dar 70%.',
              'La energía de pulso en el plasma es nominal.',
            ],
            Truth: 'Cada espejo de proyección ha perdido dos puntos de reflectancia por el ' +
              'carbono que el propio haz arranca de los hidrocarburos residuales. Dos ' +
              'puntos parece nada. Hay once reflexiones entre el foco intermedio y la ' +
              'oblea, y 0,68 elevado a once es tres cuartos de 0,70 elevado a once. Un ' +
              'apilamiento multicapa no tiene tolerancia que ceder.',
            Complaint: 'La producción ha caído cerca de una sexta parte. La potencia de la fuente ' +
              'en el foco intermedio está exactamente en su valor, así que el grupo de la ' +
              'fuente ha cerrado su incidencia.',
            Label: 'Crecimiento de carbono en los espejos de proyección',
          },
          tinOnCollector: {
            Evidence: [
              'La energía de pulso en el plasma es nominal y la potencia en el foco ' +
                'intermedio está un 40% baja. La pérdida está entre los dos, y entre los ' +
                'dos solo está el colector.',
              'La potencia en la oblea guarda proporción con la del foco intermedio: la ' +
                'óptica de proyección deja pasar lo que recibe.',
              'La imagen de campo lejano muestra el colector al 60% de limpio.',
            ],
            Truth: 'Se han acumulado residuos de estaño en el colector y ahora refleja cerca ' +
              'del 60% de lo que refleja uno limpio. Los de la fuente tienen razón: el ' +
              'plasma produce toda su luz. Se pierde en el primer espejo que encuentra, ' +
              'que es el único espejo de la máquina en la línea de visión de una ' +
              'explosión de estaño cincuenta mil veces por segundo.',
            Complaint: 'La producción lleva tres semanas bajando y ya está cerca de una cuarta ' +
              'parte por debajo. Los de la fuente dicen que su energía de pulso es ' +
              'estable como una roca.',
            Label: 'Estaño en el colector',
          },
          dropletTimingDrift: {
            Evidence: [
              'La energía de pulso en el plasma ha bajado al 60% de lo cualificado. Un ' +
                'colector sucio no puede hacer eso: está después del plasma.',
              'La potencia en el foco intermedio ha bajado en la misma fracción, así que ' +
                'en el colector no se pierde nada más.',
              'El barrido de sincronía encuentra el pulso principal retrasado.',
            ],
            Truth: 'El pulso principal llega tarde, cuando el prepulso ya ha dejado que la ' +
              'gota aplanada se salga del foco, así que cada disparo produce cerca del ' +
              '60% de la luz en banda que debería. La luz falta EN EL PLASMA: nunca se ' +
              'generó, y el colector refleja perfectamente lo que recibe. Cambiarlo ' +
              'costaría treinta horas y no cambiaría nada.',
            Complaint: 'La producción ha caído cerca de una cuarta parte desde que se cambió la ' +
              'boquilla del generador de gotas. El turno de noche cree que el colector ' +
              'por fin tiene demasiado estaño y quiere reservar el cambio.',
            Label: 'El pulso láser no acierta en la gota',
          },
          euvHealthy: {
            Evidence: [
              'La potencia está donde debe en el plasma, en el foco intermedio y en la ' +
                'oblea.',
              'La rugosidad en el monitor es nominal.',
            ],
            Truth: 'No pasa nada. La decisión correcta es seguir exponiendo.',
            Complaint: 'Turno de rutina. Producción según el plan, obleas monitor en especificación.',
            Label: 'Nada anda mal',
          },
        },
        reading: {
          doseCrossCheck: {
            Label: 'Contraste de dosis con el fotodiodo de referencia en la platina (2.0 h)',
          },
          opticsTransmission: {
            Label: 'Comprobación de transmisión espejo a espejo con la máquina abierta (6.0 h)',
          },
          collectorImage: {
            Label: 'Imagen de campo lejano del colector (2.0 h)',
          },
          dropletTiming: {
            Limit: 'en el blanco',
            Label: 'Barrido de sincronía gota-pulso en la fuente (1.5 h)',
          },
          lerMonitor: {
            Label: 'Rugosidad de borde en la oblea monitor',
          },
          waferPower: {
            Label: 'Potencia en la oblea frente a la del foco intermedio',
          },
          ifPower: {
            Label: 'Potencia en el foco intermedio',
          },
          plasmaEnergy: {
            Label: 'Energía de pulso en banda en el plasma',
          },
        },
        aspect: {
          doseControl: {
            Action: 'Recalibrar el sensor de energía de la rendija contra el fotodiodo de ' +
              'referencia',
            Label: 'Sensor de dosis',
          },
          optics: {
            Action: 'Ejecutar la limpieza in situ con radicales de hidrógeno en los espejos de ' +
              'proyección',
            Label: 'Óptica de proyección',
          },
          collector: {
            Action: 'Cambiar el colector por uno limpio y enviar este a retirar el estaño',
            Label: 'Espejo colector',
          },
          source: {
            Action: 'Resincronizar el prepulso y el pulso principal con la gota, y recentrar el ' +
              'chorro de gotas',
            Label: 'Generador de gotas y sincronía del láser',
          },
        },
        SecondaryLabel: 'Rugosidad de borde de línea',
        ToolLabel: 'EUV-22 Escáner EUV con fuente de estaño',
        GuardNoPrint: 'Todavía no se ha impreso nada. Deja que termine la sección de impresión de ' +
          'arriba, para que el lote se exponga con una receta que alguien ha mirado.',
        NoteWouldExpose: 'Este lote se expondría a {{dose}} mJ/cm², con bordes rugosos de {{ler}} nm ' +
          '3σ.',
        ExposeLot: 'Exponer la oblea y enviarla al CD-SEM',
        NoteNoLot: 'No hay lote en el portaobleas. Trae aquí una oblea recubierta para ' +
          'exponerla.',
        SectionPrint: 'Imprimir con luz que se puede contar',
        SectionCascade: 'Un fotón, una lluvia de electrones (Monte Carlo)',
        SectionThroughput: 'De vatios en la fuente a obleas por hora',
        AbsorbCar: 'CAR polimérico',
        AbsorbCarF: 'CAR fluorado',
        AbsorbMorSn: 'MOR de óxido de estaño',
        AbsorbMorHf: 'MOR de óxido de hafnio',
        AbsorbSi: 'Silicio (sustrato)',
        AbsorbBar: '{{el}}: {{share}}% de μ con {{mass}}% de la masa',
        AbsorbElementsTitle: 'Quién absorbe: μ por elemento',
        AbsorbElementsX: 'elemento en la película',
        AbsorbElementsY: 'parte de μ (por µm)',
        AbsorbWithout: 'sin el átomo añadido',
        AbsorbCurveTitle: 'Cuánta luz retiene la película',
        AbsorbCurveX: 'espesor de la película (nm)',
        AbsorbCurveY: 'absorbido (%)',
        AbsorbShort: 'μ = {{mu}} por µm · {{pct}}% de los fotones absorbidos',
        AbsorbHelp: 'El balance de fotones completo',
        AbsorbStatus: 'μ = {{mu}} por µm · longitud de atenuación {{length}} nm · {{absorbed}} de {{incident}} fotones por nm² absorbidos ({{pct}}%) · los 5 nm superiores reciben {{ratio}}× lo que reciben los 5 nm inferiores',
        AbsorbGain: 'Un {{pct}}% de {{el}} en masa multiplica μ por {{factor}} a la misma densidad. Una resina de óxido metálico aporta además la densidad.',
        AbsorbResist: 'Resina',
        AbsorbThickness: 'Espesor de la película',
        AbsorbDose: 'Dosis',
        AbsorbAdd: 'Añadir un átomo',
        AbsorbAddNone: 'Nada',
        AbsorbAddFraction: 'Su fracción de la masa',
        SectionAbsorb: 'Cuántos fotones retiene la resina: absorción átomo por átomo (secciones eficaces fotoeléctricas)',
        AbsorbNote: 'La cascada de arriba parte de un fotón absorbido y la impresión de abajo los cuenta, pero una dosis entrega fotones a la superficie, no dentro de la película. A 92 eV la absorción es el efecto fotoeléctrico y nada más, así que la atenuación de la película es una suma sobre sus átomos: μ = ρ Σ wᵢ(μ/ρ)ᵢ, con la sección eficaz de cada elemento fijada por sus capas internas. Por átomo, el estaño absorbe unas treinta veces lo que el carbono, el oxígeno tres veces y media y el hidrógeno casi nada. Por gramo la diferencia es mucho menor, y por eso añadir una décima parte de la masa de un polímero como estaño solo aumenta la absorción en alrededor de una sexta parte; una resina de óxido metálico gana por carga y densidad a la vez. Observe las barras: en un polímero el oxígeno absorbe más que su fracción de masa, y en un cúmulo de estaño el estaño se lleva la mayor parte de μ. Una película polimérica de 35 nm retiene aproximadamente un fotón de cada siete, y una película más oscura y gruesa da más dosis arriba que abajo, lo que imprime una pared inclinada. Las secciones eficaces son las tablas de Henke a 91,84 eV, menos seguras para el estaño y el hafnio en sus resonancias 4d y 4f; lo que hace después un fotón absorbido le corresponde a la cascada.',
        SectionMirror: 'El espejo: cuarenta pares de molibdeno y silicio (matriz de transferencia)',
        SectionSource: 'La fuente: iones de estaño que brillan a 13,5 nm',
        PrintNote: 'Cada nanómetro cuadrado recibe un número de Poisson de fotones alrededor ' +
          'de lo que pide la imagen aérea, cada fotón lanza sus ácidos a unos ' +
          'nanómetros, el horneado los difumina y la resina se disuelve donde llegó ' +
          'ácido suficiente. Aquí nadie le dice al modelo que la rugosidad va como ' +
          'uno sobre la raíz cuadrada de la dosis: eso lo hace el conteo. Sube la ' +
          'dosis y los bordes se suavizan mientras la línea adelgaza; bájala para ' +
          'ganar productividad y los bordes se vuelven rugosos hasta que, pasado un ' +
          'precipicio, un espacio se puentea o una línea se rompe en algún punto. Más ' +
          'difusión del ácido oculta el ruido y difumina la imagen con él. La dosis ' +
          'nominal es el mando del químico de resinas: fija dónde se imprimen las ' +
          'líneas de medio paso.',
        PrintSweep: 'Barrer la dosis',
        PrintAgain: 'Exponer otra oblea',
        Na055: 'High-NA 0,55',
        Na033: 'NA 0,33',
        KnobSizeDose: 'Dosis nominal de la resina',
        KnobDiffusion: 'Difusión del ácido en el horneado',
        KnobPitch: 'Paso de línea',
        KnobPrintDose: 'Dosis',
        PrintScale: '{{w}} nm a lo largo de las líneas · {{h}} nm a lo ancho',
        PrintWorking: 'Exponiendo, fotón a fotón…',
        StatNilsNote: 'contraste de la imagen aérea {{contrast}}%',
        StatNils: 'Pendiente logarítmica de la imagen',
        StatFailuresNote: '{{breaks}} rotas y {{bridges}} puenteadas, de {{rows}} filas de línea',
        StatFailures: 'Fallos estocásticos',
        StatLerNote: '3σ a lo largo del borde; rugosidad de anchura {{lwr}} nm',
        StatLer: 'Rugosidad de borde de línea',
        StatCdNote: 'objetivo {{half}} nm, la mitad del paso',
        StatCd: 'Anchura de línea impresa',
        StatPhotonsNote: '{{abs}} de ellos absorbidos en la resina',
        StatPhotons: 'Fotones por nm²',
        SweepHere: 'esta exposición',
        SweepFail: 'Filas rotas o puenteadas ÷ 20',
        SweepLer: 'Rugosidad de borde de línea',
        SweepY: 'LER 3σ (nm) · filas fallidas ÷ 20',
        SweepX: 'dosis (mJ/cm²)',
        SweepTitle: 'Rugosidad y fallos frente a la dosis, con la resina recalibrada en cada una',
        CascadeNote: 'Un fotón de 92 eV lo absorbe un átomo, que expulsa un fotoelectrón con la ' +
          'mayor parte de su energía. Ese electrón arranca otros, y cada ionización ' +
          'cuesta unos diez electronvoltios, hasta que ninguno es lo bastante rápido ' +
          'para ionizar nada más; donde se detienen los lentos, un generador ' +
          'fotoácido puede capturarlos y liberar un ácido. Así que la imagen no se ' +
          'escribe donde cayó la luz sino unos nanómetros alrededor, y el número por ' +
          'fotón es en sí aleatorio. Una resina de óxido metálico absorbe cuatro ' +
          'veces más —otra vez los electrones 4d del estaño, los mismos que brillan ' +
          'en la fuente— y su película más densa detiene antes a los electrones.',
        CascadeFire: 'Absorber otros siete fotones',
        ResistMor: 'Óxido metálico (estaño)',
        ResistCar: 'Químicamente amplificada (polímero)',
        KnobResist: 'Resina',
        StatAbsorbedNote: 'de los fotones que llegan a {{t}} nm de resina',
        StatAbsorbed: 'La película absorbe',
        StatBlurNote: 'RMS lateral desde donde cayó el fotón',
        StatBlur: 'Difuminado electrónico',
        StatElectronsNote: 'el fotoelectrón y todos los secundarios que liberó',
        StatElectrons: 'Electrones por fotón',
        StatEventsNote: 'el {{blank}}% de los fotones no genera ninguno',
        StatEvents: 'Ácidos por fotón',
        HistSeries: '1500 fotones',
        HistY: 'fracción de fotones',
        HistX: 'eventos químicos de un fotón',
        HistTitle: 'Ácidos por fotón absorbido',
        CascadeEvent: 'evento químico',
        CascadeAcid: 'ácido generado',
        CascadeSecondary: 'secundarios',
        CascadePhotoelectron: 'fotoelectrón',
        CascadePhoton: 'fotón absorbido',
        CascadeSubstrate: 'subcapa',
        KnobMirrors: 'Espejos tras la fuente',
        KnobMirrorR: 'Reflectancia por espejo',
        KnobDose: 'Dosis de la resina',
        KnobSource: 'Potencia de la fuente en el foco intermedio',
        WphNote: 'Cada eslabón multiplica. Una docena de reflexiones al 70% conservan menos ' +
          'del 2%, y formar una rendija uniforme conserva la mitad de eso, así que ' +
          '250 W en la fuente son unos dos vatios y medio en la oblea. Reduce a la ' +
          'mitad la dosis que necesita la resina y la herramienta casi duplica su ' +
          'producción: por eso un químico de resinas y un ingeniero de fuentes tienen ' +
          'la misma discusión, y por eso la sección siguiente muestra lo que cuesta ' +
          'una dosis menor.',
        StatWph: 'Productividad',
        StatExposureNote: 'más {{overhead}} s por oblea sin luz: cambio, alineado, desplazamiento',
        StatExposure: 'Luz encendida por oblea',
        StatWaferNote: 'mientras los espejos absorben {{heat}} W por el camino',
        StatWafer: 'Potencia en la oblea',
        StatTransmissionNote: '{{n}} reflexiones, y el iluminador conserva el {{keep}}% al formar la ' +
          'rendija',
        StatTransmission: 'Transmisión de la óptica',
        WphHere: 'esta receta',
        WphSeries: 'Fuente a {{w}} W',
        WphY: 'obleas por hora',
        WphX: 'dosis (mJ/cm²)',
        WphTitle: 'Obleas por hora, frente a la dosis que necesita la resina',
        MirrorNote: 'Todo material desvía la luz de 13,5 nm solo unos pocos por ciento, así que ' +
          'ninguna superficie sola refleja mucho. Apila un metal pesado y un ' +
          'separador ligero, cada par de media longitud de onda de espesor, y las ' +
          'débiles reflexiones de ochenta intercaras llegan en fase. La onda ' +
          'estacionaria interior pone sus nodos oscuros en el molibdeno, donde la luz ' +
          'se absorbería. Las intercaras rugosas suavizan los escalones en los que ' +
          'refleja el espejo; el carbono que la propia luz descompone del vacío ' +
          'absorbe encima. Un escáner tiene una docena de estas reflexiones seguidas, ' +
          'así que un punto perdido en un espejo se pierde doce veces.',
        KnobAngle: 'Ángulo de incidencia',
        KnobCarbon: 'Carbono depositado en el espejo',
        KnobRough: 'Rugosidad de las intercaras',
        KnobPairs: 'Pares',
        KnobGamma: 'Fracción de molibdeno en el periodo',
        KnobPeriod: 'Periodo',
        MirrorSolving: 'Resolviendo la pila…',
        StatBestNote: 'ajustado ahora: {{now}} nm',
        StatBest: 'Mejor periodo para 13,5 nm',
        StatChainNote: '{{n}} reflexiones seguidas, a 13,5 nm',
        StatChain: 'A través de todo el escáner',
        StatFwhmNote: 'anchura a media altura del pico',
        StatFwhm: 'Ancho de banda',
        StatRNote: 'pico del {{peak}}% a {{at}} nm',
        StatR: 'Reflectancia a 13,5 nm',
        PairsHere: 'este recubrimiento',
        PairsSeries: 'Reflectancia',
        PairsY: 'reflectancia a 13,5 nm (%)',
        PairsX: 'pares Mo/Si',
        PairsTitle: 'Más pares dejan de ayudar',
        MirrorEdge: 'Borde L del Si',
        MirrorChain: 'Tras toda la cadena de espejos, ×20',
        MirrorSeries: 'Este recubrimiento',
        MirrorY: 'reflectancia (%)',
        MirrorX: 'longitud de onda (nm)',
        MirrorTitle: 'Un espejo, frente a la longitud de onda',
        FieldX: 'profundidad desde la superficie (nm)',
        FieldTitle: '|E|² en la parte superior del espejo',
        SourceNote: 'La escalera se calcula: cada peldaño es una solución autoconsistente de la ' +
          'ecuación de Schrödinger para ese ion, y coincide con los valores del NIST ' +
          'donde el NIST los tiene. Los estados de carga se calculan sobre ella, con ' +
          'la ionización por impacto electrónico frente a la recombinación. Dónde ' +
          'emiten los iones se mide, no se calcula: un cálculo de un solo electrón ' +
          'sitúa el salto 4d→4f cerca de 17 nm, y es el intercambio entre los ' +
          'electrones 4d y 4f —un efecto de muchos electrones— lo que lleva la luz de ' +
          'Sn⁸⁺ a Sn¹³⁺ a 13,5 nm a la vez. Esa acumulación es la razón por la que un ' +
          'plasma de estaño supera en cincuenta veces a un cuerpo negro en la banda ' +
          'que importa.',
        LaserSolid: 'Estado sólido, 1 µm',
        LaserCo2: 'CO₂, 10,6 µm',
        KnobLaser: 'Láser de excitación',
        KnobTeValue: '{{ev}} eV · {{kk}} mil K',
        KnobTe: 'Temperatura electrónica',
        StatCeNote: 'de la energía del láser, en banda, medida en fuentes reales',
        StatCe: 'Conversión en producción',
        StatPhotonNote: 'solo el siguiente electrón de Sn¹⁰⁺ cuesta {{ev}} eV: más del doble del fotón que devuelve un ion',
        StatPhoton: 'Un fotón',
        StatBlackNote: 'en la misma banda: por qué un emisor de líneas, y por qué estaño',
        StatBlack: 'Un cuerpo negro pondría',
        StatInBandNote: 'de lo que emiten estos iones, dentro de 13,5 nm ± 1%',
        StatInBand: 'En banda',
        StatMeanNote: 'el ion más abundante es Sn{{q}}+',
        StatMean: 'Carga media',
        ScanHere: 'este plasma',
        ScanSolid: 'Láser de 1 µm, 10²¹ electrones/cm³',
        ScanCo2: 'Láser de CO₂, 10¹⁹ electrones/cm³',
        ScanY: 'fracción de la luz en banda (%)',
        ScanX: 'temperatura electrónica (eV)',
        ScanTitle: 'Dónde debe dejar el láser el plasma',
        SpectrumBand: 'banda del 2%',
        SpectrumBlack: 'Un cuerpo negro a la misma temperatura',
        SpectrumTin: 'Iones de estaño a esta temperatura',
        SpectrumY: 'potencia emitida, pico = 1',
        SpectrumX: 'longitud de onda (nm)',
        SpectrumTitle: 'La luz que sale',
        ChargeBand: 'estos emiten en banda',
        ChargeSeries: 'Iones',
        ChargeY: 'fracción del estaño',
        ChargeX: 'estado de carga q, para iones de estaño Sn q+',
        ChargeTitle: 'Qué iones contiene un plasma a {{te}} eV',
        LadderPhoton: 'un fotón de 13,5 nm',
        LadderNist: 'Medido (NIST)',
        LadderSolved: 'Resuelto (Hartree-Fock-Slater)',
        LadderY: 'energía de ionización (eV)',
        LadderX: 'estado de carga q del ion que se ioniza',
        LadderTitle: 'La escalera: la energía para arrancar el siguiente electrón',
      },
      panel: {
        HelpMore: 'Más sobre esto',
        AboutSim: 'Qué muestra esto y qué deja fuera el modelo',
        BenchKnobs: 'Mandos',
        BenchReadouts: 'Lecturas',
        BenchRun: 'Ejecución',
        DownForPm: 'En la planta esta máquina está bloqueada por mantenimiento preventivo: ' +
          'tiene la carcasa quitada y un técnico dentro, y por eso su pantalla y el ' +
          'tablero de la bahía indican PARADA. Lo que operas aquí es su proceso ' +
          'cualificado, al que se la devuelve. Un lote procesado desde este panel se ' +
          'procesa con ese proceso, como ocurrirá cuando se retire la etiqueta.',
        ValueOnTarget: 'en objetivo',
        SolverHeadMeasured: 'medido',
        SolverHeadClosedForm: 'fórmula cerrada',
        SolverHeadSolved: 'numérico',
        LabelClosed: 'analítico',
        LabelSolved: 'numérico',
        TravelCard: 'Hoja de ruta',
        ThermalBudgetLine: 'Presupuesto térmico: {{min}} min a 1000 °C en {{steps}} paso(s) calientes — suficiente para mover el boro ≈ {{nm}} nm',
        SectionStoredRecipes: 'Recetas guardadas',
        SectionWafer: 'Oblea',
        SectionLot: 'Lote',
        SectionProcessState: 'Estado del proceso',
        SectionRecipe: 'Receta',
        SectionShift: 'Turno',
      },
      cleanroom: {
        NextThoseAddersBecomeCln09: 'Esos añadidos se convierten en el conteo de partículas entrante de CLN-09, ' +
          'y luego en la densidad de defectos de INSP-13, y luego en un número de ' +
          'rendimiento en PRB-14. Sigue uno de ellos por la línea y ve cuánto vale en ' +
          'die un minuto de portador abierto.',
        AxisXAirChangesPerHour: 'cambios de aire por hora',
        TitleRecoveryTimeAgainstAirChanges: 'Tiempo de recuperación frente a cambios de aire',
        LabelDepositionVelocity: 'Velocidad de deposición',
        NoteAfterFilterLoading: 'tras la carga del filtro',
        LabelEffectiveAch: 'ACH efectivo',
        LabelRecovery: 'Recuperación',
        HypSteadyStateConcentrationShouldGo: 'La concentración en estado estacionario debería ir como generación entre ' +
          'ventilación, así que duplicar los cambios de aire debería reducir la ' +
          'concentración más o menos a la mitad — con rendimientos decrecientes en ' +
          'términos absolutos. El tiempo de recuperación debería caer igual. Si es ' +
          'así, la ventilación es una palanca con un precio que sube abruptamente y ' +
          'un suelo bajo el cual no puede bajar mientras haya gente en la sala.',
        AskedWhatDoesVentilationBuyAnd: '¿Qué compra la ventilación, y cuánto tiempo se siente un percance?',
        SumAirChangesRecoveryTimeAnd: 'Cambios de aire, tiempo de recuperación, y el reloj de exposición de un ' +
          'portador abierto.',
        NextEveryNumberHereIsPer: 'Cada número aquí es por minuto de exposición. Pregúntame cuánto vale el ' +
          'propio tiempo de exposición — es la única variable de esta lista que ' +
          'controla un cronograma de herramientas y no una persona.',
        LabelWrongOrder: 'Orden equivocado',
        LabelStreetClothes: 'Ropa de calle',
        LabelPenetrationGowned: 'Penetración, vestido',
        HypEachGarmentIsAFilter: 'Cada prenda es un filtro, y los filtros en serie multiplican en vez de ' +
          'sumar — así que la combinación debería ser dramáticamente mejor que ' +
          'cualquier pieza sola, y QUITAR una pieza debería costar mucho más de lo ' +
          'que esa pieza parece aportar por sí misma. El orden debería importar por ' +
          'la misma razón: una prenda puesta después de otra más sucia está ' +
          'contaminada antes de empezar a trabajar.',
        AskedHowMuchDoesTheGowning: '¿Cuánto compra realmente el protocolo de vestimenta?',
        SumGarmentPenetrationAsAProduct: 'La penetración de las prendas como producto, y por qué el orden de ' +
          'vestirse es una regla.',
        NextTheDownflowCarriesParticlesFrom: 'El flujo descendente lleva las partículas desde donde esté una persona ' +
          'hacia lo que quede debajo y aguas abajo de ella. Con eso en mente, ¿dónde ' +
          'deberías pararte en el pasillo mientras un puerto de carga está abierto — ' +
          'y el monitor en la pared te dice si te equivocaste?',
        LabelRoomAverageTheMonitor: 'promedio de sala (el monitor)',
        LabelWorkZone: 'zona de trabajo',
        AxisYParticlesM: 'partículas/m³',
        AxisXPeopleInTheBay: 'personas en la bahía',
        TitleRoomAverageAndWorkZone: 'Promedio de sala y zona de trabajo, frente a ocupación',
        LabelZoneRoom: 'Zona / sala',
        LabelWaferSees: 'La oblea ve',
        LabelMonitorReads: 'El monitor lee',
        HypACeilingMonitorMeasuresA: 'Un monitor de techo mide un promedio de sala, y un promedio asume que la ' +
          'fuente está repartida uniformemente por la sala. Una persona de pie ante ' +
          'una herramienta no está repartida uniformemente: es una fuente puntual a ' +
          'un metro de producto expuesto. Si eso importa, la concentración en la zona ' +
          'de trabajo debería ser sustancialmente mayor que el promedio de sala, y la ' +
          'brecha debería crecer con cada persona añadida.',
        AskedIsTheNumberOnThe: '¿El número en la pared es el número que experimenta la oblea?',
        SumRoomAverageAgainstWorkZone: 'Promedio de sala frente a zona de trabajo, y por qué un monitor en la ' +
          'pared puede estar en lo cierto y ser inútil.',
      },
      intents: {
        NextHoldingIsTheEasyHalf: 'Retener es la mitad fácil. ¿Qué pasa ahora con el lote que ya está en el ' +
          'puerto de carga, y qué necesitarías saber de él antes de llamarlo desecho ' +
          'en vez de espera?',
        NextAQualPassesOnTheDay: 'Una calificación pasa el día en que se hace. ¿Qué tendrías que ver en el ' +
          'siguiente lote para concluir que esta fue suerte y no un equipo sano, y ' +
          'cuántas obleas querrías antes de creerte cualquiera de las dos respuestas?',
        LabelBenchReadings: 'Lecturas del banco',
        LabelNonUniformity: 'No uniformidad',
        LabelMonitorRate: 'Velocidad en la monitor',
        VerdictWithAWatchItem: 'liberado, con un punto en vigilancia',
        VerdictHeld: 'Retenido',
        VerdictQualified: 'Calificado',
        LabelVerdict: 'Decisión',
        HypAQualificationIsADecision: 'Una calificación es una decisión, no una explicación. Si la oblea monitor ' +
          'cae dentro de la ventana de especificación y cada lectura del banco está ' +
          'dentro de su propio límite, el equipo se libera. Si falla cualquiera de ' +
          'las dos cosas queda retenido, y una pasada con la cámara en nominal y otra ' +
          'con la receta calificada deberían decir cuál de las dos es responsable, ' +
          'sin llegar a un diagnóstico completo.',
        AskedIsThisToolFitToRun: '¿Está este equipo en condiciones de procesar producto ahora mismo, y qué ' +
          'hago al respecto?',
        SumRunTheMonitorWafer: 'Procesa la oblea monitor, contrasta el banco con sus límites y toma la ' +
          'decisión.',
        LabelAsFound: 'Tal como se encontró',
        LabelNoEffect: 'sin efecto',
        AxisYRecovered: '% recuperado',
        AxisXCandidateRanked: 'candidato (ordenado)',
        TitleHowMuchEachSingleRepair: 'Cuánto recupera cada reparación individual',
        LabelDeviationAsFound: 'Desviación tal como se encontró',
        LabelSeparation: 'Separación',
        LabelNextBest: 'Siguiente mejor',
        LabelMostLikelyCause: 'Causa más probable',
        HypIfASingleChamberAspect: 'Si un solo aspecto de la cámara es responsable, entonces restaurar ' +
          'exactamente ese a nominal —y nada más— debería recuperar el resultado, ' +
          'mientras que restaurar cualquier otro no. Si ninguna restauración ' +
          'individual ayuda, la cámara no es la causa.',
        AskedTheRecipeHasNotChanged: 'La receta no ha cambiado pero el resultado sí. ¿Qué se movió en la cámara?',
        SumIsolateAChamberFaultBy: 'Aísla una falla de cámara restaurando un sospechoso a la vez.',
        AxisYNonUniformity: 'no uniformidad %',
        LabelBestUniformity: 'Mejor uniformidad',
        LabelRateChange: 'Cambio de velocidad',
        LabelAtYourSetting: 'En tu ajuste',
        SumSweepAnySingleRecipeKnob: 'Barre cualquier perilla de la receta y ve todas las consecuencias a la vez.',
        LabelHeavyLoad: 'Carga pesada',
        TitleRadialRateTheSpreadThat: 'Velocidad radial — la dispersión que fija la ventana de sobregrabado',
        LabelMeanRate: 'Velocidad media',
        LabelNuHeavyLoad: 'NU con carga pesada',
        LabelNuAsRunning: 'NU tal como corre',
        HypOpticalEmissionFromTheEtch: 'La emisión óptica del producto de grabado cae cuando la película se ' +
          'despeja, así que la traza marca el punto final. Pero la oblea no se ' +
          'despeja toda a la vez —se despeja en una ventana fijada por la ' +
          'uniformidad— así que el sobregrabado requerido, y el óxido perdido durante ' +
          'él, deberían escalar con la no uniformidad.',
        AskedHowIsTheEndOf: '¿Cómo se detecta el final del grabado, y cuánto sobregrabado hace falta?',
        SumEndpointDetectionOveretchAndWhat: 'Detección de punto final, sobregrabado, y lo que la no uniformidad cuesta ' +
          'aguas abajo.',
        NextTheSheathIsThinnerThan: 'La vaina es más delgada que el camino libre medio de los iones a esta ' +
          'presión, así que los iones la cruzan sin chocar y llegan en una banda ' +
          'estrecha de energía. Sube la presión lo suficiente y eso deja de ser ' +
          'cierto. ¿Qué le pasa al perfil cuando los iones empiezan a llegar en todos ' +
          'los ángulos?',
        AxisXSourcePowerW: 'potencia de fuente (W)',
        TitleEtchRateVersusSourcePower: 'Velocidad de grabado frente a potencia de fuente con bias fijo',
        NoteImprovesWithSofterIons: 'mejora con iones más suaves',
        LabelRate2001600W: 'Velocidad, 200 -> 1600 W',
        NoteScale: 'Escala',
        DesignSourcePowerSwept200To: 'Potencia de fuente barrida de 200 a 1600 W con potencia de bias constante. ' +
          'En cada punto se resolvió la descarga para densidad de plasma, flujo de ' +
          'Bohm, voltaje de vaina por balance de potencia, y espesor de vaina por ley ' +
          'de Child.',
        HypTheBiasSupplyDeliversA: 'La fuente de bias entrega un número fijo de vatios a los iones que cruzan ' +
          'la vaina. La energía por ion es por tanto vatios divididos por corriente ' +
          'iónica. Si eso es correcto, subir la potencia de FUENTE —que crea más ' +
          'iones— debería hacer cada uno MENOS energético, aunque el ajuste de bias ' +
          'nunca se haya movido.',
        AskedWhereDoesIonBombardmentEnergy: '¿De dónde sale la energía de bombardeo iónico, y qué perilla la controla?',
        NextBiasRaisesIonEnergySource: 'El bias sube la ENERGÍA de los iones. La potencia de fuente sube su ' +
          'NÚMERO. Si necesitas más velocidad sin perder selectividad, ¿a cuál ' +
          'recurres — y qué le hace la vaina a la energía iónica cuando subes la ' +
          'fuente?',
        AxisXBiasPowerW: 'potencia de bias (W)',
        TitleRateAndSelectivityBothRespond: 'Velocidad y selectividad responden ambas al bias — en direcciones opuestas',
        LabelProfileAt200W: 'Perfil a 200 W',
        LabelProfileAt0W: 'Perfil a 0 W',
        LabelSelectivityAt500W: 'Selectividad a 500 W',
        LabelRateAt500W: 'Velocidad a 500 W',
        LabelOutOfReach: 'fuera de alcance',
        DesignBiasSweptFrom0To: 'Bias barrido de 0 a 500 W con potencia de fuente, presión y flujo fijos. ' +
          'Se midió velocidad de grabado, selectividad Si:SiO2, ángulo de pared ' +
          'lateral, y la energía iónica resultante.',
        HypBiasPowerSetsIonEnergy: 'La potencia de bias fija la energía de los iones, no su número. El silicio ' +
          'tiene un canal espontáneo de flúor y un umbral iónico bajo; el óxido no ' +
          'tiene ninguno de los dos. Así que subir el bias debería acelerar ambos, ' +
          'pero el óxido más rápido en términos relativos — es decir, la velocidad se ' +
          'compra con selectividad.',
        SumTradeRateAgainstSelectivityAnd: 'Intercambia velocidad contra selectividad y perforación del óxido.',
        LabelLoadTheLowLoadProduct: 'Carga el producto de baja carga',
        NextARecipeQualifiedOnA: 'Una receta calificada en un producto con 15% de área abierta ahora tiene ' +
          'que correr en uno con 65%. ¿Re-temporizas el grabado o recalificas la ' +
          'receta? Pregúntame qué cuesta cada opción en pérdida de óxido en el borde ' +
          'de la oblea.',
        TitleRadialProfileAtEachLoad: 'Perfil radial con cada carga',
        LabelMeanRateL305: 'velocidad media',
        AxisXOpenAreaFraction: 'fracción de área abierta',
        TitleEtchRateVersusOpenArea: 'Velocidad de grabado frente a área abierta',
        LabelNuAtHeavyLoad: 'NU con carga pesada',
        LabelLoadingRatio: 'Razón de carga',
        LabelRateAt90Open: 'Velocidad al 90% abierto',
        LabelRateAt4Open: 'Velocidad al 4% abierto',
        HypEveryExposedSquareMillimetreOf: 'Cada milímetro cuadrado expuesto de silicio compite por el mismo ' +
          'suministro finito de flúor. Si eso es lo que ocurre, la velocidad debería ' +
          'caer al subir el área abierta, y la oblea debería volverse menos uniforme ' +
          'al mismo tiempo — porque una carga más pesada empuja el grabado hacia el ' +
          'control por transporte.',
        AskedWhyDoesAnIdenticalRecipe: '¿Por qué una receta idéntica da una velocidad de grabado distinta en un ' +
          'producto distinto?',
        LabelLoadARecipeThatRuns: 'Carga una receta que corre rápida en el borde (90% de área abierta)',
        LabelAsRunning: 'Tal como corre',
        AxisYFAtomsM3: 'átomos de F m⁻³',
        TitleEtchantDensityAtTheSurface: 'Densidad de agente de grabado en la superficie — el suministro detrás de ' +
          'la velocidad',
        AxisXRR0Centre: 'r / R (0 = centro)',
        TitleEtchRateAcrossTheWafer: 'Velocidad de grabado a lo largo del radio de la oblea',
        LabelBestCondition: 'Mejor condición',
        LabelSignature: 'Firma',
        HypAFastEdgeMeansThe: 'Un borde rápido significa que el borde de la oblea recibe mejor suministro ' +
          'de agente de grabado que el centro. Si eso es cierto, cualquier cosa que ' +
          'suba el suministro respecto a la demanda —más gas, menos bias, difusión ' +
          'más rápida— debería aplanar el mapa. Si ninguna lo hace, la causa no es el ' +
          'suministro en absoluto.',
        AskedWhyDoesTheEtchRate: '¿Por qué la velocidad de grabado se inclina del centro al borde, y qué la ' +
          'está causando aquí?',
        SumDiagnoseACentreToEdge: 'Diagnostica una firma centro-borde y encuentra qué mecanismo la causa.',
      },
      metro: {
        NextYouHaveOneAlarmAnd: 'Tienes una alarma y un lote retenido. Retener cuesta dinero y enviar ' +
          'producto malo también. ¿Qué medirías a continuación para decidir, y ' +
          'cuántas lecturas extra harían falta para que la respuesta dejara de ser un ' +
          'volado?',
        LabelNoisyGauge: 'instrumento ruidoso',
        LabelStepAt30: 'escalón en 30',
        TitleTheDriftingRunMeasuredAnd: 'La corrida con deriva — medida, y la verdad detrás',
        LabelCpkNoisyGauge: 'Cpk, instrumento ruidoso',
        LabelStepCaughtAfter: 'Escalón atrapado tras',
        LabelDriftCaughtAtLot: 'Deriva atrapada en el lote',
        NoteIn60LotsOfA: 'en 60 lotes de un proceso que nunca se movió',
        LabelFalseAlarmsStable: 'Falsas alarmas, estable',
        HypControlRulesFireOnWhatever: 'Las reglas de control disparan con lo que tengan delante, y lo que tienen ' +
          'delante es el proceso más el instrumento. Así que un proceso perfectamente ' +
          'estable debería producir alarmas igualmente, una deriva genuina debería ' +
          'activar las reglas de racha antes que la regla de tres sigma, y un ' +
          'instrumento más ruidoso debería destruir la capacidad sin que el proceso ' +
          'cambie en absoluto.',
        AskedTheChartFiredIsThe: 'El gráfico disparó. ¿Se está moviendo el proceso, o es ruido?',
        SumSeparateARealProcessDrift: 'Separa una deriva real del proceso del ruido de medición y de la suerte.',
        NextIfTheGaugeIsNot: 'Si el instrumento no es capaz, puedes promediar varias lecturas para ' +
          'reducir su ruido. ¿Qué te cuesta eso, y en qué punto sale más barato ' +
          'comprar una herramienta mejor?',
        LabelTightProcess: 'Proceso estrecho',
        LabelLooseProcess: 'Proceso holgado',
        LabelThisProcess: 'Este proceso',
        NoteRepeatability: 'Repetibilidad',
        DesignTwentyFiveRepeatMeasurementsOf: 'Veinticinco mediciones repetidas de una sola oblea inmutable, y luego esa ' +
          'misma repetibilidad comparada contra tres variaciones de proceso ' +
          'distintas.',
        HypAGaugeHasOneRepeatability: 'Un instrumento tiene una repetibilidad, pero la capacidad no es una ' +
          'propiedad del instrumento. Es el cociente entre la dispersión de la ' +
          'medición y la dispersión total. Así que la misma herramienta debería ' +
          'resultar capaz para un proceso holgado e incapaz para uno estrecho, sin ' +
          'que nada de la herramienta cambie.',
        AskedIsThisMeasurementToolCapable: '¿Es esta herramienta de medición capaz de ver el proceso que vigila?',
        SumCompareMeasurementSpreadWithProcess: 'Compara la dispersión de medición con la del proceso — la capacidad es un ' +
          'cociente.',
        NextIfTheResidualCannotTell: 'Si el residuo no puede decirte que el modelo está mal, ¿qué puede? Piensa ' +
          'a qué tendría que ser sensible una segunda medición independiente para ' +
          'atrapar esto.',
        LabelRecipeIndex: 'índice de la receta',
        LabelCorrectIndex: 'índice correcto',
        TitleMeasuredSpectrumAndTheFitted: 'Espectro medido y el modelo ajustado',
        LabelResidualRecipe: 'Residuo, receta',
        LabelResidualCorrect: 'Residuo, correcto',
        LabelRecipeModel: 'Modelo de la receta',
        LabelCorrectModel: 'Modelo correcto',
        HypTheToolMeasuresReflectedLight: 'La herramienta mide luz reflejada, no espesor. Encuentra el espesor de ' +
          'película cuyo espectro de MODELO mejor coincide con lo que vio. Si el ' +
          'modelo es correcto la respuesta debería serlo; si el modelo tiene el ' +
          'índice de refracción equivocado el ajuste igual convergerá, igual ' +
          'reportará un número confiado, y estará mal — y el residuo apenas ' +
          'protestará.',
        AskedTheToolReportedAThickness: 'La herramienta reportó un espesor. ¿Es el correcto?',
        SumShowThatAMeasurementIs: 'Muestra que una medición es un ajuste de modelo, y puede estar ' +
          'precisamente equivocada.',
      },
      dep: {
        TitleThicknessAcrossTheRadiusUnder: 'Espesor a lo largo del radio bajo cada condición',
        LabelBestCondition: 'Mejor condición',
        LabelParasitic: 'Parásito',
        LabelAsRunning: 'Tal como corre',
        HypAProperlySaturatedProperlyPurged: 'Una película ALD bien saturada y bien purgada debería ser casi ' +
          'perfectamente uniforme, porque cada sitio toma exactamente una molécula ' +
          'sin importar cuánto gas pasó por encima. Así que la no uniformidad ' +
          'significa que el crecimiento está siguiendo la entrega de gas en algún ' +
          'punto: o la dosis nunca saturó, o el CVD parásito está añadiendo película ' +
          'encima del ALD. Esos tienen arreglos distintos, y probar cada uno por ' +
          'separado los distingue.',
        AskedIsThisThicknessMapA: '¿Este mapa de espesor es un problema de dosis o de purga?',
        SumDecideWhetherAThicknessMap: 'Decide si un mapa de espesor es un problema de dosis o de purga.',
        TitleFlatWaferGrowthOverThe: 'Crecimiento en oblea plana en el mismo barrido — no se mueve',
        LabelStepCoverage: 'cobertura de escalón',
        AxisYCoverageAtTheBottom: '% de cobertura en el fondo',
        TitleStepCoverageVersusDoseExposure: 'Cobertura de escalón frente a dosis — la exposición es la palanca',
        NoteAlreadySaturatedUpTop: 'ya saturado arriba',
        LabelFlatGrowthUnchanged: 'Crecimiento plano sin cambio',
        LabelOnAFlatWafer: 'En una oblea plana',
        HypInsideADeepFeatureThe: 'Dentro de un rasgo profundo el precursor avanza rebotando en las paredes, ' +
          'y cada rebote es una oportunidad de reaccionar antes de llegar al fondo. ' +
          'Si eso es lo que limita la conformalidad, entonces el arreglo es ' +
          'exposición —una dosis más larga— y debería mejorar el fondo sin cambiar en ' +
          'absoluto el crecimiento en la oblea plana, porque la superficie plana ya ' +
          'está saturada.',
        SumShowWhyConformalityIsA: 'Muestra por qué la conformalidad es una cuestión de dosis, no de química.',
        NextThePurgeHasToClear: 'La purga tiene que vaciar la cámara, y la cámara se vacía con su tiempo de ' +
          'residencia. Si subieras el flujo portador, ¿podrías acortar la purga aún ' +
          'más, y qué más cambiaría?',
        LabelCycleTime: 'tiempo de ciclo',
        AxisYSecondsPerCycle: 'segundos por ciclo',
        TitleCycleTimeVersusPurgeTime: 'Tiempo de ciclo frente a tiempo de purga — el rendimiento que estás ' +
          'comprando',
        AxisXPurgeMs: 'purga (ms)',
        TitleParasiticCvdVersusPurgeTime: 'CVD parásito frente a tiempo de purga',
        LabelCycleTimeSaved: 'Tiempo de ciclo ahorrado',
        LabelShortestSafePurge: 'Purga segura más corta',
        LabelResidenceTime: 'Tiempo de residencia',
        HypPurgingEmptiesTheChamberExponentially: 'La purga vacía la cámara exponencialmente, con el tiempo de residencia del ' +
          'gas. El precursor que quede cuando llegue el co-reactivo reacciona en fase ' +
          'gaseosa en lugar de esperar un sitio libre: eso es CVD. Así que una purga ' +
          'corta debería comprar tiempo de ciclo y pagarlo en crecimiento parásito y ' +
          'uniformidad, no en velocidad de crecimiento.',
        AskedHowShortCanThePurge: '¿Cuán corta puede ser la purga antes de que el proceso deje de ser ALD?',
        SumTradeCycleTimeAgainstParasitic: 'Intercambia tiempo de ciclo contra CVD parásito y uniformidad.',
        NextBothEndsOfTheWindow: 'Ambos extremos de la ventana crecen MÁS RÁPIDO que el medio. Si alguien te ' +
          'mostrara solo un número de espesor y te dijera que la herramienta corría ' +
          'caliente, ¿cómo lo distinguirías de una herramienta corriendo fría?',
        LabelGrowthHere: 'Crecimiento aquí',
        LabelParasiticHere: 'Parásito aquí',
        LabelYourSetpoint: 'Tu punto de consigna',
        HypAdsorptionNeedsHeatToComplete: 'La adsorción necesita calor para completarse, pero demasiado calor deja ' +
          'que el precursor se desorba o se descomponga por su cuenta. Si eso es ' +
          'correcto habrá una banda en el medio donde el crecimiento es plano y ' +
          'esencialmente todo es autolimitante, con crecimiento parásito subiendo en ' +
          'ambos extremos por dos razones distintas.',
        AskedWhereIsTheTemperatureBand: '¿Dónde está la banda de temperatura en la que este proceso sigue siendo ALD?',
        SumMapTheAldWindowAnd: 'Mapea la ventana ALD y encuentra dónde el crecimiento deja de ser ' +
          'autolimitante.',
        NextASaturatedDoseOnA: 'Una dosis saturada en una oblea plana puede aun así dejar sin precursor el ' +
          'fondo de un rasgo profundo, porque el precursor tiene que llegar hasta ' +
          'abajo antes de reaccionar. ¿Qué relación de aspecto tiene tu producto, y ' +
          'esta dosis sigue saturando en el fondo?',
        AxisYNonUniformity: 'no uniformidad %',
        TitleNonUniformityFollowsSaturation: 'La no uniformidad sigue a la saturación',
        AxisXDoseMs: 'dosis (ms)',
        LabelUniformityAtYourDose: 'Uniformidad a tu dosis',
        LabelDoseNeeded: 'Dosis necesaria',
        LabelSaturatedGrowth: 'Crecimiento saturado',
        LabelSelfLimiting: 'Autolimitante',
        HypIfTheSurfaceSaturatesGrowth: 'Si la superficie se satura, el crecimiento por ciclo subirá con la dosis y ' +
          'luego se aplanará, y todo lo que pase del codo será idéntico. Si sigue ' +
          'subiendo, la superficie no es el factor limitante y el proceso es CVD ' +
          'vestido con una receta de ALD.',
        AskedDoesGrowthStopRespondingTo: '¿El crecimiento deja de responder a la dosis? ¿Es este proceso realmente ' +
          'autolimitante?',
        NextDoseCost: 'Una dosis más larga arregla la zanja pero alarga cada uno de tus {{cycles}} ciclos. Calcula lo que eso cuesta en tiempo de proceso, y decide después si subir la presión del precursor compraría la misma exposición más barata.',
        SumSweepDoseTimeAndFind: 'Barre el tiempo de dosis y encuentra dónde el crecimiento deja de responder.',
      },
      config: {
        LabelDeepseek: 'DeepSeek',
      },
      route: {
        step: { wellDriveIn: 'Recocido del pozo', gateOxidation: 'Oxidación de puerta', spacerDensify: 'Densificado del espaciador', spikeAnneal: 'Recocido de pico' },
        stepPurpose: {
          'a-day-per-cooldown-the-test-is-the-bottleneck': 'Un día por enfriamiento: el ensayo es el cuello de botella',
          'aluminium-wire-which-goes-superconducting-with-the-chip': 'Hilo de aluminio, que se vuelve superconductor junto con el chip',
          'a-fridge-takes-a-chip-not-a-wafer': 'Un refrigerador admite un chip, no una oblea',
          'area-sets-the-frequency-and-ten-percent-wide-is-a-collision': 'El área fija la frecuencia, y un diez por ciento de más es una colisión',
          'a-nanometre-of-aluminium-oxide-counted-in-cycles': 'Un nanómetro de óxido de aluminio, contado en ciclos',
          'transfer-it-into-the-superconducting-film': 'Transferirlo a la película superconductora',
          'capacitor-pads-resonators-and-the-junction-leads': 'Placas del condensador, resonadores y los terminales de la unión',
          'a-lossy-surface-is-a-lossy-qubit': 'Una superficie con pérdidas es un cúbit con pérdidas',
          'bin-it-and-decide-what-escapes': 'Clasifícalo, y decide qué se escapa',
          'and-the-die-is-under-183-mpa-for-the-rest-of-its-life': 'Y el dado queda bajo 183 MPa el resto de su vida',
          'onto-a-substrate': 'Sobre un sustrato',
          'and-the-wafer-stops-being-a-wafer': 'Y la oblea deja de ser una oblea',
          'the-first-honest-yield-number-anyone-has-had': 'La primera cifra honesta de rendimiento que alguien ha tenido',
          'last-look-before-the-wafer-is-cut-up': 'Último vistazo antes de cortar la oblea',
          'a-defect-free-doped-to-order-layer-over-the-bulk': 'Una capa sin defectos y dopada a medida sobre el sustrato',
          'before-it-is-allowed-into-the-front-end': 'Antes de dejarla entrar al front-end',
          'one-crystal-becomes-a-thousand-wafers': 'Un cristal se convierte en mil obleas',
          '72-hours-for-one-ingot-and-one-ingot-is-1000-wafers': '72 horas por lingote, y un lingote son ~1000 obleas',
          'the-next-layer-prints-on-this-surface': 'La siguiente capa se imprime sobre esta superficie',
          'slurry-is-a-defect-if-it-is-left-on': 'La lechada es un defecto si se queda encima',
          'take-off-everything-above-the-trench': 'Quita todo lo que queda por encima de la trinchera',
          'bottom-up-fill-and-an-overburden-for-cmp': 'Relleno de abajo arriba, y un sobreespesor para el CMP',
          'copper-poisons-silicon-so-it-is-fenced-in': 'El cobre envenena el silicio, así que se lo encierra',
          'polymer-off-before-anything-is-deposited-on-it': 'Fuera el polímero, antes de depositar nada encima',
          'ash-the-resist-off-before-anything-is-deposited': 'Elimina el resist antes de depositar nada',
          'and-the-wire-that-sits-on-top-of-the-via': 'Y el cable que se asienta sobre la vía',
          'down-to-the-layer-below': 'Hasta la capa de abajo',
          'where-the-wires-go': 'Por donde van los cables',
          'the-insulator-the-trenches-go-into': 'El aislante donde van las trincheras',
          'because-everything-after-this-assumes-it-worked': 'Porque todo lo que sigue asume que funcionó',
          'activate-the-dopant-and-move-it-as-little-as-possible': 'Activa el dopante moviéndolo lo menos posible',
          'self-aligned-to-the-gate-that-was-just-etched': 'Autoalineada a la puerta recién grabada',
          'the-gate-electrode': 'El electrodo de puerta',
          'the-thinnest-most-consequential-film-in-the-device': 'La película más fina y de mayores consecuencias del dispositivo',
          'deep-and-expensive-in-budget': 'Profunda, y cara en presupuesto térmico',
          'the-tub-the-device-sits-in': 'La cubeta donde se asienta el dispositivo',
          'a-furnace-drives-contamination-in-it-does-not-remove-it': 'Un horno mete la contaminación hacia dentro, no la elimina',
          'print-one-line-in-four': 'Imprime una línea de cada cuatro',
          'four-lines-where-there-was-one': 'Cuatro líneas donde había una',
          'anisotropic-again': 'Anisotrópico otra vez',
          'the-first-spacers-are-now-the-mandrels': 'Los primeros espaciadores son ahora los mandriles',
          'two-lines-where-there-was-one': 'Dos líneas donde había una',
          'anisotropic-clears-the-horizontals': 'Anisotrópico: despeja las horizontales',
          'ald-this-thickness-sets-the-half-pitch': 'ALD — este espesor fija el semipaso',
          'the-knob-that-centres-the-pattern': 'El mando que centra el patrón',
          'the-only-exposure-in-the-whole-module': 'La única exposición de todo el módulo',
          'transfer-that-too': 'Transfiere esas también',
          'print-the-lines-in-between': 'Imprime las líneas intermedias',
          'transfer-it': 'Transfiérela',
          'print-every-other-line': 'Imprime una línea de cada dos',
          'transfer-resist-into-the-film': 'Transfiere el resist a la película',
          'print-the-pattern-directly-at-13-5-nm': 'Imprime el patrón directamente a 13.5 nm',
          'print-the-pattern-directly': 'Imprime el patrón directamente',
        },
        stepLabel: {
          'cool-down-and-characterise': 'Enfriar y caracterizar',
          'wire-bond-to-the-sample-holder': 'Soldar hilos al portamuestras',
          'dice-into-chips': 'Cortar en chips',
          'measure-every-junction': 'Medir cada unión',
          'grow-the-tunnel-barrier': 'Crecer la barrera túnel',
          'etch-the-circuit': 'Grabar el circuito',
          'expose-the-circuit': 'Exponer el circuito',
          'substrate-clean': 'Limpieza del sustrato',
          'final-test-and-burn-in': 'Test final y burn-in',
          'mould-and-cure': 'Moldeo y curado',
          'die-attach-and-wire-bond': 'Fijación del dado y wire bond',
          'grind-and-dice': 'Rectificado y corte',
          'wafer-sort': 'Clasificación de obleas',
          'final-inspection': 'Inspección final',
          'epitaxial-device-layer': 'Capa epitaxial de dispositivo',
          'incoming-clean': 'Limpieza de entrada',
          'slice-lap-etch-polish': 'Corte, lapeado, grabado, pulido',
          'crystal-growth': 'Crecimiento del cristal',
          'thickness-and-dishing-check': 'Control de espesor y dishing',
          'post-cmp-clean': 'Limpieza pos-CMP',
          'copper-cmp': 'CMP del cobre',
          'copper-plate': 'Electrodepósito de cobre',
          'barrier-and-seed': 'Barrera y semilla',
          'post-etch-clean': 'Limpieza pos-grabado',
          'resist-strip': 'Remoción del resist',
          'trench-etch': 'Grabado de trincheras',
          'via-etch': 'Grabado de vías',
          'trench-lithography': 'Litografía de trincheras',
          'dielectric-deposition': 'Depósito del dieléctrico',
          'post-gate-metrology': 'Metrología pos-puerta',
          'spike-anneal': 'Recocido spike',
          'source-drain-implant': 'Implantación de fuente/drenador',
          'polysilicon-deposition': 'Depósito de polisilicio',
          'gate-oxidation': 'Oxidación de puerta',
          'well-drive-in': 'Difusión del pozo',
          'well-implant': 'Implantación del pozo',
          'pre-gate-clean': 'Limpieza pre-puerta',
          'pull-the-first-spacers': 'Retira los primeros espaciadores',
          'spacer-2-etch': 'Grabado del espaciador 2',
          'deposit-spacer-2': 'Deposita el espaciador 2',
          'pull-the-mandrel': 'Retira el mandril',
          'spacer-1-etch': 'Grabado del espaciador 1',
          'deposit-spacer-1': 'Deposita el espaciador 1',
          'trim-the-mandrel': 'Recorta el mandril',
          'print-the-mandrel': 'Imprime el mandril',
          'etch-population-4': 'Grabado, población 4',
          'etch-population-3': 'Grabado, población 3',
          'etch-population-2': 'Grabado, población 2',
          'etch-population-1': 'Grabado, población 1',
          'expose-population-4': 'Exposición, población 4',
          'expose-population-3': 'Exposición, población 3',
          'expose-population-2': 'Exposición, población 2',
          'expose-population-1': 'Exposición, población 1',
          'etch-second-population': 'Grabado, segunda población',
          'expose-second-population': 'Exposición, segunda población',
          'etch-first-population': 'Grabado, primera población',
          'expose-first-population': 'Exposición, primera población',
          'pattern-transfer-etch': 'Grabado de transferencia del patrón',
          expose: 'Exposición',
        },
        NoteTakeTheCall: 'Cuatro causas candidatas, y una de ellas es que sea simplemente una ' +
          'máquina. Las alarmas de estación son gratis y las gráficas por capa no, y ' +
          'esa es la decisión: hora y media de tiempo de máquina para ver una ' +
          'tendencia, frente a dictaminar desde un resumen que se ve verde de ' +
          'cualquier modo.',
        FaultIntro: 'Cualquier otra falla en este simulador es una falla de máquina: algo va ' +
          'mal en una cámara y la cámara lo dice. Estas tres no. En cada una, todas y ' +
          'cada una de las visitas están dentro de su especificación y el producto ' +
          'está fuera de la suya — así que no hay lectura mala que encontrar, ni ' +
          'alarma que atender, ni máquina que reparar. La competencia consiste en ' +
          'dejar de mirar la máquina, que es el hábito más difícil de enseñar, porque ' +
          'todos los instintos de un buen técnico dicen que vaya a revisar la ' +
          'máquina.',
        AxisChlorineDoseS: 'dosis de cloro (s)',
        AxisEpcNm: 'EPC (nm)',
        NoteTheSameInstrumentDep02: 'El mismo instrumento que DEP-02 lleva para ALD, y la misma prueba: un ' +
          'semiciclo verdaderamente autolimitante sube y luego se APLANA. Una curva ' +
          'que sigue trepando significa que más precursor sigue quitando más ' +
          'material. Nota que esta curva sola no demuestra ALE — una receta ' +
          'sobre-energética también satura. El número de sinergia es la prueba que ' +
          'los separa.',
        NoteBelowSaturationDoseNow: 'BAJO saturación — la dosis ahora fija la velocidad',
        NoteSaturated: 'saturado',
        ValueSputtering: 'pulverizando',
        ValueStarved: 'bajo saturación',
        ValueInWindow: 'en ventana',
        NotePartOfThisEtchIs: 'parte de este grabado es continuo',
        NoteNeitherHalfEtchesAlone: 'ninguna mitad graba sola — esto es ALE',
        LabelEtchRate: 'Velocidad de grabado',
        LabelSelectivity: 'Selectividad',
        LabelRegime: 'Régimen',
        LabelSynergy: 'Sinergia',
        mat: {
          'silicon-oxide': 'Óxido',
          'silicon-nitride': 'Nitruro',
          silicon: 'Silicio',
        },
        SectionFilm: 'Película',
        SecondaryLabel: 'Dispersión de CD en la última capa',
        ToolLabel: 'La ruta — cada estación, en orden',
        fault: {
          justOneTool: {
            Evidence: [
              'Una estación está fuera de especificación, cosa que en un fallo de ruta ' +
                'genuino nunca pasa.',
              'El gráfico por capas está plano, la unión en objetivo, y ninguna regla de ' +
                'control ha disparado.',
              'El cambio fue abrupto y con fecha — los fallos de ruta llegan gradualmente.',
            ],
            Truth: 'No es un fallo de ruta. CMP-11 está alarmando en tasa de remoción — un ' +
              'problema ordinario de una estación con un diagnóstico ordinario, y su ' +
              'propio informe de turno lo nombrará en veinte minutos. La mayoría de las ' +
              'excursiones de rendimiento SON una máquina, y un estudiante que ha ' +
              'aprendido a echar mano por reflejo de la maquinaria de ruta ha aprendido ' +
              'la mitad equivocada de la lección. Lee primero las alarmas de estación; ' +
              'son gratis.',
            Complaint: 'Rendimiento cinco puntos abajo desde el martes. El ingeniero de ' +
              'rendimiento ha abierto la revisión de ruta y quiere hablar de acumulación.',
            Label: 'Es una sola máquina, y la ruta es una distracción',
          },
          sequenceDrift: {
            Evidence: [
              'Reglas de carta de control disparadas sin un solo punto fuera de límite.',
              'La oblea dorada muestra el desplazamiento acumulado directamente.',
              'El gráfico por capas de CMP está plano y la unión en objetivo.',
            ],
            Truth: 'Una máquina deriva unos 0,18 por capa. En once capas eso es 1,8 — el ' +
              'sesenta por ciento de la especificación unilateral — y ni una lectura ha ' +
              'salido de sus límites de control, porque la deriva es pequeña frente a los ' +
              'límites y grande frente a la dispersión corrida a corrida. Ocho puntos ' +
              'consecutivos a un lado de la línea central son un suceso de uno entre 128 ' +
              'en un proceso centrado, y por eso existe la regla y por eso solo una carta ' +
              'a través de visitas la tiene.',
            Complaint: 'El rendimiento paramétrico resbala un poco cada semana y nadie puede ' +
              'señalar un lote donde se torció. Nada ha alarmado.',
            Label: 'Once mediciones, todas en especificación, y algo se mueve',
          },
          budgetSpent: {
            Evidence: [
              'Ningún recocido individual supera su límite.',
              'El corte de la unión está pasado de 78 nm.',
              'El gráfico por capas de CMP está plano y ninguna regla de carta de control ' +
                'ha disparado.',
            ],
            Truth: 'El drive-in del pozo se alargó de 300 a 880 s para arreglar una queja de ' +
              'resistividad. Sigue siendo un recocido legal — el límite por paso es 900 s ' +
              '— y gastó presupuesto del que dependía el recocido spike del final. La ' +
              'profundidad de unión va como la raíz del TOTAL, así que el último paso de ' +
              'un flujo está limitado por todos los anteriores, y la cuenta no tiene ' +
              'dueño.',
            Complaint: 'Los bins paramétricos se han desplazado en todo el producto. Cada paso de ' +
              'horno está dentro de su ventana y THRM-04 diagnostica limpio en cada ' +
              'visita.',
            Label: 'La unión es demasiado honda y ningún recocido fue demasiado largo',
          },
          stackAccumulation: {
            Evidence: [
              'La altura de escalón post-CMP pasa en las diez capas, contra el límite tal ' +
                'como está escrito.',
              'Graficada contra el NÚMERO DE CAPA sube y se asienta alto en vez de ' +
                'quedarse plana.',
              'Ninguna estación alarma y la unión está en objetivo.',
            ],
            Truth: 'El acondicionador del pad de CMP-11 está gastado, así que la eficiencia de ' +
              'planarización cayó de 0,90 a 0,55. Cada pulido aún deja menos que el ' +
              'límite de 80 nm por visita — y pasa hacia arriba más topografía que antes, ' +
              'y diez capas de eso se componen hasta 70 nm. Ambas partes dicen la verdad. ' +
              'El límite por visita nunca se derivó del presupuesto de foco, y el hueco ' +
              'entre los 80 nm y los 55 nm que el escáner necesitaba es el espacio donde ' +
              'se escondió el fallo.',
            Complaint: 'Litografía desecha en metal 8 en cada lote y culpa al foco. CMP dice estar ' +
              'en especificación en las diez capas y tiene los datos para probarlo.',
            Label: 'El metal 8 no imprime, y CMP-11 está en especificación en todas las capas',
          },
          routeHealthy: {
            Evidence: [
              'Cada comprobación por visita pasa y ninguna estación alarma.',
              'Los gráficos por capa están planos y la unión en objetivo.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es seguir corriendo.',
            Complaint: 'Revisión de rendimiento. La línea corre según pronóstico.',
            Label: 'Nada mal',
          },
        },
        NoteSCycleTheRieRecipe: '{{a}} s/ciclo — la receta RIE de al lado corre a ~200',
        NoteEvAgainstAEvWindow: '{{a}} eV frente a una ventana de {{b}}–{{c}} eV',
        NoteNmOverCycles: '{{a}} nm en {{b}} ciclos',
        NoteSameChamberSameGasesSame: 'La misma cámara, los mismos gases, las mismas fuentes. Lo que hace de esto ' +
          'ALE es que la química y los iones se han separado en el tiempo, y la ' +
          'energía iónica se ha bajado por debajo del punto donde puede grabar nada ' +
          'por sí sola. Dos órdenes de magnitud más lento que la receta de arriba, y ' +
          'así se quitan los dos últimos nanómetros cuando equivocarse no es una ' +
          'opción.',
        SectionEnergyWindowIsTheRemoval: 'Ventana de energía — ¿es autolimitada la mitad de remoción?',
        NoteTheModifiedLayerHasA: 'La capa modificada tiene un umbral de pulverización más bajo que el ' +
          'material de debajo, y entre los dos umbrales hay una banda donde la capa ' +
          'modificada sale y el sustrato no. Por debajo no se quita nada; por encima ' +
          'estás pulverizando, y el proceso ya no es atómico en nada. Correr por ' +
          'encima de la ventana es la manera más común de tener una receta que se ' +
          'llama ALE y no lo es.',
        SectionSaturationCurveIsTheDose: 'Curva de saturación — ¿es autolimitada la mitad de dosis?',
        SectionStoredAleRecipes: 'Recetas ALE almacenadas',
        KnobWafers: '{{n}}k obleas',
        flow: {
          'quantum-chip': {
            Purpose: 'Ocho operaciones, una unión de un nanómetro de grosor y un ensayo que ' +
              'lleva un día a 10 mK.',
            Name: 'Chip de prueba con transmón',
          },
          'product-saqp': {
            Purpose:
              'Del silicio a la pieza clasificada, con el bucle de interconexión una vez ' +
              'por capa de metal.',
            Name: 'Ruta completa de producto — SAQP',
          },
          'back-end': {
            Purpose: 'Una oblea terminada se convierte en piezas clasificadas.',
            Name: 'Ensamblaje y prueba',
          },
          'gate-saqp': {
            Purpose:
              'Pozo, óxido de puerta, polisilicio, patrón, y el fuente/drenador que le ' +
              'sigue.',
            Name: 'Capa de puerta — SAQP',
          },
          substrate: {
            Purpose: 'Polisilicio y una semilla se convierten en una oblea pulida.',
            Name: 'Sustrato',
          },
          'metal-layer': {
            Purpose: 'Grabar el dieléctrico, rellenarlo de cobre, quitar el exceso.',
            Name: 'Capa de metal damasquinada',
          },
          le4: {
            Purpose: 'Cuatro máscaras para el mismo paso que SAQP alcanza con una.',
            Name: 'Patronaje cuádruple LE⁴',
          },
          saqp: {
            step7: {
              Label: 'Retira los primeros espaciadores',
            },
            step6: {
              Label: 'Grabado del espaciador 2',
            },
            step5: {
              Label: 'Deposita el espaciador 2',
            },
            step4: {
              Label: 'Retira el mandril',
            },
            step3: {
              Label: 'Grabado del espaciador 1',
            },
            step2: {
              Label: 'Deposita el espaciador 1',
            },
            step1: {
              Label: 'Recorta el mandril',
            },
            step0: {
              Label: 'Imprime el mandril',
            },
            Purpose:
              'Una máscara, ocho operaciones, y un paso final fijado por un espesor de ' +
              'película.',
            Name: 'SAQP',
          },
          lele: {
            Purpose:
              'Dos máscaras, cuatro operaciones. El solape entre ellas se vuelve error de ' +
              'CD.',
            Name: 'Doble patronaje LELE',
          },
          'single-euv': {
            Purpose: 'Una máscara, dos operaciones, y el paso que a los demás les cuesta ocho.',
            Name: 'Exposición única, EUV',
          },
          'single-duv': {
            Purpose: 'Una máscara, dos operaciones, y un tope duro en 36 nm de semipaso.',
            Name: 'Exposición única, 193i',
          },
        },
        LabelGateOxide: 'Óxido de puerta',
        LabelWellDriveIn: 'Drive-in del pozo',
        TextTheThreeMechanisms: 'Los tres mecanismos, desarrollados',
        TextTakeTheCall: 'Toma la decisión',
        TextHowYouWouldSeeIt: 'Cómo lo verías',
        TextTheFault: 'El fallo',
        TextWhatTheProductSays: 'Lo que dice el producto',
        TextWhatTheToolSays: 'Lo que dice la máquina',
        TextMostExpensive: 'Más caras',
        TextMostVisited: 'Más visitadas',
        TextShowWhatWasActuallyWrong: 'Mostrar qué estaba mal de verdad',
        TextRouteFaults: 'Fallos de ruta',
        TextComparePatterning: 'Comparar patronaje',
        LabelEtchPerCycle: 'Grabado por ciclo',
        LabelSurfaceCoverage: 'Cobertura de superficie',
        NoteTheMaskThresholdIsFar: 'el umbral de la máscara está muy por encima de la capa modificada',
        SectionAleRecipe: 'Receta ALE',
        case: {
          'slow-drift': {
            HowToSeeIt:
              'Las reglas de Western Electric. Ocho puntos seguidos a un lado de la línea ' +
              'central son un suceso de uno entre 128 en un proceso centrado, así que no ' +
              'es azar. Nada está fuera de límites; el PATRÓN es la señal, y solo una ' +
              'carta a través de visitas lo tiene.',
            ProductSays:
              'El rendimiento paramétrico resbala y nadie puede señalar el lote donde se ' +
              'torció.',
            ToolSays:
              'Cada lectura está dentro de especificación y de los límites de control. ' +
              'Ninguna alarma ha sonado.',
            Truth:
              'Un controlador de flujo másico deriva unos 0,4 nm equivalentes por capa. ' +
              'En once capas son 4 nm de desplazamiento — dentro de especificación en ' +
              'todas y cada una de las visitas, y un proceso que ya no está centrado.',
            Label: 'Once mediciones, todas en especificación, y la máquina está rota',
          },
          'thermal-account': {
            HowToSeeIt:
              'Suma el presupuesto térmico a lo largo de la ruta en vez de comprobar cada ' +
              'paso contra su propio límite. El último recocido de un flujo está limitado ' +
              'por todos los anteriores, y la cuenta no tiene dueño.',
            ProductSays: 'Profundidad de unión fuera de especificación al final del módulo de puerta.',
            ToolSays:
              'Cada paso de horno está dentro de su ventana de tiempo y temperatura. ' +
              'THRM-04 diagnostica limpio en cada visita.',
            Truth:
              'El drive-in del pozo se alargó para arreglar una queja de resistividad. ' +
              'Sigue siendo un recocido legal por sí solo. Gastó presupuesto del que ' +
              'dependía el recocido spike del final del flujo, y la unión que sale es un ' +
              '30% demasiado profunda.',
            Label: 'La unión es demasiado profunda y ningún recocido fue demasiado largo',
          },
          'planarisation-drift': {
            HowToSeeIt:
              'Grafica el rango post-CMP contra el NÚMERO DE CAPA en vez de contra la ' +
              'especificación. Una pila sana converge a una línea plana; esta sube y ' +
              'sigue subiendo, porque una serie geométrica con razón menor converge más ' +
              'arriba.',
            ProductSays:
              'La dispersión de CD en el escáner supera el presupuesto de foco en el ' +
              'metal 8, y litografía culpa al escáner.',
            ToolSays:
              'Cada pasada de CMP en cada capa está dentro de su propia especificación. ' +
              'Tasa de remoción, uniformidad y rango tras el pulido leen normal.',
            Truth:
              'El acondicionador del pad está gastado, así que la eficiencia de ' +
              'planarización cayó de 0,90 a 0,55. Cada pulido sigue quitando lo que pide ' +
              'la receta y sigue dejando menos que el límite de 45 nm por visita — pero ' +
              'pasa hacia arriba más topografía que antes, y diez capas de eso se ' +
              'componen.',
            Label: 'El metal 8 no imprime, y CMP-11 está en especificación en todas las capas',
          },
        },
        AsideDrift:
          'La deriva total en once capas es {{total}} — el sesenta por ciento de la ' +
          'especificación unilateral, gastada sin que una sola lectura saliera de la ' +
          'ventana. Ocho puntos consecutivos al mismo lado de la línea central tienen ' +
          'una probabilidad de una entre 128 en un proceso centrado, y por eso es una ' +
          'regla y no una corazonada.',
        AsidePlanarisation:
          'Una pila sana se asienta en {{healthy}} nm y esta en {{worn}} nm. Ambas ' +
          'son series geométricas — R(n+1) = (R(n)+F)(1−η) — y donde se asientan es ' +
          'F(1−η)/η, fijado por la eficiencia y no por ninguna pasada. El límite por ' +
          'visita se escribió en {{visit}} nm porque eso es lo que la máquina ' +
          'aguantaba; el presupuesto de foco necesitaba {{route}} nm. El fallo vive ' +
          'en el hueco entre esos dos números, y el hueco es el defecto real.',
        VerdictNoRule: 'ninguna regla disparada',
        VerdictNoReadingOut: 'ninguna lectura fuera de especificación ni de límites de control',
        VerdictJunction: 'unión de {{junction}} nm frente a {{limit}} nm',
        VerdictAnnealsOk: 'cada recocido dentro de su propia ventana',
        VerdictCdSpread: 'dispersión de CD {{spread}} nm frente a {{budget}} nm',
        VerdictInSpecAll: 'en especificación en las 10 capas',
        LabelSpike: 'Spike',
        LabelDensify: 'Densificado',
        LimitRouteNeeds: 'lo que necesita la ruta {{limit}} {{unit}}',
        LimitPerVisit: 'límite por visita {{limit}} {{unit}}',
        LimitControl: 'límite de control',
        LimitSpec: 'especificación',
        StepListMore: '… y {{more}} más, la mayoría la capa de metal repetida',
        StepListHead: 'Operaciones — 1 a {{shown}} de {{total}}',
        TileGoodDieNote: '{{good}} de {{gross}} die',
        TilePerGoodDie: 'Por die bueno',
        TileDieYieldNote: 'con die de {{area}} cm²',
        TileDieYield: 'Rendimiento de die',
        TileLineYieldNote: 'obleas enteras que sobreviven',
        TileLineYield: 'Rendimiento de línea',
        TileThermalNote: 'segundos equivalentes a 1000 °C',
        TileThermal: 'Presupuesto térmico',
        TileDefectsNote: 'cada toque cuesta algo',
        TileDefects: 'Defectos añadidos',
        TileLithoNote: 'del coste, en {{masks}} de {{steps}} pasos',
        TileLithoShare: 'Cuota de lito',
        TileCostNote: 'de ello en retícula',
        PerWaferSuffix: '/oblea',
        TileCost: 'Coste',
        TileMasksNote: 'cada una es una retícula, comprada una vez',
        TileOperationsNote: 'la oblea se detiene tantas veces',
        TileOperations: 'Operaciones',
        ThPerGoodDie: '$/die bueno',
        ThYield: 'Rendimiento compuesto',
        ThLitho: 'Lito',
        ThPerWafer: '$/oblea',
        ThMasks: 'Máscaras',
        ThSteps: 'Pasos',
        ThRoute: 'Ruta',
        CompareIntro:
          'Cinco maneras de poner un patrón en una oblea. Las tres últimas alcanzan ' +
          'un paso que ninguna exposición única a 193 nm resuelve, y llegan cambiando ' +
          'máscaras por operaciones: la máscara se compra una vez y el paso se paga ' +
          'en cada oblea. Cambia abajo el tamaño de die o el volumen y el ranking se ' +
          'mueve — esa es la respuesta real: no hay mejor ruta, solo la mejor ruta ' +
          'para un producto.',
        KnobVolume: 'Volumen de producción',
        KnobDieArea: 'Área del die',
        group: {
          quantum: 'Dispositivos cuánticos',
          product: 'Producto',
          layers: 'Módulos de capa',
          patterning: 'Módulos de patronaje',
        },
        SideNote:
          'Una fábrica no es una fila de máquinas. Es un bucle que se recorre una vez ' +
          'por capa, y un producto es el orden en que la oblea le da la vuelta.',
        reading: {
          goldenWafer: {
            Label: 'Re-medir las últimas 11 capas en oblea dorada (6.0 h)',
          },
          junction: {
            Label: 'Corte de la unión — destructivo (4.0 h)',
          },
          layerTrend: {
            Limit: 'plano, y bajo 55 nm',
            Label: 'Rango post-CMP contra número de capa (1.5 h)',
          },
          stationAlarms: {
            ValueNone: 'ninguna',
            ValueCmp: 'CMP-11 con velocidad de remoción baja',
            Limit: 'ninguna',
            Label: 'Estaciones fuera de especificación',
          },
          spcRules: {
            ValueNone: 'ninguna',
            Limit: 'ninguna',
            Label: 'Reglas de control disparadas en la secuencia',
          },
          thermalTotal: {
            Limit: 'ningún paso sobre 900 s',
            Label: 'Presupuesto térmico gastado en este flujo',
          },
          cmpVisits: {
            Value: '{{a}} de {{b}} dentro de {{c}} nm',
            Limit: 'las 10',
            Label: 'Escalón post-CMP, cada capa',
          },
        },
        aspect: {
          singleStation: {
            Action:
              'Ir a la máquina que alarma y correr su propio informe de turno — esto no ' +
              'es un problema de ruta',
            Label: 'Una sola estación, y aplica el diagnóstico ordinario',
          },
          toolDrift: {
            Action: 'Encontrar la máquina que deriva en el gráfico por capas y re-centrarla',
            Label: 'Una máquina derivando a lo largo de la secuencia de capas',
          },
          thermalBudget: {
            Action: 'Devolver el drive-in del pozo a 300 s y buscar otra vía para la resistividad',
            Label: 'Presupuesto térmico gastado antes del último recocido',
          },
          planarisation: {
            Action:
              'Reavivar el acondicionador del pad de CMP-11, y re-derivar el límite de ' +
              'escalón por visita del presupuesto de foco, no de lo que aguanta la ' +
              'máquina',
            Label: 'Acumulación a través del stack de metal',
          },
        },
      },
      etch: {
        GuardNotRun: 'La cámara no ha funcionado. Inicia el proceso arriba: el lote recibe lo ' +
          'que el plasma hizo de verdad, no lo que pedía la receta.',
        GuardEtched: 'Este patrón ya está en la película. Hace falta una nueva exposición antes ' +
          'de que haya algo más que transferir.',
        GuardNoPattern: 'No hay nada impreso en este lote. Una grabadora transfiere un patrón de ' +
          'resina a la película de debajo: expón primero la oblea en LITH-03 o ' +
          'EUV-22.',
        TransferLot: 'Transferir el patrón y enviar el lote a limpieza',
        NoteNoLot: 'No hay lote en la esclusa de carga. Trae una oblea con patrón para ' +
          'transferirlo a la película.',
        SecondaryLabel: 'No uniformidad',
        ToolLabel: 'ETCH-01 · Grabado ICP de polisilicio',
        SectionAtomicLayerMode: 'Modo de capa atómica — la misma cámara, operada de otra manera',
        SectionSheath: 'De dónde vienen los iones — la vaina en el borde de la oblea (campo eléctrico)',
        SheathNote:
          'Un corte por el borde de la oblea: el plasma como frontera plana arriba, la oblea y su ' +
          'anillo de enfoque abajo, y entre ambos la vaina — que es lo que muestra la imagen. El ' +
          'brillo es el potencial, claro en el plasma y oscuro en la oblea; las líneas amarillas ' +
          'son sus equipotenciales; y las flechas indican adónde van los iones — ' +
          'campo abajo, lo que sobre una superficie plana significa hacia abajo y nada más. Esa es ' +
          'toda la razón de que un grabado por plasma corte una pared vertical: los iones no ' +
          'apuntaron, apuntó el campo. Ahora el anillo. Nuevo, su cara superior está al ras de la ' +
          'oblea y el campo pasa plano por el borde, así que el último chip ve exactamente lo mismo ' +
          'que el centro. Deje correr la cámara y el anillo se erosiona: las equipotenciales se ' +
          'hunden sobre la superficie más baja, de modo que a cualquier altura el potencial sobre ' +
          'el anillo es mayor que sobre la oblea, y el campo justo encima del borde adquiere un ' +
          'tirón lateral del anillo hacia la oblea. Dos cosas ocurren a la vez — los iones del ' +
          'borde se inclinan hacia dentro, sobre la esquina expuesta de la oblea, así que los ' +
          'chips más exteriores graban un perfil torcido, y las líneas de campo que antes se ' +
          'repartían por el anillo convergen ahora en esa esquina como convergen en un pararrayos, ' +
          'de modo que el borde recibe más y corre rápido. Ese es el mapa de borde rápido del fallo ' +
          'de anillo gastado, visto. Suba el bias y todas las flechas crecen y ninguna gira: el ' +
          'campo es el de Laplace, y Laplace es lineal, así que la inclinación pertenece a la ' +
          'geometría y solo un anillo nuevo la corrige.',
        SheathCaveat:
          'Límites honestos. Una vaina real es una capa de carga espacial de unos milímetros cuya ' +
          'frontera es el propio plasma; esto es un hueco conductor bajo una placa fija. La forma ' +
          '—plana sobre lo plano, curvada sobre un escalón— es la misma, y la curvatura es la ' +
          'lección. No hay ningún Ejecutar que pulsar ni reloj alguno: este campo no lleva tiempo ' +
          'dentro, así que el solucionador lo asienta en cuanto se mueve un mando, y lo que varía ' +
          'aquí es el anillo y no el minuto. El anillo toca la oblea, mientras que uno real queda ' +
          'a una fracción de milímetro, y ' +
          'está al potencial de la oblea: un anillo real se acopla por RF a través de su propia ' +
          'capacidad, que cambia al adelgazarse y puede empujar la vaina sobre el anillo en el ' +
          'sentido contrario — por eso las cámaras modernas alimentan el anillo con su propia RF ' +
          'para dirigir la inclinación. Aquí lo único que se mueve es la geometría.',
        SheathTilt: 'iones en el borde inclinados hacia dentro',
        SheathTiltTitle: 'Cómo llegan los iones, a lo ancho de la oblea',
        SheathTiltX: 'a lo ancho de la oblea (mm) — el borde está a la derecha',
        SheathTiltY: 'inclinación respecto a la vertical (°, hacia dentro positiva)',
        SheathTiltSeries: 'Ángulo de llegada de los iones',
        SheathTiltStatus:
          '{{a}}° en el borde, {{b}}° en el centro de la oblea · el anillo dobla esta línea; la polarización no la mueve',
        SheathEdgeFlux: 'corriente borde / centro',
        SheathWear: 'desgaste del anillo',
        SheathDirected: 'deriva/difusión',
        SectionTrench: 'Radicales dentro de una zanja — por qué las líneas estrechas se retrasan (grabado dependiente de la relación de aspecto)',
        TrenchNote:
          'El mapa de oblea de arriba es un empobrecimiento — todos los sitios abiertos de la oblea ' +
          'tirando de un mismo suministro, que es el efecto de carga. Esto es el mismo ' +
          'empobrecimiento mil veces más pequeño. Cuatro zanjas bajo un mismo plasma, de 90 a ' +
          '720 nm de ancho y todas de 900 nm de profundidad, así que quedan en relaciones de ' +
          'aspecto de 10, 5, 2,5 y 1,25. Los radicales difunden desde arriba y se consumen allí ' +
          'donde encuentran silicio: los fondos, y cada pared lateral del camino. Pulse Ejecutar y ' +
          'lea los cuatro fondos en la gráfica. El fondo de la zanja 10:1 ve una fracción de lo ' +
          'que ve la de 1,25:1, bajo el mismo plasma y en el mismo instante, así que graba más ' +
          'despacio — y un diseño que lleve ambas termina antes la ancha. Eso es el grabado ' +
          'dependiente de la relación de aspecto, o retraso RIE, y es la razón de que un grabado ' +
          'se cronometre por su rasgo más estrecho y sobregrabe todo lo demás. Ahora haga que la ' +
          'superficie consuma más: caliente la oblea, o suba el bias para que los iones lleguen ' +
          'más duros. La zanja estrecha se empobrece aún más y el retraso EMPEORA. La receta ' +
          'agresiva que gana en velocidad pierde en uniformidad entre rasgos.',
        TrenchCaveat:
          'Lo que aquí está calibrado es la superficie: la energía de activación es el valor de ' +
          'flúor sobre silicio del propio panel y el refuerzo iónico sigue su ley de raíz cuadrada ' +
          'por encima del umbral, así que todas las direcciones son las del panel. El transporte ' +
          'es difusión continua haciendo las veces del flujo molecular que una zanja de 100 nm ve ' +
          'de verdad a 20 mTorr, y la constante de velocidad está elegida para dejar la zanja más ' +
          'estrecha visiblemente empobrecida en la receta cualificada. Así que el orden de los ' +
          'cuatro fondos y su respuesta a los mandos están ganados; los porcentajes no son cifras ' +
          'para llevar de vuelta a la herramienta, y el reloj tampoco — una zanja real se llena en ' +
          'microsegundos.',
        TrenchWide: 'fondo 1,25:1',
        TrenchNarrow: 'fondo 10:1',
        TrenchLag: 'retraso',
        reading: {
          houseutilities: {
            Limit: 'cabecera estanca, PCW a temperatura, gas seco < 50 ppm',
            Label: 'Revisión de servicios generales con facilities (2 h)',
          },
          seasoning: {
            Label: 'Cupón de pared / comprobación de acondicionamiento por OES (1 h)',
          },
          sealcheck: {
            Label: 'Prueba de fugas de helio en esta cámara (6 h)',
          },
          ringgauge: {
            Label: 'Galga de espesor del anillo de enfoque (30 min)',
          },
          base: {
            Label: 'Presión base',
          },
          leak: {
            Label: 'Tasa de subida de presión',
          },
          power: {
            Label: 'Potencia de fuente entregada',
          },
          pump: {
            Label: 'Horas de bomba',
          },
          rf: {
            Label: 'Horas de RF en el anillo',
          },
          flowverify: {
            Label: 'Verificación de caudal, tasa de subida (45 min)',
          },
          tempverify: {
            Label: 'Oblea instrumentada, temperatura del chuck (2 h)',
          },
        },
        aspect: {
          power: {
            Label: 'Match de RF / potencia entregada',
            Action: 'Reajustar el match',
          },
          flow: {
            Label: 'Suministro de gas (MFC)',
            Action: 'Calibrar o sustituir el MFC',
          },
          vacuum: {
            Label: 'Integridad de vacío (fuga / bomba)',
            Action: 'Encontrar la fuga, revisar la bomba',
          },
          walls: {
            Label: 'Estado de las paredes',
            Action: 'Limpieza y acondicionado de cámara',
          },
          chuck: {
            Label: 'Control de temperatura del chuck',
            Action: 'Revisar el enfriador',
          },
          ring: {
            Label: 'Anillo de enfoque',
            Action: 'Sustituir el anillo de enfoque',
          },
          house: {
            Label: 'Nada en este equipo — escalar a instalaciones',
            Action: 'Detener el trabajo en el equipo y plantearlo a instalaciones',
          },
        },
        fault: {
          none: {
            Label: 'Sin fallo — cámara sana',
            Complaint: 'El equipo está cualificado y funciona dentro de especificación.',
            Truth: 'No pasa nada.',
            Evidence: [
              'Todas las lecturas de la cámara están en sus valores nominales.',
            ],
          },
          mfcDrift: {
            Label: 'Controlador de flujo entregando de menos',
            Complaint:
              'La tasa de grabado es baja y la oblea se está volviendo no uniforme. Nadie ' +
              'cambió la receta.',
            Truth:
              'El controlador de flujo de CF4 lee su consigna pero deja pasar alrededor ' +
              'del 42% de ella. El presupuesto de flúor lo fija lo que realmente entra en ' +
              'la cámara, no lo que dice la pantalla.',
            Evidence: [
              'Una verificación de caudal (tasa de subida) no coincide con la lectura del ' +
                'MFC.',
              'La potencia RF entregada es normal, lo que descarta el match.',
              'La tasa de subida es normal, lo que descarta una fuga.',
              'Una limpieza de cámara no lo arregla.',
            ],
          },
          chamberLeak: {
            Label: 'Fuga de vacío',
            Complaint:
              'La tasa ha bajado y la selectividad se ha vuelto rara. Empezó tras el ' +
              'último PM.',
            Truth:
              'Una junta de puerta quedó pellizcada al remontar. Entra aire; el oxígeno ' +
              'captura el flúor en fase gaseosa, así que el agente de grabado nunca llega ' +
              'a la oblea aunque el caudal y la potencia sean correctos.',
            Evidence: [
              'La tasa de subida está muy por encima del límite de 1 mTorr/min — este es ' +
                'el ensayo decisivo.',
              'La presión base también está elevada.',
              'La verificación de caudal y la potencia entregada salen normales.',
            ],
          },
          matchDetuned: {
            Label: 'Red de acoplo RF desajustada',
            Complaint:
              'La tasa es baja. La alarma de potencia reflejada se borró sola, así que el ' +
              'turno lo dejó correr.',
            Truth:
              'La red de acoplo solo está pasando alrededor del 70% de la consigna al ' +
              'plasma. Menos potencia absorbida significa un plasma más tenue y menos ' +
              'iones - y, como los vatios de polarización se reparten entre menos iones, ' +
              'cada uno llega con más fuerza.',
            Evidence: [
              'La potencia entregada lee muy por debajo de la consigna — la lectura ' +
                'decisiva.',
              'La energía de los iones es MAYOR que la nominal, no menor, y ese es el ' +
                'detalle que lo separa de un problema de suministro de gas.',
              'La tasa de subida y la verificación de caudal son normales.',
            ],
          },
          pumpDegraded: {
            Label: 'Grupo de bombeo degradado',
            Complaint: 'Alarma de presión base al empezar el turno. El proceso sigue funcionando.',
            Truth:
              'El grupo de bombeo ya no alcanza la presión base. El gas residual eleva la ' +
              'presión efectiva de la cámara por encima de la consigna, lo que frena la ' +
              'difusión hacia el centro de la oblea.',
            Evidence: [
              'La presión base es varias veces su valor normal.',
              'La tasa de subida está algo elevada, pero muy lejos de una fuga real.',
              'El caudal y la potencia entregada son normales.',
            ],
          },
          heavySeasoning: {
            Label: 'Cámara con la limpieza vencida',
            Complaint:
              'La tasa lleva toda la semana bajando poco a poco. Cada lote sale algo más ' +
              'lento que el anterior.',
            Truth:
              'Se han acumulado depósitos en las paredes a lo largo de muchas obleas. ' +
              'Recombinan el flúor antes de que llegue a la oblea y a la vez desajustan ' +
              'el match — una sola causa raíz produciendo dos síntomas que parecen dos ' +
              'fallos distintos.',
            Evidence: [
              'Las obleas desde la última limpieza superan con mucho el límite de PM.',
              'La potencia entregada es algo baja Y el suministro de radicales es bajo — ' +
                'las dos cosas a la vez.',
              'La tasa de subida y la verificación de caudal son normales.',
              'Una limpieza de cámara lo restaura; un reajuste del match solo ayuda un ' +
                'rato.',
            ],
          },
          chillerFault: {
            Label: 'El enfriador del chuck no mantiene la consigna',
            Complaint:
              'Los perfiles salen socavados en los dos últimos lotes. La tasa está algo ' +
              'alta.',
            Truth:
              'El chuck está funcionando unos 85 C por encima de la consigna. La rama ' +
              'espontánea del flúor es de tipo Arrhenius con la temperatura, así que ' +
              'crece — y la química espontánea no tiene dirección, de modo que la pared ' +
              'lateral se socava.',
            Evidence: [
              'La lectura de temperatura del chuck no coincide con una medida ' +
                'independiente sobre la oblea.',
              'La tasa SUBE, no baja, lo que descarta de golpe todos los fallos del lado ' +
                'del suministro.',
              'El ángulo de pared ha caído mientras la energía de los iones no ha cambiado.',
            ],
          },
          wornFocusRing: {
            Label: 'Anillo de enfoque desgastado más allá del límite',
            Complaint:
              'El borde de la oblea despeja mucho antes que el centro. La tasa media ' +
              'parece correcta.',
            Truth:
              'El anillo de enfoque se ha erosionado y ya no moldea el campo en el borde ' +
              'de la oblea, así que el flujo de iones en el borde va alto. La tasa media ' +
              'apenas se mueve, y por eso esto se esconde de un gráfico de tasa y solo ' +
              'aparece en un mapa de uniformidad.',
            Evidence: [
              'Las horas de RF sobre el anillo superan el límite de sustitución.',
              'La tasa media de grabado está cerca de la nominal — un gráfico de control ' +
                'de tasa no lo detectaría.',
              'El mapa de uniformidad es marcadamente rápido en el borde, y el perfil es ' +
                'un anillo, no una inclinación.',
            ],
          },
          houseVacuumLeak: {
            Label: 'Fuga en el colector de vacío compartido',
            Complaint:
              'La tasa ha bajado y la selectividad se ha vuelto rara: exactamente como el ' +
              'sello de puerta pellizcado tras el último mantenimiento. Ya cambiamos ese ' +
              'sello. DEP-02 también se queja.',
            Truth:
              'Una brida del colector de vacío de proceso compartido se movió durante los ' +
              'trabajos en la subplanta el fin de semana pasado y está fugando. Cada ' +
              'cámara de ese colector lo ve como su propia fuga, porque desde dentro de ' +
              'la cámara lo es. Lo único que no es cierto es que sea la fuga de esta ' +
              'cámara, y cada hora dedicada a abrir este equipo es una hora en que el ' +
              'colector sigue fugando hacia todos los demás equipos.',
            Evidence: [
              'La tasa de subida está muy por encima del límite y la presión base también ' +
                'ha subido: la fuga clásica.',
              'Una prueba de fugas con helio en ESTA cámara sale limpia. Esa es la ' +
                'lectura que la convierte de reparación en escalado.',
              'La verificación de caudal y la potencia entregada son ambas normales.',
              'Los demás equipos del mismo colector reportan su propia versión de lo ' +
                'mismo, que es la pista que no cuesta nada y casi siempre se ignora.',
            ],
          },
          warmPcw: {
            Label: 'Agua de refrigeración llegando caliente',
            Complaint:
              'La tasa es baja, alrededor del 15%. La potencia entregada también lee ' +
              'bajo. El turno ya reajustó el match una vez y volvió igual.',
            Truth:
              'La planta de agua enfriada está entregando agua de suministro unos 22 C ' +
              'por encima de lo nominal. Nada en este equipo está roto. Llega por dos ' +
              'vías: el chuck sube unos 19 C porque el enfriador no tiene nada frío a lo ' +
              'que ceder calor y, mucho más importante, el generador de RF y el match ' +
              'están en el mismo circuito y se están derateando, así que la potencia ' +
              'entregada cae. Por eso se lee como un match desajustado y por eso ajustar ' +
              'el match no lo arregla.',
            Evidence: [
              'La potencia entregada es baja, así que parece exactamente una red de match ' +
                'desajustada.',
              'Reajustar el match no recupera casi nada. Un desajuste real sí habría ' +
                'recuperado.',
              'Todas las obleas siguen en especificación, y esa es la trampa: nada fuerza ' +
                'el tema, y el equipo sigue funcionando un 15% lento en silencio hasta que ' +
                'alguien nota la productividad.',
              'La comprobación de servicios generales muestra el agua de refrigeración ' +
                'muy por encima de su temperatura de suministro.',
            ],
          },
        },
        SectionProcessState: 'Estado del proceso',
        SectionWaferMap: 'Mapa de oblea — patrón de {{sites}} puntos',
        SectionRadialProfile: 'Perfil radial',
        OesGround: 'fundamental',
        OesElectron: 'electrón entra',
        OesPhoton: 'fotón sale',
        OesSpectrumTitle: 'Lo que ve el espectrómetro',
        OesSpectrumX: 'longitud de onda (nm)',
        OesSpectrumY: 'intensidad por nm (el área de una línea es su intensidad; Ar 750 a 3 eV, 2,1×10¹⁷ m⁻³ = 1)',
        OesSpectrumSeries: 'emisión',
        OesTraceTitle: 'Dos líneas a lo largo del proceso: el punto final',
        OesTraceX: 'tiempo de proceso (s)',
        OesTraceY: 'intensidad de línea (Ar 750 a 3 eV, 2,1×10¹⁷ m⁻³ = 1)',
        OesTraceSiF: 'SiF 440 nm (producto)',
        OesTraceF: 'F 703,7 nm',
        OesClearing: 'la película se despeja',
        OesActinometry: 'Actinometría F 703,7 / Ar 750,4: {{ratio}}, ×{{fold}} desde el inicio de la corrida',
        OesActinometryNow: 'Actinometría F 703,7 / Ar 750,4: {{ratio}}',
        OesActinometryWatching: 'Actinometría F 703,7 / Ar 750,4: {{ratio}}, ×{{fold}} desde que este panel empezó a observar, con la corrida ya en marcha',
        OesUnwatched: 'Parte de esta corrida ocurrió con este panel cerrado. Allí la línea del producto es el registro de la propia cámara; la línea del flúor se deja en blanco, porque nada leía entonces el flúor de la cámara y no se puede reconstruir.',
        OesActinometryNone: 'La actinometría necesita argón en la alimentación, y esta receta no lo tiene.',
        OesFailed: 'El modelo de emisión no se pudo cargar.',
        OesStatus: 'T_e {{te}} eV · n_e {{ne}} m⁻³ · {{line}} nm: k = {{k}} m³/s, intensidad {{rel}}',
        OesLoading: 'Cargando el modelo de emisión…',
        OesTeSource: 'Temperatura electrónica',
        OesFollow: 'Seguir la cámara',
        OesManual: 'Fijarla a mano',
        OesTe: 'T_e',
        OesPause: 'Pausar',
        OesRun: 'Ejecutar',
        OesReset: 'Reiniciar',
        SectionEmission: 'De dónde sale la señal de punto final: electrones que excitan átomos, átomos que emiten líneas (niveles y una maxwelliana)',
        EmissionNote: 'La traza de punto final de arriba es un espectrómetro mirando el resplandor, y ese resplandor es mecánica cuántica usada en cada turno. Un átomo en la cámara tiene niveles discretos. Un electrón del plasma solo puede subirlo a uno si llega con al menos la energía de ese nivel, y cuando el átomo cae emite un fotón cuya longitud de onda fijan los dos niveles: λ = hc/(E_sup − E_inf). La línea de 750 nm del argón necesita un electrón de 13,5 eV; los electrones siguen una maxwelliana de unos pocos eV, así que la luz sale de la cola, y esa cola va como exp(−13,5/T_e): por eso un cambio pequeño de T_e mueve mucho las líneas del argón y la banda del producto, con umbral de 2,9 eV, apenas se mueve. La tasa de cada línea se calcula: una sección eficaz de Bethe desde su umbral, promediada sobre la distribución. El flúor se produce por disociación y se consume mientras haya silicio que atacar; el SiF es el producto. Así que en el punto final ocurren dos cosas a la vez: la banda de SiF se apaga y la línea de flúor sube, porque ya nada consume flúor. El argón es el gas de balance y nada lo consume, así que el punto final que una fab vigila en realidad es flúor sobre argón (actinometría): una deriva del propio plasma mueve ambas líneas y se cancela. Los niveles son valores del NIST. Se omiten cascadas, extinción y la estructura vibracional de la banda, dibujada como un solo rasgo ancho.',
        SectionEndpoint: 'Traza de fin de ataque por emisión óptica',
        SectionIedf: 'Distribución de energía de iones — escala microscópica',
        IedfNote:
          'La distribución de arriba se resuelve a partir de la descarga, no está ' +
          'tabulada: el flujo de Bohm fija cuántos iones llegan, la potencia de ' +
          'polarización dividida por esa corriente fija con cuánta fuerza golpean, y ' +
          'la cola de baja energía es la fracción que intercambió carga al cruzar la ' +
          'vaina.',
        SectionRunControl: 'Control de ejecución',
        RunControlNote:
          'Un grabado real de esta película tarda unos dos minutos y medio. El ' +
          'multiplicador solo escala el tiempo de proceso — la física se resuelve en ' +
          'cada paso igualmente.',
        SectionChamber: 'Estado de la cámara — lo que la receta no controla',
        SectionRecipe: 'Receta — {{code}}',
        SectionStoredRecipes: 'Recetas almacenadas',
        StoredRecipesNote: 'Cargar una receta almacenada carga también una oblea nueva.',
        ModeThickness: 'Espesor restante',
        ModeRate: 'Tasa de grabado',
        ModeDeviation: 'Desviación respecto a la media',
        TileEtchRate: 'Tasa de grabado',
        TileNonUniformity: 'No uniformidad',
        TileSelectivity: 'Selectividad',
        TileRegime: 'Régimen',
        TileIonEnergy: 'Energía de iones',
        TileIonFlux: 'Flujo de iones',
        TileSidewall: 'Pared lateral',
        TileProcessTime: 'Tiempo de proceso',
        TileCleared: 'Despejado',
        FastEdge: 'borde rápido',
        FastCentre: 'centro rápido',
        Flat: 'plano',
        RegimeTransport: 'transporte',
        RegimeMixed: 'mixto',
        RegimeReaction: 'reacción',
        FromSheathSolve: 'del cálculo de la vaina',
        Anisotropic: 'anisótropo',
        Undercut: 'socavado',
        FilmIntact: 'película intacta',
        OxideLost: 'óxido perdido {{nm}} nm',
        UnitNmRemaining: 'nm restantes',
        CaptionDeviation: 'azul = más lento que la media, rojo = más rápido',
        CaptionNonUniformity: '{{percent}}% de no uniformidad',
        ChartProfileTitle: 'Tasa de grabado y suministro de reactivo a lo largo del radio',
        ChartProfileX: 'r / R  (0 = centro)',
        ChartProfileSeries: 'tasa de grabado',
        ChartEndpointTitle: 'Emisión del producto de grabado — cae cuando la película se despeja',
        ChartEndpointX: 'tiempo de proceso (s)',
        ChartEndpointY: 'señal',
        ChartIedfTitle: 'Distribución de energía de iones — media {{ev}} eV',
        ChartIedfX: 'energía de iones (eV)',
        ChartIedfY: 'fracción',
        ChartMean: 'media',
        PreventiveMaintenance: 'Mantenimiento preventivo',
        MaintButton: '{{label}} — {{hours}} h de parada',
        AssistantBlurb:
          'Hazme una pregunta y diseñaré un experimento, lo ejecutaré contra este ' +
          'solucionador e informaré de lo que realmente salió — y luego te devolveré ' +
          'una pregunta más afilada.',
        AssistantLead:
          'Arranca el proceso y observa el mapa de oblea, y luego pregúntame por qué ' +
          'se ve así.',
      },
      patterning: {
        NoteMeanIsStillOnTarget: 'la media es {{a}} nm y sigue en objetivo',
        NoteCentred: 'centrado',
        NoteSpacersMeet: 'Los espaciadores se encuentran: un espaciado de {{a}} nm no es un ' +
          'espaciado. La película es demasiado gruesa para este pitch, o el mandril ' +
          'se recortó de más.',
        ResultFourLines: 'Cuatro líneas donde había una — pitch {{a}} nm',
        ResultAnisotropicAgain: 'Anisotrópico otra vez',
        ResultAldOverFirstPass: '{{a}} nm de ALD sobre los espaciadores de la primera pasada, que ahora son ' +
          'los mandriles',
        ResultTwoLines: 'Dos líneas donde había una — pitch {{a}} nm',
        ResultAnisotropicClears: 'Anisotrópico: despeja las superficies horizontales, deja las paredes en pie',
        ResultAldConformalSidewall: '{{a}} nm de ALD, {{b}}% conformal → {{c}} nm en la pared lateral',
        ResultTrimBothSides: '{{a}} → {{b}} nm (ambos lados)',
        ResultNmLinesOnPitch: 'líneas de {{a}} nm a {{b}} nm de pitch',
        NoteMeanSpacingWalks: 'Espaciado medio {{a}} nm — exactamente en el objetivo — con una dispersión ' +
          'de {{b}} nm entre los cuatro. El pitch camina. Pon el trim del mandril en ' +
          '{{c}} nm para centrarlo, seis pasos aguas arriba de donde mediste el ' +
          'problema.',
        NoteAllFourSpacingsAre: 'Los cuatro espaciados son de {{a}} nm. El patrón está centrado.',
        SectionPasses: 'Pasadas',
        SectionRoute: 'Ruta',
        SecondaryLabel: 'Uniformidad de CD de línea',
        ToolLabel: 'PAT-21 Celda de patronado cuádruple autoalineado',
        NextTheRouteSurvivedOnPaper: 'La ruta sobrevivió en el papel. Ahora pregunta qué le hacen a la misma ' +
          'receta una variación de espesor ALD del 2% y una pared de mandril no ' +
          'vertical — la condición en esta herramienta lleva ambas.',
        AxisXSpacer1ThicknessNm: 'espesor del espaciador 1 (nm)',
        TitleFinalPitchAgainstFirstSpacer: 'Pitch final frente a espesor del primer espaciador',
        LabelTrimmedMandrel: 'Mandril recortado',
        HypPitchDivisionIsSubtractionEach: 'La división de pitch es una resta. Cada espaciador toma ancho del hueco ' +
          'donde se deposita, y hay un punto donde el hueco restante ya no es lo ' +
          'bastante ancho para alojar la siguiente película. Si es así, el fallo ' +
          'debería ser abrupto y no gradual — la receta funciona, funciona, funciona ' +
          'y de pronto no hay sitio.',
        AskedWhereDoesThisRouteStop: '¿Dónde deja de ser geométricamente posible esta ruta?',
        SumTheGeometricLimitsOfPitch: 'Los límites geométricos de la división de pitch, y qué se agota primero.',
        LabelFinalCd: 'CD final',
        AxisXSpacer2ThicknessNm: 'espesor del espaciador 2 (nm)',
        TitleFinalCdFollowsTheSpacer: 'El CD final sigue a la película espaciadora',
        NoteToPrintThisDirectly: 'para imprimir esto directamente',
        LabelScannerNeeded: 'Escáner necesario',
        LabelMultiplication: 'Multiplicación',
        HypIfSaqpWorksTheWay: 'Si el SAQP funciona como se supone, el escáner fija solo el pitch del ' +
          'mandril y todo lo que sigue es división. El pitch final debería entonces ' +
          'seguir al espesor del ESPACIADOR — un número fijado por una herramienta de ' +
          'deposición con control subnanométrico — y ser del todo insensible a cuán ' +
          'bien resuelve el escáner.',
        AskedWhichKnobDecidesThePitch: '¿Qué perilla decide el pitch que se envía?',
        SumADepositedFilmThicknessNot: 'Un espesor de película depositada, no el escáner — y lo que eso compra.',
        NextEveryOtherLineInThis: 'Cada línea alterna de este patrón vino de un lugar distinto. Si la mitad ' +
          'son sistemáticamente estrechas, ¿qué le hace eso a un mapa de rendimiento ' +
          'en PRB-14 — y se vería como un defecto o como un fallo paramétrico?',
        LabelMeanSpacing: 'espaciado medio',
        AxisYMeanSpacingNm: 'espaciado medio (nm)',
        TitleAndTheMeanWhichBarely: 'Y la media, que apenas se entera',
        LabelIdealTrim: 'trim ideal',
        AxisYWalkNm: 'walk (nm)',
        AxisXMandrelTrimNm: 'trim del mandril (nm)',
        TitlePitchWalkAgainstMandrelTrim: 'Pitch walk frente a trim del mandril',
        LabelIdealTrimL126: 'Trim ideal',
        NoteLooksFineEitherWay: 'se ve bien de cualquier modo',
        LabelMeanSpacingL122: 'Espaciado medio',
        AskedWhyAreTheSpacesIn: '¿Por qué los espacios del patrón final no son todos del mismo ancho?',
        SumPitchWalkTheTrimThat: 'Pitch walk, el trim que lo causa, y el trim que lo arreglaría.',
        fault: {
          slopedMandrel: {
            Evidence: [
              'Ángulo de pared del mandril 4,5° contra un límite de 1°.',
              'Uniformidad de CD final fuera de límite, paso aún centrado.',
              'Cobertura de escalón y dispersión de espesor del ALD, ambas nominales.',
            ],
            Truth: 'El grabado del mandril está dejando una pared de 4,5°. Un espaciador ' +
              'depositado sobre una pared inclinada es más grueso abajo que arriba, así ' +
              'que la línea grabada ya no es el espesor de película — la misma pérdida de ' +
              'uniformidad de CD que produce un ALD derivando, desde el grabador. Las ' +
              'lecturas del ALD son lo que los separa, y están limpias.',
            Complaint: 'La misma uniformidad de CD degradada, y el ALD se re-cualificó ayer.',
            Label: 'Pared del mandril no vertical',
          },
          aldNonUniform: {
            Evidence: [
              'Dispersión de espesor ALD al 4,2% contra un límite del 1,5%.',
              'Uniformidad de CD final fuera de límite mientras el pitch walk es cero.',
              'Cobertura de escalón y perfil del mandril, ambos nominales.',
            ],
            Truth: 'La ducha del ALD ha derivado y la dispersión de espesor en oblea es del ' +
              '4,2%. La línea final ES la película, así que la uniformidad de la película ' +
              'es la uniformidad de la línea — y como afecta a todos los espaciadores por ' +
              'igual, no desvía el paso en absoluto. Uniformidad fuera con paso centrado ' +
              'es la firma.',
            Complaint: 'La uniformidad de CD de línea se ha degradado en la oblea. El paso está ' +
              'perfectamente centrado.',
            Label: 'Espesor del espaciador variando por la oblea',
          },
          thinSidewallFilm: {
            Evidence: [
              'Cobertura de escalón al 86% contra un suelo del 97%.',
              'El CD-SEM muestra los espaciados desviándose.',
              'El mapa de espesor ALD y el control de CD del escáner son ambos nominales.',
            ],
            Truth: 'El ALD está al 86% de cobertura de escalón, así que la película en la ' +
              'PARED — que es la única parte que se convierte en línea — es más delgada ' +
              'que la que mide el monitor de espesor sobre el campo. El paso lo fija ese ' +
              'espesor de pared, así que se desvía, y la deposición se ve perfectamente ' +
              'en especificación en toda superficie plana que alguien haya medido. ' +
              'Litografía hace bien en negarse.',
            Complaint: 'El paso ha empezado a desviarse. Se ha pedido a litografía que apriete el ' +
              'control de CD del mandril y se niega alegando que el suyo es el mejor de ' +
              'la fábrica.',
            Label: 'Espaciador más delgado en la pared que arriba',
          },
          patHealthy: {
            Evidence: [
              'Cada lectura del banco es nominal.',
              'El paso está centrado y la dispersión de CD es pequeña.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lote.',
            Complaint: 'Lote de rutina. Paso final y CD ambos en objetivo.',
            Label: 'Nada mal',
          },
        },
        NoteNmHalfPitchDuvLimit: '{{a}} nm de semipaso, límite DUV {{b}} nm',
        NoteSetToNmThisIs: 'fijado a {{a}} nm — este es el mando que centra el patrón',
        NoteTheScannerAloneWouldGive: 'el escáner solo daría {{a}} nm',
        NoteNmPrintedDivided: '{{a}} nm impresos, divididos {{b}}×',
        SectionThePitchStopsBeingThe: 'El paso deja de ser asunto del escáner',
        NoteA193NmScannerCannot: 'Un escáner de 193 nm no puede imprimir por debajo de unos 36 nm de ' +
          'semipaso y no ha podido desde 2007. Todo lo más fino se hace imprimiendo ' +
          'algo grueso y dividiéndolo: deposita una película conforme sobre una línea ' +
          'impresa, grábala anisotrópicamente para que sobreviva solo en las paredes, ' +
          'y saca la línea de en medio. Dos líneas donde había una. Hazlo dos veces ' +
          'para cuatro. El CD final es un espesor de película — que DEP-02 controla ' +
          'al uno por ciento, donde LITH-03 maneja varios.',
        TextPatternCollapsed: 'Patrón colapsado',
        SectionCellState: 'Estado de la celda',
        SectionThePatternThatComesOut: 'El patrón que sale',
        TextRunTheCellOnThis: 'Pasar la celda sobre este lote',
        LabelSaqpTwoPasses: 'SAQP — dos pasadas',
        LabelSadpOnePass: 'SADP — una pasada',
        LabelFinalPitch: 'Paso final',
        LabelPitchWalk: 'Pitch walk',
        LabelFinalLineCd: 'CD de línea final',
        NoteThisIsAFilmThickness: 'esto es un espesor de película, no una estructura impresa',
        LabelCdUniformity: 'Uniformidad de CD',
        LabelIdealMandrelTrim: 'Recorte ideal del mandril',
        LabelAgainstAScannerAlone: 'Contra un escáner solo',
        AssistantLead: 'Asistente de indagación PAT-21',
        AssistantBlurb:
          'Pregunta por qué los espacios se alternan, qué fija realmente el paso ' +
          'final, o dónde se queda sin sitio la ruta. Cada respuesta es la ruta de ' +
          'ocho pasos ejecutada con esta receta.',
        reading: {
          cdUniformity: {
            Label: 'Uniformidad de CD de línea final, 49 sitios (2.0 h)',
          },
          pitchWalk: {
            Limit: 'dispersión < 1 nm',
            Label: 'CD-SEM de cada espacio en un periodo, 200 sitios (3.0 h)',
          },
          scannerCd: {
            Label: 'Control de CD del mandril en el escáner',
          },
          sidewallAngle: {
            Label: 'Ángulo del mandril respecto a la vertical',
          },
          aldMap: {
            Label: 'Dispersión de espesor ALD en oblea',
          },
          conformalityCheck: {
            Label: 'Cobertura de escalón ALD en una estructura monitor',
          },
        },
        aspect: {
          scanner: {
            Action: 'Nada que reparar aquí — el mandril se consume y su CD no llega al producto',
            Label: 'Control de CD del escáner en LITH-03',
          },
          sidewall: {
            Action: 'Reajustar el grabado del mandril para un perfil vertical',
            Label: 'Ángulo de pared del mandril en ETCH-01',
          },
          aldUniformity: {
            Action: 'Reajustar la ducha del ALD y re-mapear el espesor sobre la oblea',
            Label: 'Uniformidad de espesor del espaciador en DEP-02',
          },
          conformality: {
            Action:
              'Re-cualificar la ventana de saturación ALD — la pared es más delgada que ' +
              'el techo',
            Label: 'Conformalidad del espaciador en DEP-02',
          },
        },
      },
      cmp: {
        NextHoldingIsTheEasyHalf: 'Retener es la mitad fácil. Las obleas ya pulidas en este turno salieron ' +
          'contra la misma hoja: ¿qué tendrías que medir para decidir si alguna debe ' +
          'volver?',
        NextTheSheetPassedToday: 'La hoja pasó hoy. ¿Qué línea de ella está más cerca de su límite, y ' +
          'cuántos lotes crees que le quedan antes de que sea justo esa la que ' +
          'retenga el equipo?',
        BridgingClear: 'limpio',
        BridgingShorted: 'CORTOCIRCUITO',
        LabelBridging: 'Metal puenteado',
        LabelPlanarity: 'Planaridad',
        VerdictWithAWatchItem: 'liberado, con un punto en vigilancia',
        VerdictHeld: 'Retenido',
        VerdictQualified: 'Calificado',
        LabelVerdict: 'Decisión',
        HypAQualificationIsADecision: 'Una calificación es una decisión, no una explicación. Si la oblea monitor ' +
          'sale lo bastante plana para la capa siguiente, dentro del tiempo de ciclo ' +
          'que la línea puede absorber, sin metal puenteado, y cada lectura del banco ' +
          'está dentro de su propio límite, el equipo se libera. Si no, queda ' +
          'retenido, y la receta calificada sobre el mismo hardware debería decir si ' +
          'el responsable es la receta o el equipo.',
        AskedIsThisPolisherFit: '¿Está esta pulidora en condiciones de procesar producto ahora mismo, y qué ' +
          'hago al respecto?',
        SumPolishTheMonitorWafer: 'Pule la oblea monitor, contrasta el banco con sus límites y toma la ' +
          'decisión.',
        UnitNmAcrossDie: 'nm a través del die',
        NoteTheRegionsAboveAre: 'Las regiones de arriba son una LISTA: cada una se despeja por su propia ' +
          'fórmula, se ahueca por su propia ley, y ninguna sabe que sus vecinas ' +
          'existen. Abajo, el mismo die es un MAPA, avanzado en el tiempo — el pad es ' +
          'un kernel de escala milimétrica que presiona más fuerte donde la ' +
          'superficie sobresale, así que la planarización es una retroalimentación y ' +
          'no un coeficiente, y JUNTO A QUÉ se sienta un bloque de trazado cambia ' +
          'cómo se pule. La línea de corte de abajo cruza el medio del die: pad de ' +
          'soldadura, array denso, línea aislada, campo entre medio.',
        NoteTheTwoModelsDisagree: 'Los dos modelos discrepan aquí en el reloj, lo que suele significar que la ' +
          'receta salió del régimen en el que se ajustó la lista de regiones — un ' +
          'escalón entrante pesado o un sobre-pulido extremo. La malla es la que ' +
          'sigue haciendo física: la presión sigue a la superficie que realmente está ' +
          'presionando.',
        NoteOnThisRecipeTheTwo: 'Con esta receta los dos coinciden en el reloj y en el orden de los fallos: ' +
          'la malla contiene las propias leyes de la lista de regiones, así que donde ' +
          'las regiones son realmente independientes las reproduce. Lo que la lista ' +
          'nunca puede contener es la LONGITUD del pad: un pad rígido planariza y uno ' +
          'blando pule conformalmente, la erosión depende del vecindario, y el ' +
          'sobre-pulido des-aplana el die — la altura de escalón cae a su mínimo en ' +
          'el punto final y la erosión la recompra después.',
        ValueNoSuchTerm: 'no existe tal término',
        ValueInTheKernel: 'en el kernel: el trazado se pule según su compañía',
        MetricNeighbourhood: 'Vecindario',
        ValuePerRegionTimes: 'tiempos por región — sin campo, sin mapa',
        ValueSOfSequence: '{{a}} s de secuencia, lo último el campo abierto',
        MetricClearing: 'Despeje',
        MetricDieSurfaceRange: 'Rango de superficie del die',
        MetricWorstErosion: 'Peor erosión',
        MetricWorstDishing: 'Peor ahuecamiento',
        MetricEndpoint: 'Punto final',
        LegendTheRegionList: 'la lista de regiones',
        LegendTheDieStepped: 'el die, avanzado en el tiempo ({{a}}×{{a}} celdas)',
        ValueClears: 'despeje',
        LabelRegion: 'Región',
        LabelRemoval: 'remoción',
        LabelRemovalNonUniformity: 'No uniformidad de remoción',
        LabelPolishTime: 'Tiempo de pulido',
        SecondaryLabel: 'Tiempo de pulido',
        ToolLabel: 'CMP-11 · Pulidora de damasquinado de cobre',
        NextIfTheLayoutBuildsThe: 'Si el trazado incorpora el fallo, el arreglo no está en esta herramienta. ' +
          '¿Qué cambiarías en un diseño para que se pula plano, y quién tiene que ' +
          'aprobarlo?',
        AxisYDishingNm: 'ahuecamiento (nm)',
        AxisXLineWidthUm: 'anchura de línea (um)',
        TitleDishingAgainstFeatureWidthAt: 'Ahuecamiento frente a anchura del rasgo, a densidad de patrón fija',
        NoteSamePolishSameWafer: 'mismo pulido, misma oblea',
        LabelFineArrayDishes: 'El array fino se ahueca',
        LabelWidestFeatureDishes: 'El rasgo más ancho se ahueca',
        LabelClearsLast: 'Se despeja al final',
        LabelClearsFirst: 'Se despeja primero',
        HypTwoSeparateLayoutPropertiesAre: 'Lo causan dos propiedades del trazado distintas, y es fácil confundirlas. ' +
          'Cuánto de una región está EN RELIEVE decide cuándo se despeja, porque solo ' +
          'la parte elevada toca el pad y carga con toda la fuerza. Cuán ANCHO es un ' +
          'rasgo decide si se ahueca, porque el pad tiene que doblarse dentro de un ' +
          'hueco para alcanzar el metal del fondo y solo puede doblarse hasta cierto ' +
          'punto. La densidad fija el momento; la anchura fija el daño.',
        AskedYouWantToKnowWhy: 'Quieres saber por qué algunos rasgos del die salen bien y otros quedan ' +
          'arruinados por el mismo pulido.',
        SumShowThatTheLayoutNot: 'Demuestra que es el trazado, no la pulidora, quien decide qué rasgos fallan.',
        NextIfMatchedSpindlesMakeThe: 'Si los husillos igualados hacen la velocidad uniforme, entonces cualquier ' +
          'firma radial que quede en esta herramienta es un problema de presión. Dos ' +
          'cosas en el portador pueden causar una, y empujan en direcciones opuestas. ' +
          '¿Cuál es cuál?',
        LabelMatchedToPlaten: 'igualado a la platina',
        AxisYRemovalNonUniformity: 'no uniformidad de remoción (%)',
        AxisXCarrierSpeedRpm: 'velocidad del portador (rpm)',
        TitleRemovalUniformityAgainstCarrierSpeed: 'Uniformidad de remoción frente a velocidad del portador, platina fija',
        LabelEdgeSpeedThen: 'Velocidad de borde entonces',
        LabelCarrierAtHalf: 'Portador a la mitad',
        LabelNonUniformityMatched: 'No uniformidad, igualados',
        NoteIdenticalAtEveryRadius: 'idéntico en cada radio',
        LabelMatchedCentreToEdge: 'Igualados, centro a borde',
        HypAPointOnTheWafer: 'Un punto de la oblea se mueve respecto al pad a w_p x d + (w_p - w_c) x r, ' +
          'donde d es el desplazamiento entre los dos centros y r es dónde está el ' +
          'punto sobre la oblea. Cuando las dos velocidades son iguales el segundo ' +
          'término es exactamente cero, así que cada punto ve la misma velocidad sin ' +
          'importar dónde esté. Si eso es correcto, igualar los husillos debería dar ' +
          'una remoción perfectamente uniforme y cualquier desajuste debería imprimir ' +
          'una firma radial directamente sobre la oblea.',
        AskedYouWantToKnowWhyL193: 'Quieres saber por qué ambos husillos giran a la misma velocidad, y qué le ' +
          'pasa a la oblea cuando no lo hacen.',
        SumShowThatMatchingTheTwo: 'Demuestra que igualar los dos husillos hace la velocidad del pad uniforme, ' +
          'exactamente.',
        NextOverPolishBuysInsuranceAgainst: 'El sobre-pulido compra un seguro contra el cortocircuito. ¿Qué decide ' +
          'cuánto seguro necesitas, y qué perilla de esta herramienta cambia eso, en ' +
          'lugar de cambiar cuánto tiempo pules?',
        LabelYourRecipe: 'tu receta',
        LabelDishingWidestFeature: 'ahuecamiento, rasgo más ancho',
        LabelMetalStillBridging: 'metal aún haciendo puente',
        AxisXOverPolish: 'sobre-pulido (%)',
        NoteOfTheScannerBudget: 'del presupuesto del escáner',
        LabelCdCostAt20: 'Coste de CD al 20%',
        NoteSameRecipeMoreTime: 'misma receta, más tiempo',
        LabelDishingAt120: 'Ahuecamiento al 120%',
        NoteWidestFeature: 'el rasgo más ancho',
        LabelDishingAt20: 'Ahuecamiento al 20%',
        NoteMetalStillBridging: 'metal aún haciendo puente',
        LabelShortsBelow: 'Cortocircuitos por debajo de',
        HypTheseTwoFailuresPullIn: 'Estos dos fallos tiran en direcciones opuestas y no existe un ajuste que ' +
          'evite ambos, porque el detector de punto final se dispara con un cambio de ' +
          'fricción global y no puede ver el último porcentaje de área que aún lleva ' +
          'metal. El sobre-pulido es lo que paga ese punto ciego, y cada segundo de ' +
          'sobre-pulido se gasta también en ahuecar lo que se despejó primero.',
        SumFindTheTradeBetweenMetal: 'Encuentra el compromiso entre metal que sigue haciendo puente y metal ' +
          'desbastado de más.',
        fault: {
          zoneDrift: {
            Evidence: [
              'La remoción es centro-rápida — el signo opuesto a un anillo gastado, y lo ' +
                'único que separa a los dos.',
              'Ambas especificaciones siguen pasando. Nada se desecha y nada forzará una ' +
                'decisión.',
              'Una oblea de mapeo de presión discrepa de lo que reportan los reguladores ' +
                'de zona.',
              'El anillo mide bien, lo que descarta el otro fallo de presión.',
            ],
            Truth: 'Un regulador de la zona central del portador ha derivado y entrega un 40% ' +
              'más de presión de la que reporta. El centro de cada oblea pule rápido, ' +
              'aclara pronto y luego se sobre-pule mientras el borde termina. Planaridad ' +
              'y tiempo de ciclo siguen pasando, que es exactamente la trampa: la máquina ' +
              'está 16% no uniforme y cada oblea sale, así que nada fuerza el asunto ' +
              'hasta que la deriva llega lo bastante lejos para empezar a desechar.',
            Complaint: 'El mapa de oblea se ha vuelto centro-rápido. Todo sigue pasando, así que ' +
              'el turno quiere seguir corriéndola.',
            Label: 'Zona central del portador leyendo alto',
          },
          wornRing: {
            Evidence: [
              'La remoción es rápida en el borde — la lectura borde-menos-centro es ' +
                'fuertemente positiva.',
              'La planaridad está fuera de especificación, y lo está peor en el borde.',
              'Una zona de portador derivada da la firma radial OPUESTA. La dirección es ' +
                'todo el diagnóstico y un mapa de oblea lo muestra de un vistazo.',
              'El espesor del anillo mide por debajo de su límite de reemplazo.',
            ],
            Truth: 'El anillo de retención es sacrificial y este ha pasado su límite. Ya no ' +
              'sobresale lo bastante para mantener la almohadilla abajo por delante de la ' +
              'oblea, así que la almohadilla rebota al cruzar el borde y los últimos ' +
              'milímetros ven más presión de la que pedía la receta. El borde aclara ' +
              'pronto y luego se sobre-pule el resto de la corrida mientras el centro ' +
              'alcanza, y por eso es a la vez no uniforme y ahuecado, no solo no ' +
              'uniforme.',
            Complaint: 'Los sitios de borde están sobre-pulidos y el ahuecado en el borde está ' +
              'fuera de especificación. El centro de la oblea está bien.',
            Label: 'Anillo de retención gastado',
          },
          agedSlurry: {
            Evidence: [
              'La planaridad está muy fuera de especificación mientras tasa y uniformidad ' +
                'están cerca de lo nominal.',
              'El daño es UNIFORME por la oblea, lo que descarta ambos fallos de presión ' +
                'a la vez — nada de un portador o un anillo produce un defecto sin firma ' +
                'radial.',
              'Una valoración del slurry muestra el inhibidor muy por debajo de su límite.',
              'La perfilometría en un monitor muestra ahuecado en las estructuras anchas ' +
                'y erosión en los arrays.',
            ],
            Truth: 'El bidón lleva demasiado en la máquina y el inhibidor de corrosión se ha ' +
              'descompuesto. El abrasivo apenas se ve afectado, y por eso la tasa de ' +
              'remoción casi no se mueve — pero el inhibidor hacía dos trabajos. Pasivaba ' +
              'el dieléctrico, de donde venía la selectividad del proceso, y protegía el ' +
              'cobre rebajado entre contactos. Sin él, cada línea ancha se ahueca y cada ' +
              'array denso se erosiona, uniformemente por toda la oblea.',
            Complaint: 'El ahuecado en los pads de conexión se ha triplicado. La tasa parece ' +
              'normal, la uniformidad parece normal.',
            Label: 'Slurry pasado de vida útil',
          },
          glazedPad: {
            Evidence: [
              'El tiempo de ciclo está lejísimos mientras planaridad y uniformidad son ' +
                'exactamente nominales. Nada de la oblea ha cambiado.',
              'La tasa de remoción ha caído más de la mitad.',
              'Una comprobación de corte muestra que el acondicionador no está quitando ' +
                'material de la almohadilla.',
              'Este es el fallo que un sistema de calidad no puede ver. Solo el registro ' +
                'de la máquina lo atrapa.',
            ],
            Truth: 'El acondicionador de diamante ha dejado de cortar, así que la superficie ' +
              'de la almohadilla se ha cerrado y ya no lleva slurry al contacto. La ' +
              'remoción cae para la misma fuerza y velocidad. Lo que lo hace tan fácil de ' +
              'ignorar es que la máquina va controlada por endpoint: simplemente pule más ' +
              'tiempo y produce una oblea idéntica. El defecto no está en la oblea en ' +
              'absoluto, está en la capacidad de la línea, y seguirá invisible hasta que ' +
              'alguien mire una gráfica de producción en vez de una de calidad.',
            Complaint: 'Los tiempos de pulido llevan toda la semana subiendo — casi tres minutos ' +
              'en una receta que corría en noventa segundos. Cada oblea sigue midiendo ' +
              'perfecta. Nadie ve un problema.',
            Label: 'Almohadilla vitrificada, acondicionador sin cortar',
          },
          healthy: {
            Evidence: [
              'Planaridad, uniformidad y tiempo de ciclo están todos en sus valores ' +
                'cualificados.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lote.',
            Complaint: 'La almohadilla se cambió el lunes y la máquina pasó su cualificación. Nada ' +
              'reportado.',
            Label: 'Pulidora cualificada',
          },
        },
        TextArrivedWithNmOfSurface: 'Llegó con {{a}} nm de variación de superficie.',
        NoteNmTotal: '{{a}} nm en total',
        TextNm: '{{a}} / {{b}} nm',
        region: { pad: 'Pad de soldadura, 80 um', array: 'Matriz densa, 0.5 um', iso: 'Línea aislada, 2 um' },
        TextMetal: '{{a}} — {{b}}% de metal',
        NoteRecessedMetalAtOfBlanket: 'metal rebajado al {{a}}% del blanket',
        NoteNm: '< {{a}} nm',
        NoteEndpointAtS: 'endpoint a {{a}} s',
        NoteMSPadSpeed: '{{a}} m/s de velocidad de pad',
        SectionRemovalAcrossTheWafer: 'Remoción a través de la oblea',
        NoteTurnThePlatenAndThe: 'Gira el plato y el portador a la misma velocidad y cada punto de la oblea ' +
          've la misma velocidad de pad — exactamente, no aproximadamente. Sepáralos ' +
          'y aparece una firma radial que ningún ajuste de presión quitará.',
        SectionTheDieRegionByRegion: 'El die, región por región',
        NoteEndpointFiresWhenTheBulk: 'El endpoint dispara cuando la mayor parte de la oblea transiciona, no ' +
          'cuando termina la última región. Lo que aclara antes espera — y se ' +
          'sobre-pule mientras espera.',
        SectionWhatTheNextLayerInherits: 'Lo que hereda la capa siguiente',
        NoteAScannerHasOneFocal: 'Un escáner tiene un solo plano focal y esta superficie tiene un rango. Lo ' +
          'que quede aquí se gasta del presupuesto de foco de litografía dos pasos ' +
          'más tarde, en un escáner que funciona perfectamente.',
        SectionSolvedBesideTheFormulaIt: 'Resuelto, junto a la fórmula que sustituye',
        TextDishingErosion: 'ahuecado / erosión',
        AxisXPositionOnTheDieMm: 'posición en el die (mm)',
        AxisYNmAboveTheLowestPoint: 'nm sobre el punto más bajo',
        LabelRemovalRate: 'Tasa de remoción',
        LabelPlanarityAcrossDie: 'Planaridad en el die',
        LabelMetalBridging: 'Puentes de metal',
        LabelSlurrySelectivity: 'Selectividad del slurry',
        AxisXNormalisedRadius: 'radio normalizado',
        AxisYMetalRemovedNm: 'metal removido (nm)',
        LabelSurfaceHandedOn: 'Superficie que entrega',
        NoteAcrossTheDie: 'a través del die',
        LabelCdSpreadItCauses: 'Dispersión de CD que causa',
        NoteAtLith03FromDefocus: 'en LITH-03, solo por desenfoque',
        LabelOfTheCdBudget: 'Del presupuesto de CD',
        NoteNoLotOnThePlaten:
          'No hay lote en el plato. Envía aquí una oblea desde una máquina aguas ' +
          'arriba para pulirla.',
        NoteThisLotIsStillAn:
          'Este lote sigue siendo un lingote. WPR-08 tiene que cortarlo antes de ' +
          'poder pulirlo.',
        TextPolishThisWaferAndPass: 'Pulir esta oblea y pasarla',
        reading: {
          profilometry: {
            Label: 'Perfilometría en oblea monitor (4 h)',
          },
          zonemap: {
            Label: 'Oblea de mapeo de presión, todas las zonas (3 h)',
          },
          ringgauge: {
            Label: 'Galga de espesor del anillo (1 h)',
          },
          inhibitor: {
            Label: 'Valoración del slurry, ensayo de inhibidor (1 h)',
          },
          padcut: {
            Label: 'Comprobación de corte del acondicionador (2 h)',
          },
          short: {
            Limit: 'limpio',
            Label: 'Puentes de metal tras el pulido',
          },
          edge: {
            Label: 'Remoción borde menos centro',
          },
          nu: {
            Label: 'No uniformidad de remoción',
          },
          rate: {
            Label: 'Tasa de remoción en blanco',
          },
          cycletime: {
            Label: 'Tiempo de pulido hasta endpoint',
          },
        },
        aspect: {
          carrier: {
            Action: 'Recalibrar los reguladores de zona con una oblea de mapeo de presión',
            Label: 'Presiones de zona del portador',
          },
          slurry: {
            Action: 'Vaciar el circuito, poner bidón nuevo, enjuagar y re-certificar',
            Label: 'Suministro de slurry',
          },
          ring: {
            Action: 'Cambiar el anillo de retención y re-cero del portador',
            Label: 'Anillo de retención',
          },
          pad: {
            Action: 'Rodar una almohadilla nueva y re-cualificar el barrido del acondicionador',
            Label: 'Almohadilla de pulido',
          },
        },
        AssistantBlurb:
          'Pregunta sobre el sobrepulido, las velocidades de husillo, o por qué una ' +
          'estructura de un chip falla mientras su vecina sobrevive al mismo pulido. ' +
          'Cada respuesta es un experimento ejecutado contra esta pulidora en el ' +
          'estado en que realmente se encuentra.',
        AssistantLead: 'Asistente de indagación de CMP-11',
      },
      plating: {
        TransportSurface: 'Cu(II) en la oblea',
        TransportOfLimit: 'del límite',
        TransportBurning: 'superficie agotada — se quema',
        SectionTransport: 'De dónde viene la corriente límite (transporte de materia)',
        TransportNote: 'La corriente en la oblea consume cobre del baño, y el baño lo repone por difusión a través ' +
          'de una capa en la superficie. El color es cuánto Cu(II) hay realmente. Suba la corriente y ' +
          'la superficie se vacía; súbala lo suficiente y llega a cero, que es lo que significa ' +
          '"depósito quemado" — cobre llegando tan rápido como permite la difusión y no más, ' +
          'depositándose como polvo en vez de película. Lo agudo es lo que pasa al cambiar la fuerza ' +
          'del baño: la misma corriente deja siempre el mismo hueco bajo la superficie, empiece el baño ' +
          'donde empiece, así que la superficie se vacía a una corriente exactamente proporcional al ' +
          'cobre que contiene. Esa es la recta que el panel de arriba usa como tope, con su pendiente ' +
          'explicada en vez de elegida. Un límite honesto: esto resuelve un campo óhmico, así que aquí ' +
          'la corriente sigue subiendo pasado el límite en vez de aplanarse como haría una celda real. ' +
          'Lea la superficie, no la corriente.',
        NoteBothModelsAgreeOn: 'Ambos modelos coinciden en este rasgo, y eso vale tanto como los ' +
          'desacuerdos — un solver que nunca reprodujera la fórmula donde la fórmula ' +
          'funciona simplemente estaría mal. Lleva la relación de aspecto más allá de ' +
          'seis, o deja sin acelerador el baño, y míralos separarse.',
        NoteTheTwoModelsReturn: 'Los dos modelos devuelven VEREDICTOS distintos sobre este rasgo, no solo ' +
          'números distintos. El criterio de carrera mapea un cociente a una fracción ' +
          'de vacío mediante una recta ajustada; la malla o tiene celdas sobrantes al ' +
          'final o no las tiene. Solo uno de los dos puede contrastarse con una ' +
          'sección transversal, y ese es el punto.',
        NoteAboveValidatedDomain: 'Esta concentración de acelerador está por encima de {{a}}× el baño ' +
          'calificado, que es donde este solver deja de ser confiable. Su balance de ' +
          'cobertura admite un desboque allá arriba que no ha sido contrastado con ' +
          'mediciones, así que lee la imagen como ilustración y no como resultado.',
        ValueAtTheBottomByDefinition: '{{a}}, en el fondo por definición',
        ValueAtOfTheDepth: '{{a}} al {{b}} % de la profundidad',
        MetricPeakAcceleratorCoverage: 'Cobertura pico del acelerador',
        ValueXAssumed: '{{a}}× (supuesto)',
        ValueXMeasured: '{{a}}× (medido)',
        MetricBottomUpGain: 'Ganancia de abajo-arriba',
        ValueATimeNotAnEvent: 'un tiempo, no un evento',
        ValueNever: 'nunca',
        ValueSteps: '{{a}} pasos',
        MetricMouthClosesAt: 'La boca se cierra en',
        ValueNoSuchQuantity: 'no existe tal cantidad',
        ValueNone: 'ninguno',
        ValueNmWideNmDown: '{{a}} nm de ancho, a {{b}} nm de profundidad',
        MetricVoidAsAPlace: 'Vacío, como lugar',
        MetricVoidAsAFraction: 'Vacío, como fracción',
        ValueFilled: 'lleno',
        MetricVerdict: 'Veredicto',
        NoteTheCellAboveDecides: 'La celda de arriba decide el rasgo haciendo competir dos números: cuánto ' +
          'tarda el fondo en llegar arriba contra cuánto tardan las paredes en ' +
          'encontrarse. Abajo, el frente de cobre se AVANZA sobre una malla. Nada en ' +
          'ella decide si la zanja se vacía — las paredes o se encuentran antes de ' +
          'que llegue el piso o no, y lo que sobra es el vacío. Así que hay una ' +
          'sección transversal que mirar, cosa que una carrera entre dos números ' +
          'nunca puede producir.',
        LabelNonUniformity: 'No uniformidad',
        ValueVoid: 'vacío',
        ValueSeam: 'costura',
        ValueClean: 'limpio',
        LabelFill: 'Llenado',
        LabelVoidLegend: 'vacío',
        LabelDielectric: 'dieléctrico',
        SecondaryLabel: 'Ganancia de abajo-arriba',
        ToolLabel: 'ECD-12 Celda de electrodepósito de cobre',
        NextEveryNanometreOfOverburdenHere: 'Cada nanómetro de sobrecarga aquí es tiempo de pulido en CMP-11, y el ' +
          'tiempo de pulido es ahuecamiento. ¿Cuál es el depósito más delgado que aún ' +
          'llena la zanja por completo?',
        AxisXCurrentDensityMaCm: 'densidad de corriente (mA/cm²)',
        TitleFieldThicknessAgainstCurrentDensity: 'Espesor de campo frente a densidad de corriente',
        LabelFieldThickness: 'Espesor de campo',
        AskedWhatSetsPlatedThicknessAnd: '¿Qué fija el espesor electrodepositado, y qué cuesta depositar más rápido?',
        NextTryTheSameSweepAgainst: 'Prueba el mismo barrido contra la calidad de llenado. ¿Dónde está el ' +
          'espesor de semilla lo bastante grueso para depositar plano y lo bastante ' +
          'delgado para aún llenar la zanja — y existe uno aquí?',
        AxisYEdgeCentre: 'borde − centro (%)',
        AxisXSeedThicknessNm: 'espesor de semilla (nm)',
        TitleTerminalEffectAgainstSeedThickness: 'Efecto terminal frente a espesor de semilla',
        NoteOverburden: 'Sobrecarga',
        NoteEdgeOverCentre: 'borde sobre centro',
        LabelSeedSheetR: 'R de hoja de semilla',
        HypCurrentEntersTheWaferThrough: 'La corriente entra a la oblea por contactos en su borde y tiene que viajar ' +
          'por la capa semilla para llegar al centro. Esa semilla es metal delgado, ' +
          'así que es una resistencia, y el centro queda a menor potencial que el ' +
          'borde. Si ese es el mecanismo, una semilla MÁS GRUESA debería aplanar el ' +
          'perfil — y debería hacerlo sin tocar el baño en absoluto.',
        AskedWhyDoesPlatedThicknessLean: '¿Por qué el espesor electrodepositado se inclina del borde al centro?',
        NextTheAdditivesBreakDownAs: 'Los aditivos se degradan mientras trabajan, así que un baño que llenó esta ' +
          'mañana puede no llenar esta tarde. ¿Cómo se vería ese fallo en un escaneo ' +
          'de inspección en INSP-13 — y se vería como algo en absoluto?',
        AxisYFractionOfTrench: 'fracción de la zanja',
        TitleVoidFraction: 'Fracción de vacío',
        LabelConformalNoAdvantage: 'conformal — sin ventaja',
        AxisXAcceleratorQualifiedBath: 'acelerador (× baño calificado)',
        TitleBottomUpGainAgainstAccelerator: 'Ganancia de abajo-arriba frente a acelerador',
        LabelAspectRatio: 'Relación de aspecto',
        HypIfPlatingWereUniformA: 'Si el electrodepósito fuera uniforme, una zanja se recubriría al mismo ' +
          'ritmo en todas partes, la boca se cerraría antes de que el fondo llenara, ' +
          'y cada rasgo contendría un vacío. Que normalmente no lo haga significa que ' +
          'el crecimiento en el FONDO debe ser más rápido que en la boca. Si el ' +
          'acelerador es lo que lo logra, quitarlo debería reintroducir el vacío que ' +
          'el proceso existe para evitar.',
        AskedWhyDoesTheTrenchEnd: '¿Por qué la zanja acaba con un vacío en vez de cobre sólido?',
        SumBottomUpSuperfillAndWhat: 'Superfill de abajo hacia arriba, y qué pasa cuando el balance de aditivos ' +
          'está mal.',
        fault: {
          starvedBath: {
            Evidence: [
              'Cobre en el baño 14 g/L frente a un mínimo de 34 g/L.',
              'Espesor bajo con la falta de uniformidad del depósito quemado, en todas las obleas.',
              'Potencial de ánodo, resistencia de contacto, edad y temperatura del baño nominales.',
              'El panel de servicios de instalaciones muestra la línea de química de ECD-12 con ' +
                'fuga y su suministro enclavado cerrado.',
            ],
            Truth:
              'La línea de química de electrodeposición del subfab ha fugado dentro de su camisa de ' +
              'contención, el sensor de la bandeja bajo el armario disparó y el enclavamiento de ' +
              'fuga paró la bomba de suministro, que es exactamente para lo que está. Aquí arriba ' +
              'eso significa que el baño no se ha repuesto en todo el turno: cada oblea le saca ' +
              'cobre y está en un tercio del cobre de la receta. La corriente límite bajó con él, ' +
              'así que los mismos 20 mA/cm² superan lo que el baño puede alimentar y el depósito se ' +
              'quema: fino y rugoso en el borde, donde la corriente se concentra. Nada en esta celda ' +
              'rellena un baño cuyo suministro está enclavado dos plantas más abajo.',
            Complaint:
              'Cobre fino en toda la oblea y el depósito se ha vuelto mate y pulverulento en el ' +
              'borde. Ánodo, anillo de contacto y temperatura del baño están bien; el baño se ' +
              'dosificó según el programa.',
            Label: 'Baño sin cobre: fuga en la línea de suministro',
          },
          dirtyContact: {
            Evidence: [
              'Resistencia de contacto a 70 mΩ contra un límite de 15 mΩ.',
              'Mapa de espesor bajo y fuera de uniformidad a la vez.',
              'El potencial de ánodo es nominal.',
            ],
            Truth: 'El anillo de contacto está sucio. Mata de hambre a la oblea de corriente ' +
              'como lo hace un ánodo pasivado, y añade algo que el ánodo nunca podría: la ' +
              'pérdida no es igual en cada dedo, así que la oblea se deposita desigual ' +
              'además de delgada. Delgada más no uniforme es la firma.',
            Complaint: 'Cobre delgado Y el mapa se ha vuelto pesado hacia el borde en un sector.',
            Label: 'Resistencia del anillo de contacto alta',
          },
          passivatedAnode: {
            Evidence: [
              'Potencial de ánodo 1,4 V sobre nominal contra un límite de 0,4 V.',
              'Mapa de espesor bajo.',
              'La resistencia del anillo de contacto es nominal.',
            ],
            Truth: 'Ha crecido una película sobre el ánodo y cuesta potencial de mando, así ' +
              'que la oblea ve menos corriente de la que la fuente cree entregar. Menos ' +
              'corriente es menos cobre, exactamente como dice Faraday. El anillo de ' +
              'contacto está limpio, que es lo que separa esto de la otra manera de matar ' +
              'de hambre a una oblea.',
            Complaint: 'El cobre sale delgado y CMP está sobre-puliendo para compensar.',
            Label: 'Ánodo pasivado',
          },
          exhaustedAdditives: {
            Evidence: [
              'Edad del baño a 32 Ah/L contra un límite de 12 Ah/L.',
              'El corte muestra un relleno con huecos.',
              'Espesor y uniformidad perfectamente en objetivo, que es lo que hace este ' +
                'fallo invisible sin destruir una oblea.',
            ],
            Truth: 'El baño está a 32 Ah/L y el acelerador se ha descompuesto. A Faraday no le ' +
              'importan los aditivos — bajó la cantidad correcta de cobre — pero la ' +
              'realimentación CEAC que rellena una zanja desde el fondo se fue, así que ' +
              'cada estructura tiene un hueco por el medio. Pasa toda medición no ' +
              'destructiva que la fábrica posee, y falla en manos del cliente.',
            Complaint: 'Fiabilidad ha devuelto piezas con vías abiertas de un lote que pasó todas ' +
              'las pruebas en sort. Electrodeposición dice que las obleas eran perfectas: ' +
              'espesor correcto, uniformidad correcta, tiempo de ciclo correcto.',
            Label: 'Paquete de aditivos agotado',
          },
          ecdHealthy: {
            Evidence: [
              'Cada lectura del banco es nominal.',
              'El corte muestra un relleno limpio de abajo arriba.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lote.',
            Complaint: 'Lote de rutina. Espesor y uniformidad en objetivo.',
            Label: 'Nada mal',
          },
        },
        NoteSPerWafer: '{{a}} s por oblea',
        NoteSeedSqTheCentreIs: 'semilla {{a}} Ω/sq — el centro es lo más lejos del contacto',
        NoteAcceleratorCoverageAtTheTrench: 'cobertura de acelerador {{a}} en el fondo de la zanja',
        NoteGainAgainstNeededAtAr: 'ganancia {{a}}× frente a {{b}}× necesaria a AR {{c}}',
        SectionAPositiveFeedbackMadeOf: 'Una realimentación positiva hecha de geometría',
        NoteTheAcceleratorIsConservedOn: 'El acelerador se conserva sobre la superficie donde se asienta. Cuando el ' +
          'fondo de una zanja se rellena, esa superficie se CONTRAE — las mismas ' +
          'moléculas ocupan menos área, la cobertura sube, el fondo va más rápido y ' +
          'su área se encoge aún más rápido. Ese es todo el mecanismo del relleno de ' +
          'abajo arriba, y por eso el superrelleno trabaja más duro exactamente en ' +
          'las estructuras que más lo necesitan. La ganancia tiene que superar el ' +
          'doble de la relación de aspecto, o las paredes se encuentran antes y la ' +
          'zanja queda hueca.',
        SectionSolvedBesideTheFormulaIt: 'Resuelto, junto a la fórmula que sustituye',
        LabelCopperLate: 'cobre, tardío',
        LabelCopperEarly: 'cobre, temprano',
        SectionCellState: 'Estado de la celda',
        SectionBathAndRecipe: 'Baño y receta',
        TextPlateTheWaferAndPass: 'Electrodepositar la oblea y pasarla a CMP-11',
        LabelBottomUpGain: 'Ganancia de abajo arriba',
        LabelOverburden: 'Sobreespesor',
        NoteEveryNanometreIsCmp11: 'cada nanómetro es tiempo de ahuecado en CMP-11',
        LabelTerminalEffect: 'Efecto terminal',
        Note4AcrossTheWafer: '< 4% a través de la oblea',
        LabelChargePassed: 'Carga pasada',
        AssistantLead: 'Asistente de indagación ECD-12',
        AssistantBlurb:
          'Pregunta por qué una zanja quedó con hueco, por qué el borde deposita más ' +
          'grueso, o cuánto tiempo hay que platear. Cada respuesta es un llenado ' +
          'ejecutado en este baño.',
        reading: {
          bathCopper: {
            Limit: '> 34 g/L',
            Label: 'Cobre en el baño frente al objetivo de reposición',
          },
          houseutilities: {
            Limit: 'todos los sistemas nominales',
            Label: 'Llamar a instalaciones por el panel de servicios (0.5 h)',
          },
          crossSection: {
            Limit: 'llena',
            Label: 'Corte FIB de la zanja — destructivo (3.0 h)',
          },
          thickness: {
            Label: 'Mapa de espesor a cuatro puntas, 49 puntos (1.0 h)',
          },
          bathTemp: {
            Label: 'Temperatura del baño',
          },
          contactOhms: {
            Label: 'Resistencia del anillo de contacto',
          },
          anodeVolts: {
            Label: 'Potencial de ánodo sobre nominal',
          },
          bathAge: {
            Label: 'Edad del baño desde el último sangrado',
          },
        },
        aspect: {
          house: {
            Action: 'Detener el trabajo en la herramienta y plantearlo a instalaciones',
            Label: 'Nada en esta herramienta: escalar a instalaciones',
          },
          temperature: {
            Action: 'Recalibrar el termopar del baño y reajustar el lazo del calefactor',
            Label: 'Temperatura del baño',
          },
          contact: {
            Action: 'Limpiar el anillo y re-verificar la resistencia de cada dedo',
            Label: 'Anillo de contacto',
          },
          anode: {
            Action: 'Decapar la película del ánodo y reactivarlo, o cambiar el ánodo',
            Label: 'Ánodo',
          },
          bath: {
            Action: 'Sangrar y alimentar el baño, y reajustar la dosificación con un análisis CVS',
            Label: 'Paquete de aditivos',
          },
        },
      },
      thermal: {
        TunnelDrawnMag: 'la imagen más allá de la pared está dibujada ×{{m}} más brillante de lo ' +
          'que es',
        NoteOnTheMonitor: 'en la oblea monitor',
        LabelJunction: 'Profundidad de unión',
        NextHoldingIsTheEasyHalf: 'Retener es la mitad fácil. Los lotes que corrieron antes en este tubo ' +
          'salieron contra la misma hoja de banco: ¿qué tendrías que medir, y en qué ' +
          'obleas, para decidir si alguno debe volver?',
        NextOneMonitorInAFiftyWafer: 'Una sola oblea monitor liberó un lote de cincuenta. ¿En qué punto de la ' +
          'barquilla la pondrías, y qué te diría una segunda en otro lugar de la ' +
          'carga que esta no puede decirte?',
        LabelActivation: 'Dosis activada',
        LabelSheetResistance: 'Resistencia de capa',
        LabelGateOxide: 'Óxido de puerta',
        VerdictWithAWatchItem: 'liberado, con un punto en vigilancia',
        VerdictHeld: 'Retenido',
        VerdictQualified: 'Calificado',
        LabelVerdict: 'Decisión',
        HypAQualificationIsADecision: 'Una calificación es una decisión, no una explicación. Si la oblea monitor ' +
          'vuelve con su óxido dentro de la ventana y su resistencia de capa dentro ' +
          'de la tolerancia respecto al objetivo, y cada lectura del banco está ' +
          'dentro de su propio límite, el horno se libera. Si no, queda retenido, y ' +
          'la receta calificada en el mismo tubo debería decir si el responsable es ' +
          'la receta o el equipo.',
        AskedIsThisFurnaceFit: '¿Está este horno en condiciones de procesar producto ahora mismo, y qué ' +
          'hago al respecto?',
        SumRunTheMonitorWafer: 'Procesa la oblea monitor, contrasta el banco con sus límites y toma la ' +
          'decisión.',
        NotePerNm3: 'el propio silicio tiene 50 átomos por nm³',
        BudgetSpikeCaveat: 'Los dos paneles inferiores de la gráfica son este mismo experimento ' +
          'puntuado por las dos definiciones de “presupuesto térmico” en circulación. ' +
          '∫T dt — temperatura por tiempo — dice que estos flujos difieren un 4%: sus ' +
          'dos curvas se superponen, y su unidad, °C·s, no puede producir una ' +
          'profundidad. ∫D dt, en nm², los separa cien veces, y es la que predice ' +
          'adónde fue el boro. El experimento está escrito en la propia escena — sin ' +
          'mandos, porque ambas obleas deben compartir todo salvo el pico — y se ' +
          'detiene solo al final del flujo; Reset repite los noventa minutos.',
        BudgetSpikeNote: 'Dos obleas, la misma dosis de boro implantada, el mismo objetivo: 90 ' +
          'minutos a 900 °C. CONSTANTE recibe exactamente eso y da en el blanco. CON ' +
          'PICO es idéntica salvo diez minutos a 1200 °C, a los veinte minutos — un ' +
          'paso de un flujo de noventa minutos, y termina varias veces más profunda ' +
          'en la sonda, con casi todo el daño hecho en esos diez minutos. Eso es un ' +
          'presupuesto: el horno no promedia el flujo, integra D sobre él, y D es ' +
          'exponencial en la temperatura.',
        SectionBudgetSpike: 'Presupuesto térmico — diez minutos de noventa',
        BudgetSteady: 'constante',
        BudgetSpiked: 'con pico',
        BudgetSpiking: 'PICO — oblea izquierda a 1200 °C',
        BudgetShared: 'mismo flujo — ambas obleas idénticas',
        BudgetLabelSpiked: 'CON PICO · 10 min a 1200 °C',
        BudgetLabelSteady: 'ESTABLE · 900 °C todo el tiempo',
        BudgetNowTemp: 'ahora {{t}} °C',
        BudgetDtNote: 'El ∫D·dt propio de cada paso, en nm² — la cantidad que la unión realmente ' +
          'integra, y la reconciliación de las dos unidades de arriba: la ficha de ' +
          'presupuesto divide esta misma área entre D a 1000 °C para expresarla como ' +
          'segundos equivalentes. Trazado por paso, la exponencial se muestra sola: ' +
          'un paso corto y caliente puede gastar más que uno largo y frío.',
        SeriesStepDt: 'D·t por paso',
        ChartDtAxis: 'D·t (nm²)',
        ChartBudgetDtTitle: 'El D·t de cada paso — la temperatura es lo que gasta el presupuesto',
        HopBuilding: 'Construyendo el cristal — colocando el intersticial y relajando 217 átomos ' +
          'sobre el campo de fuerzas…',
        HopGlowNote: 'El átomo amarillo es el intersticial. Se localiza en vivo, como el átomo ' +
          'más alejado de todos los sitios de la red ideal — y por eso el marcador ' +
          'sigue al defecto a través de cada kick-out, cuando otro átomo hereda el ' +
          'intersticio. A temperatura el defecto a veces se reparte entre dos átomos ' +
          'que comparten un sitio (una mancuerna); entonces brillan los dos, porque ' +
          'los dos son realmente el defecto.',
        HopMsdNote: 'La gráfica junto al cristal lleva dos curvas, y la diferencia entre ellas es la lección. ' +
          'La continua es una medida: hasta dónde ha llegado realmente el intersticial, muestreada ' +
          'de forma continua, así que tiembla con la red, sube cuando cae un salto y retrocede ' +
          'cuando el siguiente desalojo va en sentido inverso. Una sola trayectoria de unos pocos ' +
          'saltos no es la media de nada — tres ejecuciones a la misma temperatura llegaron a 9,5, ' +
          '51 y 3,4 Å², que es sencillamente el aspecto de una muestra pequeña de un paseo ' +
          'aleatorio, así que no le midas la pendiente. La discontinua suma en cambio los cuadrados ' +
          'de las longitudes de salto: el paseo que esos mismos saltos harían si ninguno se ' +
          'desandara. Solo sube, y es la línea de la que sale D — su pendiente entre seis, en las ' +
          'mismas unidades que usa la difusión de entrada. Cuando la curva continua cae por debajo ' +
          'de la discontinua estás viendo la correlación, el intersticial volviendo por donde vino, ' +
          'que es la razón de que una difusividad medida lleve un factor de correlación. Lee ' +
          'también los tramos planos: el salto es un proceso de Poisson, así que incluso a 1650 K ' +
          'dos picosegundos sin ningún salto son perfectamente normales, y una ejecución quiere ' +
          'diez picosegundos antes de que se le lea nada. El control deja ambas líneas planas para ' +
          'siempre: sin defecto no hay nada que seguir ni nada que contar, D = 0.',
        HopMsdStatus: 'El intersticial se ha alejado {{a}} Å² del punto de partida · suma de saltos Σλ² = {{b}} Å² → D ≈ {{c}} Å²/ps (6·D·t)',
        HopChartY: 'desplazamiento² (Å²)',
        HopMeasured: 'Hasta dónde ha llegado el intersticial',
        HopJumpSum: 'Paseo aleatorio a partir de los saltos (Σλ²)',
        HopChartX: 'tiempo simulado (ps)',
        HopArrTitle: 'Gráfico de Arrhenius — un punto por corrida de {{a}} ps, pendiente = −Eₐ/k',
        HopArrX: '1000 / T (K⁻¹)',
        HopArrY: 'ln(saltos por ps)',
        HopArrPerPs: 'saltos/ps',
        HopArrPoints: 'Corridas completadas',
        HopArrFit: 'Recta de mínimos cuadrados',
        HopArrEmpty: 'Corra el intersticial durante {{a}} ps a un ajuste del horno: la corrida se detiene sola y aparece aquí como un punto. Dos ajustes trazan la recta.',
        HopArrOne: 'Una corrida hasta ahora — un segundo ajuste del horno traza la recta',
        HopArrStatus: '{{n}} corridas · la pendiente da Eₐ ≈ {{ea}} eV · cada punto lleva un error de conteo de Poisson de ±1/√saltos',
        HopArrSkipped: '{{n}} corrida(s) sin saltos quedan fuera del eje logarítmico',
        HopArrClear: 'Borrar los puntos',
        HopArrNote: 'Así se mide una energía de activación, en un horno y aquí por igual. La tasa de saltos va como exp(−Eₐ/kT), de modo que su logaritmo frente a 1/T es una recta y la pendiente es −Eₐ/k. Cada corrida de {{a}} ps del intersticial es una medición: hágala a varios ajustes del horno y los puntos se alinean, con la dispersión propia de un conteo de Poisson — una corrida de seis eventos conoce su propia tasa a un 40%. Las repeticiones a un mismo ajuste muestran esa dispersión directamente, y por eso la recta se ajusta a todos los puntos en lugar de trazarse entre dos. El cristal perfecto no se representa: su tasa es cero a toda temperatura, y cero no tiene logaritmo.',
        SectionTunnel: 'Por qué el óxido de puerta tiene un suelo: el efecto túnel',
        TunnelNote: 'THRM-04 crece este óxido, y la receta de arriba puede hacerlo tan fino como se quiera. El transistor no puede usarlo. Por debajo de unos dos nanómetros el óxido no detiene al electrón del canal, porque un electrón es una onda y una onda alcanza a través de una pared que no tiene energía para escalar. Esto es la ecuación de Schrödinger unidimensional resuelta para un paquete que encuentra una barrera rectangular de la altura del desplazamiento de bandas silicio–SiO₂. La fracción que atraviesa cae por un factor fijo con cada décima de nanómetro añadida, así que pasar un óxido de 2 nm a 1,2 nm multiplica la fuga por miles — y por eso la industria pasó a una película más gruesa de óxido de hafnio en vez de una más fina de dióxido de silicio. Se usa una sola masa efectiva, 0,5 m₀, a ambos lados de la barrera y dentro de ella; el decaimiento dentro del óxido es en lo que descansa la lección, y esa es la masa para la que es correcta. El silicio a cada lado se dibuja como su red solo para orientar: el electrón de este modelo se mueve por él como partícula libre con la masa efectiva, que es lo que un cristal hace con un electrón a estas energías, así que nada se resuelve contra esos átomos. "Color por fase" pinta la onda según la fase de ψ: las franjas son su longitud de onda, se mueven con el paquete y se acortan donde va más rápido; "Azul y verde" es intensidad simple, verde más allá de la pared.',
        TunnelChartTitle: 'Probabilidad más allá del óxido',
        TunnelChartX: 'tiempo (fs)',
        TunnelChartY: 'log₁₀ de la fracción',
        TunnelMeasured: 'Paquete propagado',
        TunnelFormula: 'fórmula para este paquete',
        TunnelFailed: 'No se pudo cargar el solucionador de Schrödinger.',
        TunnelLoading: 'Cargando el solucionador de Schrödinger…',
        TunnelStatus: '{{a}} fs · más allá del óxido {{b}} ({{c}}) · reflejado {{d}}%',
        TunnelOneIn: 'un electrón de cada {{n}}',
        TunnelNone: 'por debajo de una parte en mil millones',
        TunnelSum: 'Σ|ψ|² = {{s}} (reflejado + dentro + a través, márgenes incluidos)',
        TunnelColourLabel: 'Colores de fase',
        TunnelChartShort: 'Matriz de transferencia: pasa {{a}} · {{b}} décadas por nm de óxido',
        TunnelChartHelp: 'Qué son estos dos números',
        TunnelChartStatus: 'Matriz de transferencia sobre esta pared, promediada sobre la dispersión de energías del paquete: {{a}} (solo a la energía media {{c}}) · {{b}} décadas por nanómetro de óxido a esta energía',
        TunnelThickTitle: 'Transmisión frente al espesor del óxido',
        TunnelThickX: 'espesor del óxido (nm)',
        TunnelRuns: 'Corridas completadas',
        TunnelLineAt: 'fórmula a {{e}} eV, barrera de {{v}} eV',
        TunnelThickEmpty: 'Cada corrida completada cae aquí como un punto a su espesor. Cambie el espesor y corra de nuevo hasta que la exponencial quede dibujada.',
        TunnelThickStatus: 'Puntos en la gráfica: {{n}} · la línea discontinua cae {{b}} décadas por nanómetro a la energía y barrera actuales',
        TunnelClear: 'Borrar los puntos',
        TunnelThickNote: 'Este es el experimento que la gráfica de arriba resume una corrida a la vez. Cada corrida completada es un punto a su espesor; hágala a tres o cuatro espesores y los puntos caen sobre una recta en este eje logarítmico — y una recta en un eje logarítmico es una exponencial. La pendiente es 2κ/ln 10 décadas por nanómetro, donde κ la fija cuánto por debajo de la barrera está la energía del electrón, así que una barrera más baja da una recta más suave: ese es todo el argumento del high-k, en una sola imagen. Los puntos hechos a otra energía u otra barrera se quedan en la gráfica y caen fuera de la recta actual, que es el lugar honesto para ellos.',
        TunnelReset: 'Reiniciar',
        TunnelSio2: 'SiO₂ · 3,1 eV',
        TunnelHfo2: 'HfO₂ · 1,5 eV',
        TunnelWidth: 'Espesor del óxido',
        TunnelEnergy: 'Energía del electrón',
        TunnelBarrier: 'Altura de la barrera',
        TunnelPastLabel: 'más allá del óxido',
        BoxNote: 'El mismo óxido, visto desde arriba y construido con átomos. Esta es la caja bidimensional del Quantum Modeler con la pared hecha de núcleos repulsivos empaquetados, a 0,32 nm — más o menos la separación entre átomos vecinos en la sílice — en tantas capas como permita el espesor: un óxido de 1,2 nm son cuatro. El paquete se ensancha al viajar, se apila contra los átomos en franjas de onda estacionaria donde se cruzan la onda incidente y la reflejada, y una copia tenue sigue más allá, dibujada ampliada en verde. Los núcleos están dimensionados para que su promedio sobre una capa sea el desplazamiento de bandas, así que el bloque de arriba es exactamente su media, y la línea discontinua es la fórmula del bloque para una pared de este espesor. Los átomos transmiten dentro de decenas de por ciento de esa media a todo espesor que permite el mando, y eso se comprueba; la separación entre la curva y la línea son los átomos, no un error. "Óxido como bloque" cambia los átomos por su media. Las líneas finas son isolíneas de |ψ|² al 2, 9, 20, 36, 56 y 81 por ciento del pico inicial del paquete, en la escala ampliada más allá de la pared.',
        BoxChartTitle: 'Probabilidad más allá de la pared',
        BoxChartStatus: 'Matriz de transferencia sobre esta pared, promediada sobre la dispersión de energías del paquete: {{a}} · {{b}} décadas por nanómetro de pared a esta energía',
        BoxPastLabel: 'más allá de la pared',
        BoxStatus: '{{a}} fs · más allá de la pared {{b}} ({{c}}) · reflejado {{d}}%',
        BoxLayers: '{{n}} capas',
        TunnelTab1d: 'Línea (1D)',
        TunnelTab2d: 'Desde arriba (2D)',
        TunnelTab3d: 'Volumen (3D)',
        TunnelTabLeak: 'Fuga de puerta',
        LeakNote: 'Lo que el óxido realmente deja escapar. Las vistas de arriba responden qué fracción de un electrón pasa; a un transistor se le pregunta otra cosa, en amperios por centímetro cuadrado frente a una especificación, y esto responde a eso. Aquí la transmisión se resuelve por matriz de transferencia en vez de propagando un paquete: es exacta para este perfil, tarda microsegundos y es una comprobación independiente de los paquetes, y el arnés mantiene las tres juntas. Luego se integra sobre los electrones que de verdad llegan, la función de suministro de Tsu–Esaki, que es cero sin polarización: una barrera plana no conduce corriente por delgada que sea, así que esta vista es también donde la barrera se inclina. Lleve la polarización más allá del desplazamiento de bandas y la esquina lejana del óxido cae por debajo del electrón, la barrera se vuelve un triángulo y el túnel directo da paso a Fowler–Nordheim. Al modelo nadie le habló de ese cruce; la matriz de transferencia sólo ve el perfil. Hay dos masas en juego y son distintas: 0,5 m₀ para el decaimiento dentro del óxido, 0,19 m₀ para contar estados en el silicio. Quedan fuera, y conviene saberlo antes de citar un número: la reducción por fuerza imagen, las subbandas de la capa de inversión, cualquier camino asistido por trampas, y la temperatura, fija a 300 K. El resultado es una estimación de túnel directo buena dentro de un orden de magnitud frente a óxidos medidos, calculada sobre el bloque que promedian los átomos de arriba.',
        LeakStatus: '{{j}} A/cm² a {{v}} V · campo {{f}} MV/cm · {{r}}',
        LeakDirect: 'túnel directo',
        LeakFowler: 'Fowler–Nordheim',
        LeakBreakdown: ' · más allá de la ruptura de un óxido real',
        LeakOver: '{{n}}× por encima de la especificación de 1 A/cm²',
        LeakUnder: '{{n}}× por debajo de la especificación de 1 A/cm²',
        LeakNone: 'Sin polarización no hay corriente: vuelven de la puerta tantos electrones como van hacia ella.',
        LeakJvTitle: 'Corriente de puerta frente a la polarización',
        LeakJvX: 'polarización de puerta (V)',
        LeakJvY: 'log₁₀ A/cm²',
        LeakSeries: 'óxido de {{w}} nm',
        LeakBarrierSeries: 'barrera de {{v}} eV',
        LeakSpec: 'especificación, 1 A/cm²',
        LeakHere: 'aquí',
        LeakThickTitle: 'Corriente de puerta frente al espesor del óxido, a {{v}} V',
        LeakJvCaption: 'La curva sube {{d}} décadas por voltio donde está fijada la polarización.',
        LeakThickCaption: 'La curva cae {{d}} décadas por nanómetro de óxido a esta polarización. Cada décima de nanómetro de escalado cuesta un factor fijo de fuga, y eso es lo que acabó con el dióxido de silicio.',
        LeakBias: 'Polarización de puerta',
        LeakCathode: 'canal de silicio',
        LeakOxide: 'óxido, {{w}} nm',
        LeakAnode: 'puerta',
        LeakSpectrum: 'de dónde viene la corriente',
        LeakThisWall: 'esta pared',
        LeakShort: 'Fuga de {{j}} A/cm² a {{v}} V',
        LeakRunShort: 'la simulación confirma el {{p}}%',
        LeakHelp: 'Cómo se calcula esta corriente',
        LeakBeside: 'Esta pared a {{v}} V deja pasar {{j}} A/cm². Resuelto por matriz de transferencia sobre el mismo óxido, no medido del paquete de la izquierda: una corriente necesita la transmisión a cada energía que aporta el canal, cientos de corridas, y ambos coinciden dentro de un pequeño porcentaje.',
        LeakRunCheck: 'La corrida de la izquierda ha medido {{a}} a través de esta pared hasta ahora, frente a los {{b}} sobre los que se basa la corriente de arriba: {{p}}% del camino. La corriente es una propiedad de la pared, no de la corrida; lo que la corrida hace es confirmarla.',
        VolumeNote: 'Y en tres dimensiones. Este es el modelo 3D del Quantum Explorer: el mismo propagador de Cayley dividido en x, y y z sobre una malla de 208 × 48 × 48, los mismos núcleos empaquetados ahora apilados como capas reales de esferas, los mismos márgenes absorbentes y la misma contabilidad, resuelto en un hilo de trabajo, unos segundos por corrida, y dibujado como volumen: azul donde está el paquete, verde en su propia escala rotulada más allá de la pared. Arrastre para orbitar, arrastre con el botón derecho para desplazar, rueda para acercar. La fracción transmitida es la que miden la caja 2D y la línea 1D, y la fórmula del bloque es la misma línea discontinua; se comprueba contra ellas.',
        VolumeIdle: 'Pulse Run: el paquete se propaga en un hilo de trabajo, unos segundos por corrida.',
        VolumeRunning: 'resolviendo en un hilo de trabajo, unos segundos por corrida',
        VolumeStop: 'Detener',
        VolumeUnavailable: 'El solucionador 3D necesita un hilo de trabajo y no pudo iniciarlo aquí.',
        BoxAtoms: 'Óxido como átomos',
        BoxSlab: 'Óxido como bloque',
        BoxWallAtoms: '{{n}} capas atómicas · {{w}} nm · {{v}} eV de media',
        BoxFormulaSlab: 'bloque de la misma altura media',
        HopChartTitle: 'Hasta dónde llegó el intersticial, frente al paseo que implican sus saltos',
        BudgetCaveat: 'El experimento está guionizado dentro de la propia escena, y por eso esta ' +
          'vista no tiene mandos y se detiene sola al final del flujo: las dos obleas ' +
          'tienen que recibir exactamente la misma predeposición para que la ' +
          'comparación signifique algo, y un mando que moviera una de ellas lo ' +
          'destruiría. Reiniciar vuelve a ejecutar el flujo entero. Las dos curvas ' +
          'junto a la imagen son el presupuesto en sí: la ∫D dt de cada oblea, ' +
          'acumulada en vivo por el guion de la escena y no calculada por este panel. ' +
          'Fíjate en cuánto MENOS se separan que el dopante: alrededor de un tercio ' +
          'más de presupuesto en la oblea izquierda produce el triple de ' +
          'concentración a la profundidad de la sonda, porque la profundidad de la ' +
          'unión va como √(Dt). Un cambio modesto en el presupuesto no es un cambio ' +
          'modesto en el dispositivo.',
        HopControlNote: 'Ahora cambia al cristal perfecto y ponlo tan caliente como llegue el ' +
          'control. No se mueve nada. No despacio: nada en absoluto. Una red sin ' +
          'defectos registra exactamente cero saltos incluso con el horno puesto a ' +
          'cuarenta grados del punto de fusión, mientras que la misma celda con UN ' +
          'intersticial dentro salta cada dos o tres picosegundos. Esa es toda la ' +
          'razón por la que el daño de implantación y el recocido que lo cura ' +
          'pertenecen al mismo tema que el dopado: el dopante se mueve sobre ' +
          'defectos, y el haz de al lado acaba de fabricar muchísimos. No leas el ' +
          'cristal quieto como si dijera que una oblea sin daño nunca difunde. Una ' +
          'red real se fabrica sus propios defectos con la temperatura — vacantes e ' +
          'intersticiales aparecen y se recombinan en equilibrio térmico, a una ' +
          'concentración fijada por su energía de formación — y un horno a 1100 °C ' +
          'tiene de sobra. Lo que esta celda no puede hacer es esperar a uno: 216 ' +
          'átomos durante picosegundos es una ventana demasiado pequeña y demasiado ' +
          'corta para un suceso tan raro. Así que el control demuestra que el defecto ' +
          'es el VEHÍCULO, no que un cristal caliente no tenga ninguno.',
        BudgetABNote: 'Dos obleas, el mismo implante, la misma predeposición. Al terminar la ' +
          'predeposición el flujo se separa: la de la izquierda continúa con el resto ' +
          '— 1000 °C, luego 900 °C — y la de la derecha se templa a temperatura ' +
          'ambiente y no vuelve a calentarse. La oblea templada es el control: ' +
          'muestra cómo ERA la unión cuando acabaste de fabricarla. Mira cómo sigue ' +
          'avanzando la de la izquierda. Al final del flujo su dopante ha llegado ' +
          'varias veces más lejos, y todo ello por pasos que no tenían nada que ver ' +
          'con formarla. Eso es lo que cuenta el indicador de presupuesto de arriba, ' +
          'y por eso una unión se diseña contra el flujo entero y no contra su propio ' +
          'recocido.',
        SectionBudgetAB: 'Presupuesto térmico: lo que los pasos posteriores hacen a una unión ya ' +
          'formada',
        HopReset: 'Reiniciar la cuenta',
        HopPause: 'Pausar',
        HopRun: 'Ejecutar',
        HopTemp: 'Temperatura del horno',
        HopPerfect: 'Cristal perfecto (control)',
        HopWithDefect: 'Un intersticial',
        HopLoading: 'Cargando el motor de dinámica molecular…',
        HopFailed: 'No se pudo cargar el motor de dinámica molecular.',
        HopStatus: '{{a}} saltos · {{b}}/ps · medido {{c}} K · {{d}} ps',
        HopCaveat: 'Lo que esto deliberadamente NO muestra es un átomo de boro saltando. La ' +
          'energía de activación del boro es 3,46 eV — el mismo número que usa la ' +
          'Arrhenius de la difusión de arriba — lo que da aproximadamente dos saltos ' +
          'por segundo a 1100 °C. Esta vista llega a picosegundos, así que se queda ' +
          'corta por unos ocho órdenes de magnitud, y ningún ordenador más rápido ' +
          'cierra esa distancia. El intersticial se puede mirar solo porque su ' +
          'barrera es mucho más baja. Esa distancia es precisamente por la que la ' +
          'difusión a escala de oblea es un coeficiente ajustado dentro de una EDP y ' +
          'no una simulación de átomos: el suceso elemental es real, y demasiado raro ' +
          'para verlo.',
        HopNote: 'La escena de arriba mueve el dopante con una difusividad que sigue la ' +
          'temperatura por Arrhenius. De esto está hecho ese número: un único átomo ' +
          'de silicio situado entre posiciones de red en lugar de sobre una, ' +
          'apartando a sus vecinos de un empujón y ocupando su sitio. Pulsa Ejecutar ' +
          'y mira subir la cuenta: cada salto es un término de D. Después sube el ' +
          'horno y mira cómo el ritmo sube con él, que es el factor de Arrhenius ' +
          'ocurriendo en vez de ser afirmado.',
        SectionHop: 'De qué está hecho D(T): un átomo cambiando de sitio',
        BudgetFlowEnded: 'fin del flujo',
        BudgetQuenched: 'templada',
        BudgetFullFlow: 'flujo completo',
        BudgetPredep: 'predeposición compartida: las dos obleas idénticas',
        NoteTheSolverPutsThe: 'El solver pone la unión {{a}}× más profunda que la fórmula. La razón cabe ' +
          'en una línea: D depende de cuánto dopante hay ya, así que un implante ' +
          'pesado excava su propio camino hacia abajo, y este perfil abarca un rango ' +
          'de difusividad de {{b}}× que un único número de Arrhenius no puede ' +
          'representar.',
        NoteOnThisRecipeTheTwo: 'Con esta receta los dos coinciden al {{a}}%. Ese es el resultado honesto ' +
          'para un ciclo corto: poco difunde, el perfil sigue siendo el que dejó el ' +
          'implantador, y la fórmula es una forma perfectamente buena de obtenerlo. ' +
          'Alarga el recocido o añade un paso abajo y míralos separarse.',
        ValueAllOfIt: 'todo',
        ValueSolubilityCeiling: '{{a}} % (techo de solubilidad)',
        MetricElectricallyActive: 'Eléctricamente activo',
        Value100ByConstruction: '100 % por construcción',
        MetricDoseLeftInThe: 'Dosis restante en la oblea',
        ValueNoInterface: 'sin interfaz',
        ValueNmInterfaceMoving: '{{a}} nm, interfaz en movimiento',
        MetricSiliconConsumed: 'Silicio consumido',
        ValueOneNumberEverywhere: 'un solo número, en todas partes',
        ValueSpreadNodeToNode: '{{a}}× de dispersión, nodo a nodo',
        MetricDiffusivityAcrossThe: 'Difusividad a lo largo del perfil',
        MetricSurfaceConcentration: 'Concentración superficial',
        MetricJunctionDepth: 'Profundidad de unión',
        LegendTheFittedGaussian: 'la gaussiana ajustada',
        LegendTheEquationSolvedOn: 'la ecuación, resuelta en {{a}} pasos de tiempo',
        LabelAnneal: 'recocido',
        LabelOxidation: 'oxidación',
        SecondaryLabel: 'Error de resistencia de hoja',
        ToolLabel: 'THRM-04 · Horno vertical y RTP',
        LabelTryTheSpikeAnneal1100: 'Prueba el recocido spike (1100 °C, 2 s)',
        NextASpikeAnnealSeparatesActivation: 'Un recocido spike separa la activación de la difusión siendo caliente y ' +
          'breve. ¿Qué limita cuán breve puede ser — y qué le pasa a la oblea si la ' +
          'subes de temperatura así de rápido?',
        AxisYSheetResistanceSq: 'resistencia de hoja (Ω/sq)',
        AxisXJunctionDepthNm: 'profundidad de unión (nm)',
        TitleJunctionDepthAgainstSheetResistance: 'Profundidad de unión frente a resistencia de hoja — el compromiso, graficado',
        NoteColdAndShortActivatesNothing: 'frío y corto no activa nada',
        LabelShortAnnealActivation: 'Activación con recocido corto',
        LabelSpikeAnnealActivation: 'Activación con recocido spike',
        LabelItsSheetResistance: 'Su resistencia de hoja',
        LabelShallowestRoute: 'Ruta más superficial',
        HypJunctionDepthIsSetBy: 'La profundidad de unión la fija dónde empezó el implante más cuánto la ha ' +
          'movido el calor, y la resistencia de hoja cuánto dopante activo hay en esa ' +
          'profundidad. Cualquier cosa que haga la unión más superficial mete el ' +
          'mismo dopante en menos silicio, así que la resistencia debería subir — a ' +
          'menos que el cambio también mejore la activación.',
        AskedHowDoIMakeThe: '¿Cómo hago la unión más superficial, y qué cuesta cada ruta?',
        SumTheJunctionDepthAndResistance: 'El compromiso entre profundidad de unión y resistencia, y por qué no tiene ' +
          'lado gratis.',
        NextAGateOxideHasTo: 'Un óxido de puerta tiene que medir unos pocos nanómetros y ser ' +
          'reproducible a una fracción de eso. Con lo que acabas de ver, ¿lo ' +
          'crecerías caliente y corto, o frío y largo — y cuál te da más control ' +
          'sobre el número final?',
        TitleOxideThicknessAgainstTimeDry: 'Espesor de óxido frente a tiempo, seca y húmeda',
        NoteSqrt42WouldBe: 'sqrt(4) = 2 sería sin término lineal',
        LabelGrowthRatio: 'Razón de crecimiento',
        LabelWet120Min: 'Húmeda, 120 min',
        LabelAt120Min: 'A 120 min',
        LabelAt5Min: 'A 5 min',
        AskedWhyDoesOxideGrowthSlow: '¿Por qué el crecimiento del óxido se frena cuanto más grueso se hace el ' +
          'óxido?',
        SumTheLinearAndParabolicRegimes: 'Los regímenes lineal y parabólico, y por qué necesitan recetas distintas.',
        LateralUnderstood: 'Si un recocido de difusión mueve el dopante sólo hacia abajo, o también lateralmente bajo la máscara.',
        LateralHypothesis: 'La difusión no tiene dirección preferida. La máscara decide por dónde ENTRA el dopante en el silicio, no dónde acaba; una vez dentro, debería extenderse a lo largo de la superficie tan fácilmente como hacia el interior — y el alcance lateral debería seguir a la profundidad en vez de rezagarse cada vez más a medida que avanza el recocido.',
        NextLateralBudget: 'El alcance lateral lo fija el mismo D·t que la profundidad, así que la única forma de estrecharlo es gastar menos presupuesto térmico. Pregúntame lo que cada paso caliente aguas abajo le cuesta a esta unión — ese experimento corre en 1D, y ahora sabes que su respuesta hay que doblarla hacia los lados.',
        NextIfADownstreamStepIs: 'Si un paso aguas abajo es fijo y no puedes quitarlo, la única palanca que ' +
          'queda es el implante. Pregúntame qué le pasa a la resistencia de hoja si ' +
          'haces la unión lo bastante superficial para sobrevivir a ese paso.',
        LabelAfter1050C: 'tras 1050 °C',
        LabelJunctionDepth: 'profundidad de unión',
        AxisYJunctionDepthNm: 'profundidad de unión (nm)',
        AxisXStep0None: 'paso (0 = ninguno)',
        TitleJunctionDepthAfterEachAdded: 'Profundidad de unión tras cada paso añadido aguas abajo',
        NoteEquivalentAt1000C: 'equivalente a 1000 °C',
        LabelBudgetWorstCase: 'Presupuesto, peor caso',
        NoteAlmostFree: 'casi gratis',
        LabelAfterA450CStep: 'Tras un paso a 450 °C',
        LabelAfterA1050CStep: 'Tras un paso a 1050 °C',
        LabelJunctionAlone: 'Unión, sola',
        HypDiffusionDependsOnlyOnThe: 'La difusión depende solo del producto de difusividad por tiempo, sumado ' +
          'sobre cada paso que ha visto la oblea. Si eso es cierto, un paso añadido ' +
          'aguas abajo —uno que no tiene nada que ver con esta unión— la moverá ' +
          'igualmente, y un paso más caliente la moverá mucho más que uno más largo, ' +
          'porque la difusividad es exponencial en temperatura y solo lineal en ' +
          'tiempo.',
        AskedTheImplantDidNotChange: 'El implante no cambió, pero la unión sí. ¿De dónde salió el calor?',
        SumShowThatEveryHotStep: 'Muestra que cada paso caliente gasta de una misma cuenta compartida.',
        fault: {
          wetHouseHeader: {
            Evidence: [
              'Óxido grueso con la unión intacta — la firma de la humedad, no de un tubo ' +
                'caliente.',
              'El punto de rocío de la línea está alto Y una trampa nueva no lo arregló. ' +
                'Juntos, esos dos hechos apuntan aguas arriba y a ningún otro sitio.',
              'La comprobación de servicios encuentra la cabecera húmeda.',
              'La máquina de ALD se queja al mismo tiempo. Su síntoma no suena en nada a ' +
                'este, porque el agua hace algo completamente distinto allí — pero es la ' +
                'misma agua.',
            ],
            Truth: 'El secador de gas seco de la planta falló húmedo el fin de semana y la ' +
              'cabecera lleva cerca de un 2% de agua — arrastre, no la deriva a nivel de ' +
              'ppm de un secador saturado. Llega a toda máquina que respire nitrógeno u ' +
              'oxígeno de planta, y lo que hace al llegar depende de para qué usaba el ' +
              'gas esa máquina. Fíjate en lo separados que están los umbrales: el ALD se ' +
              'arruina con ppm de una cifra, porque el agua es su co-reactivo, mientras ' +
              'que un horno creciendo óxido en vapor no nota nada por debajo de un uno ' +
              'por ciento. Hace falta un fallo grosero para que ambas se quejen a la vez, ' +
              'y por eso los dos grupos casi nunca conectan sus problemas. En esta ' +
              'máquina parece exactamente una trampa de humedad saturada, porque es la ' +
              'misma agua llegando por otra ruta — aguas arriba de la trampa en vez de en ' +
              'ella. Por eso cambiar la trampa no logró nada, y por eso el punto de rocío ' +
              'de la línea sigue leyendo húmedo después del cambio.',
            Complaint: 'Óxido de puerta grueso otra vez, como la trampa de humedad del trimestre ' +
              'pasado. La trampa se cambió el lunes y no cambió nada. DEP-02 dice que su ' +
              'crecimiento también se ha ido arriba.',
            Label: 'Agua en la cabecera de oxígeno de planta',
          },
          lowDose: {
            Evidence: [
              'Resistencia de hoja muy alta mientras el óxido, la unión y el presupuesto ' +
                'térmico son todos nominales. Nada térmico está implicado en absoluto.',
              'La concentración pico está a la mitad, cosa que ningún error de recocido ' +
                'puede hacer — un recocido extiende dopante, no lo quita.',
              'La oblea del monitor de dosis discrepa del registro del implantador.',
            ],
            Truth: 'La integración de corriente de haz está mal y las obleas recibieron cerca ' +
              'de la mitad de la dosis que el implantador registró. La mitad del dopante ' +
              'es la mitad de los portadores, así que la resistencia de hoja sube en ' +
              'picado. Los pasos térmicos son todos perfectamente normales, y por eso la ' +
              'unión apenas se mueve — simplemente hay menos dopante en el mismo perfil ' +
              'difundido.',
            Complaint: 'La resistencia de hoja se ha ido muy arriba. El óxido de puerta está bien.',
            Label: 'Implantador sub-dosificando',
          },
          hotAnneal: {
            Evidence: [
              'Resistencia de hoja baja y unión honda, con el óxido de puerta exactamente ' +
                'en objetivo — así que lo que pasó, pasó después de la oxidación.',
              'El presupuesto térmico está arriba en un 90%, que es donde mirar primero ' +
                'cuando una unión se mueve.',
              'Una dosis implantada baja también movería la resistencia de hoja, pero ' +
                'HACIA ARRIBA, y no movería la unión. Esta la baja y ahonda la unión.',
              'Una oblea de calibración lee unos 50 C por encima de lo que reporta el ' +
                'pirómetro.',
            ],
            Truth: 'El pirómetro del RTP ha derivado, así que el controlador lleva las ' +
              'lámparas a unos 1100 C mientras reporta 1050 C. La difusividad es ' +
              'Arrhenius, así que cincuenta grados casi doblan el Dt gastado en el ' +
              'recocido: el dopante se extiende más, la unión se ahonda y la resistencia ' +
              'de hoja cae.',
            Complaint: 'La resistencia de hoja corre baja y la unión es más honda de lo que dice ' +
              'el modelo.',
            Label: 'Pirómetro del RTP leyendo bajo',
          },
          wetLine: {
            Evidence: [
              'El óxido está grueso y la unión NO se ha movido. Eso es decisivo — un tubo ' +
                'caliente no podría dejar la unión en paz.',
              'La resistencia de hoja es exactamente nominal, así que nada ha cambiado en ' +
                'el presupuesto térmico.',
              'Un punto de rocío en la línea de O2 lee muy por encima de su límite.',
            ],
            Truth: 'La trampa de humedad de la línea de O2 seco está saturada y cerca del 5% ' +
              'del oxidante que llega al tubo es agua. La oxidación húmeda es un orden de ' +
              'magnitud más rápida que la seca, así que hasta unos pocos por ciento ' +
              'engordan el óxido gravemente. Nada más del proceso lo nota: el agua no ' +
              'difunde dopante, así que la unión y la resistencia de hoja quedan ' +
              'intactas.',
            Complaint: 'Óxido de puerta grueso otra vez, igual que el problema del horno del ' +
              'trimestre pasado.',
            Label: 'Humedad en la línea de oxígeno seco',
          },
          hotFurnace: {
            Evidence: [
              'El óxido está grueso Y la unión se ha movido más honda. Solo la ' +
                'temperatura hace ambas cosas, porque solo la temperatura se gasta en todos ' +
                'los pasos.',
              'Humedad en la línea de gas engrosaría exactamente el mismo óxido y dejaría ' +
                'la unión exactamente donde estaba. Esa comparación es el diagnóstico.',
              'Una oblea de perfil lee unos 30 C por encima de la consigna.',
            ],
            Truth: 'Los termopares de zona han derivado y el tubo está realmente a unos 930 C. ' +
              'El crecimiento de óxido es Arrhenius, así que 30 C compran un 40% más de ' +
              'óxido — pero la oblea también pasa veinte minutos 30 C caliente, y ese ' +
              'tiempo se gasta del mismo presupuesto térmico del que tira el recocido. La ' +
              'unión también se mueve.',
            Complaint: 'El óxido de puerta salió un 40% grueso. Receta intacta, la consigna lee ' +
              '900 C.',
            Label: 'Horno de oxidación caliente',
          },
          healthy: {
            Evidence: [
              'El óxido de puerta y la resistencia de hoja están ambos en sus valores ' +
                'cualificados.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lote.',
            Complaint: 'Las obleas monitor de esta mañana están en objetivo. Nada reportado.',
            Label: 'Horno cualificado',
          },
        },
        NoteXA: 'x/A = {{a}}',
        SectionSolvedBesideTheFormulaIt: 'Resuelto, junto a la fórmula que sustituye',
        NoteEverythingAboveWidensAGaussian: 'Todo lo de arriba ensancha una gaussiana con la raíz de dos D t, con D ' +
          'leída de una recta de Arrhenius a la temperatura de recocido. Abajo, la ' +
          'ecuación de difusión se RESUELVE en una malla — unos cientos de nodos ' +
          'pasados por el ciclo térmico, con D evaluada de la concentración local en ' +
          'cada nodo y la interfaz de óxido avanzando en la oblea mientras crece. ' +
          'Donde el recocido es corto, ambas caen una sobre otra. Donde es largo, no, ' +
          'y esa brecha es el argumento para construir solucionadores.',
        AxisYLogCm: 'log₁₀ nm⁻³',
        reading: {
          implantlog: {
            Label: 'Monitor de dosis contra el registro del implantador (6 h)',
          },
          pyrometer: {
            Label: 'Pirómetro del RTP contra oblea de calibración (3 h)',
          },
          houseutilities: {
            Limit: 'cabecera estanca, PCW a temperatura, gas seco < 50 ppm',
            Label: 'Comprobación de servicios con instalaciones (2 h)',
          },
          moisture: {
            Label: 'Punto de rocío en la línea de O2 seco (2 h)',
          },
          profilewafer: {
            Label: 'Oblea de perfil del horno, todas las zonas (4 h)',
          },
          setpoints: {
            Limit: 'según receta',
            Label: 'Consignas de horno / recocido',
          },
          budget: {
            Label: 'Presupuesto térmico gastado',
          },
          junction: {
            Label: 'Profundidad de unión (SIMS)',
          },
          rs: {
            Label: 'Resistencia de hoja (cuatro puntas)',
          },
          oxide: {
            Label: 'Óxido de puerta (elipsómetro)',
          },
        },
        aspect: {
          house: {
            Action: 'Parar el trabajo en la máquina y avisar a instalaciones',
            Label: 'Nada en esta máquina — escalar a instalaciones',
          },
          implanter: {
            Action: 'Recalibrar la integración de corriente de haz, repetir el monitor de dosis',
            Label: 'Control de dosis del implantador',
          },
          rtp: {
            Action: 'Calibrar el pirómetro contra una oblea instrumentada',
            Label: 'Pirómetro del RTP',
          },
          gasline: {
            Action: 'Cambiar la trampa de humedad, purgar y re-certificar la línea de O2 seco',
            Label: 'Línea de gas de oxidación',
          },
          furnace: {
            Action: 'Pasar una oblea de perfil y recalibrar los termopares de zona',
            Label: 'Temperatura del horno de oxidación',
          },
        },
        SectionLot: 'El lote en el horno',
        LotNote:
          'El panel de arriba recuece lo que describen los mandos. El horno de abajo ' +
          'recuece LA OBLEA: la implantación que realmente recibió en IMP-10, sobre ' +
          'cada paso térmico que ya ha atravesado. Ejecuta el mismo ciclo dos veces y ' +
          'la unión sigue moviéndose, porque la oblea recuerda y los mandos no.',
        LotEmpty:
          'No hay lote en el tubo. Envía una oblea desde una máquina anterior para ' +
          'recocerla.',
        LotIngot:
          'Este lote sigue siendo un lingote. WPR-08 tiene que cortarlo antes de que ' +
          'un horno pueda tocarlo.',
        LotCard: 'Hoja de ruta',
        LotImplant: 'Lleva una implantación: ',
        LotNoImplant:
          'Esta oblea no registra ninguna implantación: los mandos de la receta la ' +
          'sustituyen.',
        LotPrior: 'paso(s) térmico(s) ya anotados: este ciclo se suma a ellos',
        LotFresh: 'Aún sin historial térmico. Este es su primer ciclo.',
        LotRun: 'Ejecutar este ciclo sobre el lote y pasarlo',
        SectionResult: 'Resultado',
        SectionProfile: 'Perfil de dopante',
        SectionDriveIn: 'Redistribución — difusión 2D en vivo',
        SumShowThatADriveIn: 'Mostrar que una redistribución ensancha cada elemento tanto como lo profundiza.',
        LabelJunctionDepth2D: 'Profundidad de unión',
        LabelUnderTheMask: 'Bajo la máscara',
        NoteEachSide: 'a cada lado',
        LabelLateralOverDepth: 'Lateral / profundidad',
        NoteRoughlyConstant: 'casi constante en el tiempo',
        LabelDopedWidth: 'Anchura dopada',
        NoteFromAWindow: 'desde una ventana de {{nm}} nm',
        TitleDepthAndLateralReach: 'Profundidad y alcance lateral, en una redistribución',
        AxisXAnnealMinutes: 'tiempo de recocido (min)',
        AxisYNm: 'nm',
        LabelDownDepth: 'hacia abajo (profundidad de unión)',
        LabelSidewaysUnderMask: 'lateral (bajo la máscara)',
        TitleLateralAsAFraction: 'Alcance lateral como fracción de la profundidad',
        AxisYFraction: 'lateral / profundidad',
        LabelRatio: 'proporción',
        DriveInNote:
          'Una simulación en vivo de boro difundiéndose por una ventana de 0,6 µm en ' +
          'una máscara de SiO₂, a escala real (el cuadro mide 2 µm de ancho). La difusividad ' +
          'del boro sigue la temperatura del recocido según Arrhenius — cien grados son un ' +
          'orden de magnitud en D — así que prueba el mismo tiempo a 1000 °C y a 1100 °C. Y ' +
          'observa el borde de la máscara: la unión se extiende lateralmente por debajo a ' +
          'aproximadamente 0,8× su profundidad, algo que ningún perfil 1D puede mostrar.',
        DiLoading: 'Cargando el motor de difusión…',
        DiFailed: 'El motor de difusión no se pudo cargar.',
        DiPause: 'Pausar',
        DiRun: 'Ejecutar',
        DiReset: 'Reiniciar',
        DiAnnealLabel: 'Recocido',
        DiAnneal: 'recocido {{c}} °C',
        DiJunction: 'unión {{um}} µm',
        DiLateral: 'bajo máscara {{um}} µm',
        DiClock: 't = {{min}} min',
        DiOpenInEnergy2D: 'Abrir esta escena en Energy2D ↗',
        DiOpenInEnergy2DTitle: 'Abre esta escena en el editor de Energy2D, en una pestaña nueva, donde se pueden cambiar su geometría y sus materiales y su asistente de IA puede reconstruirla.',
        DiGraphTitle: 'Concentración en las sondas',
        DiGraphX: 'tiempo de recocido (min)',
        ProfileNote:
          'Dónde acabó el dopante. La implantación lo puso a una profundidad; cada ' +
          'paso caliente desde entonces lo ha extendido. La unión es donde el perfil ' +
          'cae hasta el dopado del sustrato.',
        SectionSteps: 'Pasos térmicos posteriores',
        StepsNote:
          'Pasos que vienen DESPUÉS de formar esta unión y no tienen nada que ver con ' +
          'ella. Añade uno y observa cómo la unión se mueve igualmente. La difusión ' +
          'solo ve el D·t total, y el silicio no sabe de quién era cada paso.',
        SectionOxide: 'Crecimiento del óxido — lineal, luego parabólico',
        OxideNote:
          'Un óxido delgado está limitado por la reacción en la interfaz con el ' +
          'silicio y crece al ritmo del tiempo. Uno grueso está limitado por el ' +
          'oxígeno que difunde a través del óxido ya presente, así que el crecimiento ' +
          'se frena a la raíz cuadrada del tiempo. La misma ecuación, los dos ' +
          'regímenes.',
        SectionBudget: 'Adónde va el presupuesto',
        BudgetNote:
          'Cada paso convertido al tiempo a 1000 °C que habría producido la misma ' +
          'difusión. Comparable entre pasos, a diferencia de un número en cm².',
        SectionAmbient: 'Ambiente',
        AmbientDry: 'O₂ seco',
        AmbientWet: 'Húmedo (vapor)',
        AmbientDryShort: 'seco',
        AmbientWetShort: 'húmedo',
        AmbientNote:
          'El vapor aporta mucho más oxidante, así que la oxidación húmeda va varias ' +
          'veces más rápida.',
        SectionDopant: 'Dopante',
        DopantBoron: 'Boro',
        DopantPhosphorus: 'Fósforo',
        DopantArsenic: 'Arsénico',
        DopantNote:
          'Un átomo más pesado difunde más despacio para el mismo calor, y por eso se ' +
          'usa arsénico donde la unión tiene que quedarse superficial y boro donde ' +
          'no.',
        SectionRecipe: 'Receta',
        SectionStored: 'Recetas guardadas',
        SectionShift: 'Turno',
        NoSteps:
          'Aún no hay pasos posteriores. La unión de abajo es lo que produjeron la ' +
          'implantación y su propio recocido.',
        Remove: 'quitar',
        StepContact: 'Recocido de contacto, 450 °C, 30 min',
        StepInterlayer: 'Óxido intermedio, 700 °C, 60 min',
        StepReflow: 'Reflujo, 900 °C, 20 min',
        StepSecondGate: 'Segundo óxido de puerta, 1050 °C, 30 min',
        StepOxidation: 'oxidación',
        StepAnneal: 'recocido',
        TileOxide: 'Espesor del óxido',
        TileRegime: 'Régimen de crecimiento',
        RegimeLinear: 'lineal',
        RegimeMixed: 'mixto',
        RegimeParabolic: 'parabólico',
        TileJunction: 'Profundidad de unión',
        NoteBefore: '{{nm}} nm antes de los pasos posteriores',
        NoteNoSteps: 'sin pasos posteriores',
        TileSheetR: 'Resistencia de capa',
        TileActive: 'Fracción activa',
        NoteLattice: 'dopante en sitios de la red',
        TilePeak: 'Concentración pico',
        TileBudget: 'Presupuesto térmico',
        NoteEquivalent: 'segundos equivalentes a 1000 °C',
        TileDt: 'D·t total',
        TileDownstream: 'Pasos posteriores',
        ChartProfileTitle: 'Concentración de dopante frente a profundidad (escala log)',
        ChartDepth: 'profundidad (nm)',
        SeriesBefore: 'antes de los pasos posteriores',
        SeriesAsProcessed: 'tal como se procesó',
        AnnSubstrate: 'dopado del sustrato',
        AnnJunction: 'unión',
        ChartOxideTitle: 'Espesor del óxido frente al tiempo a {{temp}} °C ({{ambient}})',
        ChartTime: 'tiempo (min)',
        ChartThickness: 'espesor (nm)',
        SeriesOxide: 'óxido',
        AnnYourTime: 'tu tiempo',
        ChartBudgetTitle:
          'Duración del paso — pero es la temperatura la que realmente gasta el ' +
          'presupuesto',
        ChartStepIndex: 'índice de paso',
        ChartSeconds: 'segundos',
        SeriesDuration: 'duración',
        AssistantBlurb:
          'Hazme una pregunta y diseñaré un experimento, lo ejecutaré contra este ' +
          'solucionador e informaré de lo que realmente salió — y luego te devolveré ' +
          'una pregunta más afilada.',
        AssistantLead:
          'Cada paso caliente del flujo gasta de una misma cuenta. Pregúntame cuánto ' +
          'cuesta un paso, o por qué se movió una unión sin que nadie tocara la ' +
          'implantación.',
      },
      gowning: {
        AirflowWafer: 'en la oblea',
        AirflowWall: 'monitor de pared',
        AirflowRatio: 'producto/pared',
        SectionAirflow: 'Las dos salas, vistas (flujo de aire y dispersión)',
        AirflowNote:
          'Un corte de la bahía: filtros en el techo empujando aire hacia abajo a la velocidad de ' +
          'diseño, un operario enbatado de pie en él, producto abierto en el equipo a su lado, y el ' +
          'suelo elevado llevándose el aire. El color es lo que el operario desprende. Fíjese a dónde ' +
          'va. Aquí no hay pluma térmica, y no debe haberla: un cuerpo caliente puede elevar aire a un ' +
          'cuarto de metro por segundo, y los filtros van a casi el doble. Ser lo bastante rápido para ' +
          'dominar a las personas que trabajan debajo es exactamente para lo que sirven los 0,45 m/s. ' +
          'Lo que el operario hace en cambio es estorbar: el aire tiene que rodear un cuerpo, y la ' +
          'estela de ese obstáculo es lo que lleva lo desprendido al producto. Si ve la nube subir por ' +
          'encima de la cabeza, eso es la estela dándose la vuelta, no calor ascendiendo: el flujo ' +
          'justo encima de un cuerpo en corriente descendente se invierte cada cierto tiempo al ' +
          'desprenderse vórtices — medido aquí a 0,28 m/s hacia arriba en un instante y 0,19 m/s hacia ' +
          'abajo al siguiente. Nada en esta bahía está más caliente que el resto, así que no hay nada ' +
          'que lo eleve. Ahora lea las dos sondas juntas. El monitor de pared no lee casi nada mientras ' +
          'el producto junto al operario se contamina: esa es toda la objeción del módulo contra él. Y ' +
          'subir el flujo descendente hace que el producto lea más, no menos, porque el aire es lo que ' +
          'arrastra lo desprendido hacia abajo y el equipo lo desvía de lado sobre la oblea abierta. El ' +
          'remedio es colocarse en otro sitio, no más aire.',
        SecondaryLabel: 'Recuperación tras un percance',
        ToolLabel: 'GOWN-00 Esclusa de vestimenta y bahía de protocolo',
        fault: {
          starvedMakeup: {
            Evidence: [
              'Presión de la bahía negativa frente a un mínimo de +5 Pa.',
              'Recuento en la zona de trabajo fuera de clase.',
              'Filtros, prendas, enclavamiento y tiempo de recuperación nominales: el aire se ' +
                'renueva; lo que llega es el aire del pasillo.',
              'Todas las bahías de la planta leen lo mismo, y el panel de servicios de ' +
                'instalaciones muestra el aire de reposición al 55% del diseño con la sala en negativo.',
            ],
            Truth:
              'La unidad de aire de reposición del subfab tiene los prefiltros colmatados y entrega ' +
              'alrededor del 55% del aire exterior de diseño. Esta bahía renueva su aire a pleno ' +
              'caudal — recirculadores, filtros de techo y prendas están bien — pero el aire de ' +
              'reposición es contra lo que se equilibran los extractores, y perdido ese equilibrio ' +
              'toda la sala limpia ha caído por debajo del pasillo. Todas las bahías leen negativo ' +
              'a la vez y respiran aire del pasillo por cada rendija. Perseguir la extracción de ' +
              'esta bahía no encuentra nada, porque nada cambió aquí; la cascada es del edificio, y ' +
              'solo el edificio puede devolverla.',
            Complaint:
              'Recuentos altos en todas las bahías a la vez, y todas leen negativo frente al ' +
              'pasillo. No se puso en marcha nada; nadie tocó una extracción.',
            Label: 'Toda la sala limpia en negativo: aire de reposición insuficiente',
          },
          starvedAir: {
            Evidence: [
              'Tiempo de recuperación muy por encima del límite de 0,85 minutos.',
              'La caída de presión del banco de filtros está cómodamente dentro de su ' +
                'límite: el aire no está bloqueado, simplemente no llega.',
              'El panel de servicios de instalaciones muestra el aire entregado al 55% del ' +
                'diseño.',
            ],
            Truth:
              'Dos de las cuatro manejadoras de recirculación tienen las correas patinando ' +
              'y la bahía recibe alrededor del 55% del caudal de diseño. Nada en esta sala ' +
              'está roto: los filtros están a media vida, la cascada de presión se ' +
              'mantiene, las batas están frescas. El número que lo delata es la ' +
              'RECUPERACIÓN: cuánto tarda la bahía en tragarse un alboroto lo fijan los ' +
              'cambios de aire y nada más, así que una recuperación lenta con un banco de ' +
              'filtros limpio es el edificio, no la sala. Cambiar los filtros de techo ' +
              'cuesta doce horas y no recupera casi nada.',
            Complaint:
              'La bahía lee más sucia con el mismo personal y tarda visiblemente más en ' +
              'volver tras un evento de puerta. Los filtros de techo se cambiaron el mes ' +
              'pasado.',
            Label: 'Bahía privada de aire por las manejadoras generales',
          },
          defeatedInterlock: {
            Evidence: [
              'El enclavamiento lee ANULADO.',
              'Conteo en zona de trabajo fuera de clase.',
              'Todas las demás lecturas son nominales, y los conteos son episódicos y no ' +
                'sostenidos.',
            ],
            Truth: 'Alguien ha puesto cinta al enclavamiento de la esclusa para que ambas ' +
              'puertas puedan estar abiertas a la vez, porque el bloqueo estaba ' +
              'retrasando una instalación de máquina. Cada vez que lo hacen, la bahía ' +
              'queda brevemente conectada al pasillo. Las ráfagas SON la firma: un fallo ' +
              'continuo no va y viene, y ninguna ventilación arregla una puerta que se ' +
              'mantiene abierta.',
            Complaint: 'Los conteos suben a ráfagas durante el día en vez de quedarse altos. Todo ' +
              'lo medible está en especificación entre las ráfagas.',
            Label: 'Enclavamiento de esclusa anulado',
          },
          wornGarments: {
            Evidence: [
              'Ciclos de traje pasados del límite de 30.',
              'Conteo en zona de trabajo fuera de clase.',
              'Caída de presión de filtros, presión de bahía y tiempo de recuperación, ' +
                'todos nominales.',
            ],
            Truth: 'Esta bahía está sacando de un lote de trajes en su ciclo de lavado 51. La ' +
              'tela se abre al lavarse, y un traje que se ha abierto deja de ser un ' +
              'filtro — pasa varias veces lo que uno fresco. Todo lo mecánico de la bahía ' +
              'está bien.',
            Complaint: 'Conteos arriba en una sola bahía, y la ventilación comprueba perfecta.',
            Label: 'Lote de trajes pasado de ciclos de lavado',
          },
          negativePressure: {
            Evidence: [
              'Presión de bahía a −14 Pa contra un suelo de +5 Pa.',
              'Conteo en zona de trabajo fuera de clase.',
              'Filtros, trajes y enclavamiento todos nominales — no es un problema de ' +
                'bahía, es un problema de edificio.',
            ],
            Truth: 'El nuevo extractor ha llevado la bahía a −14 Pa, así que aspira aire del ' +
              'pasillo por cada rendija y pasacables en vez de empujar aire limpio hacia ' +
              'fuera. La bahía ya no es la sala más limpia en su propia frontera, que es ' +
              'el principio entero sobre el que se construye una sala limpia.',
            Complaint: 'Conteos de partículas arriba en toda la bahía desde que se comisionó el ' +
              'extractor de una máquina nueva al lado.',
            Label: 'Bahía en presión negativa',
          },
          loadedFilters: {
            Evidence: [
              'Caída de presión de filtros pasada del límite de 220 Pa.',
              'Tiempo de recuperación muy por encima del límite de 0,85 min.',
              'Presurización, trajes y enclavamiento están todos bien.',
            ],
            Truth: 'Los bancos de filtros están cargados y los ventiladores no pueden empujar ' +
              'el aire de antes, así que la bahía corre muy por debajo de sus ' +
              'renovaciones de diseño. El estado ESTACIONARIO apenas se mueve — ' +
              'generación entre ventilación no es muy sensible cuando la generación es ' +
              'baja — y el tiempo de RECUPERACIÓN se mueve muchísimo, porque ese lo fija ' +
              'la ventilación sola. Una bahía que se recupera despacio es una bahía que ' +
              'está sucia más tiempo tras cada perturbación.',
            Complaint: 'La bahía tarda una eternidad en asentarse tras abrir una máquina. El ' +
              'monitor de pared sigue en clase y nadie puede señalar nada mal.',
            Label: 'Bancos de filtros cargados',
          },
          gownHealthy: {
            Evidence: [
              'Cada lectura del banco es nominal.',
              'El conteo en zona de trabajo está dentro del límite de clase.',
            ],
            Truth: 'No hay nada mal. La bahía sostiene la clase en el producto igual que en la ' +
              'pared.',
            Complaint: 'Recorrido de rutina. El monitor de pared lee en clase.',
            Label: 'Nada mal',
          },
        },
        garment: {
          mask: 'Mascarilla',
          gloves: 'Guantes',
          boots: 'Botas',
          hood: 'Capucha',
          coverall: 'Mono',
        },
        SectionGowning: 'Vestimenta',
        NoteOverMinOpenCln09: 'más de {{a}} min abierto — CLN-09 tiene que quitarlas',
        NoteEffectiveAirChangesH: '{{a}} renovaciones de aire efectivas/h',
        NotePeopleAtActivity: '{{a}} personas al {{b}}% de actividad',
        NoteOf5Garments: '{{a}} de 5 prendas{{b}}',
        NoteIsoAtMLimit: 'ISO {{a}} a {{b}} µm — límite {{c}}',
        SectionAChainDoesNotAverage: 'Una cadena no promedia',
        NoteTheGarmentsMultiplyLeavingThe: 'Los trajes multiplican. Dejarse la capucha no cuesta un quinto de la ' +
          'protección, cuesta el factor entero que la capucha aportaba — y el monitor ' +
          'de sala en la pared apenas lo notará, porque lo que ve la OBLEA lo domina ' +
          'la persona más cercana, no la media de la bahía. No puedes ventilarte para ' +
          'salir de un fallo de vestimenta.',
        SectionStoredScenarios: 'Escenarios almacenados',
        SectionBayState: 'Estado de la bahía',
        SectionBayAndProtocol: 'Bahía y protocolo',
        TextExposeTheLotToThis: 'Exponer el lote a esta bahía',
        LabelCorrectOrder: 'Orden correcto',
        LabelOutOfOrder: 'Fuera de orden',
        LabelAtTheWafer: 'En la oblea',
        LabelRoomMonitor: 'Monitor de sala',
        NoteWhatTheWallReadsAnd: 'lo que lee la pared, y no es lo que ve el producto',
        LabelGarmentPenetration: 'Penetración del traje',
        LabelShedIntoTheBay: 'Emitido a la bahía',
        LabelRecoveryAfterUpset: 'Recuperación tras perturbación',
        LabelOntoTheWafer: 'Sobre la oblea',
        AssistantLead: 'Asistente de indagación GOWN-00',
        AssistantBlurb:
          'Pregunta por qué una bahía que marca dentro de clase puede seguir ' +
          'contaminando obleas, o cuánto vale una prenda en partículas. Cada ' +
          'respuesta es una ejecución contra esta bahía tal como está.',
        reading: {
          houseutilities: {
            Label: 'Llamar a instalaciones por el panel de servicios (0.5 h)',
          },
          recovery: {
            Label: 'Prueba de humo y tiempo de recuperación, bahía parada (2.0 h)',
          },
          workZone: {
            Label: 'Contador portátil en la zona de trabajo, una hora (1.0 h)',
          },
          interlock: {
            ValueBad: 'ANULADO',
            ValueOk: 'funcionando',
            Limit: 'funcionando',
            Label: 'Enclavamiento de la esclusa',
          },
          garmentCycles: {
            Label: 'Ciclos de lavado del lote de trajes',
          },
          pressure: {
            Label: 'Presión de la bahía contra el pasillo',
          },
          filterDp: {
            Label: 'Caída de presión del banco de filtros',
          },
        },
        aspect: {
          house: {
            Action: 'Dejar de cambiar cosas en la bahía y elevarlo a instalaciones',
            Label: 'Nada en esta sala — escalar a instalaciones',
          },
          interlock: {
            Action: 'Reparar el enclavamiento, y averiguar quién lo anuló y por qué',
            Label: 'Enclavamiento de la esclusa',
          },
          garments: {
            Action: 'Retirar el lote de trajes gastados y re-emitir de un ciclo de lavado nuevo',
            Label: 'Stock de trajes',
          },
          pressurisation: {
            Action: 'Encontrar qué está extrayendo de la bahía y restaurar la cascada de presión',
            Label: 'Presurización de la bahía',
          },
          filters: {
            Action: 'Cambiar los bancos de filtros cargados y re-equilibrar el techo',
            Label: 'Filtros de techo',
          },
        },
      },
      gas: {
        ValueNotNeeded: 'no hace falta',
        NoteTheAlarmArrivesAfter: 'la alarma llega cuando la bahía ya es peligrosa',
        NoteThePurgeAsSpecifiedLeaves: 'La purga tal como está especificada deja {{a}} ppm — a esta razón se ' +
          'necesitan {{b}} ciclos, no {{c}}. No abras el panel.',
        NoteThePurgeAsSpecifiedReaches: 'La purga tal como está especificada alcanza {{a}} ppm, que está bajo el ' +
          'límite. El panel puede abrirse.',
        VerdictImmediatelyDangerous: 'Inmediatamente peligroso para la vida o la salud',
        VerdictAboveTheExposureLimit: 'Sobre el límite de exposición',
        VerdictBelowTheExposureLimit: 'Bajo el límite de exposición',
        NoteAlarmsAtPpmToRespond: 'alarma a {{a}} ppm, {{b}} s para responder',
        ValueNoWarning: 'ninguno',
        NoteStaysBelowTheDangerous: 'se mantiene bajo el nivel peligroso',
        NoteImmediatelyDangerousAt: 'inmediatamente peligroso a los {{a}} s',
        NoteEveryNumberOnThis: 'Cada número de este panel puede responderse en papel antes de conectar un ' +
          'cilindro: cuántos ciclos de purga necesita una línea, a qué llega una fuga ' +
          'en estado estacionario, y cuánto tiempo compra realmente un detector. La ' +
          'purga es una serie geométrica, así que la CALIDAD del ciclo vence al ' +
          'número de ciclos — diez ciclos a diez-a-uno y diez a dos-a-uno toman el ' +
          'mismo tiempo y aterrizan a siete órdenes de magnitud de distancia.',
        ValueNever: 'nunca',
        gasNote: {
          ammonia: 'El único de esta lista que olerías mucho antes de que te hiciera daño — lo ' +
            'que crea malos hábitos.',
          chlorine: 'Más pesado que el aire, así que se acumula a nivel del suelo donde un ' +
            'detector de techo no lo verá.',
          hydrogenFluoride: 'El ion fluoruro persigue el calcio hasta el hueso. El efecto sistémico ' +
            'llega horas después.',
          phosphine: 'Pirofórica y agudamente tóxica a la vez, así que una fuga es a la vez ' +
            'incendio y envenenamiento.',
          arsine: 'Cinco partes por mil millones. Destruye los glóbulos rojos; el daño está ' +
            'hecho antes de los síntomas.',
          silane: 'Se enciende en aire, a veces con retardo — lo que deja que una fuga se ' +
            'acumule primero.',
        },
        gasLabel: {
          ammonia: 'Amoníaco (NH₃)',
          chlorine: 'Cloro (Cl₂)',
          hydrogenFluoride: 'Fluoruro de hidrógeno (HF)',
          phosphine: 'Fosfina (PH₃)',
          arsine: 'Arsina (AsH₃)',
          silane: 'Silano (SiH₄)',
        },
        NotePyrophoricIgnites: 'Pirofórico: se enciende en aire sin fuente de ignición.',
        SectionSpecies: 'Especie',
        SecondaryLabel: 'Lo que llega a la chimenea',
        ToolLabel: 'GAS-19 Búnker de gases y distribución química',
        NextFac06SolvesTheSame: 'FAC-06 resuelve la misma física de conductancia para vacío en vez de para ' +
          'suministro. La ecuación es idéntica y la consecuencia de equivocarse no lo ' +
          'es — pregúntale qué le cuesta una línea previa restringida a una cámara de ' +
          'proceso.',
        TitleLineConductanceAgainstDiameter: 'Conductancia de línea frente a diámetro',
        LabelPyrophoric: 'Pirofórico',
        LabelConductance: 'Conductancia',
        HypMolecularFlowConductanceGoesAs: 'La conductancia en flujo molecular va como el cubo del diámetro e inversa ' +
          'con la longitud, así que la geometría de la línea debería dominar el ' +
          'suministro mucho más de lo que sugiere la intuición — un cambio modesto de ' +
          'diámetro debería ser un gran cambio de conductancia. Lo que llega a la ' +
          'chimenea debería depender del lavador y no de la línea en absoluto.',
        AskedWhatDoesTheDeliveryLine: '¿Qué hace la línea de suministro, y qué llega a la chimenea?',
        SumLineConductanceScrubberLoadingAnd: 'Conductancia de línea, carga del lavador, y lo que sale del edificio.',
        NextTheBayVentilationIsWhat: 'La ventilación de la bahía es lo que fija el estado estacionario. Si un ' +
          'ventilador corre a media velocidad y nadie lo ha notado, ¿cuál de estos ' +
          'números cambia primero — y algo en el panel lo mostraría antes que una ' +
          'fuga?',
        LabelNoWarning: 'sin aviso',
        AxisYSecondsFromAlarmToIdlh: 'segundos de alarma a IDLH',
        AxisXLeakRateSccm: 'tasa de fuga (sccm)',
        TitleWarningTimeAgainstLeakRate: 'Tiempo de aviso frente a tasa de fuga',
        LabelSeverity: 'Severidad',
        LabelSteadyState: 'Estado estacionario',
        LabelAlarmsAt: 'Alarma a',
        NoteAlarmToIdlh: 'de alarma a IDLH',
        HypADetectorAlarmsAtA: 'Un detector alarma a una concentración, y la bahía sigue llenándose ' +
          'mientras responde. La cantidad útil no es el nivel de alarma sino los ' +
          'SEGUNDOS entre alarmar y que la atmósfera se vuelva inmediatamente ' +
          'peligrosa. Si la fuga es lo bastante rápida, ese intervalo se encoge a ' +
          'nada y el detector se vuelve un registro de lo ocurrido en vez de un ' +
          'aviso.',
        AskedDoesTheDetectorGiveEnough: '¿El detector da suficiente aviso como para valer la pena?',
        SumAlarmThresholdResponseTimeAnd: 'Umbral de alarma, tiempo de respuesta, y los segundos entre la alarma y el ' +
          'IDLH.',
        NextThePurgeNitrogenCarriesIts: 'El nitrógeno de purga lleva su propia humedad, y una purga húmeda hace ' +
          'sólidos en la línea en vez de limpiarla. ¿Qué le haría eso a la curva de ' +
          'arriba — y el número de ciclos del procedimiento seguiría siendo el ' +
          'correcto?',
        TitleResidualConcentrationAgainstPurgeCycles: 'Concentración residual frente a ciclos de purga',
        LabelPurgeTime: 'Tiempo de purga',
        LabelSetTo: 'Ajustado a',
        LabelCyclesNeeded: 'Ciclos necesarios',
        LabelResidual: 'Residual',
        HypEachPurgeCycleReplacesA: 'Cada ciclo de purga reemplaza una FRACCIÓN fija de lo que hay en la línea, ' +
          'no una cantidad fija. Eso hace la concentración residual exponencial en el ' +
          'número de ciclos — así que ir de 100% a 1% toma más o menos tantos ciclos ' +
          'como ir de 1% a 0.01%, y la intuición de que "con unas pocas purgas basta" ' +
          'estará equivocada por órdenes de magnitud.',
        AskedHowManyPurgeCyclesDoes: '¿Cuántos ciclos de purga necesita realmente esta línea?',
        SumCyclePurgingIsExponentialAnd: 'El purgado por ciclos es exponencial — y la última década de concentración ' +
          'es la lenta.',
        fault: {
          weakExtract: {
            Evidence: [
              'Caudal de extracción al 35% contra un suelo del 90%.',
              'El estacionario de la liberación modelada es mucho más alto que el diseño.',
              'La purga, la válvula y el lavador son todos nominales.',
            ],
            Truth: 'El ventilador de extracción entrega el 35% del caudal de diseño. La ' +
              'concentración estacionaria es tasa de liberación entre ventilación, así ' +
              'que un tercio del caudal es el triple de concentración para cualquier fuga ' +
              'del búnker — y también alarga el tiempo de despejarla, que es la mitad que ' +
              'nadie calcula.',
            Complaint: 'La bahía huele levemente a disolvente los días cálidos.',
            Label: 'Extracción de bahía bajo diseño',
          },
          spentScrubber: {
            Evidence: [
              'Carga del medio al 95% contra un límite del 60%.',
              'Muestra de chimenea por encima del límite.',
              'El modelo de liberación en la bahía está completamente sin cambios.',
            ],
            Truth: 'El medio del lavador está al 95% de carga. El abatimiento no falla cerrado ' +
              '— se degrada, así que la eficiencia de destrucción cae y lo que no se ' +
              'destruye sube por la chimenea. Cada lectura dentro de la bahía está sin ' +
              'cambios, porque nada dentro de la bahía cambió: este fallo está ' +
              'enteramente aguas abajo de la gente a la que protege, y solo una muestra ' +
              'de chimenea lo ve.',
            Complaint: 'Medio ambiente ha cuestionado una lectura de chimenea. Todo dentro de la ' +
              'bahía es normal y siempre lo ha sido.',
            Label: 'Medio de abatimiento agotado',
          },
          seizedEfv: {
            Evidence: [
              'La prueba de función de la válvula lee NO CERRÓ.',
              'La liberación modelada pasa de largo el IDLH.',
              'Humedad, medio y extracción, todos nominales — nada está mal con el gas ni ' +
                'con la bahía.',
            ],
            Truth: 'La válvula de exceso de flujo falló su última prueba de función y se firmó ' +
              'como superada. Es el tope mecánico que convierte una línea cortada en una ' +
              'fuga manejable en vez de una liberación de cilindro completa — así que hoy ' +
              'no hay nada mal, y el tamaño del accidente que este búnker puede tener se ' +
              'ha multiplicado por ocho. Este es el fallo que solo se descubre el día que ' +
              'importa.',
            Complaint: 'Revisión de rutina antes de un cambio de cilindro. Nada está mal y nada ha ' +
              'pasado.',
            Label: 'Válvula de exceso de flujo agarrotada abierta',
          },
          wetHeader: {
            Evidence: [
              'Humedad de cabecera a 24 ppm contra un límite de 1 ppm.',
              'El análisis de la línea tras la purga está muy por encima del límite de ' +
                'exposición.',
              'La válvula de exceso de flujo, el lavador y la extracción están bien — ' +
                'esto es la purga, y solo la purga.',
            ],
            Truth: 'La cabecera de nitrógeno está a 24 ppm de humedad. El agua se adsorbe en ' +
              'la pared de la línea y desorbe despacio, así que cada vaciado ya no ' +
              'alcanza el vacío que uno seco alcanzaría y la razón efectiva por ciclo se ' +
              'derrumba — y esa razón es por lo que la purga multiplica, diez veces ' +
              'seguidas. Diez ciclos en consigna dejaron cuatro órdenes más de arsina en ' +
              'la línea de lo que el procedimiento asume. El panel contó ciclos, que no ' +
              'es lo mismo que contar moléculas.',
            Complaint: 'Un técnico reportó un olor a algo en el último cambio de cilindro de ' +
              'arsina. El panel registró diez ciclos de purga completados, que es el ' +
              'procedimiento cualificado.',
            Label: 'Nitrógeno de purga húmedo',
          },
          gasHealthy: {
            Evidence: [
              'Cada lectura del banco es nominal.',
              'La purga verifica por debajo del límite de exposición.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es proceder con el cambio.',
            Complaint: 'Revisión previa al cambio de cilindro. Todo en el panel está en verde.',
            Label: 'Nada mal',
          },
        },
        TextPpmAgainstAPpmLimit: '{{a}} ppm frente a un límite de {{b}} ppm',
        NoteSccmIntoMAtAch: '{{a}} sccm en {{b}} m³ a {{c}} ACH',
        NoteCyclesAreNeeded: '{{a}} ciclos — se necesitan {{b}}',
        NoteImmediatelyDangerousAtPpm: 'inmediatamente peligroso a {{a}} ppm',
        SectionArithmeticYouDoBeforeYou: 'Aritmética que haces antes de abrir nada',
        SectionStoredScenarios: 'Escenarios almacenados',
        SectionBunkerState: 'Estado del búnker',
        SectionAssessment: 'Evaluación',
        SectionPanelAndBay: 'Panel y bahía',
        LabelExposureLimit: 'Límite de exposición',
        LabelResidualAfterPurge: 'Residual tras la purga',
        LabelLeakSteadyState: 'Fuga, estado estacionario',
        LabelToExposureLimit: 'Hasta el límite de exposición',
        LabelWarningTheDetectorBuys: 'Aviso que compra el detector',
        LabelReachingTheStack: 'Llegando a la chimenea',
        NoteAfterAbatementAndAbatementDegrades: 'tras el abatimiento — y el abatimiento se degrada en silencio',
        TextSignOffThisBayAs: 'Firmar esta bahía como segura para abrir',
        AssistantLead: 'Asistente de indagación GAS-19',
        AssistantBlurb:
          'Pregunta cuántos ciclos de purga hacen falta de verdad, si el detector ' +
          'avisa a tiempo, o qué llega a la chimenea. Cada respuesta es una ' +
          'evaluación ejecutada en este panel.',
        reading: {
          stackSample: {
            Label: 'Muestra y análisis de chimenea (5.0 h)',
          },
          releaseModel: {
            Limit: '< 3 ppm, aviso > 0 s',
            Label: 'Prueba de gas trazador, bahía evacuada (4.0 h)',
          },
          purgeVerify: {
            Label: 'Analizar la línea tras la purga, analizador portátil (3.0 h)',
          },
          extractFlow: {
            Label: 'Caudal de extracción de la bahía',
          },
          mediaHours: {
            Label: 'Carga del medio del lavador',
          },
          efvTest: {
            ValueBad: 'NO CERRÓ',
            ValueOk: 'aprobada',
            Limit: 'superada',
            Label: 'Válvula de exceso de flujo, última prueba',
          },
          moisture: {
            Label: 'Humedad del nitrógeno de purga',
          },
        },
        aspect: {
          ventilation: {
            Action: 'Restaurar la extracción al caudal de diseño',
            Label: 'Ventilación de la bahía',
          },
          scrubber: {
            Action: 'Cambiar el medio del lavador y re-certificar la eficiencia en chimenea',
            Label: 'Medio de abatimiento',
          },
          excessFlow: {
            Action: 'Cambiar la válvula y probarla antes de reconectar el cilindro',
            Label: 'Válvula de exceso de flujo',
          },
          purgeGas: {
            Action:
              'Cambiar el secador de la cabecera, purgar la distribución y re-certificar ' +
              'a humedad',
            Label: 'Calidad del nitrógeno de purga',
          },
        },
      },
      crystal: {
        LatticeStatus: '{{a}} átomos · medido {{b}} K · {{c}} ps',
        LatticeTemp: 'Temperatura del cristal',
        TraceTitle: 'Distancia al sitio de red — vibración térmica',
        TraceX: 'tiempo simulado (ps)',
        TraceY: 'desplazamiento (Å)',
        TraceHost: 'Un átomo de silicio',
        TraceSiRef: 'rms del Si a 300 K, Debye–Waller por rayos X',
        TraceStatus: 'rms desde el sitio en los últimos {{a}} ps: {{b}} Å · enlace 2,35 Å',
        TraceIdle: 'Pulse Ejecutar: la distancia rms al sitio se mide sobre los últimos {{a}} ps de la corrida',
        LatticeReset: 'Restablecer',
        LatticePause: 'Pausar',
        LatticeRun: 'Ejecutar',
        LatticeFailed: 'El motor de dinámica molecular no se pudo cargar.',
        LatticeLoading: 'Cargando el motor de dinámica molecular…',
        NoteLattice: 'El lingote, átomo a átomo: una red de silicio cúbica de diamante — cada ' +
          'átomo enlazado covalentemente a cuatro vecinos — vibrando en vivo bajo el ' +
          'campo de fuerzas Stillinger–Weber. Nada fija los átomos en su ' +
          'sitio; el cristal mantiene su forma porque esta disposición es el estado ' +
          'fundamental del potencial. Arrastre para rotar, desplace la rueda para ' +
          'acercar. Caliéntelo hacia el punto de fusión (1687 K) y observe cómo la ' +
          'vibración térmica difumina el orden que tanto le costó ganar al horno de ' +
          'crecimiento.',
        NoteBands: 'Lo que esa disposición hace a los electrones, según el modelo de enlace fuerte de AIMS: no es una curva memorizada. La franja sombreada es la banda prohibida: energías que ningún electrón de este cristal puede tener. El mínimo de conducción del silicio no está encima de su máximo de valencia, así que la línea discontinua se inclina: cruzar la banda prohibida exige un cambio de momento además de energía, algo que un fotón no puede aportar por sí solo. Por eso el silicio da detectores excelentes y láseres pésimos.',
        BandsAxisEnergy: 'energía sobre la banda de valencia (eV)',
        BandsLoading: 'Resolviendo el modelo de enlace fuerte…',
        BandsFailed: 'El modelo de bandas no está disponible para este cristal.',
        BandsGap: 'brecha {{ev}} eV',
        BandsDirect: 'directa en {{at}}',
        BandsIndirect: 'indirecta, {{from}} → {{to}}',
        BandsMeasured: 'medida {{ev}} eV',
        SectionLattice: 'El producto — una red cristalina de silicio (dinámica molecular)',
        NoteSpecificationBand: 'Banda de especificación {{a}} – {{b}} Ω·cm. {{c}} de {{d}} rebanadas están ' +
          'dentro.',
        LabelGrowthTime: 'Tiempo de crecimiento',
        LabelPullRate: 'Velocidad de tirado',
        SecondaryLabel: 'Velocidad de tirado frente al techo',
        ToolLabel: 'CZ-07 Estirador de cristales Czochralski',
        NextTheFurnaceAtThrm04: 'El horno de THRM-04 precipita este oxígeno en el volumen a propósito. ' +
          'Pregúntale qué le pasa a una oblea que llegó con demasiado poco.',
        AxisYOxygenCm: 'oxígeno (nm⁻³)',
        AxisXCrucibleRotationRpm: 'rotación del crisol (rpm)',
        TitleOxygenAgainstCrucibleRotation: 'Oxígeno frente a rotación del crisol',
        NoteAcrossTheSweep: 'a lo largo del barrido',
        LabelRotationRange: 'Rango de rotación',
        LabelWith03TMagnet: 'Con imán de 0.3 T',
        LabelOxygenAsSet: 'Oxígeno según ajuste',
        HypTheMeltSitsInA: 'El fundido está en un crisol de sílice, así que el crisol se está ' +
          'disolviendo en él todo el tiempo. Si esa es la fuente, entonces cualquier ' +
          'cosa que agite el fundido con más fuerza debería llevar más oxígeno a la ' +
          'interfaz — y cualquier cosa que amortigüe la convección debería llevar ' +
          'menos.',
        AskedWhatPutsOxygenInThe: '¿Qué mete oxígeno en el cristal, y qué puede moverlo?',
        SumCrucibleDissolutionMeltConvectionAnd: 'Disolución del crisol, convección del fundido, y por qué el oxígeno se ' +
          'especifica en vez de eliminarse.',
        NextVGHasTwoHalves: 'v/G tiene dos mitades y solo has movido una. ¿Qué le pasa a la ventana si ' +
          'la zona caliente cambia el gradiente en su lugar — y cuál de las dos es ' +
          'más fácil de mantener estable durante treinta horas?',
        LabelPerfectSiliconBoundary: 'frontera de silicio perfecto',
        AxisYVGCmMinK: 'v/G  cm²/(min·K)',
        AxisXPullRateMmMin: 'velocidad de tirado (mm/min)',
        TitleVoronkovRatioAgainstPullRate: 'Cociente de Voronkov frente a velocidad de tirado',
        NoteLatentHeat: 'calor latente',
        LabelRateCeiling: 'Techo de velocidad',
        LabelVGAsSet: 'v/G según ajuste',
        HypIfPullRateMatteredOnly: 'Si la velocidad de tirado importara solo para el rendimiento, cada ' +
          'velocidad aquí daría el mismo cristal más o menos rápido. Si Voronkov ' +
          'tiene razón, el cociente v/G cruza una frontera en algún punto de este ' +
          'rango y el defecto puntual dominante cambia de especie — lo cual es un ' +
          'cambio en lo que el cristal ES, no en cuán rápido se hizo.',
        AskedWhatDoesPullRateActually: '¿Qué fija realmente la velocidad de tirado, además de cuánto dura el ' +
          'crecimiento?',
        NextEveryWaferInABox: 'Cada oblea en una caja vino de un lugar distinto de esta curva. Si un ' +
          'proceso aguas abajo es sensible a la resistividad del sustrato, ¿en qué ' +
          'convierte eso a un "lote"?',
        LabelSpecLow: 'límite inferior de spec',
        AxisXDistanceFromSeedMm: 'distancia desde la semilla (mm)',
        NoteSpread: 'Dispersión',
        DesignOneGrowthAsSetThen: 'Un crecimiento tal como está ajustado, y luego ese mismo crecimiento ' +
          'juzgado contra tres especificaciones de resistividad distintas. Nada del ' +
          'cristal cambia entre esas tres: solo cuánto de él aceptaría un cliente.',
        HypBoronIsMoreSolubleIn: 'El boro es más soluble en el silicio líquido que en el sólido, así que ' +
          'cada milímetro que congela deja el fundido un poco más rico de lo que lo ' +
          'encontró. Si eso es lo que pasa, la cola debe estar más dopada que la ' +
          'semilla —resistividad más baja— y la brecha debería ensancharse hacia el ' +
          'final en vez de derivar al azar.',
        AskedWhyDoesResistivityChangeDown: '¿Por qué cambia la resistividad a lo largo del lingote, y qué cuesta eso?',
        SumSegregationDownTheIngotAnd: 'Segregación a lo largo del lingote, y cuánto de él te deja vender una ' +
          'especificación.',
        fault: {
          shakingLift: {
            Evidence: [
              'Vibración del izado al 55% contra un límite del 10%.',
              'La sonda de zona caliente está bien — el techo está donde debe.',
              'La calibración del calefactor es nominal.',
            ],
            Truth: 'Un rodamiento de la columna de izado se ha ido y el cristal se sacude ' +
              'mientras se retira. La estructura se pierde bien POR DEBAJO del techo ' +
              'térmico, y por eso la sonda de zona caliente vuelve limpia y la corrida ' +
              'falla igual.',
            Complaint: 'Estructura perdida dos veces este mes, ambas en el cuerpo, y la zona ' +
              'caliente es nueva.',
            Label: 'Izado de semilla vibrando',
          },
          heaterDrift: {
            Evidence: [
              'Calibración del calefactor al 78% contra una ventana de ±2%.',
              'La sonda de zona caliente pone el techo por debajo de la velocidad de tiro ' +
                'de la receta.',
              'La vibración de la semilla es nominal, lo que descarta la vía mecánica de ' +
                'perder estructura.',
            ],
            Truth: 'El calefactor entrega el 78% de su potencia calibrada, así que el ' +
              'gradiente axial en la interfaz es mucho más suave de lo que asume la ' +
              'receta. El gradiente es lo que se lleva el calor latente, así que la ' +
              'velocidad máxima de tiro ha caído por debajo de una consigna que no cambió ' +
              '— la receta ahora pide más de lo que la zona caliente puede dar.',
            Complaint: 'Dos de las últimas cuatro corridas perdieron estructura cerca del hombro. ' +
              'La receta no se toca en un año.',
            Label: 'Calefactor fuera de calibración',
          },
          restrictedArgon: {
            Evidence: [
              'Flujo de argón al 45% contra un suelo del 90%.',
              'Oxígeno FTIR alto.',
              'El crisol va por su primera corrida — esto no es un problema de consumible.',
            ],
            Truth: 'La línea de argón está restringida al 45% del flujo. El argón barre el ' +
              'monóxido de silicio de la superficie del fundido antes de que pueda ' +
              'disolverse de vuelta; sin él, el oxígeno regresa. Produce exactamente el ' +
              'número FTIR de un crisol agotado, y la lectura del crisol es lo que los ' +
              'distingue.',
            Complaint: 'El mismo oxígeno alto, y el crisol se cambió la semana pasada.',
            Label: 'Barrido de argón restringido',
          },
          spentCrucible: {
            Evidence: [
              'Corridas de este crisol pasadas del límite.',
              'Oxígeno FTIR por encima de la banda de 12 ×10¹⁷.',
              'El flujo de argón es nominal, lo que descarta la otra manera de que suba ' +
                'el oxígeno.',
            ],
            Truth: 'El crisol va por su quinta corrida. La sílice se disuelve en el fundido ' +
              'durante todo el tiro, y un crisol cansado suelta casi el doble que uno ' +
              'fresco — así que el oxígeno sube mientras cada consigna de la máquina se ' +
              'queda exactamente donde estaba. No se cambió nada, y precisamente por eso ' +
              'nadie miró aquí.',
            Complaint: 'Las obleas de los dos últimos lingotes están precipitando oxígeno en el ' +
              'horno. Dispositivos pregunta si cambió el horno de tiro. No cambió.',
            Label: 'Crisol pasado de corridas',
          },
          czHealthy: {
            Evidence: [
              'Cada lectura del banco es nominal.',
              'El oxígeno del último lingote estaba en banda.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es empezar la corrida.',
            Complaint: 'Revisión previa a un tiro de tres días. Todo lee normal.',
            Label: 'Nada mal',
          },
        },
        TextCm: '{{a}} Ω·cm',
        NoteLatentHeatCeilingMmMin: 'techo de calor latente {{a}} mm/min',
        NoteVG: 'v/G = {{a}}',
        NoteCmSeedToTail: '{{a}} → {{b}} Ω·cm, de semilla a cola',
        NoteOfMmGrown: 'de {{a}} mm crecidos — {{b}}%',
        SectionWhyAnIngotIsNot: 'Por qué un lingote no es un solo material',
        SegregationLead: 'El cristal toma solo una fracción k del dopante que contiene el fundido, así que se dopa más a lo largo de su longitud.',
        SegregationHelp: 'Por qué eso da un lingote más corto, no mejor',
        NoteDopantDoesNotPartitionEqually: 'El dopante no se reparte por igual entre sólido y líquido. El cristal toma ' +
          'una fracción k de lo que el fundido contiene y rechaza el resto, así que ' +
          'el fundido se enriquece mientras el lingote crece y el cristal queda más ' +
          'dopado a lo largo. La resistividad es una especificación, y la longitud de ' +
          'lingote que la cumple es el rendimiento de esta máquina — por eso ' +
          'estrechar la ventana no da al cliente un lingote mejor, le da uno más ' +
          'corto.',
        SectionPullerState: 'Estado del horno de tiro',
        SectionHotZone: 'Zona caliente — calor y flujo en vivo',
        NoteHotZone:
          'Una simulación de transferencia de calor en vivo de la sección transversal del horno: el crisol a la ' +
          'temperatura del calentador, el silicio fundido convectando por flotabilidad y el ' +
          'lingote llevando el calor hacia el portasemilla refrigerado. Las flechas son el ' +
          'flujo del fundido — la circulación que lleva el calor a la interfaz de crecimiento.',
        NoteHotZoneMagnet:
          'La perilla de campo magnético actúa también aquí. El silicio fundido conduce, así que ' +
          'un campo aplicado frena cualquier porción de fundido que se mueva — súbalo y verá ' +
          'apagarse las flechas. En eso consiste el Czochralski magnético: la circulación es la ' +
          'que arrastra el oxígeno del crisol de cuarzo hasta la interfaz de crecimiento, de modo ' +
          'que amortiguarla es como se crece silicio de bajo oxígeno. Un fundido frenado también ' +
          'deja de oscilar en temperatura, y eso es lo que mantiene las estrías de crecimiento ' +
          'fuera del cristal.',
        CzMagnet: 'imán',
        CzLoading: 'Cargando el motor de transferencia de calor…',
        CzFailed: 'El motor de transferencia de calor no se pudo cargar.',
        CzPause: 'Pausar',
        CzRun: 'Reanudar',
        CzReset: 'Reiniciar',
        CzClock: 't = {{s}} s',
        CzProbeMelt: 'fundido',
        CzProbeInterface: 'interfaz',
        CzProbeBoule: 'lingote',
        SectionResistivityDownTheIngot: 'Resistividad a lo largo del lingote',
        TextGrowTheIngotAndSend: 'Crecer el lingote y enviarlo a WPR-08',
        BandsAbout: 'La estructura de bandas de al lado: lo que esa disposición les hace a los electrones',
        LabelUsableIngot: 'Lingote útil',
        LabelResistivitySpread: 'Dispersión de resistividad',
        LabelDefectRegime: 'Régimen de defectos',
        LabelInterstitialOxygen: 'Oxígeno intersticial',
        NoteStrengthAndGetteringOrPrecipitates: 'resistencia y gettering, o precipitados',
        NoteOneCrystalStartToFinish: 'un cristal, de principio a fin',
        AssistantLead: 'Asistente de indagación CZ-07',
        AssistantBlurb:
          'Pregunta por la segregación a lo largo del lingote, a qué velocidad se ' +
          'puede tirar este cristal, o de dónde viene el oxígeno. Cada respuesta es ' +
          'un crecimiento ejecutado en este horno.',
        reading: {
          gradientProbe: {
            Label: 'Sonda de termopar de la zona caliente, abierta (5.0 h)',
          },
          oxygenCheck: {
            Label: 'Oxígeno intersticial FTIR en el lingote previo (3.0 h)',
          },
          vibration: {
            Label: 'Vibración del izado de semilla',
          },
          argonFlow: {
            Label: 'Flujo de argón',
          },
          heaterCal: {
            Label: 'Calibración de potencia del calefactor',
          },
          crucibleRuns: {
            Label: 'Corridas de este crisol',
          },
        },
        aspect: {
          seedLift: {
            Action: 'Cambiar rodamientos de la columna y re-equilibrarla',
            Label: 'Mecanismo de izado de la semilla',
          },
          argon: {
            Action: 'Despejar la restricción de la línea de argón y reajustar el flujo',
            Label: 'Barrido de argón',
          },
          heater: {
            Action:
              'Recalibrar el calefactor contra un pirómetro de referencia y re-mapear la ' +
              'zona caliente',
            Label: 'Calibración del calefactor',
          },
          crucible: {
            Action: 'Desechar el crisol y recargar. Tres días de producción, y es inevitable',
            Label: 'Crisol de sílice',
          },
        },
      },
      waferprep: {
        NoteSurfaceCaveats: 'Tres advertencias honestas. Esta es la superficie LIMPIA, la que ve un ' +
          'equipo de vacío tras un baño de HF o un horneado in situ — una oblea al ' +
          'aire se cubre de óxido nativo en minutos. La reconstrucción no es algo a ' +
          'lo que las obleas dediquen picosegundos; es sencillamente donde se sitúa ' +
          'una superficie limpia, y partir del corte ideal es la forma de ver qué se ' +
          'intercambia. Además, los dímeros reales se INCLINAN, con un átomo de cada ' +
          'pareja más alto que el otro, porque hay carga que se desplaza entre ellos ' +
          '— eso es estructura electrónica, y un campo de fuerzas clásico no lleva ' +
          'electrones, así que aquí las parejas quedan simétricas. El fondo de la ' +
          'lámina se mantiene fijo: esos átomos representan los 775 µm de oblea que ' +
          'hay debajo y no caben en la caja.',
        NoteSurfaceDimers: 'Un corte ideal no es lo que parece una superficie. Corta la red y cada ' +
          'átomo de la capa superior pierde dos de sus cuatro enlaces — son los ' +
          'rojos, y cada uno es un enlace roto sin adónde ir. Pulsa Ejecutar y ' +
          'observa: los vecinos se inclinan uno hacia otro y se emparejan, cambiando ' +
          'un enlace colgante cada uno por un enlace nuevo entre ellos. Las parejas ' +
          'son dímeros, se alinean en filas, y esa reconstrucción 2×1 es lo que lleva ' +
          'puesto de verdad toda oblea de Si(100) limpia. Nada de esto está ' +
          'guionizado: el potencial de Stillinger–Weber dice que la superficie ' +
          'emparejada tiene menos energía, y los átomos van allí. Nunca termina del ' +
          'todo: emparejarse es una decisión local, así que algunos átomos quedan ' +
          'varados cuando sus dos posibles parejas ya se emparejaron entre sí, y una ' +
          'superficie de Si(100) real lleva ese mismo defecto. Enfría la oblea a 150 ' +
          'K, reinicia, y se queda aún más lejos: los átomos ya no tienen energía ' +
          'para encontrar a su pareja.',
        SurfaceReset: 'Volver al corte ideal',
        SurfacePause: 'Pausar',
        SurfaceRun: 'Ejecutar',
        SurfaceTemp: 'Temperatura de la oblea',
        SurfaceLoading: 'Cargando el motor de dinámica molecular…',
        SurfaceFailed: 'No se pudo cargar el motor de dinámica molecular.',
        SurfaceChartTitle: 'Dímeros formados frente al tiempo',
        SurfaceChartX: 'tiempo simulado (ps)',
        SurfaceChartY: 'dímeros',
        SurfaceChartSeries: 'Dímeros',
        SurfaceChartAllSites: 'todos los sitios emparejados',
        SurfaceStatus: '{{a}}/{{b}} dímeros · {{c}} átomos aún sin pareja · medido {{d}} K · {{e}} ' +
          'ps',
        NoteSurface: 'La sierra corta el lingote perpendicularmente a la semilla, así que la ' +
          'muesca de la oblea declara qué plano cristalino es la superficie — y para ' +
          'la lógica CMOS ese plano es el (100), porque la interfaz (100)/SiO₂ tiene ' +
          'la menor densidad de trampas eléctricas y da la mayor movilidad de ' +
          'electrones en una capa de inversión. Esta es esa cara, cortada del mismo ' +
          'silicio cúbico tipo diamante que creció el tirador. Arrastrar con el botón ' +
          'izquierdo rota, la rueda acerca, arrastrar con el derecho desplaza.',
        SectionSurface: 'La cara que presenta: una superficie de Si(100) (dinámica molecular)',
        NoteFrontOnly: 'SOLO EL FRENTE — el dorso sigue aserrado',
        NoteBothFacesBalanced: 'ambas caras equilibradas',
        LabelBow: 'Bow',
        SecondaryLabel: 'Rayas',
        ToolLabel: 'WPR-08 Corte, lapeado y pulido',
        NextCmp11FlattensEachLayer: 'CMP-11 aplana cada capa a medida que se construye, y le entrega su propio ' +
          'presupuesto de foco al mismo escáner. ¿Cuál de los dos importa más para la ' +
          'décima capa de metal?',
        AxisYTtvM: 'TTV (µm)',
        AxisXPolishTimeMin: 'tiempo de pulido (min)',
        TitleTtvAndFlatnessYieldAgainst: 'TTV y rendimiento de planitud frente a tiempo de pulido',
        LabelRoughness: 'Rugosidad',
        LabelFlatnessYield: 'Rendimiento de planitud',
        HypTtvIsAThicknessVariation: 'El TTV es una variación de espesor y el pulido debería reducirlo. El bow ' +
          'no lo es — es una forma, y una forma viene de un desbalance entre las dos ' +
          'caras. Si eso es correcto, pulir más tiempo debería aplanar el TTV y hacer ' +
          'muy poco por el bow, y trabajar una sola cara debería empeorar el bow ' +
          'dramáticamente mientras el TTV apenas se entera.',
        AskedWhatSetsTheFlatnessOf: '¿Qué fija la planitud de la oblea terminada?',
        SumTtvBowAndWarpAnd: 'TTV, bow y warp, y el presupuesto de foco que la oblea le entrega a la ' +
          'litografía.',
        NextDice15GrindsThisWafer: 'DICE-15 esmerila esta oblea hasta un décimo de su espesor después de ' +
          'construidos los dispositivos, con la misma física y nada del margen. ' +
          'Pregúntale qué cuesta el daño allí.',
        AxisYDamageM: 'daño (µm)',
        AxisXLapRemovalPerSideM: 'remoción de lapeado por cara (µm)',
        TitleResidualDamageAndStrengthAgainst: 'Daño residual y resistencia frente a remoción de lapeado',
        NotePerWafer: 'por oblea',
        LabelScratches: 'Rayas',
        LabelStrength: 'Resistencia',
        LabelResidual: 'Residual',
        LabelSawDamage: 'Daño de sierra',
        HypAnAbrasiveCutLeavesCracks: 'Un corte abrasivo deja grietas bajo la superficie que creó, más o menos en ' +
          'proporción al tamaño del grano. Si es así, una oblea que se ve perfecta ' +
          'puede aún ser débil — y los pasos de remoción tras la sierra no son ' +
          'cosméticos, son estructurales.',
        AskedHowDeepDoesTheSaw: '¿Qué tan hondo llega el daño de la sierra, y qué cuesta quitarlo?',
        SumSubsurfaceDamageAndWhyThree: 'Daño subsuperficial, y por qué tres pasos siguen a la sierra en vez de uno.',
        NextTheGapBetweenCutAnd: 'La brecha entre "cortadas" y "enviadas" en ese gráfico es planitud y ' +
          'rotura. Pregúntame qué la fija.',
        AxisXWireDiameterM: 'diámetro del alambre (µm)',
        TitleWafersFromOneIngotAgainst: 'Obleas de un lingote, frente a diámetro del alambre',
        NoteAfterFlatnessAndBreakage: 'tras planitud y rotura',
        LabelIngotLost: 'Lingote perdido',
        HypEveryCutRemovesASlot: 'Cada corte quita una ranura tan ancha como el alambre más el abrasivo que ' +
          'lleva, y ese silicio se pierde. Si el kerf es comparable al espesor de la ' +
          'oblea, la pérdida debería ser enorme — no un error de redondeo sino una ' +
          'fracción grande de un cristal que tomó treinta horas crecer.',
        AskedHowMuchOfTheIngot: '¿Cuánto del lingote sale como oblea, y qué lo decide?',
        SumKerfLossWaferCountAnd: 'Pérdida de kerf, número de obleas, y el silicio que se va como lodo.',
        fault: {
          oversizeSlurry: {
            Evidence: [
              'Tamaño de slurry al 240% de la especificación del lote.',
              'Conteo de rayas muy por encima del límite con el mapa de planitud limpio.',
              'Cada lectura de sierra y almohadilla es nominal.',
            ],
            Truth: 'El lote de slurry se ha aglomerado y corre a 2,4× su tamaño de partícula ' +
              'especificado. Las partículas sobredimensionadas no afectan la planitud en ' +
              'absoluto — aran rayas profundas sueltas, y una raya es un defecto letal en ' +
              'una oblea por lo demás impecable. Es la reparación más barata de la ' +
              'máquina y es invisible en el eje en que compiten los otros tres fallos.',
            Complaint: 'Inspección está encontrando rayas largas en obleas terminadas. La planitud ' +
              'es perfecta en todas.',
            Label: 'Lote de slurry fuera de especificación de tamaño',
          },
          glazedPad: {
            Evidence: [
              'Horas de almohadilla pasadas del límite de 45 h.',
              'Remoción en la oblea monitor por debajo del suelo de 0,28 µm.',
              'Kerf y tensión, ambos nominales.',
            ],
            Truth: 'La almohadilla está vitrificada a las 76 horas. Una almohadilla ' +
              'vitrificada deja de transportar slurry y su tasa de remoción se derrumba — ' +
              'así que el pulido ni quita el espesor ni hace el aplanado del que el ' +
              'proceso dependía. La sierra es del todo inocente, y ambas lecturas de ' +
              'sierra lo dicen.',
            Complaint: 'Planitud marginal y las obleas salen algo gruesas.',
            Label: 'Almohadilla de pulido vitrificada',
          },
          slackWire: {
            Evidence: [
              'Tensión a 19 N contra una ventana de 26 – 30 N.',
              'Mapa de planitud muy fuera, y ancho de kerf perfectamente normal.',
              'Kilómetros de hilo dentro de límite — esto no es un consumible.',
            ],
            Truth: 'Un brazo de tensión lee bajo y el hilo corre a 19 N en vez de 28. Un hilo ' +
              'flojo deambula en el corte, así que la ranura es ondulada — la misma ' +
              'planitud rechazada que produce un hilo gastado, por una causa ' +
              'completamente distinta. El kerf aquí es NORMAL, porque el hilo está ' +
              'afilado; simplemente no lo están sujetando recto.',
            Complaint: 'Los mismos rechazos de planitud, y el hilo se cambió el lunes.',
            Label: 'Tensión del hilo baja',
          },
          wornWire: {
            Evidence: [
              'Kilómetros de hilo pasados del límite de 450 km.',
              'Ancho de kerf por encima de 185 µm.',
              'La tensión está en objetivo y la almohadilla dentro de sus horas.',
            ],
            Truth: 'El hilo va por 810 km. Un hilo gastado ha perdido su abrasivo y ara en vez ' +
              'de cortar, lo que ensancha el kerf y deja una ranura más ondulada — y la ' +
              'ondulación sobrevive al lapeado y al pulido como variación de espesor. La ' +
              'medida de kerf es lo que lo distingue de las otras dos maneras de perder ' +
              'planitud.',
            Complaint: 'La planitud se ha ido en los tres últimos lingotes y el cliente ha ' +
              'rechazado dos.',
            Label: 'Hilo de sierra pasado de longitud',
          },
          wprHealthy: {
            Evidence: [
              'Cada contador de consumible está dentro de límite.',
              'El mapa de planitud está bien bajo 1,5 µm.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lingote.',
            Complaint: 'Lingote de rutina. Conteo de obleas y planitud, ambos normales.',
            Label: 'Nada mal',
          },
        },
        NoteTtvTheScannerHasTo: 'TTV — el escáner tiene que imprimir sobre esto',
        NoteFromMTheSawLeft: 'desde los {{a}} µm que dejó la sierra',
        NoteMKerfKgOfCrystal: 'kerf de {{a}} µm — {{b}} kg de cristal',
        NotePassFlatnessAndHandling: '{{a}} pasan planitud y manejo',
        SectionTheOneThatIsGenuinely: 'La que es genuinamente contraintuitiva',
        BowLead: 'Una oblea con MÁS daño puede ser MÁS PLANA que una más limpia, si el daño está en ambas caras.',
        BowHelp: 'Por qué el alabeo no mide el daño',
        NoteADamagedLayerIsA: 'Una capa dañada es una capa tensionada, y la tensión equilibrada en ambas ' +
          'caras no dobla nada. Trabaja solo el frente y la cara trasera guarda todo ' +
          'lo que dejó la sierra, así que la oblea se comba — lo que significa que ' +
          'una oblea con MÁS daño puede ser MÁS PLANA que una más limpia, si el daño ' +
          'es simétrico. El combado no es una medida de daño. Por eso el lapeado y el ' +
          'pulido son a dos caras.',
        FacesBoth: 'Ambas caras trabajadas',
        FacesFront: 'Sólo la cara frontal',
        HistoryBareWafer: 'Oblea pulida desnuda, sin películas',
        HistoryStockBlank: 'Pieza en bruto de almacén — sin lingote de CZ-07 en la sierra.',
        SectionDoubleSidedProcessing: 'Procesado a dos caras',
        SectionLineState: 'Estado de la línea',
        TextSliceTheIngotAndStart: 'Cortar el lingote y arrancar una oblea por la línea',
        LabelWafersFromTheIngot: 'Obleas del lingote',
        LabelLostAsSlurry: 'Perdido como slurry',
        LabelResidualDamage: 'Daño residual',
        LabelBreakingStrength: 'Resistencia a rotura',
        NoteSetByTheWorstFlaw: 'fijada por el peor defecto que quedó en la superficie',
        LabelFlatness: 'Planitud',
        AssistantLead: 'Asistente de indagación WPR-08',
        AssistantBlurb:
          'Pregunta cuánto silicio se lleva la sierra, qué profundidad tiene el daño, ' +
          'o qué hace que una oblea se combe. Cada respuesta es una ejecución contra ' +
          'esta línea.',
        reading: {
          scratchCount: {
            Label: 'Escaneo de campo oscuro por rayas, 25 obleas (2.0 h)',
          },
          removalRate: {
            Label: 'Remoción de pulido en oblea monitor (1.0 h)',
          },
          kerfCheck: {
            Label: 'Ancho de kerf en sección de descarte (2.0 h)',
          },
          ttvMap: {
            Label: 'Mapa capacitivo de planitud, 10 obleas (1.5 h)',
          },
          slurrySize: {
            Label: 'Tamaño de partícula del slurry, contra especificación',
          },
          padHours: {
            Label: 'Horas de la almohadilla',
          },
          tension: {
            Label: 'Tensión del hilo',
          },
          wireKm: {
            Label: 'Kilómetros de hilo usados',
          },
        },
        aspect: {
          slurry: {
            Action: 'Cambiar el lote de slurry y comprobar la distribución de tamaño de partícula',
            Label: 'Slurry de pulido',
          },
          pad: {
            Action: 'Acondicionar la almohadilla, o cambiarla si está vitrificada',
            Label: 'Almohadilla de pulido',
          },
          tension: {
            Action: 'Reajustar los brazos de tensión y re-cero de las células de carga',
            Label: 'Tensión del hilo',
          },
          wire: {
            Action: 'Cambiar la bobina de hilo y re-cualificar el kerf en una sección de descarte',
            Label: 'Hilo de sierra',
          },
        },
      },
      inspection: {
        LeadTopOfDistribution: 'El equipo informa de la parte alta de la distribución de tamaños. No está averiado; es óptica.',
        NoteThisScanWouldReport: 'Este escaneo reportaría {{a}}/cm². Si eso se acerca a lo que la oblea ' +
          'lleva es una pregunta que solo el prober puede responder.',
        SecondaryLabel: 'Tasa de falsos',
        ToolLabel: 'INSP-13 Inspección de obleas con patrón',
        NextAYieldMapAtPrb: 'Un mapa de rendimiento en PRB-14 cubre cada die por definición. ¿En qué ' +
          'convierte eso al sort — un paso de inspección, o la única inspección de ' +
          'cobertura completa en la línea?',
        AxisXWaferCoverage: 'cobertura de la oblea',
        TitleScanTimeAgainstCoverage: 'Tiempo de escaneo frente a cobertura',
        LabelTimeSaved: 'Tiempo ahorrado',
        LabelAtFullCoverage: 'Con cobertura completa',
        LabelAt10Coverage: 'Con 10% de cobertura',
        HypADensityIsAPer: 'Una densidad es un número por área, así que un escaneo parcial debería ' +
          'estimarla casi tan bien como uno completo — el muestreo funciona para ' +
          'promedios. Lo que el muestreo no puede hacer es encontrar una firma ' +
          'LOCALIZADA, porque un cúmulo en una región no escaneada no aporta nada a ' +
          'la estimación ni al mapa.',
        AskedWhatDoesScanningLessOf: '¿Qué cuesta escanear menos de la oblea?',
        SumSampledCoverageScanTimeAnd: 'Cobertura muestreada, tiempo de escaneo, y lo que un escaneo parcial puede ' +
          'y no puede decirte.',
        NextPrb14MeasuresTheYield: 'PRB-14 mide el rendimiento que esta densidad de defectos predice. Si los ' +
          'dos discrepan, ¿cuál está mal — y a cuál le apostarías un lote?',
        LabelDesignRule: 'regla de diseño',
        AxisYDetectionLimitNm: 'límite de detección (nm)',
        AxisXPixelNm: 'píxel (nm)',
        TitleDetectionLimitAndScanTime: 'Límite de detección y tiempo de escaneo frente a tamaño de píxel',
        HypScatteredIntensityFallsExtremelyFast: 'La intensidad dispersada cae extremadamente rápido con el tamaño de ' +
          'partícula, así que por debajo de cierto diámetro un defecto devuelve menos ' +
          'luz que el piso de ruido por mucho que mires. Las distribuciones de tamaño ' +
          'de defectos son leyes de potencia, es decir, los pequeños son los ' +
          'numerosos. Si ambas cosas valen, la población invisible debería ser mucho ' +
          'mayor que la visible — y parte de ella aún lo bastante grande para matar ' +
          'un die.',
        AskedWhatCanThisRecipeNot: '¿Qué no puede ver esta receta, y algo de eso importa?',
        SumTheDetectionLimitAndWhy: 'El límite de detección, y por qué cada número de inspección es una ' +
          'subestimación.',
        NextNeitherEndOfThatCurve: 'Ningún extremo de esa curva lleva la captura al 100%. Pregúntame por qué — ' +
          'la respuesta no es el umbral, y es el más importante de los dos límites.',
        AxisXThreshold: 'umbral (σ)',
        TitleCaptureAndNuisanceAgainstThreshold: 'Captura y falsos frente a umbral',
        NoteWhatIsActuallyThere: 'lo que realmente hay',
        LabelReported: 'Reportado',
        LabelNuisance: 'Falsos',
        HypTheThresholdIsALevel: 'El umbral es un nivel por encima de un piso de ruido, y los defectos ' +
          'reales y el ruido de superficie están en el mismo eje. Bajarlo debe por ' +
          'tanto atrapar más de ambos. Si es así, no hay ajuste que sea simplemente ' +
          '"más sensible" — solo una elección sobre qué error cuesta más, y el coste ' +
          'del falso se paga en horas de revisión SEM.',
        AskedWhatDoesTurningTheDetection: '¿Qué compra realmente bajar el umbral de detección?',
        SumCaptureRateAgainstNuisanceRate: 'Tasa de captura frente a tasa de falsos, y las horas de revisión que la ' +
          'pagan.',
        fault: {
          roughIncoming: {
            Evidence: [
              'Rugosidad de entrada a 1,6 nm contra un límite de 0,5 nm.',
              'Velo, potencia de láser y vibración de platina, todos nominales.',
              'El efecto está en un solo producto — un fallo de máquina estaría en todos.',
            ],
            Truth: 'No hay nada mal con el inspector. Las obleas llegan a 1,6 nm RMS en vez de ' +
              '0,3 — un SC-1 demasiado agresivo en CLN-09 — y la rugosidad superficial es ' +
              'el suelo del que crece la población de molestos. Limpiar la óptica de una ' +
              'máquina cuya óptica está limpia cuesta cuatro horas y no arregla nada.',
            Complaint: 'Los conteos de molestos se han triplicado en un producto. Se está culpando ' +
              'al inspector.',
            Label: 'La oblea es rugosa, y la máquina está bien',
          },
          stageShake: {
            Evidence: [
              'Vibración de platina al 60% contra un límite del 10%.',
              'La tasa de captura está abajo mientras potencia de láser y velo son ambos ' +
                'normales.',
            ],
            Truth: 'Un aislador de aire ha perdido presión, así que la platina lleva la ' +
              'vibración del suelo a cada escaneo. La imagen se corre, lo que cuesta ' +
              'sensibilidad igual que una fuente débil — pero la lectura de vibración lo ' +
              'nombra directamente, y el láser está bien.',
            Complaint: 'Los escaneos tardan más y los mapas se ven corridos a lo largo de la ' +
              'dirección de barrido.',
            Label: 'Aislamiento de platina degradado',
          },
          hazedOptics: {
            Evidence: [
              'El velo lee 45% contra un límite del 10%.',
              'La tasa de molestos está muy por encima del límite de revisión del 40%.',
              'El monitor de potencia del láser es perfectamente normal.',
            ],
            Truth: 'El camino de colección está velado. Dispersa luz al detector, lo que sube ' +
              'el suelo de ruido Y cuesta sensibilidad — así que la máquina es a la vez ' +
              'más ciega y más ruidosa. Esa combinación es la firma: un láser débil hace ' +
              'lo primero sin lo segundo.',
            Complaint: 'Revisión está ahogada. La mitad de los sitios que miran no tienen nada, y ' +
              'la cola es de dos días.',
            Label: 'Óptica de colección contaminada',
          },
          tiredLaser: {
            Evidence: [
              'El monitor de potencia lee 55% contra un suelo del 85%.',
              'La tasa de captura en la oblea patrón se ha derrumbado.',
              'La tasa de molestos es NORMAL — una fuente débil cuesta sensibilidad sin ' +
                'añadir ruido, que es lo que la separa de una óptica velada.',
            ],
            Truth: 'El láser está al 55% de su potencia nominal. La señal dispersada va con la ' +
              'intensidad de iluminación, así que el límite de detección ha subido y los ' +
              'defectos más pequeños simplemente han dejado de reportarse. Los conteos ' +
              'cayeron porque la máquina se quedó ciega, no porque la fábrica se ' +
              'limpiara.',
            Complaint: 'Los conteos de defectos en todos los productos llevan quince días cayendo. ' +
              'La línea cree que la fábrica se volvió más limpia.',
            Label: 'Fuente de iluminación fallando',
          },
          inspHealthy: {
            Evidence: [
              'Cada lectura del banco es nominal.',
              'La tasa de captura está donde el patrón dice que debe estar.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lote.',
            Complaint: 'Inicio de turno de rutina. La máquina ha estado tranquila.',
            Label: 'Nada mal',
          },
        },
        NoteOfTheWafer: '{{a}}% de la oblea',
        NoteOfReviewsWasted: '{{a}} de {{b}} revisiones desperdiciadas',
        NoteRealNuisance: '{{a}} reales + {{b}} molestos',
        NoteNmLight: 'luz de {{a}} nm, {{b}}σ',
        SectionWhyTheNumberIsAlways: 'Por qué el número siempre es bajo',
        NoteScatteringFromASmallParticle: 'La dispersión de una partícula pequeña va con la sexta potencia de su ' +
          'diámetro, así que hay un tamaño por debajo del cual esta máquina no ve ' +
          'nada — y los tamaños de defecto siguen una ley de potencias, con muchos ' +
          'más pequeños que grandes. La máquina reporta la cima de la distribución. ' +
          'No está rota; es óptica.',
        SectionScanResult: 'Resultado del escaneo',
        LabelDetectionLimit: 'Límite de detección',
        LabelCaptureRate: 'Tasa de captura',
        NoteOfTheDefectsActuallyPresent: 'de los defectos realmente presentes',
        LabelReportedDensity: 'Densidad reportada',
        LabelNuisanceRate: 'Tasa de molestos',
        LabelKillerDensity: 'Densidad de letales',
        NoteBigEnoughAndOnPattern: 'bastante grandes, y sobre patrón',
        LabelScanTime: 'Tiempo de escaneo',
        NoteNoLotOnTheStage: 'No hay lote en la platina. Trae aquí una oblea para escanearla.',
        TextScanTheWaferAndPass: 'Escanear la oblea y pasarla',
        AssistantLead: 'Asistente de indagación INSP-13',
        AssistantBlurb:
          'Pregunta qué compra un umbral más bajo, qué no puede ver esta receta en ' +
          'absoluto, o si basta con un barrido parcial. Cada respuesta es un barrido ' +
          'ejecutado en esta herramienta.',
        reading: {
          nuisance: {
            Label: 'Revisión SEM de 50 sitios marcados (2.0 h)',
          },
          sensitivity: {
            Limit: '> 8% (cualificada: 10%)',
            Label: 'Oblea patrón de defectos, tasa de captura (1.0 h)',
          },
          roughness: {
            Label: 'Rugosidad de la superficie de entrada',
          },
          vibration: {
            Label: 'Vibración de la platina',
          },
          haze: {
            Label: 'Velo de la óptica',
          },
          laserPower: {
            Label: 'Monitor de potencia del láser',
          },
        },
        aspect: {
          incoming: {
            Action: 'No es una reparación — la oblea es rugosa, y el banco de arriba es el dueño',
            Label: 'Superficie de entrada',
          },
          stage: {
            Action: 'Renivelar los aisladores y reajustar el servo de la platina',
            Label: 'Platina y aislamiento',
          },
          optics: {
            Action: 'Desmontar y limpiar el camino de colección, re-cualificar sobre un patrón',
            Label: 'Óptica de colección',
          },
          laser: {
            Action: 'Cambiar el cabezal láser y recalibrar el monitor de potencia',
            Label: 'Fuente de iluminación',
          },
        },
      },
      probe: {
        ValueDie: '{{a}} die',
        signature: {
          clustered: 'agrupado',
          radial: 'radial',
          centre: 'de centro',
          edge: 'de borde',
          uniform: 'uniforme',
        },
        LotDefectsShort: 'Este lote: {{a}} defectos/cm², de ellos {{b}}/cm² letales',
        LeadMeanHidesTheMap: 'Un mismo rendimiento medio puede ser tres problemas distintos con tres responsables distintos.',
        NoteThisLotCarriesDefects: 'Este lote lleva {{a}} defectos/cm², de los cuales {{b}}/cm² son letales. ' +
          'Compáralo con lo que INSP-13 puso en la hoja de ruta — los dos no ' +
          'coincidirán, y la diferencia es lo que el inspector no pudo ver.',
        NoteNoLotOnTheProber: 'No hay lote en el prober. Corre contra los {{a}}/cm² asumidos por la línea ' +
          'hasta que llegue una oblea.',
        LabelEdgeExclusion: 'Exclusión de borde',
        SecondaryLabel: 'Incertidumbre de medición',
        ToolLabel: 'PRB-14 Prueba y clasificación de obleas',
        NextFt18AppliesAGuard: 'FT-18 aplica su propia banda de guarda a la pieza encapsulada, sobre una ' +
          'medición con su propio error de instrumento. Pregúntale qué cuestan dos ' +
          'bandas de guarda en serie — y si la segunda compra algo que la primera no ' +
          'compró.',
        LabelParametric: 'Paramétrico',
        LabelSignature: 'Firma',
        HypSortYieldIsAProduct: 'El rendimiento de sort es un producto de tres términos independientes y ' +
          'solo uno trata de la oblea. Si la pérdida son defectos, limpiar la tarjeta ' +
          'de puntas no la moverá; si es contacto, se moverá mucho y el mapa se ' +
          'parecerá a la tarjeta y no al proceso. La banda de guarda es una tercera ' +
          'cosa por completo — una decisión, no un fallo.',
        AskedWhereIsThisYieldActually: '¿A dónde se está yendo realmente este rendimiento?',
        SumSeparatingAProcessSignatureFrom: 'Separar una firma de proceso de un problema de contacto y una banda de ' +
          'guarda.',
        LabelNegativeBinomial: 'binomial negativa',
        AxisXClusteringLowHeavilyClustered: 'agrupamiento α  (bajo = muy agrupado)',
        TitleTwoModelsOneWafer: 'Dos modelos, una oblea',
        LabelImpliedDensity: 'Densidad implícita',
        NotePoisson: 'Poisson',
        LabelNegativeBinomialL196: 'Binomial negativa',
        HypPoissonAssumesEveryDefectLands: 'Poisson asume que cada defecto cae con independencia de todos los demás. ' +
          'Los defectos reales llegan en rayas, en vetas de recubrimiento, en anillos ' +
          'de borde — así que se solapan, y varios defectos caen sobre die que ya ' +
          'estaban muertos. Si es así, el agrupamiento debería SUBIR el rendimiento ' +
          'por encima de Poisson para el mismo conteo total, y Poisson debería ser ' +
          'pesimista exactamente cuando una línea tiene una firma.',
        AskedWhyDoTheYieldModels: '¿Por qué discrepan los modelos de rendimiento, y cuál debería usar la línea?',
        SumClusteringAndWhyTheSimplest: 'El agrupamiento, y por qué el modelo de rendimiento más simple es siempre ' +
          'pesimista.',
        NextThatCurveAssumedDefectsLand: 'Esa curva asumió que los defectos caen de forma independiente. No lo hacen ' +
          '— se agrupan. Pregúntame qué le hace el agrupamiento a los mismos números, ' +
          'porque es la diferencia entre un modelo que predice tu línea y uno que no.',
        LabelGoodDie: 'die buenos',
        TitleGoodDieOut: 'Die buenos que salen',
        AxisXDieAreaCm: 'área del die (cm²)',
        TitleDefectYieldAgainstDieArea: 'Rendimiento por defectos frente a área del die',
        LabelTestTime: 'Tiempo de prueba',
        LabelEdgeLoss: 'Pérdida de borde',
        HypADieFailsIfAny: 'Un die falla si cualquier defecto letal cae sobre él, así que la ' +
          'probabilidad de sobrevivir cae exponencialmente con el área. Si eso es ' +
          'correcto, duplicar el die debería costar mucho más que la mitad del ' +
          'rendimiento, y ninguna mejora de proceso de tamaño realista compensará una ' +
          'decisión de diseño tomada antes de cortar la máscara.',
        AskedHowMuchDoesDieArea: '¿Cuánto importa el área del die para el rendimiento, comparada con todo lo ' +
          'demás?',
        SumYieldIsExponentialInDie: 'El rendimiento es exponencial en el área del die, así que el área es la ' +
          'palanca más grande y la posee el diseño.',
        fault: {
          chuckStarved: {
            Evidence: [
              'Vacío del chuck 21 kPa frente a un mínimo de 60 kPa.',
              'La firma del mapa lee borde, con la planaridad de la tarjeta dentro de su límite de 6 µm.',
              'Pérdida de contacto en el patrón de cortocircuito muy por encima del 2 %, desgaste de puntas nominal.',
              'El panel de servicios de instalaciones muestra la bomba seca de PRB-14 parada y ' +
                'todas las demás bombeando.',
            ],
            Truth:
              'La bomba seca bajo esta herramienta, en el subfab, se ha gripado: su línea previa ' +
              'está cerca de la atmósfera y el chuck sujeta la oblea con una quinta parte del vacío ' +
              'debido. Una oblea que no está sujeta plana se levanta en el borde, y un borde ' +
              'levantado aterriza flojo bajo la tarjeta exactamente como lo hace una tarjeta fuera ' +
              'de plano; por eso el mapa dice BORDE mientras el medidor de planaridad dice que la ' +
              'tarjeta está bien. Recalzar la tarjeta, limpiar las puntas: nada en esta herramienta ' +
              'devuelve un vacío que esta herramienta no produce. Ninguna otra herramienta de la ' +
              'planta se ha movido, porque ninguna otra comparte esta bomba.',
            Complaint:
              'Los mapas se han cargado hacia el borde de la noche a la mañana y la pérdida de ' +
              'contacto ha subido en todos los productos. La tarjeta se recalzó la semana pasada y ' +
              'su planaridad está bien.',
            Label: 'Chuck sin vacío por su bomba seca',
          },
          noisyGauge: {
            Evidence: [
              'Sigma del gauge al 14% de la especificación contra un límite del 5%.',
              'La firma del mapa es uniforme — el ruido no tiene forma.',
              'Chuck, planaridad y desgaste de puntas, todos nominales.',
            ],
            Truth: 'La unidad de medición paramétrica está ruidosa — el sigma del gauge es el ' +
              '14% del ancho de especificación contra un límite del 5%. Die buenos cerca ' +
              'de un límite fallan en un contacto y pasan en el siguiente. El rendimiento ' +
              'MEDIO apenas se mueve, y por eso pasó desapercibido; el desacuerdo entre ' +
              're-pruebas es el único síntoma que podía atraparlo.',
            Complaint: 'Reprobar la misma oblea da un reparto de bins distinto cada vez. El ' +
              'rendimiento medio es más o menos correcto.',
            Label: 'Cadena de medición paramétrica ruidosa',
          },
          chuckOffset: {
            Evidence: [
              'Desviación del chuck +9 °C contra un límite de ±2 °C.',
              'La firma del mapa lee radial, que sigue al chuck y no al proceso.',
              'El rendimiento de clasificación está SIN CAMBIO — este fallo no desecha ' +
                'die, mueve dónde caen los límites respecto a la población.',
              'La pérdida de contacto es normal — esto no es un problema de contactos.',
            ],
            Truth: 'El termopar del chuck ha derivado y la oblea está 9 °C por encima de la ' +
              'temperatura que el registro reporta. Los paramétricos son sensibles a ' +
              'temperatura, así que toda la población se ha desplazado contra límites que ' +
              'se fijaron a la temperatura correcta. Los die están bien.',
            Complaint: 'Los bins paramétricos se han movido. Nada falla continuidad, y los die que ' +
              'fallan están dispersos radialmente.',
            Label: 'Chuck corriendo caliente',
          },
          cardOutOfPlane: {
            Evidence: [
              'Planaridad 14 µm contra un límite de 6 µm.',
              'La firma del mapa lee borde.',
              'El desgaste de puntas está dentro de límites, así que no es un problema de ' +
                'limpieza de contacto.',
            ],
            Truth: 'La tarjeta está 14 µm fuera de plano, así que un lado aterriza suave. Esos ' +
              'die nunca hacen contacto y se clasifican como fallos. La firma es la ' +
              'delatora: una tarjeta fuera de plano falla el BORDE, y un mapa de borde no ' +
              'es lo que parece una población de defectos.',
            Complaint: 'Los mapas se han vuelto pesados en el borde. Todos culpan a la exclusión ' +
              'de borde.',
            Label: 'Tarjeta de prueba fuera de plano',
          },
          wornTips: {
            Evidence: [
              'Contactos desde la última limpieza muy pasados del límite de 150.000.',
              'La pérdida de contacto en el patrón de cortos supera el 2%.',
              'La pérdida está en todos los productos, cosa que un defecto de oblea no ' +
                'haría.',
            ],
            Truth: 'Las puntas han pasado su límite de contactos y ya no atraviesan el óxido ' +
              'del pad de forma fiable. Die eléctricamente buenos están fallando ' +
              'continuidad — la pérdida está en el CONTACTO, no en la oblea, y un patrón ' +
              'de resistencia de contacto lo dice directamente.',
            Complaint: 'Rendimiento cuatro puntos abajo en todos los productos de esta celda desde ' +
              'el martes.',
            Label: 'Puntas de prueba gastadas',
          },
          probeHealthy: {
            Evidence: [
              'Cada lectura del banco es nominal.',
              'La firma del mapa es uniforme.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lote.',
            Complaint: 'Lote de rutina. El rendimiento está donde el modelo dice que debe.',
            Label: 'Nada mal',
          },
        },
        NoteMmRingHToProbe: 'anillo de {{a}} mm, {{b}} h de prueba',
        NotePoissonWouldSay: 'Poisson diría {{a}}%',
        NoteCentreEdge: 'centro {{a}}%, borde {{b}}%',
        NoteGoodOfProbed: '{{a}} buenos de {{b}} probados',
        SectionReadTheMapNotThe: 'Lee el mapa, no el número',
        NoteAMeanYieldOf78: 'Un rendimiento medio del 78% puede ser un 78% uniforme, o un 95% en el ' +
          'centro y muerto en el borde, o una oblea perfecta con un rayón cruzado. ' +
          'Tres problemas distintos con tres dueños distintos — y la media no puede ' +
          'distinguirlos.',
        InvBandEdge: 'Borde de la banda de conducción',
        InvFermi: 'Nivel de Fermi',
        InvInterface: 'óxido | silicio',
        InvLevel: '{{ladder}} nivel {{n}}',
        InvShape: 'Dónde está cada electrón (|ψ|²)',
        InvBandTitle: 'El pozo que cava la compuerta, y sus niveles',
        InvBandX: 'profundidad desde el óxido (nm)',
        InvBandY: 'energía del electrón (eV, borde de banda del sustrato = 0)',
        InvDensityTitle: 'Dónde se sitúa la carga del canal',
        InvDensityX: 'profundidad desde el óxido (nm)',
        InvDensityY: 'densidad de electrones (10¹⁹ cm⁻³)',
        InvDensityQ: 'Cuántico',
        InvDensityCl: 'Clásico (Fermi–Dirac)',
        InvCentroid: 'centroide',
        InvCvTitle: 'Lo que mide la tarjeta de puntas: capacitancia de compuerta',
        InvCvX: 'tensión de compuerta (V)',
        InvCvY: 'capacitancia (µF/cm²)',
        InvCvQ: 'Cuántico',
        InvCvCl: 'Clásico (Fermi–Dirac)',
        InvCox: 'el óxido solo',
        InvHere: 'esta tensión de compuerta',
        InvFailed: 'No se pudo cargar el solucionador del canal.',
        InvStatus: 'φ_s = {{phis}} V · N_inv = {{ninv}} cm⁻² · centroide de carga a {{zavg}} nm del óxido · óxido efectivo +{{dt}} nm · desplazamiento del umbral +{{dvt}} mV',
        InvLoading: 'Cargando el solucionador del canal…',
        InvStatusDepleted: 'φ_s = {{phis}} V · aún sin inversión: la compuerta no ha atraído un canal a la superficie · desplazamiento del umbral +{{dvt}} mV',
        InvComputing: 'Resolviendo el barrido de capacitancia y el umbral…',
        InvSweepFailed: 'No se pudo calcular el barrido de capacitancia.',
        InvRight: 'Gráfica derecha',
        InvRightCv: 'Capacitancia vs tensión de compuerta',
        InvRightDensity: 'Carga vs profundidad',
        InvVg: 'Tensión de compuerta',
        InvTox: 'Espesor del óxido',
        InvNa: 'Dopaje del sustrato',
        SectionInversion: 'Por qué el umbral no está donde dice la fórmula: el canal como pozo cuántico (Schrödinger–Poisson)',
        InversionNote: 'Cada tensión de umbral que mide esta tarjeta se decide a un nanómetro bajo el óxido por un hecho que la fórmula clásica de lámina de carga omite: los electrones que la compuerta atrae a la superficie no pueden situarse en la superficie. El campo de la compuerta dobla la banda de conducción en un pozo triangular contra el óxido, y un pozo tan estrecho solo permite niveles discretos, cada uno con una función de onda nula en el óxido y con máximo a un nanómetro de él. Esto es ese pozo resuelto de verdad: la ecuación de Poisson a través de compuerta, óxido y sustrato, la ecuación de Schrödinger para las dos escaleras de valles del silicio (100), iteradas hasta que cada una concuerda con la otra. A la izquierda, el diagrama de bandas con sus niveles y la forma de cada electrón; a la derecha, lo que ve la tarjeta de puntas: la capacitancia de compuerta, cuántica frente a una solución clásica con la misma estadística de Fermi–Dirac, de modo que la brecha entre las curvas es solo cuantización. Ninguna alcanza la del óxido, porque un gas de electrones denso tiene su propia capacitancia; la cuántica queda aún más baja porque la carga que aporta cada voltio adicional llega unos ångströms más lejos del óxido, y esa separación da el tamaño de la brecha con un margen de un tercio. El desplazamiento del umbral en la línea de estado es la diferencia entre ambas soluciones a la misma carga de lámina. La compuerta es polisilicio n⁺ tratado como metal; se omiten el agotamiento del poli, las trampas de interfaz y la polarización de drenador, y los huecos son clásicos, así que el lado de acumulación no está cuantizado aquí.',
        SectionShortChannel: 'Por qué la puerta dejó de encoger: la barrera entre fuente y drenador, y los electrones que la atraviesan por efecto túnel (matriz de transferencia, paquete propagado)',
        ShortChannelNote: 'Un transistor se apaga levantando una barrera entre su fuente y su canal, y la corriente que se fuga mientras está apagado es la cola de electrones con energía térmica suficiente para pasar por encima: sesenta milivoltios de puerta por cada década de corriente, y ningún dispositivo térmico hace mejor. Una aleta larga llega a ese límite: su cuerpo está totalmente agotado, así que nada comparte la excursión de la puerta, y la puerta larga del modelo conmuta en el límite térmico. Esto es lo que una puerta corta le hace a esa barrera. La fuente y el drenador también son electrodos, y su influencia alcanza el canal a lo largo de una longitud de escala que fijan el cuerpo y el óxido, así que en cuanto la puerta mide solo unas pocas de esas longitudes pierde parte de su control y es el propio drenador quien baja la barrera: la pendiente subumbral se degrada más allá de sesenta y el umbral cae con la tensión de drenador. Ese umbral se lee con un criterio corregido por la reinyección del propio drenador, de modo que el DIBL de una puerta larga es la fracción de milivoltio por voltio que debe ser. La barrera además se adelgaza, y una de dos nanómetros es transparente para electrones que no pueden treparla. Ambas cosas salen de un mismo cálculo: la electrostática es el modelo de longitud de escala (Young; Yan, Ourmazd y Lee para la doble puerta), y la transmisión es la misma matriz de transferencia que THRM-04 aplica a un óxido de puerta, cercana a uno por encima de la barrera y con caída exponencial por debajo, de modo que la emisión termoiónica y el efecto túnel son una sola curva y no dos modelos. La onda de la izquierda es esa transmisión propagada en vez de calculada. Es un paquete largo a propósito: un paquete es un abanico de energías, y solo uno largo es lo bastante estrecho para quedar bajo la cima de la barrera, así que lo que sale por el otro lado —dibujado de nuevo tras la puerta, ampliado— es efecto túnel, y cae cientos de veces de una puerta de 10 nm a una de 20. Aun así no es una sola energía, y por eso se imprimen tres números: lo que entregó el paquete, la matriz de transferencia promediada sobre las energías del propio paquete, y T solo a su energía media. Los dos primeros coinciden dentro del uno por ciento hasta una puerta de 20 nm y del tres a 40; el tercero puede ser un millón de veces menor. Baje la longitud de puerta y vea cómo la parte de la fuga que hace túnel supera la mitad, cómo se desploma la relación encendido/apagado y cuál es la longitud a partir de la cual la puerta ya no puede apagar el dispositivo. El dopado, en una aleta tan fina, desplaza el umbral por su carga de cuerpo y deja intacta la pendiente, y por eso las aletas reales se dejan sin dopar. La dispersión queda fuera, así que la corriente de encendido es una cota balística superior; el confinamiento a través del cuerpo es la sección anterior; y la corriente absoluta descansa sobre un espesor de canal supuesto, de modo que la lección está en las proporciones y no en los amperios.',
        ScBandTitle: 'La barrera a lo largo del canal, y un electrón que llega a ella',
        ScBandX: 'posición a lo largo del canal (nm)',
        ScBandY: 'energía del electrón (eV, borde de banda de la fuente = 0)',
        ScBand: 'Borde de la banda de conducción',
        ScWave: 'Dónde está el electrón (|ψ|²)',
        ScGate: 'puerta',
        ScTop: 'cima de la barrera',
        ScLaunch: 'lanzado a',
        ScTransferTitle: 'Lo que mide el probador: corriente frente a tensión de puerta',
        ScTransferX: 'tensión de puerta (V)',
        ScTransferY: 'log₁₀ de la corriente de drenador (µA/µm)',
        ScTransferHigh: 'Drenador a {{vd}} V',
        ScTransferLow: 'Drenador a {{vd}} V',
        ScCriterion: 'criterio de umbral',
        ScHere: 'esta tensión de puerta',
        ScVt: 'umbral',
        ScScaleOffTitle: 'Lo que cuesta escalar: corriente de apagado frente a longitud de puerta',
        ScScaleTunTitle: 'Qué parte de la fuga pasó por efecto túnel',
        ScScaleX: 'longitud de puerta (nm)',
        ScScaleOffY: 'log₁₀ de la corriente de apagado (µA/µm)',
        ScScaleTunY: 'fracción por debajo de la cima de la barrera (%)',
        ScScaleOff: 'Puerta a masa',
        ScScaleTun: 'Por efecto túnel',
        ScThisGate: 'esta puerta',
        ScStatusShort: 'Barrera {{top}} meV · el {{tun}}% de la corriente pasa por túnel bajo ella · {{current}} µA/µm',
        ScNeverOnShort: 'Nunca conduce: el umbral supera los {{max}} V que alcanza la puerta · pendiente {{s}} mV/dec',
        ScPacketShort: '{{fs}} fs · a través de la puerta {{through}} · matriz de transferencia {{matrix}}',
        ScStatus: 'Barrera de {{top}} meV y {{width}} nm de ancho a media altura · longitud de escala {{lambda}} nm · el {{grip}}% de la excursión de puerta llega a la cima de la barrera · el {{tun}}% de esta corriente va por debajo de la cima · {{current}} µA/µm aquí',
        ScMeasured: 'Pendiente {{s}} mV/dec · DIBL {{dibl}} mV/V · umbral {{vt}} V · corriente de apagado {{off}} µA/µm · encendido/apagado {{ratio}}',
        ScPunch: 'Perforado: la puerta no puede apagar este dispositivo. Deja pasar {{off}} µA/µm con la puerta a masa, así que no hay umbral, ni pendiente, ni desplazamiento de umbral que informar.',
        ScPacket: '{{fs}} fs · atraviesa la puerta {{through}} · la matriz de transferencia sobre las energías de este paquete da {{matrix}}, y solo a su energía media {{single}} · reflejado {{back}} · aún llegando {{coming}} · probabilidad contabilizada {{norm}}',
        ScComputing: 'Resolviendo las curvas de transferencia, la pendiente y el desplazamiento de umbral…',
        ScSweepFailed: 'No se han podido calcular las curvas de transferencia.',
        ScFailed: 'El solucionador de canal corto no se ha podido cargar.',
        ScLoading: 'Cargando el solucionador de canal corto…',
        ScNone: 'ninguno',
        ScRun: 'Lanzar un electrón',
        ScPause: 'Pausar',
        ScReset: 'Reiniciar',
        ScRight: 'Gráfico de escalado',
        ScRightOff: 'Corriente de apagado',
        ScRightTun: 'Fracción por túnel',
        ScLength: 'Longitud de puerta',
        ScVg: 'Tensión de puerta',
        ScVd: 'Tensión de drenador',
        ScTox: 'Óxido de puerta',
        ScBody: 'Espesor del cuerpo',
        ScNa: 'Dopado del cuerpo',
        ScEnergy: 'Energía de lanzamiento',
        ScEnergyValue: '{{ev}} ± {{spread}} meV, {{pct}}% de la barrera',
        ScEnergyNone: 'no queda barrera a la que apuntar: lanzado a {{ev}} meV',
        ScWavePast: 'Tras la puerta, ampliado ×{{gain}}',
        ScStale: 'Se movió un mando: las curvas atenuadas son las del dispositivo anterior mientras se resuelven las de este…',
        ScNeverOn: 'Nunca enciende: el umbral está por encima de los {{max}} V que alcanza la puerta. Solo pasa {{on}} µA/µm a puerta plena · pendiente {{s}} mV/dec, medida en lo alto del intervalo · corriente de apagado {{off}} µA/µm',
        ScNaValue: '{{na}} cm⁻³ · +{{shift}} mV de umbral',
        ScBenchDevice: 'El dispositivo',
        ScBenchBias: 'La polarización y el electrón',
        SectionSortResult: 'Resultado de clasificación',
        LabelSortYield: 'Rendimiento de clasificación',
        LabelMapSignature: 'Firma del mapa',
        LabelDefectYield: 'Rendimiento por defectos',
        LabelImpliedKillerDensity: 'Densidad letal implícita',
        NoteInvertedFromTheYield: 'invertida desde el rendimiento',
        LabelContactYield: 'Rendimiento de contacto',
        NoteLostToTheCardNot: 'perdido por la tarjeta, no por la oblea',
        TextProbeTheWaferAndPass: 'Probar la oblea y pasar los die buenos',
        AssistantLead: 'Asistente de indagación PRB-14',
        AssistantBlurb:
          'Pregunta por qué un chip más grande rinde peor, qué modelo de rendimiento ' +
          'necesita esta línea, o si la pérdida es de la oblea o de la tarjeta de ' +
          'puntas. Cada respuesta es un sorteo ejecutado sobre este lote.',
        reading: {
          chuckVacuum: {
            Limit: '> 60 kPa',
            Label: 'Vacío del chuck',
          },
          houseutilities: {
            Limit: 'todos los sistemas nominales',
            Label: 'Llamar a instalaciones por el panel de servicios (0.5 h)',
          },
          contactResistance: {
            Label: 'Resistencia de contacto sobre patrón de cortos (0.5 h)',
          },
          mapShape: {
            Limit: 'uniforme o agrupada',
            Label: 'Mapa de oblea completo, leer la firma (1.5 h)',
          },
          gauge: {
            Label: 'Sigma del gauge paramétrico',
          },
          chuckTemp: {
            Label: 'Desviación de temperatura del chuck',
          },
          planarity: {
            Label: 'Planaridad de la tarjeta',
          },
          tipWear: {
            Label: 'Contactos desde la última limpieza',
          },
        },
        aspect: {
          house: {
            Action: 'Detener el trabajo en la herramienta y plantearlo a instalaciones',
            Label: 'Nada en esta herramienta: escalar a instalaciones',
          },
          gauge: {
            Action: 'Recalibrar la unidad de medición paramétrica y su cableado',
            Label: 'Cadena de medición paramétrica',
          },
          chuck: {
            Action: 'Recalibrar el termopar del chuck y reajustar el lazo',
            Label: 'Temperatura del chuck',
          },
          planarity: {
            Action: 'Re-calzar la tarjeta y re-medir planaridad en los cuatro cuadrantes',
            Label: 'Planaridad de la tarjeta',
          },
          tips: {
            Action: 'Limpiar las puntas en placa abrasiva, o cambiar la tarjeta si están gastadas',
            Label: 'Puntas de prueba',
          },
        },
      },
      litho: {
        ExposeLot: 'Exponer el lote y enviarlo a grabado',
        NextHoldingIsTheEasyHalf: 'Retener es la mitad fácil. Los lotes impresos antes en este turno pasaron ' +
          'por la misma óptica: ¿qué medida te diría si alguno debe volver, y sigue ' +
          'siendo posible esa medida una vez revelada la resina?',
        NextASendAheadIsOneField: 'Una oblea de avanzada es un campo en una oblea. ¿Qué tendrías que exponer, ' +
          'y cuántos campos, antes de creerte el número con el que acabas de liberar ' +
          'el equipo?',
        PatternResolved: 'resuelto',
        PatternUnresolved: 'NO IMPRIMIÓ',
        LabelResolved: 'Patrón',
        LabelRoughness: 'Rugosidad, 3σ',
        LabelPrintedCd: 'CD impreso',
        VerdictWithAWatchItem: 'liberado, con un punto en vigilancia',
        VerdictHeld: 'Retenido',
        VerdictQualified: 'Calificado',
        LabelVerdict: 'Decisión',
        HypAQualificationIsADecision: 'Una calificación es una decisión, no una explicación. Si la oblea de ' +
          'avanzada resuelve, cae dentro de la ventana de CD con la rugosidad bajo su ' +
          'límite, y cada lectura del banco está dentro de su propio límite, el ' +
          'escáner se libera. Si no, queda retenido, y la receta calificada con la ' +
          'misma óptica debería decir si el responsable es la receta o el equipo.',
        AskedIsThisScannerFit: '¿Está este escáner en condiciones de imprimir producto ahora mismo, y qué ' +
          'hago al respecto?',
        SumExposeTheSendAhead: 'Expón la oblea de avanzada, contrasta el banco con sus límites y toma la ' +
          'decisión.',
        UnitNmCd: 'nm de CD',
        NoteResolutionImprovesLinearly: 'La resolución mejora linealmente con la NA y el presupuesto de foco cae ' +
          'al menos con la misma rapidez que su CUADRADO — más deprisa, en cuanto ' +
          'los ángulos dejan de ser pequeños, que a estas aperturas ya lo han ' +
          'dejado. Así que High-NA compra 1.7× en semipitch y devuelve 2.9× en ' +
          'profundidad de foco, en una herramienta que empezaba con 72 nm. Por eso ' +
          'existe CMP-11. Y la óptica anamórfica que hace posible NA 0.55 reduce a ' +
          'la mitad el campo a lo largo del escaneo, así que un die de 26×32 mm ' +
          'ahora toma dos exposiciones y una costura. La energía del fotón no ' +
          'cambia, así que la estocástica que ya era el limitante sigue exactamente ' +
          'igual de mal.',
        ValueFieldsStitched: '{{a}} — cosidos',
        ValueFields: '{{a}} campo',
        HeadPerDie: 'por die',
        HeadField: 'campo',
        HeadFocusBudget: 'presupuesto de foco',
        HeadHalfPitch: 'semipitch',
        NotePresetEuvFine: 'Cambia también la fuente a EUV. Imprime, pero con una fracción de los ' +
          'fotones.',
        NotePresetTightPitch: 'Más allá de lo que la iluminación convencional maneja bien: contraste ' +
          '0.57, y la ventana encoge.',
        NotePresetUnderdosed: '20 mJ/cm². La línea imprime demasiado ancha — menos luz quita menos resist.',
        NotePresetFocusExcursion: '90 nm de desenfoque. El contraste cae a 0.42 y la línea sale muy fuera de ' +
          'medida.',
        NotePresetBaseline: 'Dosificado a medida a 180 nm de pitch, mejor foco. Contraste 0.97, NILS ' +
          '7.1, LER 2.4 nm.',
        LabelUnderdosed: 'SUBDOSIFICADO',
        SecondaryLabel: 'LER 3 sigma',
        ToolLabel: 'LITH-03 · Escáner de inmersión y track',
        NextIfDoseCannotFixRoughness: 'Si la dosis no puede arreglar la rugosidad por sí sola, ¿qué queda? ' +
          'Considera qué le hace el horneado post-exposición a un borde, y qué te ' +
          'cuesta al mismo tiempo.',
        AxisYLerNm3: 'LER (nm, 3σ)',
        AxisXDoseMjCm: 'dosis (mJ/cm²)',
        TitleRoughnessAgainstDoseAndThe: 'Rugosidad frente a dosis — y el suelo que no puede cruzar',
        LabelFailuresIn300: 'Fallos en 300',
        NoteDoseOnlyBuysBackThe: 'la dosis solo recupera el término de fotones',
        LabelAtTwiceTheDose: 'Al doble de dosis',
        LabelCdSpread3: 'Dispersión de CD (3σ)',
        HypDoseIsDeliveredAsA: 'La dosis se entrega como un número finito de fotones. Si la posición del ' +
          'borde se decide contándolos, el conteo fluctúa como su raíz cuadrada, y el ' +
          'borde debería moverse una cantidad que cae al subir la dosis, pero nunca ' +
          'hasta cero, porque el propio resist también está hecho de un número finito ' +
          'de moléculas.',
        AskedWhyDoesTheSameExposure: '¿Por qué la misma exposición, repetida, no imprime la misma línea dos veces?',
        SumPhotonStatisticsLineEdgeRoughness: 'Estadística de fotones, rugosidad de borde de línea, y por qué EUV cambió ' +
          'las reglas.',
        NextEverythingAboveUsedConventionalIllumination: 'Todo lo anterior usó iluminación convencional. La iluminación fuera de eje ' +
          'desecha el centro de la fuente y conserva solo los ángulos que aún forman ' +
          'imagen a pitch apretado. ¿Qué haría eso con los números de arriba, y qué ' +
          'costaría?',
        LabelProductionMinimum: 'mínimo de producción',
        AxisYDepthOfFocusNm: 'profundidad de foco (nm)',
        AxisXPitchNm: 'pitch (nm)',
        TitleDepthOfFocusAgainstPitch: 'Profundidad de foco frente a pitch',
        LabelManufacturable: 'Manufacturable',
        LabelDoseLatitude: 'Latitud de dosis',
        LabelDepthOfFocus: 'Profundidad de foco',
        DesignFocusSwept140NmAgainst: 'Foco barrido ±140 nm frente a dosis barrida ±40%, a tres pitches. Se contó ' +
          'dónde el CD se mantiene dentro de ±10% del objetivo.',
        HypAProcessWindowIsThe: 'Una ventana de proceso es el solapamiento de dos tolerancias, no de una. ' +
          'Si la ventana es ancha, el rasgo sobrevivirá a una oblea imperfectamente ' +
          'plana y a una fuente que deriva; si es estrecha, el rasgo puede imprimirse ' +
          'de maravilla en una prueba y aun así fallar en producción.',
        AskedHowMuchFocusAndDose: '¿Cuánto error de foco y de dosis puede absorber este rasgo antes de ' +
          'salirse de especificación?',
        SumMeasureDepthOfFocusAnd: 'Mide la profundidad de foco y la latitud de dosis, y di si es ' +
          'manufacturable.',
        LabelReturnToBestFocus: 'Volver al mejor foco',
        TitleCdAgainstFocusAtThree: 'CD frente a foco a tres dosis',
        LabelNilsAsSet: 'NILS según ajuste',
        LabelDiagnosis: 'Diagnóstico',
        LabelDoseSensitivity: 'Sensibilidad a la dosis',
        HypDoseAndFocusLeaveDifferent: 'Dosis y foco dejan huellas distintas. La dosis mueve el CD de forma ' +
          'monótona —más luz siempre adelgaza una línea— mientras que el foco es ' +
          'simétrico respecto al mejor foco, así que mueve el CD igual estés en el ' +
          'lado que estés. Si el CD responde asimétricamente al foco, la herramienta ' +
          'no está en el mejor foco; si responde solo a la dosis, es un error de ' +
          'dosis.',
        AskedTheCdIsNotOn: 'El CD no está en el objetivo. ¿Cuál de las dos perillas es responsable?',
        SumSeparateADoseErrorFrom: 'Separa un error de dosis de un error de foco, que se parecen en una ' +
          'lectura de CD.',
        fault: {
          warmPcwLitho: {
            Evidence: [
              'CD ancho con contraste, NILS y LER nominales — la firma de dosis.',
              'El contraste del medidor de dosis está LIMPIO y la curva de contraste de ' +
                'la resina también. Los dos sospechosos habituales han sido descartados, ' +
                'que es el punto en el que la mayoría empieza a adivinar.',
              'La comprobación de servicios muestra el agua de refrigeración muy por ' +
                'encima de la temperatura de suministro.',
              'El grabador va lento al mismo tiempo, por lo que parece una razón ' +
                'completamente distinta. No es una razón distinta.',
            ],
            Truth: 'La planta de agua fría está entregando agua de suministro unos 22 C por ' +
              'encima de lo nominal. Nada en ninguna máquina está roto; cada máquina que ' +
              'expulsa calor a ese circuito simplemente ha dejado de poder sostener su ' +
              'propia consigna. El escáner lo siente a través de su láser: una fuente ' +
              'excímera se refrigera por agua y se degrada cuando su refrigerante se ' +
              'calienta, así que entrega unos tres cuartos de la dosis que el medidor ' +
              'cree que entregó. El medidor no miente — mide la energía de pulso que le ' +
              'dijeron que esperara, y la máquina no tiene manera de saber que su propia ' +
              'agua está caliente.',
            Complaint: 'Líneas imprimiendo anchas, un 7%. Parece el problema de la fuente de ' +
              'primavera, pero el contraste del medidor de dosis salió limpio y la curva ' +
              'de contraste de la resina también. ETCH-01 corre lento al mismo tiempo y ' +
              'nadie cree que las dos cosas estén conectadas.',
            Label: 'Agua de refrigeración caliente',
          },
          hotPlate: {
            Evidence: [
              'El LER está POR DEBAJO de su banda. Eso no es buena noticia, es una señal ' +
                '— nada más en esta máquina hace un borde más suave que el proceso ' +
                'cualificado.',
              'El contraste de imagen y el NILS están intactos, así que la óptica está ' +
                'bien. El difuminado ocurre después de la exposición, no durante.',
              'El barrido de termopares de la placa lee bien por encima de la consigna.',
            ],
            Truth: 'La placa de horneado post-exposición corre caliente, así que el ácido ' +
              'difunde cuatro veces más lejos durante el horneado. El difuminado corta en ' +
              'ambos sentidos y esa es toda la lección: suaviza el perfil de ácido, lo ' +
              'que genuinamente mejora la rugosidad de borde, y también lava el perfil, ' +
              'lo que encoge la línea. La máquina produce bordes más bonitos en ' +
              'estructuras fuera de especificación.',
            Complaint: 'El CD lleva una semana tendiendo estrecho. Lo raro: los bordes de línea se ' +
              'ven MEJOR que de costumbre.',
            Label: 'Placa de horneado caliente',
          },
          focusDrift: {
            Evidence: [
              'El contraste de imagen y el NILS están ambos bien abajo. Ningún fallo de ' +
                'dosis o de resina puede hacer eso — cambian dónde se cruza el umbral, no ' +
                'la nitidez con que la imagen lo cruza.',
              'El LER está ARRIBA, lo que sigue del NILS perdido.',
              'Una exposición a través de foco encuentra el foco óptimo a unos 35 nm de ' +
                'donde la máquina cree que está.',
            ],
            Truth: 'El sensor de nivelación ha derivado, así que la oblea queda a unos 35 nm ' +
              'del plano focal mientras la máquina cree estar en foco óptimo. El ' +
              'desenfoque destruye la imagen aérea antes de tocar nada más: el contraste ' +
              'cae de 0,97 a 0,80 y el NILS con él, y una imagen blanda imprime una línea ' +
              'estrecha y rugosa.',
            Complaint: 'El CD corre estrecho y las imágenes de SEM se ven blandas en los bordes de ' +
              'línea.',
            Label: 'Sensor de nivelación desviado',
          },
          weakResist: {
            Evidence: [
              'CD ancho con contraste, NILS y LER nominales — indistinguible en la ' +
                'máquina de una fuente que ha perdido salida.',
              'El contraste del medidor de dosis vuelve LIMPIO. Esa es la lectura que lo ' +
                'decide, y lo decide descartando la otra en vez de encontrando algo.',
              'Una curva de contraste en oblea monitor muestra gamma a la mitad del valor ' +
                'cualificado.',
            ],
            Truth: 'El lote de resina ha perdido cerca de la mitad de su contraste — lleva ' +
              'demasiado en la estantería. Una resina de menor contraste se disuelve más ' +
              'gradualmente a través del gradiente de ácido, así que el borde cae más ' +
              'afuera y la línea imprime ancha. La imagen es perfecta; el material no.',
            Complaint: 'Líneas imprimiendo anchas otra vez, igual que el problema de la fuente en ' +
              'primavera. La dosis se recalibró la semana pasada.',
            Label: 'Lote de resina fuera de especificación de contraste',
          },
          sourceDrift: {
            Evidence: [
              'El CD es ancho y todo lo demás en la máquina es nominal. Contraste, NILS y ' +
                'LER intactos, así que ni la óptica ni el horneado están implicados.',
              'Un lote de resina degradado se ve EXACTAMENTE igual en todas las lecturas ' +
                'disponibles en la máquina. Separarlos requiere el contraste del medidor de ' +
                'dosis, y nada más lo hará.',
              'El contraste contra un detector de referencia muestra la dosis entregada ' +
                'muy por debajo de la consigna.',
            ],
            Truth: 'La fuente excímera ha envejecido y ahora entrega alrededor del 80% de la ' +
              'dosis que el medidor cree entregar. Se genera menos ácido, el umbral de ' +
              'revelado se cruza más afuera en la imagen, y la línea imprime ancha. Nada ' +
              'de la IMAGEN ha cambiado — contraste, NILS y rugosidad están exactamente ' +
              'en lo nominal — que es lo que lo hace tan difícil de ver.',
            Complaint: 'Las líneas imprimen un 6% anchas en toda la oblea. Misma receta, misma ' +
              'retícula, y la máquina dice que entregó exactamente la dosis pedida.',
            Label: 'Salida de la fuente caída',
          },
          healthy: {
            Evidence: [
              'El CD está en objetivo, el contraste y la rugosidad están ambos en los ' +
                'valores cualificados.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lote.',
            Complaint: 'La máquina pasó su comprobación diaria de CD. Nada reportado.',
            Label: 'Escáner cualificado',
          },
        },
        TextNmOfIncomingTopographyCd: '{{a}} nm de topografía de entrada → el CD se dispersa ',
        LabelBaseline193i: 'LÍNEA BASE 193i',
        LabelFocusExcursion: 'EXCURSIÓN DE FOCO',
        LabelTightPitch110Nm: 'PASO AJUSTADO 110 nm',
        LabelEuv36NmPitch: 'EUV, PASO 36 nm',
        LabelHighNaEuv: 'EUV de alta NA',
        reading: {
          resistlot: {
            Label: 'Curva de contraste de la resina en monitor (4 h)',
          },
          plateprofile: {
            Label: 'Barrido de termopares de la placa (90 min)',
          },
          focusprobe: {
            Label: 'Exposición a través de foco en oblea monitor (3 h)',
          },
          houseutilities: {
            Limit: 'cabecera estanca, PCW a temperatura, gas seco < 50 ppm',
            Label: 'Comprobación de servicios con instalaciones (2 h)',
          },
          dosemeter: {
            Label: 'Contraste del medidor de dosis, detector de referencia (2 h)',
          },
          setpoint: {
            Limit: 'según receta',
            Label: 'Consigna de dosis / consigna de foco',
          },
          nils: {
            Label: 'Pendiente logarítmica normalizada',
          },
          contrast: {
            Label: 'Contraste de la imagen aérea',
          },
          ler: {
            Label: 'Rugosidad de borde de línea, 3 sigma',
          },
          cd: {
            Label: 'CD impreso (SEM)',
          },
        },
        aspect: {
          house: {
            Action: 'Parar el trabajo en la máquina y avisar a instalaciones',
            Label: 'Nada en esta máquina — escalar a instalaciones',
          },
          resist: {
            Action: 'Retirar el lote, medir una curva de contraste, dispensar de botella nueva',
            Label: 'Lote de resina',
          },
          bakeplate: {
            Action: 'Revisar los termopares de la placa y renivelar los pines de proximidad',
            Label: 'Placa de horneado post-exposición',
          },
          stage: {
            Action: 'Re-cualificar el sensor de nivelación con una oblea a través de foco',
            Label: 'Nivelación y foco',
          },
          source: {
            Action: 'Contrastar el medidor de dosis, recalibrar la salida de la fuente',
            Label: 'Fuente de exposición y control de dosis',
          },
        },
        SectionLot: 'El lote en el chuck',
        LotNote:
          'Un escáner enfoca un solo plano, y la oblea trae su propia superficie. Lo ' +
          'que CMP-11 no aplanó se paga aquí, directamente del presupuesto de CD — en ' +
          'un escáner que funciona perfectamente. Aquí es donde un problema de CMP se ' +
          'nota por primera vez, dos máquinas después.',
        LotEmpty:
          'No hay lote en el chuck. Los mandos de arriba exponen una oblea idealmente ' +
          'plana.',
        LotIngot:
          'Este lote sigue siendo un lingote. WPR-08 tiene que cortarlo antes de ' +
          'poder exponerlo.',
        LotCard: 'Hoja de ruta',
        SectionPrinted: 'Resultado impreso',
        SectionSource: 'Fuente',
        SourceDuv: 'Inmersión 193 nm (NA 1,35)',
        SourceEuv: 'EUV 13,5 nm (NA 0,33)',
        SourceNote:
          'Cambiar la fuente cambia tanto lo que se puede resolver como cuántos ' +
          'fotones entrega una dosis dada. Esas dos consecuencias tiran en ' +
          'direcciones opuestas, y en eso consiste todo el debate sobre EUV.',
        SectionAerial: 'Imagen aérea y respuesta de la resina',
        SectionAerialWave: 'De dónde sale la imagen — dos órdenes interfiriendo (óptica ondulatoria)',
        AerialWaveNote:
          'La gráfica de arriba está construida a partir de una fórmula. Esto es aquello de lo que ' +
          'la fórmula habla. Dos haces coherentes — los órdenes de difracción +1 y −1 que la lente ' +
          'ha capturado — se cruzan en el plano de la oblea, y el brillo es el cuadrado del campo ' +
          'de onda promediado en el tiempo, que es lo que una resina integra. Aquí nada calcula una ' +
          'imagen: dos fuentes lineales radian, el solucionador propaga, y las franjas son donde ' +
          'los dos haces se suman y se cancelan. Su separación es el paso que se está imprimiendo. ' +
          'Pulse Ejecutar y observe llegar los haces y ensamblarse las franjas; la imagen queda ' +
          'quieta desde el momento en que se encuentran, porque un patrón estacionario es lo que ' +
          'hacen dos ondas que se cruzan. Luego cambie la fuente de arriba. Una apertura numérica ' +
          'mayor no es más que un ángulo más pronunciado entre los dos órdenes, y un ángulo más ' +
          'pronunciado es una franja más fina — que es todo λ / 2NA, ocurriendo en vez de ' +
          'afirmarse.',
        AerialWaveImmersion:
          'Y para esto sirve el agua. El índice del medio entra como la velocidad de la onda, ' +
          'dejando la frecuencia intacta, porque eso es lo que le pasa a la luz al entrar en un ' +
          'líquido: conserva la frecuencia y acorta la longitud de onda. Así que el mismo ángulo ' +
          'imprime líneas más finas bajo el agua. Pero el premio de verdad es el que delata la ' +
          'aritmética — NA es n sen θ, y un seno no puede pasar de uno, así que una lente en seco ' +
          'nunca puede llegar a NA 1, esté construida como esté. Un escáner de inmersión a 1,35 no ' +
          'es una lente mejor. Es la misma geometría en un medio donde el ángulo existe.',
        AerialWaveCaveat:
          'Tres límites honestos. La caja mide seis longitudes de onda de lado, sea cual sea la ' +
          'longitud de onda, porque 193 nm y 13,5 nm no caben en una misma malla — así que la ' +
          'imagen va en longitudes de onda y las lecturas se convierten a nanómetros con la fuente ' +
          'que usted haya seleccionado. Las fuentes miden seis longitudes de onda en vez de ser ' +
          'infinitas, así que los haces difractan en sus propios bordes y el periodo medido cae ' +
          'dentro de un tres por ciento en lugar de exactamente; con la apertura más pequeña la ' +
          'caja sólo alberga un par de franjas que medir. Y este es deliberadamente el caso de DOS ' +
          'haces — el paso más fino que una apertura puede imprimir, que es el límite k₁ = 0,25 que ' +
          'supone el número de resolución del panel. Una imagen de producción a un paso relajado ' +
          'lleva también el orden cero, y ese tercer haz es lo que calcula la gráfica de arriba y ' +
          'lo que esta deja fuera.',
        ImageWaferPlane: 'oblea',
        ImageAngle: 'órdenes a',
        ImageHalfPitch: 'semipaso',
        ImageVersus: 'frente a',
        ImageContrast: 'contraste',
        ImageAxisX: 'a lo ancho de la oblea (nm)',
        ImageAxisY: 'intensidad relativa',
        ImageLabelSolved: 'lo que imprimió el solucionador de ondas',
        ImageLabelIdeal: 'λ / 2NA, el periodo que deben dos haces',
        ImageVerdict:
          'El solucionador imprimió un semipaso de {{measured}} nm sólo a partir de un ángulo. El ' +
          'límite de resolución de la herramienta, 0,25 λ / NA, dice {{limit}} nm. {{dry}}',
        ImageDryImpossible:
          'En aire no hay ningún ángulo que haga esto: sen θ tendría que pasar de uno. Para eso está el agua.',
        ImageDryAngle: 'En aire la misma apertura necesitaría ±{{angle}}°.',
        SectionStack: 'Qué ocurre cuando llega — ondas estacionarias en la fotorresina',
        StackNote:
          'Una oblea no es una pantalla. La luz que llega entra EN la fotorresina, la ' +
          'atraviesa, se refleja en lo que haya debajo y vuelve a subir por fotorresina que ' +
          'ya ha expuesto. La onda que baja y la que sube son dos mitades del mismo haz, así ' +
          'que son coherentes e interfieren: la fotorresina no se expone con un haz uniforme ' +
          'sino con una onda estacionaria, planos claros y oscuros apilados en su ' +
          'profundidad. Esos planos son la razón de que una pared de fotorresina revelada ' +
          'quede festoneada en vez de lisa, y se ven en el lienzo: la pila vertical de ' +
          'manchas que la imagen aérea por sí sola habría dibujado como franjas limpias.',
        StackNoteSwing:
          'Ahora arrastre el espesor. La fotorresina es una cavidad óptica, así que si el ' +
          'viaje de ida y vuelta regresa en fase depende de su grosor, y la dosis que ' +
          'absorbe la película sube y baja conforme avanza — lo que significa que el ancho ' +
          'de línea también. Esa es la curva de oscilación, y es la razón de que el espesor ' +
          'de la fotorresina sea una especificación de unos pocos nanómetros y no una ' +
          'preferencia, y de que a una pista de recubrimiento le importe la velocidad de ' +
          'giro hasta unas pocas rpm. Después active el recubrimiento antirreflectante. Los ' +
          'rizados se desploman y la curva de oscilación se aplana, porque ya no hay onda ' +
          'reflejada con la que pueda interferir la entrante. Ese es todo el trabajo de la ' +
          'capa, y es el mismo truco que el recubrimiento de un objetivo fotográfico.',
        StackNoteCaveat:
          'Tres límites honestos. Esta sección está fijada a DUV por inmersión en vez de ' +
          'seguir al selector de fuente, porque la historia trata de una fotorresina de una ' +
          'fracción de longitud de onda de espesor y 193 nm y 13,5 nm no pueden serlo a la ' +
          'vez. El sustrato no es silicio: un modelo de onda escalar no puede sostener un ' +
          'índice como el 0,88 − 2,78i del silicio a 193 nm, así que es un sustituto ' +
          'absorbente de índice alto con una reflectancia comparable, y la pila exacta ' +
          'dibujada junto al solucionador usa el mismo sustituto para que ambos respondan a ' +
          'la misma pregunta. Y el patrón resuelto queda a menos de una celda de malla — ' +
          'unos seis nanómetros — de donde lo sitúa la solución exacta, que es lo que cuesta ' +
          'una interfaz abrupta en una malla de cien celdas; el periodo, el contraste y la ' +
          'forma son de lo que trata la comparación, así que la curva exacta se dibuja ' +
          'anclada a la medida.',
        StackResistTop: 'fotorresina',
        StackBarc: 'recubrimiento',
        StackSubstrate: 'sustrato',
        StackResist: 'fotorresina',
        StackRipple: 'rizado',
        StackCoated: 'con recubrimiento',
        StackAxisDepth: 'profundidad en la fotorresina (nm)',
        StackLabelExact: 'la pila de capas exacta',
        StackAxisThickness: 'espesor de la fotorresina (nm)',
        StackAxisDose: 'dosis media en la fotorresina',
        StackLabelBare: 'sobre sustrato desnudo',
        StackLabelCoated: 'con el recubrimiento antirreflectante',
        StackLabelHere: 'el solucionador, a este espesor',
        StackThickness: 'Espesor de la fotorresina',
        StackBare: 'Sustrato desnudo',
        StackWithBarc: 'Con recubrimiento antirreflectante',
        StackVerdict:
          'El solucionador depositó planos claros y oscuros separados {{measured}} nm. Dos ' +
          'ondas que cruzan una película baten a la mitad de la longitud de onda que tienen ' +
          'en ella, medida en el eje de profundidad, que para esta fotorresina y este ángulo ' +
          'es {{predicted}} nm. Observe que no es {{normal}} nm: los órdenes llegan a setenta ' +
          'grados de la vertical, y su coseno vale el sesenta por ciento de la respuesta. Ese ' +
          'mismo periodo es el que repite la curva de oscilación, porque son la misma física: ' +
          'una cavidad que vuelve a estar en fase cada vez que se añade media longitud de ' +
          'onda interna de fotorresina.',
        StackThin:
          'Esta película es demasiado delgada para contener un rizado que merezca medirse: ' +
          'apenas hay un plano oscuro en ella. Suba el espesor y el periodo pasa a ser algo ' +
          'que se le puede preguntar al solucionador.',
        SectionFocus: 'De dónde sale la profundidad de foco — el tercer haz',
        FocusNote:
          'La escena de dos secciones más arriba cruza dos haces, y dos haces no tienen ' +
          'ninguna dependencia del foco: mueva la oblea a lo largo del eje y ambos órdenes ' +
          'adquieren exactamente la misma fase, de modo que el patrón que forman es el mismo ' +
          'en todas partes. Toda curva de Bossung de este panel viene por tanto de otro sitio, ' +
          'y es este: el orden CERO, la luz no difractada que pasa de largo. Viaja por el eje ' +
          'mientras los primeros órdenes viajan en ángulo, así que los tres se desfasan a ' +
          'medida que avanzan, y la imagen que forman sube, cae hasta desaparecer y vuelve con ' +
          'las líneas y los espacios intercambiados. Fíjese en cómo las franjas del lienzo se ' +
          'estrechan a la mitad de su separación a media altura: ese es el momento en que el ' +
          'orden cero ha quedado un cuarto de ciclo atrás y solo quedan los dos primeros.',
        FocusNoteDipole:
          'Después quite el orden cero. La curva se aplana — no se reduce, se aplana — y la ' +
          'imagen también. Ese es todo el argumento de la iluminación fuera de eje y de dipolo: ' +
          'una imagen construida con dos haces en el borde de la pupila no tiene límite de ' +
          'profundidad de foco, porque no hay un tercer haz con el que desfasarse. Lo que ' +
          'cuesta es generalidad, ya que los dos haces se colocan para un paso concreto y todo ' +
          'lo demás de la máscara se imprime peor.',
        FocusBest: 'mejor foco',
        FocusGone: 'imagen perdida',
        FocusPitch: 'paso',
        FocusThree: 'tres órdenes',
        FocusTwo: 'dos órdenes',
        FocusVanishes: 'imagen perdida a',
        FocusNoDof: 'ninguna dependencia del foco',
        FocusAxisDepth: 'profundidad bajo el mejor foco (nm)',
        FocusAxisImage: 'imagen al paso impreso',
        FocusLabelExact: 'coseno de la fase de desenfoque exacta',
        FocusLabelParaxial: 'la fase paraxial de manual',
        FocusLabelHalf: 'la componente a medio paso',
        FocusAllThree: 'Los tres órdenes',
        FocusJustTwo: 'Orden cero retirado (dipolo)',
        FocusVerdictThree:
          'La imagen cae hasta desaparecer {{null}} nm por debajo del mejor foco y vuelve ' +
          'invertida: líneas donde estaban los espacios. Eso es la profundidad de foco: no un ' +
          'emborronamiento, sino un contraste que oscila, y es la curva de Bossung que el panel ' +
          'de arriba dibuja a partir de una fórmula. Observe cuánto se ha separado la curva ' +
          'paraxial discontinua de la resuelta al llegar al fondo de la caja; a setenta grados ' +
          'en agua, π z λ / P² no es la fase de desenfoque.',
        FocusVerdictTwo:
          'Sin el orden cero la curva es una línea plana en cero y la componente a medio paso ' +
          'que hay debajo no se mueve: la imagen es la misma a cualquier profundidad de la ' +
          'caja. Dos haces no tienen ninguna dependencia del foco, porque mover la oblea les da ' +
          'a ambos exactamente la misma fase adicional. Este es todo el argumento de la ' +
          'iluminación de dipolo y demás variantes fuera de eje: descarte el orden que viaja ' +
          'de otra manera y la profundidad de foco deja de ser una restricción, al precio de ' +
          'imprimir bien un paso y mal el resto.',
        SectionMask: 'Donde las líneas se acaban — la imagen en dos dimensiones',
        MaskNote:
          'Todas las escenas ópticas anteriores forman la imagen de una red de líneas y espacios, ' +
          'que es un objeto unidimensional: varía a lo ancho de las líneas y es igual para ' +
          'siempre a lo largo de ellas. Ese es el objeto adecuado para el límite de resolución y ' +
          'el equivocado para la pregunta a la que un diseñador de máscaras dedica el día, que es ' +
          'qué ocurre donde las líneas SE ACABAN. Los extremos de línea se retraen. Las esquinas ' +
          'se niegan a imprimirse cuadradas. Los contactos nunca recogen la luz que recoge una ' +
          'abertura grande. Nada de eso aparece en una sección, porque nada de eso ocurre en una ' +
          'dimensión: así que esta es una superficie, y se puede girar.',
        MaskNoteWhy:
          'Un borde impreso es una curva de nivel de esta superficie a la dosis a la que la ' +
          'fotorresina se revela, que es la lámina que la corta, y el contorno plano de debajo es ' +
          'la máscara tal como se dibujó. La distancia entre ambos es lo que la corrección de ' +
          'proximidad óptica cobra por cerrar, y la razón de que no pueda cerrarse con dosis se ' +
          've en la forma: la dosis sube o baja la lámina, lo que mueve todos los bordes a la ' +
          'vez, mientras que la esquina y el extremo de línea están mal en distinta medida y en ' +
          'distinto sentido. Arrastre el desenfoque y vea cómo se hunde la superficie: el extremo ' +
          'de línea cae primero, porque ya era la parte de la imagen con menos contraste de sobra.',
        MaskDrag:
          'Arrastre para girarla. El contorno plano es la máscara tal como se dibujó; la lámina es donde se revela la fotorresina.',
        MaskEnds: 'Dos extremos de línea',
        MaskHoles: 'Contactos',
        MaskElbow: 'Una esquina',
        MaskDefocus: 'Desenfoque',
        MaskSigma: 'Coherencia parcial σ',
        MaskReadEnds:
          'Separación dibujada {{drawn}} nm; cada extremo se retrae {{pull}} nm, así que la ' +
          'separación se imprime de {{printed}} nm, un {{pct}}% mayor que la dibujada.',
        MaskReadHoles:
          'Dibujado de 110 nm; el contacto central alcanza {{peak}} del campo claro y se imprime ' +
          'de {{width}} nm. Un contacto nunca recoge la luz que recoge una abertura grande, y por ' +
          'eso las capas de contactos se dosifican por separado.',
        MaskReadElbow:
          'El brazo se imprime de {{width}} nm de ancho frente a los 100 nm dibujados, y la ' +
          'esquina no se imprime cuadrada en absoluto: el exterior se redondea y el interior se ' +
          'rellena. Ningún cambio de dosis lo arregla, porque la dosis mueve todos los bordes a la vez.',
        SectionPeb: 'El horneado intermedio — qué le hace una longitud de difuminado a una forma (difusión de ácido)',
        PebNote:
          'La receta de arriba lleva el horneado posterior a la exposición como un solo número, una ' +
          'longitud de difusión, y el modelo de imagen aérea lo convoluciona en una sola línea. Este ' +
          'es el horneado que ese número representa: fotoácido, generado donde cayó la luz, ' +
          'extendiéndose por la resina mientras la placa está caliente. La imagen es una vista en ' +
          'PLANTA — mirando la oblea desde arriba y no a través de ella — porque es la única forma de ' +
          'ver lo que una longitud de difuminado no puede contarle. La línea amarilla es donde se ' +
          'detendrá el revelador, y está dibujada al nivel en que un borde recto largo imprime ' +
          'exactamente a medida. Vigile ese borde: no se mueve, por largo que sea el horneado. Ahora ' +
          'mire todo lo demás. La esquina exterior del codo se redondea hacia dentro, la interior se ' +
          'rellena hacia fuera, y los dos extremos de línea se retiran el uno del otro hasta que el ' +
          'hueco entre ellos es visiblemente más ancho de lo dibujado.',
        PebNoteWhy:
          'Por qué esos tres y no el borde recto: un borde difuminado es un equilibrio entre ácido ' +
          'que se va y ácido que llega, y a lo largo de un borde recto ambos coinciden exactamente. ' +
          'En una esquina convexa al lado expuesto le falta un cuadrante, así que se va más de lo que ' +
          'llega y el contorno retrocede. En una esquina cóncava, al revés, y avanza. En el extremo ' +
          'de una línea el ácido se escapa en tres direcciones a la vez, y en cuanto el difuminado es ' +
          'comparable al ancho de línea el extremo se rinde y camina hacia atrás — por eso el hueco ' +
          'apenas se mueve con unos pocos nanómetros de difuminado y luego se abre deprisa. Nada de ' +
          'esto es un error de dosis. La dosis mueve todos los bordes igual, así que puede cambiar ' +
          'una línea por un espacio y nunca devolver una esquina. Esa es toda la razón de que exista ' +
          'la corrección de proximidad óptica, y de que sus serifas vayan en las esquinas exteriores ' +
          'y sus recortes en las interiores.',
        PebNoteCaveats:
          'Tres límites honestos. El ácido empieza siendo la máscara misma, con bordes que ninguna ' +
          'lente entregaría, así que lo que ve es la aportación del horneado por sí sola y no el ' +
          'horneado encima de la óptica — de esa mitad se ocupa la sección de arriba. Aquí no se ' +
          'revela nada: el contorno es donde se detendría el revelador, no resina disolviéndose. Y el ' +
          'marco son 300 nm en cien celdas, así que un difuminado por debajo de unos tres nanómetros ' +
          'es más fino que la malla y la imagen deja de resolverlo — que es más o menos donde la ' +
          'aportación de un horneado real deja de importar al lado de la óptica.',
        PebBlur: 'difuminado',
        PebBake: 'un horneado de',
        PebBakeAt: 's a',
        PebCorner: 'la esquina se redondea',
        PebGap: 'el hueco se abre',
        PebOver: 'HORNEADO DE MÁS, pasada la receta',
        PebVerdict:
          'Con {{blur}} nm de difuminado el borde recto no se ha movido nada, y todo lo demás sí: la ' +
          'esquina exterior se ha redondeado {{corner}} nm hacia dentro, la interior se ha rellenado ' +
          '{{notch}} nm hacia fuera, y el hueco entre los dos extremos de línea se ha abierto ' +
          '{{gap}} nm. Un cambio de dosis mueve todos los bordes a la vez y no puede deshacer nada de ' +
          'esto. Ese es todo el argumento de la corrección de proximidad óptica — y, en la placa ' +
          'misma, la razón de que un horneado se especifique a una fracción de grado: con cerca de un ' +
          'electronvoltio de energía de activación, un grado es alrededor del {{perDegree}}% del ' +
          'difuminado.',
        AerialNote:
          'La traza superior es la luz que llega a la oblea, construida a partir de ' +
          'los órdenes de difracción que la lente pudo captar. La inferior es el ' +
          'ácido que queda tras el horneado posterior a la exposición. La línea es lo ' +
          'que sobrevive al revelado, y su anchura es el CD.',
        SectionBossung: 'Curvas de Bossung — CD frente a foco, una línea por dosis',
        BossungNote:
          'La medición con la que vive un litógrafo. Curvas planas significan que el ' +
          'foco importa poco; curvas que se abren en abanico significan que dosis y ' +
          'foco interactúan, y la ventana utilizable es donde todas se mantienen a la ' +
          'vez dentro de la tolerancia de CD.',
        MeasureBossung: 'Medir curvas de Bossung',
        SectionWindow: 'Ventana foco-exposición',
        WindowNote:
          'Para cada foco, el rango de dosis que sigue imprimiendo en especificación. ' +
          'La oblea nunca es perfectamente plana ni el escáner perfectamente estable, ' +
          'así que un proceso solo es utilizable si esta región es lo bastante ancha ' +
          'para absorber ambas cosas.',
        MeasureWindow: 'Medir la ventana',
        SectionStochastics: 'Estocástica — imprimir lo mismo 300 veces',
        PhotonCurveEuv: 'Escáner EUV, fotones de 92 eV',
        PhotonCurveArf: 'Escáner ArF, fotones de 6,4 eV',
        PhotonCurveSame: 'La imagen EUV hecha de fotones ArF',
        PhotonCurveHere: 'Esta oblea',
        PhotonChartTitle: 'Rugosidad del borde frente a la dosis',
        PhotonChartX: 'dosis (mJ/cm²)',
        PhotonChartY: 'rugosidad del borde de línea, 3σ (nm)',
        PhotonStatus: 'Fotones de {{energy}} eV · {{perNm2}} por nm², {{perPixel}} por píxel · LER {{ler}} nm · línea {{width}} nm · {{defects}} filas con defecto',
        PhotonPicImage: 'Lo que forma la lente',
        PhotonPicCounts: 'Los fotones que llegan',
        PhotonPicPrint: 'Lo que se revela, con los bordes dibujados',
        PhotonSource: 'De quién son los fotones',
        PhotonChoiceEuv: 'Escáner EUV',
        PhotonChoiceArf: 'Escáner de inmersión ArF',
        PhotonChoiceSame: 'La imagen EUV, fotones ArF',
        PhotonDose: 'Dosis',
        PhotonBlur: 'Alcance de la resina (ácido, electrones secundarios)',
        PhotonReroll: 'Exponer otra oblea',
        SectionPhotons: 'De qué está hecha la imagen: fotones, contados (estadística de Poisson sobre la imagen aérea)',
        PhotonsNote: 'Toda imagen de este panel es un promedio. Una resina nunca recibe el promedio: recibe un número finito de fotones, cada uno absorbido aquí o no aquí, y la cuenta que cae en los pocos nanómetros que deciden un borde es una extracción de Poisson con media N y dispersión √N. La dosis es energía, y un fotón EUV de 92 eV lleva catorce veces la energía de uno ArF de 6,4 eV, así que la misma dosis en mJ/cm² llega como catorce veces menos cuantos. Aquí el propio formador de imágenes de Fourier del panel produce la imagen aérea de líneas de 60 nm con paso de 120 nm, la imagen se entrega píxel a píxel como una cuenta de Poisson con semilla, la cuenta se difumina con el alcance propio de la resina y se revela al umbral habitual, y los bordes impresos se miden fila por fila. La rugosidad cae con la raíz cuadrada de la dosis. Escáner contra escáner, el borde ArF es el más rugoso a este paso, porque su lente da el borde menos abrupto y la pendiente recupera lo que los fotones pierden; por eso la curva discontinua aísla al fotón: la misma imagen EUV hecha de cuantos ArF, √14 más suave. Los fotones contados son los incidentes; la absorción y el rendimiento del ácido reducen aún más la cuenta efectiva, así que el caso EUV real es peor de lo que se muestra.',
        StochasticsNote:
          'Todas las exposiciones de abajo usan ajustes idénticos. La dosis es ' +
          'energía, pero imprimir es contar: en cada estructura cae un número finito ' +
          'de fotones, y en dimensiones pequeñas ese recuento fluctúa lo suficiente ' +
          'para mover el borde. La dispersión es el límite de rendimiento.',
        RunExposures: 'Ejecutar 300 exposiciones',
        SectionRecipe: 'Receta de exposición',
        DoseToSize: 'Dosificar a medida',
        SectionStored: 'Recetas guardadas',
        SectionResist: 'Por qué la resina responde a esta longitud de onda — un fotón elevando un electrón (PPP-CIS)',
        ResistChartTitle: 'Lo que absorbe la molécula, frente a las líneas con las que se expone',
        ResistChartX: 'longitud de onda (nm)',
        ResistChartY: 'absorbancia, relativa',
        ResistStatus: '{{formula}}, {{bonds}} enlaces dobles · absorbe con más fuerza a {{lambda}} nm · responde a {{lines}}',
        ResistBlind: 'ninguna — esta molécula es transparente a todas',
        ResistConjugation: 'Longitud de conjugación',
        ResistBonds: '{{n}} enlaces dobles · {{formula}}',
        ResistFailed: 'No se pudo cargar el motor de espectroscopía molecular.',
        ResistLoading: 'Cargando el motor de espectroscopía molecular…',
        ResistNote: 'Todo lo anterior es óptica: órdenes recogidos, una imagen formada, una dosis entregada. Y todo ello da por supuesto el único paso que no es óptica: que cuando llega el fotón, la resina hace algo. Lo que hace es elevar un electrón desde el orbital lleno más alto de la molécula hasta el vacío más bajo, y una molécula solo puede absorber un fotón cuya energía coincida con ese hueco. Demasiado rojo y la luz atraviesa una película que bien podría ser vidrio. El hueco lo fija hasta dónde puede extenderse el electrón: un electrón π en una cadena conjugada es una partícula en una caja cuya longitud es la conjugación, y una caja más larga tiene niveles más juntos. Alargue la cadena y vea la banda avanzar desde el ultravioleta profundo hasta el visible, pasando una a una por cada línea de exposición. Por eso un sensibilizador hecho para la línea i de 365 nm es transparente a 193 nm e inútil allí, y por eso cada paso hacia longitudes de onda menores exigió una química de resina nueva y no una lámpara nueva.',
        ResistWhatNote: 'ESTAS NO SON RESINAS FOTOSENSIBLES. Son polienos todo-trans - una cadena de carbono con enlaces dobles y simples alternos - y una resina real es una resina novolaca con un sensibilizador de diazonaftoquinona, o un acrilato con un generador de fotoácido: moléculas grandes cuya química este modelo no incluye. Lo que la serie de polienos sí recoge con exactitud es la relación que el panel necesita, la longitud de conjugación frente a la longitud de onda de absorción, en la forma más limpia en que se presenta. Fíjese en que ninguna cadena cae sobre el ArF de 193 nm: las cortas absorben por debajo y las largas por encima, lo cual retrata bien por qué los 193 nm exigieron otra familia de cromóforo y no una versión más corta de la anterior. El motor es el PPP-CIS de AIMS, y sitúa el eteno en 161 nm frente a los 165 medidos.',
        SectionShift: 'Turno',
        TilePrintedCd: 'CD impreso',
        DidNotPrint: 'no imprimió',
        NoteTarget: 'objetivo {{nm}} nm',
        TileCdError: 'Error de CD',
        NoteSpec: 'especificación ±10%',
        K1BelowLimit: 'por debajo del límite físico',
        K1Aggressive: 'agresivo',
        K1Comfortable: 'cómodo',
        TileContrast: 'Contraste de imagen',
        NoteNils: 'pendiente logarítmica de la imagen en el borde',
        TileLer: 'Rugosidad del borde de línea',
        TilePhotons: 'Fotones entregados',
        TilePhotonEnergy: 'Energía del fotón',
        TileWavelengthNa: 'Longitud de onda / NA',
        NotMeasured: 'Aún sin medir',
        ChartAerialTitle: 'La luz sobre la oblea, y el ácido que deja atrás',
        ChartPosition: 'posición a lo largo de un paso (nm)',
        ChartNormalised: 'normalizado',
        SeriesAerial: 'imagen aérea',
        SeriesAcid: 'ácido tras el horneado',
        AnnDevelop: 'umbral de revelado',
        ChartBossungTitle: 'CD frente a foco a varias dosis',
        ChartFocusOffset: 'desplazamiento de foco (nm)',
        ChartCd: 'CD (nm)',
        AnnTarget: 'objetivo',
        ChartWindowTitle: 'Latitud de dosis frente a foco — DOF {{dof}} nm',
        ChartDoseLatitude: 'latitud de dosis (%)',
        SeriesDoseLatitude: 'latitud de dosis',
        AnnNeeded: '5% necesario en producción',
        ChartStochTitle: '300 exposiciones idénticas — sigma {{sigma}} nm',
        ChartStochFailures: ', {{n}} fallaron por completo',
        ChartPrintedCd: 'CD impreso (nm)',
        ChartCount: 'recuento',
        SeriesExposures: 'exposiciones',
        AssistantBlurb:
          'Hazme una pregunta y diseñaré un experimento, lo ejecutaré contra este ' +
          'solucionador e informaré de lo que realmente salió — y luego te devolveré ' +
          'una pregunta más afilada.',
        AssistantLead:
          'Aquí llegan dos números a la oblea: dosis y foco. Ninguno fija el CD ' +
          'directamente. Pregúntame cómo llegan hasta ahí.',
      },
      clean: {
        TransportSurface: 'en la oblea',
        TransportStarved: 'empobrecido en',
        SectionTransport: 'De qué puede quedarse corto un baño — dos resistencias en serie (transporte de materia)',
        TransportCaveat: 'ESTE ES EL CASO GENERAL Y NO ESTE TANQUE, y vale la pena ser exacto con la diferencia. ' +
          'El SC-1 es aproximadamente molar en peróxido e hidróxido mientras la superficie consume unos ' +
          'pocos nanómetros por minuto, así que el suministro supera a la demanda por órdenes de ' +
          'magnitud y un baño de SC-1 real no se empobrece. El régimen de arriba viene de una constante ' +
          'de velocidad elegida para poner reacción y transporte en el mismo rango, que es donde vive la ' +
          'lección — y es un régimen que el procesado húmedo real sí alcanza: en un baño diluido o ' +
          'agotado, en un grabado rápido, y en el fondo de una estructura profunda. Tampoco es por esto ' +
          'que están los transductores. Los megasónicos quitan partículas MECÁNICAMENTE: la corriente ' +
          'acústica tira de la partícula hasta que el arrastre vence a la fuerza de van der Waals que la ' +
          'sujeta, con la cavitación ayudando. Eso es un mecanismo de desprendimiento, no de entrega. ' +
          'Lo que deja a la aritmética de la receta de arriba igualmente equivocada al multiplicar un ' +
          'factor térmico por uno megasónico — el calor y el sonido no son dos posiciones de la misma ' +
          'perilla — pero equivocada por esta razón y no por la que muestra la imagen.',
        TransportNote: 'La química tiene que llegar a la oblea antes de poder hacer nada, y aquí la oblea la consume más ' +
          'rápido de lo que un baño en reposo la repone — así que una capa sobre la superficie se queda ' +
          'corta, y el color es cuánto oxidante hay realmente presente. Agite el baño y la capa ' +
          'se adelgaza y la superficie se rellena. Ahora enfrente las dos perillas. En un baño en ' +
          'reposo, subir la temperatura de 70 a 85 °C más que duplica la velocidad de reacción pero ' +
          'solo compra alrededor de una cuarta parte más de eliminación, porque la superficie ' +
          'simplemente se empobrece más. Hágalo con el baño en movimiento y el mismo paso rinde tres ' +
          'veces. Y en un baño frío, la agitación no compra nada — la química es el cuello de botella, ' +
          'y agitarla es arreglar lo que no toca. Dos resistencias ' +
          'en serie, y solo la mayor merece ser atacada.',
        ActionRunTheCleanAnd: 'Corre la limpieza y pasa la oblea',
        NoteNoLotInTheBench: 'No hay lote en el banco. Trae una oblea aquí para limpiarla.',
        SecondaryLabel: 'Metales superficiales',
        ToolLabel: 'CLN-09 Banco de limpieza húmeda RCA',
        NextTheRinseWaterIsA: 'El agua de enjuague también es una fuente, por debajo de unos 15 MΩ·cm. Si ' +
          'la química del baño está bien y la oblea sigue saliendo sucia, ¿dónde ' +
          'mirarías primero?',
        AxisY10AtomsCm: '×10¹⁰ átomos/cm²',
        AxisXSc2TimeMin: 'tiempo en SC-2 (min)',
        TitleMetalsAndParticlesAgainstSc: 'Metales y partículas frente a tiempo de SC-2',
        NoteSc1SJob: 'el trabajo del SC-1',
        LabelMetalsAsSet: 'Metales según ajuste',
        LabelMetalsNoSc2: 'Metales, sin SC-2',
        HypTheTwoBathsHaveOpposite: 'Los dos baños tienen química opuesta por razones opuestas. El SC-1 es ' +
          'alcalino y levanta las partículas socavándolas; el SC-2 es ácido y ' +
          'disuelve los metales que la química alcalina deja chapados en la ' +
          'superficie. Si eso es correcto, el tiempo de SC-2 debería mover el número ' +
          'de metales y apenas tocar el de partículas, y quitar el SC-1 debería hacer ' +
          'lo inverso.',
        AskedWhatDoesTheSecondBath: '¿Qué quita el segundo baño que el primero no quita?',
        SumMetalsVersusParticlesAndWhy: 'Metales frente a partículas, y por qué un solo baño no puede con ambos.',
        NextThrm04GrowsAGate: 'THRM-04 crece un óxido de puerta sobre esta superficie. ¿Qué le hace medio ' +
          'nanómetro extra de óxido nativo, crecido al aire en un banco sucio, a un ' +
          'crecimiento Deal-Grove que asume que parte de silicio desnudo?',
        AxisYOxideNm: 'óxido (nm)',
        AxisXMinutesAfterHf: 'minutos tras el HF',
        TitleNativeOxideRegrowthAgainstQueue: 'Recrecimiento de óxido nativo frente a tiempo de cola',
        LabelOxideImmediate: 'Óxido, inmediato',
        HypHfStripsTheNativeOxide: 'El HF arranca el óxido nativo y deja la superficie terminada en hidrógeno. ' +
          'El aire empieza a reponer el óxido inmediatamente, y de forma logarítmica: ' +
          'rápido mientras la superficie es reactiva, luego a rastras. Si es así, la ' +
          'mayor parte de lo que cuesta una cola se gasta en su primera media hora, y ' +
          'una espera de cuatro horas es apenas peor que una de una.',
        AskedWhatDoesTheWaitBetween: '¿Qué cuesta la espera entre este banco y la siguiente herramienta?',
        SumNativeOxideRegrowthAgainstThe: 'Recrecimiento del óxido nativo contra el reloj de cola, y por qué el HF ' +
          'pone uno en marcha.',
        NextTheFloorThisCurveFlattens: 'El suelo sobre el que se aplana esta curva no es cero: es lo que el ' +
          'enjuague y el secado vuelven a poner. Pregúntame qué pasa después del baño ' +
          'de HF, que es donde eso empeora mucho.',
        AxisYNmRms: 'nm RMS',
        TitleAndTheRoughnessItCost: 'Y la rugosidad que costó',
        AxisYParticlesCm: 'partículas/cm²',
        AxisXSc1TimeMin: 'tiempo en SC-1 (min)',
        TitleParticlesRemainingAgainstSc1: 'Partículas restantes frente a tiempo en SC-1',
        LabelRoughness: 'Rugosidad',
        LabelParticles: 'Partículas',
        AskedDoesMoreBathTimeKeep: '¿Más tiempo de baño sigue dejando la oblea más limpia?',
        SumDiminishingReturnsOnBathTime: 'Rendimientos decrecientes del tiempo de baño, y la rugosidad con la que se ' +
          'pagan.',
        fault: {
          poorRinse: {
            Evidence: [
              'Resistividad de enjuague a 11 MΩ·cm contra un suelo de 17.',
              'Tanto la comprobación de partículas como la de metales están fuera.',
              'Ambas edades de baño y el transductor son nominales.',
            ],
            Truth: 'El pulidor de la planta de DI necesita regenerarse y el agua de enjuague ' +
              'está a 11 MΩ·cm. El enjuague es lo ÚLTIMO que ve la oblea, así que lo que ' +
              'lleve vuelve a depositarse después de que ambas químicas hicieran su ' +
              'trabajo perfectamente. Fallar en partículas y metales a la vez es una ' +
              'firma que ningún baño solo puede producir — y la reparación no está en ' +
              'esta máquina en absoluto.',
            Complaint: 'Partículas Y metales están ambos fuera en este banco. Ambos baños frescos ' +
              'y los megasónicos comprueban bien.',
            Label: 'Agua de enjuague fuera de especificación',
          },
          deadTransducer: {
            Evidence: [
              'Potencia megasónica entregada al 30% contra un suelo del 85%.',
              'Remoción por debajo del 98% en la oblea monitor.',
              'Ambas edades de baño son nominales — esto no es química.',
            ],
            Truth: 'El transductor megasónico entrega el 30% de la potencia comandada. Los ' +
              'megasónicos aceleran el transporte de la partícula levantada lejos de la ' +
              'superficie, así que sin ellos la química funciona y las partículas vuelven ' +
              'a asentarse. Baños frescos y mala remoción juntos es la firma.',
            Complaint: 'La remoción de partículas se ha degradado, y ambos baños se rehicieron ' +
              'esta mañana.',
            Label: 'Transductor megasónico fallando',
          },
          spentSc2: {
            Evidence: [
              'Edad del baño SC-2 pasada de vida.',
              'Metales superficiales por VPD-ICPMS muy por encima del límite de 5 ×10¹⁰.',
              'La remoción de partículas es completamente normal.',
            ],
            Truth: 'El baño SC-2 está agotado, así que los metales no se están quitando. Las ' +
              'obleas están visualmente limpias — un inspector óptico no puede ver una ' +
              'monocapa de cobre — y lo llevan directo a un horno, donde difunde y mata ' +
              'el óxido. Este es el fallo invisible para toda medición barata de la ' +
              'fábrica.',
            Complaint: 'La integridad del óxido de puerta ha caído en un producto. Los conteos de ' +
              'partículas son perfectamente normales y nadie encuentra un defecto.',
            Label: 'Baño SC-2 agotado',
          },
          spentSc1: {
            Evidence: [
              'Edad del baño SC-1 pasada de vida.',
              'La oblea monitor muestra remoción bien por debajo del 98%.',
              'Los metales superficiales son normales, así que el SC-2 hace su trabajo — ' +
                'es una química, no ambas.',
            ],
            Truth: 'El peróxido del SC-1 se ha descompuesto — el baño tiene 34 horas contra ' +
              'una vida de 16. La remoción es de primer orden en un baño activo y uno ' +
              'agotado apenas trabaja, así que las partículas pasan de largo. Nada de la ' +
              'máquina se ve distinto, porque nada de un baño transparente se ve distinto ' +
              'jamás.',
            Complaint: 'La densidad de defectos en INSP-13 se ha doblado en todo lo que pasa por ' +
              'este banco. El banco no ha alarmado y se ve exactamente como siempre.',
            Label: 'Baño SC-1 agotado',
          },
          clnHealthy: {
            Evidence: [
              'Ambos baños están frescos.',
              'La remoción de partículas y los metales superficiales están ambos en banda.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lote.',
            Complaint: 'Turno de rutina. Nada reportado contra este banco.',
            Label: 'Nada mal',
          },
        },
        NoteMinAtC: '{{a}} min a {{b}} °C',
        NoteFromCmIncoming: 'desde {{a}}/cm² de entrada',
        SectionTwoThingsThisBenchIs: 'Dos cosas que este banco intercambia',
        NoteParticleRemovalIsFirstOrder: 'La remoción de partículas es de primer orden, así que las últimas ' +
          'partículas cuestan el mayor tiempo — y cada minuto extra de SC-1 caliente ' +
          'graba silicio y arruga la superficie. Luego, si quitas el óxido con HF, ' +
          'arranca un reloj: el óxido nativo vuelve más rápido en los primeros veinte ' +
          'minutos, así que el tiempo de cola es una especificación y no una ' +
          'cortesía.',
        NoteThisLotIsStillAn:
          'Este lote sigue siendo un lingote. WPR-08 tiene que cortarlo antes de que ' +
          'esta máquina pueda tocarlo.',
        SectionBenchState: 'Estado del banco',
        LabelParticlesLeft: 'Partículas restantes',
        LabelRemovalEfficiency: 'Eficiencia de remoción',
        LabelMetalRemaining: 'Metal restante',
        Note5BeforeAFurnace: '< 5 antes de un horno',
        LabelSurfaceRoughness: 'Rugosidad superficial',
        NoteSc1EtchesAndEtching: 'el SC-1 graba, y grabar arruga',
        LabelOxideAtTheNextStep: 'Óxido en el paso siguiente',
        LabelHfBenefitLost: 'Beneficio del HF perdido',
        AssistantLead: 'Asistente de indagación CLN-09',
        AssistantBlurb:
          'Pregunta si un baño más largo ayuda, qué cuesta la cola después del HF, o ' +
          'para qué sirve el segundo baño. Cada respuesta es una limpieza ejecutada ' +
          'en este banco.',
        reading: {
          houseutilities: {
            Label: 'Llamar a instalaciones por el panel de servicios (0.5 h)',
          },
          metalCheck: {
            Label: 'Metales superficiales por VPD-ICPMS — destructivo (4.0 h)',
          },
          particleCheck: {
            Limit: '> 98% eliminado',
            Label: 'Conteo de partículas en monitor, antes y después (1.5 h)',
          },
          resistivity: {
            Label: 'Resistividad del agua de enjuague',
          },
          transducer: {
            Label: 'Potencia megasónica entregada',
          },
          sc2Age: {
            Label: 'Edad del baño SC-2',
          },
          sc1Age: {
            Label: 'Edad del baño SC-1',
          },
        },
        aspect: {
          house: {
            Action:
              'Escalar a instalaciones — el lecho mixto pulidor de la planta de agua ' +
              'ultrapura necesita regenerarse',
            Label: 'Suministro de agua de enjuague — no es esta herramienta',
          },
          megasonic: {
            Action: 'Cambiar el transductor y re-mapear el campo acústico del tanque',
            Label: 'Transductor megasónico',
          },
          sc2: {
            Action: 'Vaciar y rehacer el baño SC-2',
            Label: 'Baño SC-2',
          },
          sc1: {
            Action: 'Vaciar y rehacer el baño SC-1, y acortar el intervalo de vida del baño',
            Label: 'Baño SC-1',
          },
        },
      },
      atomView: {
        Atom: 'Átomo',
        Coordinates: 'Coordenadas',
        AtomicMass: 'Masa atómica',
        AtomicRadius: 'Radio atómico',
        ElectricCharge: 'Carga eléctrica',
        AutoRotate: 'Rotación automática',
        Axes: 'Ejes',
        Container: 'Contenedor',
        Fog: 'Niebla',
        ViewDirection: 'Dirección de vista',
        PositiveX: 'Dirección X positiva',
        NegativeX: 'Dirección X negativa',
        PositiveY: 'Dirección Y positiva',
        NegativeY: 'Dirección Y negativa',
        PositiveZ: 'Dirección Z positiva',
        NegativeZ: 'Dirección Z negativa',
        Mechanics: 'Mecánica',
        KineticEnergyColors: 'Colores de energía cinética',
        MomentumVectors: 'Vectores de momento',
        ForceVectors: 'Vectores de fuerza',
        GlobalStyle: 'Estilo global',
        BallAndStick: 'Bolas y varillas',
        Wireframe: 'Alambre',
        Stick: 'Varillas',
        SpaceFilling: 'Relleno de espacio',
        Material: 'Material',
        Diffuse: 'Difuso',
        Soft: 'Suave',
        Glossy: 'Brillante',
        Metal: 'Metal',
        Translucent: 'Translúcido',
        Glass: 'Vidrio',
        Toon: 'Caricatura',
        Flat: 'Plano',
        BackgroundColor: 'Color de fondo',
        TakeScreenshot: 'Tomar una captura de pantalla',
      },
      e2d: {
        ViewProperties: 'Propiedades de la vista…',
        Properties: 'propiedades…',
        ThisPart: 'esta pieza',
        ModelProperties: 'Propiedades del modelo…',
        Loading: 'Cargando el motor de simulación…',
        Failed: 'El motor de simulación no se pudo cargar.',
        Run: 'Ejecutar',
        Pause: 'Pausar',
        Reset: 'Reiniciar',
      },
      implant: {
        BeamWafer: 'oblea',
        BeamSpot: 'más caliente',
        BeamClamp: 'sujeción',
        SectionBeamHeat: 'Lo que el haz calienta — la oblea sobre su sujeción (transferencia de calor)',
        BeamHeatNote: 'La potencia del haz es voltios por amperios: 30 keV a 10 mA son 300 W cayendo sobre una ' +
          'oblea. Aquí entra esa potencia, la cinta barre a un metro por segundo y la sujeción ' +
          'electrostática extrae el calor a través del helio que hay detrás de la oblea. Tres cosas ' +
          'merecen atención. La oblea se estabiliza unos pocos grados por encima del plato y no más — ' +
          'es la sujeción, no el haz, la que fija la temperatura, y una oblea que pierde su gas trasero ' +
          'supera los 300 °C con el mismo haz y reticula su resina. La corriente de haz compra ' +
          'rendimiento y calor a la vez. Y la cinta no deja una línea quemada: el silicio conduce ' +
          'demasiado bien para eso, que es justamente por lo que el panel de arriba puede llevar ' +
          'honestamente una sola temperatura de oblea en vez de un mapa.',
        DamageFurnace: 'Horno de recocido',
        DamageReset: 'Restablecer',
        DamagePause: 'Pausar',
        DamageRun: 'Ejecutar',
        DamageFailed: 'El motor de dinámica molecular no se pudo cargar.',
        DamageLoading: 'Cargando el motor de dinámica molecular…',
        DamageHighlight: 'Resaltar defectos',
        DamageChartTitle: 'Defectos supervivientes frente al tiempo — el recocido',
        DamageChartX: 'tiempo simulado (ps)',
        DamageChartY: 'defectos (vacantes + intersticiales)',
        DamageChartSeries: 'Defectos supervivientes',
        DamageChartAsImplanted: 'tal como se implantó',
        RdfTitle: 'Función de distribución radial g(r) — el cristal como curva',
        RdfX: 'distancia entre pares r (Å)',
        RdfY: 'g(r)',
        RdfSeries: 'g(r) ahora',
        RdfUncorrelated: 'g = 1, sin estructura',
        RdfStatus: 'picos en {{a}} Å y {{b}} Å (ideal 2,35 y 3,84) · entre ellos g(3,0 Å) = {{c}} — cero en un cristal, lleno en los escombros',
        RdfNote: 'Los mismos átomos como histograma de todas las distancias entre pares, normalizado para que un gas sin correlación marque 1. Un cristal son picos sobre las capas marcadas y nada entre ellas; el calor ensancha los picos, y el daño hace algo distinto: llena el hueco y funde la segunda y la tercera capa. Arrastre la dosis arriba desde cero hasta el techo y vea la curva pasar de peine a joroba: esa es la diferencia entre cristalino y amorfo, tal como la vería un difractómetro. Se actualiza en vivo, así que un recocido que vuelve a afilar un pico también se ve aquí.',
        DamageStatus: '{{a}} vacantes · {{b}} intersticiales · daño del haz {{c}}% de la dosis de ' +
          'amorfización · {{d}} K',
        DamageNote: 'Cada ion que se detiene en la oblea desplaza átomos de silicio de sus ' +
          'sitios al entrar. Cada uno deja una vacante — la jaula roja, un sitio ' +
          'vacío — y queda en reposo entre sitios como un intersticial, en amarillo. ' +
          'Ese par es un par de Frenkel, y una cantidad suficiente de ellos es lo que ' +
          'significa "amorfizado": el orden ordenado del diamante sustituido por ' +
          'escombros. Esta red está gobernada por la receta de arriba: suba la dosis ' +
          'o cambie a un ion más pesado y verá crecer el daño; después suba la ' +
          'temperatura de la oblea y lo verá caer, porque un implante caliente se ' +
          'repara sobre la marcha. La celda tiene 216 átomos, así que el daño está ' +
          'escalado para ser visible, no literal.',
        CarriersTitle: 'Portadores frente a temperatura',
        CarriersX: 'temperatura (K)',
        CarriersY: 'log₁₀ nm⁻³',
        CarriersSeries: 'portadores',
        CarriersIntrinsic: 'silicio intrínseco',
        CarriersKnee: 'todos entregando',
        CarriersCrossover: 'manda el silicio',
        CarriersFailed: 'El modelo de portadores no cubre este dopaje.',
        CarriersLoading: 'Resolviendo la estadística de portadores…',
        CarriersAtRoom: '{{pct}}% entregando a 300 K',
        CarriersKneeAt: 'todos entregando hacia {{k}} K',
        CarriersCrossAt: 'el silicio manda a {{k}} K',
        CarriersDoping: 'dopaje 1e{{exp}} nm⁻³',
        NoteCarriers: 'Un átomo dopante en un sitio todavía no es un portador. La curva junto a la red es AIMS resolviendo la neutralidad de carga con los niveles dopantes ocupados según Fermi–Dirac: en frío los portadores vuelven a caer sobre sus dopantes y la concentración se desploma — por eso una oblea medida en nitrógeno líquido no se parece en nada a su especificación a temperatura ambiente. En caliente cada dopante entrega y la meseta es el dopaje que pusiste; ese tramo plano es la única razón por la que el dopaje sirve como parámetro de diseño. Más caliente aún, el silicio sube sus propios electrones a través de la banda prohibida y deja de importar lo que implantaste — el techo de temperatura de unión. El deslizador es el dopaje del pozo o del sustrato, NO el pico de este implante, que queda unos dos órdenes de magnitud más arriba. Por encima de 10¹⁸ cm⁻³ el nivel dopante se ha fundido con la banda y la estadística no degenerada del modelo deja de valer, así que el deslizador termina ahí en lugar de dibujar una curva equivocada con aplomo — y el implante que este panel acaba de hacer queda más allá.',
        SectionDamage: 'Lo que el haz rompe — daño en la red (dinámica molecular)',
        LatticeHighlight: 'Resaltar dopantes',
        LatticeStatus: '{{a}}: {{b}} átomos de dopante entre {{c}} · medido {{d}} K',
        LatticeConc: 'Concentración de dopante (exagerada)',
        TraceTitle: 'Distancia al sitio de red — vibración térmica',
        TraceX: 'tiempo simulado (ps)',
        TraceY: 'desplazamiento (Å)',
        TraceDopant: '{{a}} en su sitio',
        TraceHost: 'Átomo de Si anfitrión',
        TraceSiRef: 'rms del Si a 300 K, Debye–Waller por rayos X',
        TraceStatus: 'rms desde el sitio en los últimos {{a}} ps: {{b}} {{c}} Å · Si {{d}} Å · enlace 2,35 Å',
        TraceIdle: 'Pulse Ejecutar: la distancia rms al sitio se mide sobre los últimos {{a}} ps de la corrida',
        LatticeReset: 'Restablecer',
        LatticePause: 'Pausar',
        LatticeRun: 'Ejecutar',
        LatticeFailed: 'El motor de dinámica molecular no se pudo cargar.',
        LatticeLoading: 'Cargando el motor de dinámica molecular…',
        DopantBohr: 'a*',
        DopantMeanR: '⟨r⟩',
        DopantHalfSpacing: 'a mitad de camino del siguiente dopante',
        DopantOrbitTitle: 'Dónde está el electrón sobrante',
        DopantOrbitX: 'distancia al dopante (nm)',
        DopantOrbitY: 'densidad radial 4πr²|ψ|², pico = 1',
        DopantOrbitSeries: 'El electrón ligado',
        DopantOrbitNone: 'Sin estado ligado: el electrón es libre',
        DopantModelNc: 'modelo: el átomo desaparece',
        DopantMeasuredNc: 'transición Si:P medida',
        DopantMeasuredE: 'medido para {{dopant}}',
        DopantBindingTitle: 'Con qué fuerza se retiene, frente al dopado',
        DopantBindingX: 'log₁₀ de la concentración de dopante (cm⁻³)',
        DopantBindingY: 'energía de enlace (meV)',
        DopantBindingSeries: 'Calculada, con apantallamiento',
        DopantBindingHere: 'Esta receta',
        DopantFailed: 'El solucionador del átomo dopante no pudo cargarse.',
        DopantShort: 'a* = {{bohr}} nm · ligado por {{bind}} meV aquí · se pierde a {{nc}} cm⁻³',
        DopantHelp: 'Todos los números de este átomo',
        DopantStatus: 'a* = {{bohr}} nm · Ry* = {{ry}} meV (medido para {{dopant}}: {{measured}}) · ligado por {{bind}} meV aquí · longitud de Debye {{debye}} nm, siguiente dopante a {{spacing}} nm · desaparece a {{nc}} cm⁻³',
        DopantStatusGone: 'a* = {{bohr}} nm · sin estado ligado: la longitud de Debye es {{debye}} nm, el siguiente dopante está a {{spacing}} nm · el átomo se perdió a {{nc}} cm⁻³',
        DopantLoading: 'Cargando el solucionador del átomo dopante…',
        DopantConc: 'Concentración de dopante',
        DopantHost: 'Material y portador',
        DopantHostSiDonor: 'Si, donante (P)',
        DopantHostSiAcceptor: 'Si, aceptor (B)',
        DopantHostGe: 'Ge, donante',
        DopantHostGaAs: 'GaAs, donante',
        SectionDopant: 'Qué es un dopante: un átomo de hidrógeno dentro del silicio (Schrödinger, radial)',
        DopantNote: 'El átomo de fósforo en un sitio de silicio de arriba tiene un electrón más de los que el sitio quiere, y ese electrón no es libre: el núcleo tiene una carga positiva más que sus vecinos y el electrón lo orbita como un electrón orbita un protón. Un átomo de hidrógeno, con la atracción dividida por la constante dieléctrica del cristal y el electrón con su masa efectiva: la órbita crece hasta unos 2,4 nm y el enlace se reduce a decenas de meV, por lo que un dopante está ionizado a temperatura ambiente y congelado en nitrógeno líquido. La ecuación radial de Schrödinger se resuelve en una malla; la forma cerrada solo la comprueba. El gráfico de la derecha no tiene forma cerrada: cada dopante ionizado deja un portador libre, los portadores libres apantallan y, pasados unos 10¹⁸ por cm³, el pozo es demasiado poco profundo para retener ningún estado ligado. El silicio es entonces un metal: la transición de Mott, a un 10% del valor medido para Si:P. El modelo es isótropo, así que el donante en silicio sale en 26 meV frente a los 45 medidos; la corrección de celda central aporta el resto. El apantallamiento es el de Debye, a 300 K.',
        Orbital3dHostSi: 'silicio',
        Orbital3dHostGe: 'germanio',
        Orbital3dHostGaAs: 'arseniuro de galio',
        Orbital3dFailed: 'No se pudo cargar el generador del orbital.',
        Orbital3dLoading: 'Construyendo el orbital…',
        Orbital3dLegend: '|ψ|² del electrón ligado · brillo ∝ √densidad respecto a su propio centro · la nube se atenúa a medida que el apantallamiento reduce ese centro, nunca por debajo del 30 % · ámbar donde se solapan los electrones de dos dopantes',
        Orbital3dClipped: 'Solo el {{inside}} % del electrón queda dentro de la caja más grande que se dibuja (±{{half}} nm; ⟨r⟩ = {{mean}} nm), así que la nube se muestra recortada en los bordes.',
        DopantWallHeld: 'Apenas ligado: la energía de enlace ya es menor que el confinamiento de la propia caja de {{box}} a* del solucionador, y ⟨r⟩ se ha extendido hasta {{mean}} a*. El borde de esa caja retiene a este electrón tanto como el dopante, así que esta energía de enlace es el modelo en su límite, no una propiedad del átomo.',
        Orbital3dUnbound: 'Sin estado ligado',
        Orbital3dCaption: 'a* = {{bohr}} nm ({{bohrCells}} celdas unidad) · ⟨r⟩ = {{mean}} nm ({{meanCells}} celdas) · separación media entre dopantes {{spacing}} nm ({{spacingCells}} celdas)',
        Orbital3dCaptionGone: 'a* = {{bohr}} nm · separación media entre dopantes {{spacing}} nm ({{spacingCells}} celdas) · sin estado ligado: el electrón no pertenece a ningún dopante en particular',
        Orbital3dLattice: 'El cristal de {{host}} se dibuja como dos planos atómicos contiguos que pasan por el dopante, {{count}} átomos hasta ±{{half}} nm: una lámina, no el cristal entero, que en esta caja tendría {{full}} átomos. La barra verde del borde inferior mide un a*.',
        Orbital3dNeighbours: 'Hay {{k}} dopantes vecinos dentro de la caja, a la separación media de {{spacing}} nm. Donde sus nubes alcanzan la central, la imagen se vuelve ámbar: allí un electrón puede saltar de un dopante a otro, y ese solapamiento es la transición de Mott.',
        Orbital3dNoNeighbours: 'El siguiente dopante está a {{spacing}} nm, fuera de esta caja: con este dopaje cada electrón tiene su dopante para sí solo.',
        LatticeNote: 'La oblea implantada a escala atómica: una red de silicio cúbica de ' +
          'diamante con el dopante de la receta ocupando sitios de silicio por ' +
          'sustitución, vivo bajo ' +
          'el campo de fuerzas Stillinger–Weber. El "tipo n" no es nada más ' +
          'misterioso que estos átomos: un donante en un sitio, un electrón de ' +
          'valencia extra. Cambie la especie en la receta de arriba y la red se ' +
          'vuelve a dopar (BF₂ implanta boro; el flúor se va en el recocido). El ' +
          'control de concentración está deliberadamente exagerado: el dopaje real es ' +
          'de partes por millón, invisible en una celda tan pequeña. Arrastrar con el ' +
          'botón izquierdo rota, la rueda acerca, arrastrar con el botón derecho ' +
          'desplaza.',
        SectionLattice: 'Qué es el dopaje — la red cristalina (dinámica molecular)',
        ActionImplantAndPassThe: 'Implanta y pasa la oblea al horno',
        UnitPctOnTarget: '% del objetivo',
        NoteNoLotOnTheEndstation: 'No hay lote en la estación final. Trae aquí una oblea enmascarada para ' +
          'implantarla.',
        NoteCrystalline: 'cristalino',
        NoteAmorphisedTheAnnealIs: 'amorfizado — el recocido no es opcional',
        NoteThePanelAboveRuns: 'El panel de arriba corre cuatro fórmulas ajustadas: una ley de potencias ' +
          'para el alcance, una fracción de él para el straggle, una gaussiana en ' +
          'inclinación para la fracción canalizada, y un umbral de dosis para la ' +
          'amorfización. Abajo, el mismo implante se RESUELVE — tres mil iones ' +
          'trazados a través de un cristal, colisión por colisión. Donde la fórmula ' +
          'cerrada fue ajustada, los dos coinciden; lejos de ahí no, y esa brecha es ' +
          'el argumento para construir solvers.',
        NoteTheCriticalAngleIs: 'El ángulo crítico es {{a}}° para este ion a esta energía, y no contiene ' +
          'ningún parámetro ajustado — se deduce de la constante de red y de los dos ' +
          'números atómicos. Pon la inclinación dentro de él y el haz canaliza; ponla ' +
          'fuera y no. La fórmula de arriba tiene una sola curva en inclinación para ' +
          'toda especie y toda energía, así que no puede decirte que el arsénico a ' +
          'siete grados aún canaliza y el boro no.',
        ValueThresholdAsOneNumber: '{{a}}× el umbral, como un solo número',
        ValueVacanciesPerIon: '{{a}} vacancias/ion, como perfil',
        MetricDamage: 'Daño',
        ValueZeroGaussian: '0 (gaussiana)',
        MetricDistributionSkew: 'Asimetría de la distribución',
        ValueNoSuchQuantity: 'no existe tal cantidad',
        MetricCriticalAngle: 'Ángulo crítico',
        MetricChannelled: 'Canalizado',
        MetricStraggle: 'Straggle',
        MetricProjectedRange: 'Alcance proyectado',
        LegendTheFittedFormula: 'la fórmula ajustada',
        LegendIonsTraced: '{{a}} iones trazados',
        LabelPeakConcentration: 'Concentración pico',
        SecondaryLabel: 'No uniformidad de dosis',
        ToolLabel: 'IMP-10 Implantador iónico de alta corriente',
        NextBothOfThoseProfilesGet: 'Ambos perfiles se recuecen. ¿Cuál cambia más el horno — y eso hace al ' +
          'implante superficial mejor o peor de lo que parece aquí?',
        AxisXEnergyKev: 'energía (keV)',
        TitleProjectedRangeAgainstEnergy: 'Alcance proyectado frente a energía',
        LabelStraggleDeep: 'Straggle, profundo',
        LabelStraggleShallow: 'Straggle, superficial',
        HypEnergyBuysDepthButThe: 'La energía compra profundidad. Pero la dispersión de la distribución no ' +
          'encoge en proporción, así que un implante superficial debería ser ' +
          'RELATIVAMENTE más ancho que uno profundo — el straggle se vuelve una ' +
          'fracción mayor del alcance a medida que el alcance cae. Si eso se cumple, ' +
          '"más superficial" y "más nítido" no son la misma petición.',
        AskedWhatSetsHowDeepThe: '¿Qué fija cuán hondo va el dopante, y qué cuesta ir superficial?',
        SumEnergySetsDepthAndDose: 'La energía fija la profundidad y la dosis la concentración — y no son ' +
          'intercambiables.',
        NextThrm04HasToAnneal: 'THRM-04 tiene que recocer esto, y el recocido gasta del mismo presupuesto ' +
          'térmico que el óxido de puerta. Pregúntale al horno qué le cuesta reparar ' +
          'esto a la unión que acabas de colocar.',
        LabelDamageRatio: 'razón de daño',
        AxisYDamageAmorphisationThreshold: 'daño / umbral de amorfización',
        AxisXDoseCm: 'dosis (nm⁻²)',
        TitleLatticeDamageAgainstDose: 'Daño de red frente a dosis',
        LabelUniformity: 'Uniformidad',
        LabelDamageRatioL226: 'Razón de daño',
        HypEveryIonKnocksLatticeAtoms: 'Cada ion golpea átomos de la red fuera de sus sitios al entrar. Por debajo ' +
          'de cierta densidad acumulada el cristal se repara entre cascadas; por ' +
          'encima el daño se solapa y la capa se vuelve totalmente amorfa. Si es así, ' +
          'debería haber un umbral en dosis y no un cambio gradual — y correr la ' +
          'oblea caliente debería subirlo, porque una red tibia recuece mientras se ' +
          'daña.',
        AskedHowMuchDamageIsThis: '¿Cuánto daño está haciendo este implante, y cuándo se vuelve amorfo?',
        SumLatticeDamageAgainstDoseAnd: 'Daño de red frente a dosis, y lo que se le pide al horno que repare.',
        NextTheStageHasATilt: 'La platina tiene un error de inclinación propio, muy por debajo de un ' +
          'grado. Mira dónde esta curva es más empinada — ¿qué cuesta ahí medio grado ' +
          'de error de platina, y qué cuesta a 7°?',
        LabelTailDepth: 'profundidad de cola',
        TitleAndTheDepthItReaches: 'Y la profundidad que alcanza',
        AxisYFractionOfDose: 'fracción de la dosis',
        AxisXTiltDegrees: 'inclinación (grados)',
        TitleChannelledFractionAgainstTilt: 'Fracción canalizada frente a inclinación',
        LabelTailAt7: 'Cola a 7°',
        LabelTailOnAxis: 'Cola en eje',
        LabelTableRange: 'Alcance de tabla',
        HypSiliconIsACrystalNot: 'El silicio es un cristal, no un sólido amorfo, y a lo largo de una ' +
          'dirección de bajo índice es mayormente espacio vacío. Un ion que entra por ' +
          'un canal pierde energía mucho más despacio de lo que asumen las tablas. Si ' +
          'eso es lo que pasa, la profundidad debería ser una función fuerte de la ' +
          'INCLINACIÓN y no de la energía en absoluto — y unos pocos grados deberían ' +
          'bastar para cerrarlo.',
        AskedWhyDoesTheImplantGo: '¿Por qué el implante llega más hondo de lo que predice el alcance ' +
          'proyectado?',
        SumChannellingDownTheLatticeAnd: 'Canalización a lo largo de la red, y los siete grados de inclinación que ' +
          'la detienen.',
        fault: {
          weakSource: {
            Evidence: [
              'Presión de fuente al 35% contra un suelo del 85%.',
              'Mapa de resistencia de hoja fuera de límite.',
              'La uniformidad de barrido es nominal — el haz se barre correctamente, solo ' +
                'que no hay un haz estable que barrer.',
            ],
            Truth: 'La presión de la fuente ha caído al 35% de lo nominal. Una fuente ' +
              'hambrienta enciende un haz inestable, y un haz inestable implanta una ' +
              'oblea con dosis dispareja — la misma no uniformidad que produce un mal ' +
              'barrido, desde el otro extremo de la línea de haz. La lectura de barrido ' +
              'es lo que los separa.',
            Complaint: 'El haz está inestable, el tiempo de ciclo ha subido y los mapas están ' +
              'ruidosos.',
            Label: 'Fuente de iones fallando',
          },
          scanDrift: {
            Evidence: [
              'Uniformidad de barrido al 30% contra un suelo del 90%.',
              'Mapa de resistencia de hoja por encima del límite de no uniformidad del 1%.',
              'La dosis total del monitor es correcta, y el perfil no tiene cola.',
            ],
            Truth: 'El sistema de barrido está al 30% de su factor de uniformidad. La dosis es ' +
              'correcta en promedio y equivocada en cada punto en particular — la carga ' +
              'total es correcta, así que la copa de Faraday está contenta, y la oblea no ' +
              'está uniformemente implantada.',
            Complaint: 'Los mapas de resistencia de hoja se han rayado a lo largo de la dirección ' +
              'de barrido.',
            Label: 'Forma de onda de barrido degradada',
          },
          tiltZeroLost: {
            Evidence: [
              'Lectura de inclinación 0,6° contra una consigna de 7°.',
              'El SIMS muestra el 40% de la dosis en una cola profunda.',
              'La dosis en el monitor recocido es correcta — la copa está bien.',
            ],
            Truth: 'El codificador de inclinación ha perdido su cero y la platina está 6,4° ' +
              'desviada, así que una receta que pide 7° implanta a 0,6° — recto por el ' +
              'canal. El cuarenta por ciento del haz llega tres veces más hondo de lo ' +
              'previsto mientras el PICO se queda quieto, así que la media está bien y la ' +
              'cola es la unión. Una resistencia de hoja integra la cola y la borra, y ' +
              'así es como esto llega a producto.',
            Complaint: 'Fugas de unión en un producto. Resistencia de hoja en objetivo, dosis en ' +
              'objetivo, y el pico del perfil exactamente donde debe.',
            Label: 'Cero de la platina de inclinación perdido',
          },
          driftedCup: {
            Evidence: [
              'La pegatina de calibración está en fecha, y también lo está en una máquina ' +
                'buena.',
              'El monitor recocido vuelve al 87% de la dosis prevista.',
              'Inclinación, barrido y fuente, todos nominales.',
            ],
            Truth: 'El integrador de dosis lee un 13% alto, así que la máquina para el ' +
              'implante pronto y reporta dosis completa. No miente — mide lo equivocado ' +
              'con exactitud, que es peor, porque cada receta de la fábrica está escrita ' +
              'contra ese número. La única manera de atraparlo es dejar de medir el haz y ' +
              'medir la OBLEA.',
            Complaint: 'La resistencia de hoja está fuera en todo producto que pasa por este ' +
              'implantador. La máquina insiste en que entregó exactamente la dosis ' +
              'pedida, y tiene la carga integrada para probarlo.',
            Label: 'Copa de Faraday leyendo alto',
          },
          impHealthy: {
            Evidence: [
              'Cada lectura del banco es nominal.',
              'La dosis del monitor y el perfil comprueban bien.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lote.',
            Complaint: 'Lote de rutina. La máquina reporta la dosis que se le pidió.',
            Label: 'Nada mal',
          },
        },
        NoteMaOfBeam: '{{a}} mA de haz',
        NoteTailToNmAtTilt: 'cola hasta {{a}} nm a {{b}}° de inclinación',
        NoteCmDelivered: '{{a}}/nm² entregados',
        NoteStraggleNm: 'dispersión ±{{a}} nm',
        SectionTheFailureThatHidesIn: 'El fallo que se esconde en la media',
        NoteSiliconIsACrystalAnd: 'El silicio es un cristal, y a lo largo de un eje mayor es mayormente ' +
          'espacio vacío. Quita la inclinación y una gran parte del haz baja recta ' +
          'por un canal y se detiene tres veces más hondo de lo previsto — mientras ' +
          'el pico se queda exactamente donde dice la receta. La media está bien. La ' +
          'cola es la unión.',
        SectionSolvedBesideTheFormulaIt: 'Resuelto, junto a la fórmula que sustituye',
        SectionBeamlineState: 'Estado de la línea de haz',
        LabelProjectedRange: 'Alcance proyectado',
        LabelChannelled: 'Canalizado',
        LabelLatticeDamage: 'Daño de red',
        LabelDoseNonUniformity: 'No uniformidad de dosis',
        LabelImplantTime: 'Tiempo de implantación',
        AssistantLead: 'Asistente de indagación IMP-10',
        AssistantBlurb:
          'Pregunta por qué una unión salió más profunda de lo que dicen las tablas, ' +
          'cuándo la red se vuelve amorfa, o qué fija realmente la energía. Cada ' +
          'respuesta es una implantación ejecutada en esta línea de haz.',
        reading: {
          uniformityMap: {
            Label: 'Mapa de resistencia de hoja, 49 puntos (3.0 h)',
          },
          sims: {
            Label: 'Perfil SIMS — destructivo, externo (24 h)',
          },
          sheetRho: {
            Label: 'Recocer un monitor y medir a cuatro puntas (6.0 h)',
          },
          sourcePressure: {
            Label: 'Presión de la fuente',
          },
          scanUniformity: {
            Label: 'Factor de uniformidad del barrido',
          },
          tiltReadback: {
            Label: 'Lectura de inclinación contra consigna',
          },
          doseCal: {
            Value: 'vigente',
            Limit: 'en fecha',
            Label: 'Fecha de calibración de la copa de Faraday',
          },
        },
        aspect: {
          source: {
            Action: 'Cambiar la fuente y re-encender el haz',
            Label: 'Fuente de iones',
          },
          scan: {
            Action: 'Reajustar la forma de onda de barrido y re-mapear la uniformidad',
            Label: 'Sistema de barrido del haz',
          },
          tiltStage: {
            Action:
              'Re-cero del codificador de inclinación y verificar contra referencia ' +
              'mecánica',
            Label: 'Platina de inclinación',
          },
          faraday: {
            Action:
              'Recalibrar el integrador contra una copa de referencia y re-cualificar en ' +
              'monitores',
            Label: 'Copa de Faraday e integrador de dosis',
          },
        },
      },
      facilities: {
        SecondaryLabel: 'Bombeo hasta 10⁻⁵',
        ToolLabel: 'FAC-06 · Vacío de proceso',
        LabelPureOutgassing: 'desgasificación pura',
        LabelPureLeak: 'fuga pura',
        LabelThisChamber: 'esta cámara',
        LabelReaches1e5: 'Alcanza 10⁻⁵',
        LabelBaseAt24H: 'Base a 24 h',
        LabelBaseAt1H: 'Base a 1 h',
        DesignATwentyMinuteRateOf: 'Una subida de presión de veinte minutos en la cámara tal como está, junto ' +
          'a una fuga pura y una desgasificación pura ajustadas a la MISMA pendiente ' +
          'inicial, de modo que solo la forma pueda distinguirlas.',
        HypBothRaiseBasePressureAnd: 'Ambos elevan la presión base y ambos la elevan por el mismo mecanismo: ' +
          'caudal que la bomba tiene que evacuar. Pero una fuga se alimenta de la ' +
          'atmósfera y nunca se agota, mientras que la desgasificación se alimenta de ' +
          'una cantidad finita de material adsorbido y sí. Así que cierra la válvula: ' +
          'una fuga debería dar una línea recta para siempre, y la desgasificación ' +
          'debería curvarse.',
        AskedTheChamberWillNotReach: 'La cámara no llega a la presión base. ¿Está entrando aire, o las paredes ' +
          'están soltando gas?',
        SumSeparateARealLeakFrom: 'Separa una fuga real de una cámara húmeda por la forma de la subida.',
        NextConductanceSetsHowFastYou: 'La conductancia fija con qué rapidez llegas. No fija dónde te detienes. ' +
          '¿Qué decide la presión base en la que se asienta una cámara, y por qué una ' +
          'bomba perfecta puede aun así no alcanzarla?',
        TitleEffectiveSpeedAgainstLineDiameter: 'Velocidad efectiva frente a diámetro de la línea, con esta bomba',
        LabelTripleThePump: 'Triplicar la bomba',
        AskedPumpingIsSlowerThanExpected: 'El bombeo es más lento de lo esperado. ¿La bomba es demasiado pequeña, o ' +
          'hay algo en medio?',
        SumShowThatThePipeNot: 'Demuestra que la tubería, no la bomba, suele fijar la velocidad en la ' +
          'cámara.',
        fault: {
          wornPump: {
            Evidence: [
              'La presión base está en especificación. Cada oblea saldría. Esa es la ' +
                'trampa.',
              'El vaciado hasta el cruce ha pasado de segundos a bien pasado el límite.',
              'La velocidad medida en la boca de la bomba está muy por debajo de su ' +
                'nominal — decisivo, y cuesta dos horas.',
              'La conductancia de línea está sana, así que no se puede culpar al tubo.',
              'La tasa de subida es normal en forma, así que nada alimenta la cámara.',
            ],
            Truth: 'Los rodamientos y las holguras de álabes se han ido; la turbo entrega ' +
              'cerca de un veinteavo de su velocidad nominal. El manómetro no dice nada, ' +
              'y hay una razón: la presión base es caudal dividido por velocidad de ' +
              'bombeo, y en una cámara que bombeó toda la noche el caudal es casi nada, ' +
              'así que casi cualquier velocidad alcanza una buena base. Una bomba ' +
              'muriéndose aparece en cuánto TARDA, nunca en dónde termina. Esa es la ' +
              'única razón de que este fallo sea encontrable, y es por lo que un registro ' +
              'de instalaciones que anota tiempos de vaciado vale más que uno que anota ' +
              'presiones base.',
            Complaint: 'Nada está fallando. El vaciado tarda notablemente más de lo que el ' +
              'registro dice que solía y la bomba suena distinta. La presión base está ' +
              'bien.',
            Label: 'Grupo de bombeo degradado',
          },
          throttledLine: {
            Evidence: [
              'La velocidad efectiva en la cámara es una fracción pequeña del nominal de ' +
                'la bomba — el número decisivo.',
              'La tasa de subida es normal en pendiente y forma, así que no hay fuga y ' +
                'las paredes están secas.',
              'La presión base al final llega; lo que está mal es el TIEMPO, no el destino.',
            ],
            Truth: 'La cámara está detrás de una línea de 5 cm y dos metros de largo. La ' +
              'conductancia va con el CUBO del diámetro, así que ese tubo deja pasar una ' +
              'fracción de lo que la bomba podría tomar, y la cámara nunca ve la ' +
              'velocidad que se pagó. Triplicar la bomba cambiaría casi nada, porque una ' +
              'bomba y el tubo delante de ella suman como resistencias en serie.',
            Complaint: 'El vaciado tarda una eternidad. Alguien ya ha pedido presupuesto de una ' +
              'bomba el triple de grande.',
            Label: 'Línea de conexión larga y estrecha',
          },
          wetChamber: {
            Evidence: [
              'La pendiente de la tasa de subida SE DERRUMBA en veinte minutos según las ' +
                'superficies se vacían. Ese decaimiento es todo el diagnóstico; la presión ' +
                'a la hora no lo es.',
              'Velocidad de bomba, conductancia y juntas, todas en orden.',
              'Dejada bombeando, la presión base sigue mejorando. Una fuga habría dejado ' +
                'de mejorar.',
            ],
            Truth: 'La cámara se abrió a atmósfera y se volvió a armar húmeda. Las paredes ' +
              'están cargadas de agua adsorbida y la están soltando. Nada está roto y ' +
              'nada necesita desmontaje — necesita tiempo, o un horneado. Desarmar esta ' +
              'cámara para cazar una fuga cuesta seis horas y no encuentra nada.',
            Complaint: 'Igual que la junta del mes pasado: no baja de los 10⁻⁵ bajos tras una hora.',
            Label: 'Cámara rearmada húmeda',
          },
          realLeak: {
            Evidence: [
              'La pendiente de la tasa de subida no decae en veinte minutos. Lo que ' +
                'alimenta la cámara no se agota, cosa que ninguna desgasificación puede ' +
                'hacer.',
              'La cámara ya bombeó toda la noche, así que las paredes están secas — que ' +
                'es la única razón de que esta prueba pueda decidir algo.',
              'La velocidad de bomba y la conductancia de línea son ambas normales.',
            ],
            Truth: 'Una junta de puerta quedó pellizcada al rearmar tras el último ' +
              'mantenimiento. Entra aire continuamente. Siguen dos cosas, y la segunda es ' +
              'la que le importa a la fábrica: la presión base se atasca, y el oxígeno ' +
              'que entra con el aire captura flúor en el grabador, así que un técnico de ' +
              'proceso dos plantas arriba ve un misterio donde un técnico de ' +
              'instalaciones ve una junta.',
            Complaint: 'La cámara no baja de los 10⁻⁵ bajos. La semana pasada bombeaba bien. El ' +
              'grabador de arriba también corre lento con flujos normales y RF normal.',
            Label: 'Junta de puerta pellizcada',
          },
          healthy: {
            Evidence: [
              'La presión base está en especificación y la tasa de subida es plana.',
            ],
            Truth: 'No hay nada mal. El vacío no es tu problema.',
            Complaint: 'El sistema bombeó toda la noche y está en base. Nada reportado.',
            Label: 'Cámara en base',
          },
        },
        TextH: '{{a}} — {{b}} h.',
        NoteVSEff: 'V / S_ef',
        reading: {
          speedcheck: {
            Label: 'Comprobación de caudal en la cámara (1 h)',
          },
          pumpspeed: {
            Limit: '> 70 % del nominal',
            Label: 'Medición de velocidad en la boca de la bomba (2 h)',
          },
          outgas: {
            Label: 'Bombear la noche y re-medir (14 h)',
          },
          riseShape: {
            Limit: 'estanca',
            Label: 'Tasa de subida, 20 min con válvula cerrada (25 min)',
          },
          conductance: {
            Label: 'Conductancia de la línea',
          },
          throttle: {
            Limit: '> 720 L/s (60% de la bomba)',
            Label: 'Velocidad en la cámara',
          },
          timeto: {
            Label: 'Tiempo hasta 10⁻⁵ Torr',
          },
          base: {
            Label: 'Presión base a 1 h',
          },
        },
        aspect: {
          walls: {
            Action: 'Seguir bombeando, o hacer un horneado de la cámara',
            Label: 'Superficies de la cámara',
          },
          seals: {
            Action: 'Comprobación de fugas con helio, re-asentar la junta de la puerta',
            Label: 'Juntas y bridas',
          },
          line: {
            Action: 'Re-plomar con una línea más corta y ancha',
            Label: 'Línea de conexión',
          },
          pump: {
            Action: 'Revisar o cambiar la turbo y su bomba de apoyo',
            Label: 'Grupo de bombeo',
          },
        },
        SectionSystem: 'Sistema',
        SectionPumpdown: 'Bombeo',
        PumpdownNote:
          'Desde vacío previo. La parte rápida es la bomba vaciando el volumen; la ' +
          'cola larga son las paredes de la cámara soltando lo que absorbieron ' +
          'mientras estuvo abierta. La presión base es donde lo que entra iguala a lo ' +
          'que sale.',
        SectionRise: 'Tasa de subida — la prueba decisiva',
        RiseNote:
          'Cierra la válvula de aislamiento y observa. Una fuga real alimenta la ' +
          'cámara a ritmo constante, así que la traza es una recta que nunca se ' +
          'dobla. La desgasificación se agota a medida que las superficies se vacían, ' +
          'así que la traza se curva. Ambas empiezan con la misma pendiente — la ' +
          'FORMA es todo el diagnóstico.',
        RunRise: 'Ejecutar una prueba de tasa de subida (20 min)',
        SectionConductance: 'Conductancia — por qué el tubo importa más que la bomba',
        ConductanceNote:
          'Velocidad efectiva en la cámara frente al diámetro de la línea, con tu ' +
          'bomba. La conductancia va con el CUBO del diámetro, así que una línea un ' +
          'poco más estrecha cuesta mucho más de lo que parece — y ninguna bomba al ' +
          'otro lado puede recuperarlo.',
        SectionConfig: 'Configuración del sistema',
        SectionStored: 'Configuraciones guardadas',
        SectionShift: 'Turno',
        SectionUpw: 'Planta de agua ultrapura',
        UpwNote:
          'El circuito del que bebe cada paso húmedo. Aquí viven tres hábitos. El medidor ' +
          'lee temperatura antes que pureza: agua perfectamente pura a 45 °C marca menos de ' +
          '7 MΩ·cm, y por eso toda sonda real compensa a 25 °C. El lecho mixto sostiene 18,2 ' +
          'casi toda su vida y luego falla en los últimos puntos porcentuales, y por eso se ' +
          'regenera por caudal y nunca por la lectura. Y la resistividad solo cuenta iones: ' +
          'el circuito puede llevar diez veces su límite de TOC con el medidor sereno en ' +
          '18,2, y por eso el TOC es su propio instrumento.',
        TileUpwRaw: 'Sonda, sin compensar',
        NoteUpwRaw: 'pureza y temperatura, mezcladas',
        TileUpwComp: 'Medidor, compensado a 25 °C',
        NoteUpwComp: 'con lo que enjuaga CLN-09',
        TileUpwLeakage: 'Contaminación iónica',
        TileUpwToc: 'TOC en el punto de uso',
        NoteUpwToc: 'invisible para el medidor de resistividad',
        ChartUpwTitle: 'Resistividad del circuito vs vida del lecho mixto',
        ChartUpwX: 'capacidad del lecho usada (%)',
        SeriesUpwLoop: 'circuito a 25 °C',
        AnnUpwBed: 'tu lecho',
        AnnUpwFloor: 'piso de CLN-09',
        SectionUtilities: 'Servicios — todo el edificio, en vivo',
        DeskNote:
          'La mesa de despacho. Cada número de aquí es el edificio tal como está; los ' +
          'instrumentos detrás de ellos están en los equipos del sub-fab: baja por la ' +
          'escalera detrás de esta herramienta y haz clic en los equipos. Las ' +
          'reparaciones también se hacen abajo.',
        SectionHouse: 'Sistemas generales — toda la fábrica',
        HouseHealthy: 'Instalaciones sanas — sin incidencia',
        HouseNote:
          'Una sola incidencia en el edificio, sentida en cada equipo conectado a ' +
          'ella. Activa una y luego recorre la planta: los síntomas no se parecerán ' +
          'entre sí, y ninguno se puede reparar en el equipo donde aparece.',
        TileConductance: 'Conductancia de la línea',
        TileSpeed: 'Velocidad en la cámara',
        NotePercentOfPump: '{{pct}}% de la bomba',
        TilePumpRating: 'Capacidad de la bomba',
        NoteOwnInlet: 'en su propia entrada',
        TileBase1h: 'Base tras 1 h',
        TileBase24h: 'Base tras 24 h',
        NoteLeakFloor: 'una fuga fija un suelo',
        NoteOutgassed: 'la desgasificación ha decaído',
        TileReaches: 'Alcanza 10⁻⁵ Torr',
        Never: 'nunca',
        TileTimeConstant: 'Constante de tiempo de bombeo',
        TileVerdict: 'Veredicto de tasa de subida',
        NotRun: 'sin ejecutar',
        RunTheTest: 'ejecuta la prueba',
        NoteSlopeRatio: 'relación de pendientes {{ratio}}',
        VerdictLeak: 'fuga',
        VerdictOutgassing: 'desgasificación',
        VerdictMixed: 'mixto',
        VerdictTight: 'estanco',
        TileThroughput: 'Caudal entrante',
        NoteAt1h: 'Torr·L/s a 1 h',
        ChartPumpTitle: 'Bombeo desde vacío previo',
        ChartTimeS: 'tiempo (s)',
        ChartLogPressure: 'log₁₀ presión (Torr)',
        SeriesChamber: 'presión de cámara',
        AnnBase1h: 'base a 1 h',
        NotRunYet: 'Aún sin ejecutar',
        ChartRiseTitle: 'Tasa de subida — se lee como «{{verdict}}»',
        ChartTimeAfterValve: 'tiempo tras cerrar la válvula (s)',
        ChartPressureU: 'presión (µTorr)',
        SeriesMeasured: 'medido',
        SeriesPureLeak: 'así se vería una fuga pura',
        ChartConductanceTitle: 'Velocidad en la cámara frente al diámetro de línea ({{cm}} cm de largo)',
        ChartDiameter: 'diámetro de línea (cm)',
        ChartEffectiveSpeed: 'velocidad efectiva (L/s)',
        SeriesEffectiveSpeed: 'velocidad efectiva',
        AnnYourLine: 'tu línea',
        AnnPumpRating: 'capacidad de la bomba',
        AssistantBlurb:
          'Hazme una pregunta y diseñaré un experimento, lo ejecutaré contra este ' +
          'solucionador e informaré de lo que realmente salió — y luego te devolveré ' +
          'una pregunta más afilada.',
        AssistantLead:
          'El equipo de grabado de arriba culpa a esta sala más a menudo de lo que ' +
          'cree. Pregúntame si el vacío es el problema, y qué parte de él.',
      },
      epi: {
        SectionWell: 'Crezca solo unos nanómetros — la escalera que aparece (Schrödinger, 1D)',
        WellNote: 'Una capa delgada de SiGe entre silicio queda más baja en energía para un HUECO que el silicio de ambos lados, así que un hueco en esa capa está en una caja, y una partícula en una caja no puede tener cualquier energía. Recibe una escalera de niveles permitidos cuyo espaciado lo fija el ancho de la caja, y la caja es exactamente el espesor que crece esta receta. Estreche la capa y todos los niveles suben, porque comprimir una onda acorta su longitud de onda y una longitud de onda más corta cuesta energía. Suba lo suficiente y el nivel superior alcanza la pared y desaparece: el pozo que tenía cinco pasa a tener cuatro. Aquí no se dibuja nada a mano: los niveles son los autovalores del perfil crecido, y uno desaparece de la lista cuando el solucionador deja de encontrarlo por debajo de la barrera.',
        WellCarrierNote: 'HUECOS, NO ELECTRONES, y la asimetría es lo importante. El silicio-germanio crecido sobre silicio coloca casi todo su desnivel de bandas en la banda de valencia: la banda de conducción apenas se mueve, de modo que un electrón casi no ve pozo alguno mientras que un hueco ve uno profundo. Por eso el SiGe se usa para la base de un transistor bipolar de heterounión y para la fuente y el drenador tensados de un FET de canal p, y no para confinar electrones. El desnivel es el habitual de 0,74 eV por unidad de fracción de germanio para una capa tensada sobre sustrato de silicio, y la masa es la del hueco pesado en la dirección de crecimiento; el hueco ligero está en el mismo pozo con su propia escalera, omitido porque dos juegos de líneas entremezclados no enseñaban nada.',
        WellChartTitle: 'La capa crecida como una caja, y lo que permite',
        WellChartX: 'posición a través de la pila (nm)',
        WellChartY: 'energía para un hueco (eV)',
        WellProfile: 'El perfil crecido',
        WellDensity: 'Dónde está el hueco más bajo',
        WellTop: 'borde del pozo',
        WellLevel: 'E{{n}}',
        WellStatus: '{{n}} niveles ligados en {{width}} nm con {{ge}}% de germanio · pozo de {{depth}} meV · el más bajo a {{ground}} meV',
        WellWidth: 'Espesor crecido',
        WellGe: 'Fracción de germanio',
        WellGeValue: '{{pct}}% · {{depth}} meV de profundidad',
        WellFailed: 'No se pudo cargar el solucionador del pozo cuántico.',
        WellLoading: 'Cargando el solucionador del pozo cuántico…',
        StepBake: 'Horneado en H2',
        StepGrowth: 'Crecimiento epitaxial',
        ReactorDepletion: 'alimentación agotada en el borde lejano',
        ReactorLayer: 'flujo libre sobre superficie',
        SectionReactor: 'La misma física dentro del reactor (transporte de gas)',
        ReactorNote: 'El gas entra por la izquierda y cruza un susceptor calentado; el color es cuánto gas fuente ' +
          'queda. Dos cosas aquí son invisibles para la curva de arriba. La alimentación se agota A LO ' +
          'LARGO del flujo — cerca de un tercio ha desaparecido en el borde de salida, así que ese ' +
          'extremo crece más despacio que el de entrada, que es toda la razón de que un susceptor gire ' +
          'y de que la epitaxia se juzgue por uniformidad y no por velocidad. Y la capa límite es una ' +
          'distancia real: el flujo libre unos milímetros más arriba se mantiene a concentración plena ' +
          'mientras la superficie bajo él se empobrece, y ese hueco ES la resistencia de transporte que ' +
          'la curva dibuja como techo discontinuo. Suba el portador de hidrógeno para adelgazar la ' +
          'capa; suba la temperatura para que la superficie consuma más y se empobrezca aún más. ' +
          'Una etiqueta que conviene leer dos veces: la receta de arriba dosifica triclorosilano, y ' +
          'este es el reactor de SILANO del propio Energy2D, así que los sensores llevan el nombre ' +
          'de un gas que la herramienta no usa. Qué molécula se pirolice cambia los números y no la ' +
          'imagen — que la alimentación se agote a lo largo del flujo, y que la capa límite tenga un ' +
          'espesor, es lo que hace cualquier fuente de silicio sobre un susceptor caliente. Lea aquí ' +
          'la forma, y la velocidad en la curva de arriba.',
        fault: {
          spentSusceptor: {
            Evidence: [
              'Horas del susceptor muy pasadas del intervalo de recubrimiento.',
              'Densidad de defectos añadidos un orden de magnitud arriba en el monitor.',
              'Un leve moteado de espesor en el mapa de 49 puntos — emisividad parcheada ' +
                '— mientras velocidad, neblina y la sonda cinética se mantienen limpias.',
            ],
            Truth: 'El recubrimiento del susceptor está gastado y se desprende hacia la ' +
              'corriente de gas. Todo lo que el reactor MIDE está sano — velocidad, ' +
              'uniformidad, neblina — porque el recubrimiento no fija ninguno de ellos. ' +
              'Solo aparece como defectos en la capa, que es evidencia de INSP-13, no del ' +
              'reactor.',
            Complaint: 'Los añadidos de partículas suben lote tras lote. Nada más se ha movido.',
            Label: 'Recubrimiento del susceptor agotado',
          },
          wetHydrogen: {
            Evidence: [
              'Punto de rocío en la herramienta muy por encima del límite de -85 °C.',
              'Escaneo de neblina un orden de magnitud sobre el límite, a velocidad de ' +
                'crecimiento NORMAL.',
              'Extender el horneado no ayuda — lo que descarta un error de receta de ' +
                'horneado.',
            ],
            Truth: 'El lecho purificador de H2 se está agotando y el portador lleva agua. El ' +
              'horneado quita el óxido nativo y el ambiente húmedo lo repone de ' +
              'inmediato, así que la superficie siembra faltas de apilamiento por mucho ' +
              'que dure el horneado. La cinética de crecimiento apenas lo nota — la ' +
              'velocidad dice que nada anda mal mientras la superficie dice que todo lo ' +
              'está.',
            Complaint: 'Alarmas de neblina en cada oblea desde el fin de semana. Velocidad y ' +
              'espesor están clavados en la receta.',
            Label: 'Humedad en el hidrógeno',
          },
          tiltedLamps: {
            Evidence: [
              'Dispersión de corriente entre cuadrantes muy por encima del límite del 3%.',
              'El mapa de 49 puntos muestra la inclinación; el espesor medio es casi ' +
                'nominal.',
              'Líneas de deslizamiento en el borde de la oblea bajo grabado de decoración.',
              'La sonda cinética coincide con el punto de producción — el CONTROL de ' +
                'temperatura está bien, la DISTRIBUCIÓN no.',
            ],
            Truth: 'Un cuadrante de lámparas entrega bajo y el perfil radial está inclinado un ' +
              '9%. La velocidad media apenas se mueve en la meseta de transporte — pero ' +
              'la tensión del gradiente mueve dislocaciones a la temperatura de ' +
              'crecimiento, y el borde se desliza. Esta es la falla que la meseta NO ' +
              'PUEDE perdonar, porque el deslizamiento va del gradiente, no de la media.',
            Complaint: 'Deslizamiento de borde en los últimos dos lotes, y el mapa de espesor ' +
              'desarrolló una inclinación radial. La velocidad en promedio está bien.',
            Label: 'Cuadrante de lámparas fallando',
          },
          coldPyrometer: {
            Evidence: [
              'La sonda cinética DISCREPA del punto de producción: déficit modesto a 1120 ' +
                '°C, un colapso a 950 °C.',
              'Nivel del burbujeador y suministro de la línea fuente verifican limpios.',
              'Uniformidad ligeramente peor, porque la oblea más fría se asienta más ' +
                'abajo en la meseta.',
            ],
            Truth: 'El pirómetro derivó 60 °C optimista, así que el lazo mantiene la oblea 60 ' +
              '°C más fría de lo que todos creen. En el punto de producción la meseta de ' +
              'transporte esconde la mayor parte; baja a 950 °C y el déficit se infla, ' +
              'porque cuesta abajo de la curva de Arrhenius la velocidad ES un ' +
              'termómetro. Mismo síntoma que el burbujeador, firma de régimen opuesta.',
            Complaint: 'La misma queja que un problema de gas: las capas salen delgadas. El panel ' +
              'de gases verifica bien y el burbujeador se rellenó ayer.',
            Label: 'Pirómetro leyendo alto',
          },
          starvedBubbler: {
            Evidence: [
              'La sonda cinética muestra el MISMO déficit porcentual a 950 °C que a 1120 ' +
                '°C — una firma del lado del gas que ninguna falla de temperatura puede ' +
                'producir.',
              'El nivel del burbujeador lee bajo el tubo de inmersión.',
              'Uniformidad y neblina no se ven afectadas.',
            ],
            Truth: 'El burbujeador está bajo su tubo de inmersión y entrega cerca del 55% del ' +
              'TCS comandado. A 1120 °C el proceso está limitado por transporte, así que ' +
              'la velocidad sigue al gas uno a uno — y el lazo de temperatura dice la ' +
              'verdad, que es exactamente por qué mirarlo fijo no lleva a nada.',
            Complaint: 'Las capas salen 40% delgadas con la receta de producción. La lectura de ' +
              'temperatura es una roca durante toda la corrida.',
            Label: 'Burbujeador de TCS hambriento',
          },
          epiHealthy: {
            Evidence: [
              'Velocidad, uniformidad y neblina están todas en banda.',
              'La sonda cinética coincide con el punto de producción.',
            ],
            Truth: 'Nada anda mal. La decisión correcta es correr el lote.',
            Complaint: 'Turno de rutina. No se ha reportado nada contra el reactor.',
            Label: 'Nada anda mal',
          },
        },
        reading: {
          hazeCheck: {
            Limit: '< 3 ppm',
            Label: 'Escaneo de neblina superficial tras horneado de silicio desnudo (1.0 h)',
          },
          uniformityCheck: {
            Value: '{{a}} % de semirrango',
            Limit: '< 2%',
            Label: 'Mapa de espesor de 49 puntos en oblea monitor (1.5 h)',
          },
          kineticProbe: {
            Value: '{{a}}% del nominal a {{b}} °C, {{c}}% a 950 °C',
            Limit: 'los dos deberían coincidir',
            Label: 'Sonda cinética — recrecer a 950 °C y comparar (2.5 h)',
          },
          susceptorHours: {
            Limit: '< 240 h',
            Label: 'Horas del susceptor desde el recubrimiento',
          },
          dewpoint: {
            Limit: '< -85 °C',
            Label: 'Punto de rocío del H2 en la herramienta',
          },
          bubblerLevel: {
            ValueOk: 'en banda',
            ValueLow: 'bajo el tubo de inmersión',
            Limit: 'sobre el tubo de inmersión',
            Label: 'Nivel del burbujeador de TCS',
          },
          pyro: {
            Value: 'coincide (lazo cerrado — un desvío no puede verse aquí)',
            Limit: 'verificar con oblea instrumentada',
            Label: 'Pirómetro frente a consigna en reposo',
          },
          lampCurrent: {
            Limit: '< 3%',
            Label: 'Dispersión de corriente entre cuadrantes de lámparas',
          },
        },
        aspect: {
          susceptor: {
            Action: 'Cambia el susceptor y corre la receta de recubrimiento',
            Label: 'Recubrimiento del susceptor',
          },
          ambient: {
            Action: 'Escala al patio de gases — el lecho purificador de la línea de H2 se está ' +
              'agotando',
            Label: 'Calidad del hidrógeno',
          },
          gasline: {
            Action: 'Rellena el burbujeador y verifica el suministro con una subida de presión ' +
              'en la línea fuente',
            Label: 'Suministro de TCS',
          },
          pyrometer: {
            Action: 'Recalibra el pirómetro contra una oblea instrumentada con termopares',
            Label: 'Calibración del pirómetro',
          },
          lamps: {
            Action: 'Reemplaza el cuadrante de lámparas débil y vuelve a mapear el perfil ' +
              'radial de temperatura',
            Label: 'Balance del banco de lámparas',
          },
        },
        SecondaryLabel: 'No uniformidad de espesor',
        ToolLabel: 'EPI-20 Reactor de epitaxia a presión reducida',
        NextIfTheLayerMustBe: 'Si la capa debe ser de 4 µm y el día necesita 20 lotes, ¿comprarías el ' +
          'tiempo con TCS o con un segundo reactor — y qué cuesta cada uno que el ' +
          'otro no?',
        AxisXTcsTorr: 'TCS (Torr)',
        TitleRateAndItsPrice: 'La velocidad y su precio',
        LabelDefectsAtMax: 'Defectos al máximo',
        LabelRateAtMax: 'Velocidad al máximo',
        HypOnTheTransportPlateauThe: 'En la meseta de transporte la velocidad debería seguir a la entrega uno a ' +
          'uno. Pero la superficie solo puede consumir hasta cierta velocidad — ' +
          'empuja la fase gaseosa lo suficiente y nuclea por su cuenta, y cada núcleo ' +
          'es una partícula que aterriza en la capa.',
        AskedIsTcsAFreeThroughput: '¿Es el TCS una perilla de rendimiento gratis?',
        SumRateFollowsTheGasOne: 'La velocidad sigue al gas uno a uno — hasta que la fase gaseosa nuclea por ' +
          'su cuenta.',
        NextTheHazeScanIsClean: 'El escaneo de neblina está limpio pero el horneado corrió largo. ¿Dónde ' +
          'aparecería el daño, y qué herramienta línea abajo sería la primera en ' +
          'verlo?',
        AxisYHazePpm: 'neblina (ppm)',
        AxisXBakeMin: 'horneado (min)',
        TitleTheTwoCostsOfThe: 'Los dos costes del horneado',
        LabelSeamAsSet: 'Costura según ajuste',
        LabelHazeAsSet: 'Neblina según ajuste',
        HypTheBakeDoesTwoThings: 'El horneado hace dos cosas a la vez y no se le puede pedir que haga una. ' +
          'Quita el óxido nativo —sáltalo y el óxido siembra faltas de apilamiento ' +
          'que se leen como neblina— y evapora dopante del sustrato caliente, que el ' +
          'frente de crecimiento luego se come como una costura difuminada. Si ambas ' +
          'son reales, la neblina debería caer con el tiempo de horneado mientras la ' +
          'transición autodopada crece.',
        AskedWhatDoesThePreGrowth: '¿Qué compra realmente el horneado previo al crecimiento, y qué cuesta?',
        SumTheBakeStripsOxideAnd: 'El horneado quita el óxido y difumina la costura — ambas cosas, siempre, ' +
          'en proporción.',
        NextLayersAreComingOutThin: 'Las capas salen delgadas y la lectura de temperatura está estable. ¿En qué ' +
          'régimen volverías a medir, y qué te diría cada respuesta?',
        TitleGrowthRateAgainstTemperature: 'Velocidad de crecimiento frente a temperatura',
        LabelRegimeAsSet: 'Régimen según ajuste',
        LabelRatePlateau: 'Velocidad, meseta',
        LabelRateColdEnd: 'Velocidad, extremo frío',
        HypTheGasHasToArrive: 'El gas tiene que llegar y luego reaccionar — dos velocidades en serie, ' +
          'mandando la más lenta. Si eso es correcto, la velocidad debería trepar una ' +
          'pared de Arrhenius en el extremo frío y luego doblarse sobre una meseta ' +
          'que la temperatura no puede tocar, porque allá arriba el burbujeador es el ' +
          'cuello de botella.',
        AskedDoesTemperatureKeepBuyingGrowth: '¿La temperatura sigue comprando velocidad de crecimiento?',
        NoteSurface: '{{a}}% superficie',
        TextThisRecipeOnThisSubstrate: 'Esta receta sobre este sustrato: {{a}} µm a ',
        LabelGrowthRate: 'velocidad de crecimiento',
        LabelTransportCeiling: 'techo de transporte',
        SectionResult: 'La capa tal como creció',
        SectionSiGe: 'Silicio-germanio — la banda prohibida de la aleación',
        SiGeNote: 'Abre la válvula de germanio y la capa deja de ser silicio. AIMS resuelve un modelo de enlace fuerte de la aleación en la composición que elijas: la brecha cae desde los 1,12 eV del silicio hacia los 0,66 eV del germanio, y eso es lo que se compra con una base de SiGe o un canal tensado. Pero la curva es un CRISTAL VIRTUAL — cada átomo suyo es un promedio ficticio de silicio y germanio — y la aleación real tiene un rasgo que el promedio no puede producir: su mínimo de conducción sigue siendo de tipo silicio hasta x ≈ 0,85 y luego pasa al del germanio, dejando un codo que ninguna curva suave reproduce. Por eso no se dibuja ninguna línea medida junto a la calculada.',
        SiGeCurveTitle: 'Banda prohibida según la composición',
        SiGeCurveX: 'fracción de germanio x',
        SiGeCurveY: 'brecha (eV)',
        SiGeCurveSeries: 'brecha de enlace fuerte',
        SiGeAtX: 'x = {{x}}',
        SiGeCrossover: 'Δ → L en la aleación real',
        SiGeGeFraction: 'fracción de germanio {{x}}',
        SectionRegime: 'Dos resistencias en serie',
        RegimeLead: 'Dos velocidades en serie, y manda la más lenta. El punto es esta receta.',
        LotLead: 'El cristal responde: el dopante del sustrato se difumina en la primera fracción de micra de la capa.',
        RegimeNote:
          'El gas tiene que llegar, y luego tiene que reaccionar: dos velocidades en ' +
          'serie, y manda la más lenta. Por debajo del cruce la reacción es el cuello ' +
          'de botella y la velocidad es un termómetro; por encima, el burbujeador ' +
          'manda y cincuenta grados apenas importan. La producción corre en la meseta ' +
          'a propósito: es el régimen que perdona a las lámparas. El punto es esta ' +
          'receta.',
        SectionRecipe: 'Receta',
        SectionStored: 'Recetas guardadas',
        SectionLot: 'El lote en el susceptor',
        LotNote:
          'La capa crece SOBRE el cristal que tiró CZ-07, y el cristal responde: el ' +
          'dopante se evapora del sustrato y se difumina en la primera fracción de ' +
          'micra. Cuanto más dopado el soporte — y más largo el horneado — más ancha ' +
          'la costura difuminada. Una vez que la capa existe, los dispositivos viven ' +
          'en ella: cada unión aguas abajo se forma contra el dopado epitaxial, no ' +
          'contra el volumen.',
        LotEmpty:
          'No hay lote en el susceptor. Envía una oblea desde WPR-08 o CLN-09 para ' +
          'crecer sobre ella.',
        LotIngot:
          'Este lote sigue siendo un lingote. WPR-08 tiene que cortarlo antes de ' +
          'crecer una capa.',
        LotCard: 'Hoja de ruta',
        LotBlank: 'Oblea de almacén: sustrato supuesto en 1.0 × 10⁻⁶/nm³.',
        LotRun: 'Crecer la capa y pasar el lote',
        SectionShift: 'Turno',
        TileRate: 'Velocidad de crecimiento',
        NoteSpitting: 'por encima del límite de nucleación en fase gas',
        TileRegime: 'Resistencia limitante',
        TileThickness: 'Espesor',
        TileNu: 'No uniformidad',
        TileSeam: 'Costura autodopada',
        NoteSeam: 'el sustrato respondiendo',
        NoteSeamQuiet: 'sustrato ligero, costura tranquila',
        TileHaze: 'Neblina',
        TileSlip: 'Deslizamiento',
        SlipYes: 'EN EL BORDE',
        SlipNo: 'ninguno',
        TileCycle: 'Ciclo',
        ChartHere: 'esta receta',
        ChartSpit: 'nucleación',
        AssistantBlurb:
          'Hazme una pregunta y diseñaré un experimento, lo ejecutaré contra este ' +
          'simulador y te contaré lo que realmente salió — y te devolveré una ' +
          'pregunta más afilada.',
        AssistantLead:
          'Barre la temperatura y mira cómo la velocidad se dobla hacia la meseta; ' +
          'luego pregúntame por qué la producción vive allí arriba.',
      },
      dicing: {
        NoteNoStressRelief: 'SIN alivio de tensiones',
        NoteAfterStressRelief: 'tras alivio de tensiones',
        ActionGrindDiceAndPass: 'Esmerila, corta y pasa los die',
        NoteNoLotOnTheChuck: 'No hay lote en el chuck. Envía aquí una oblea probada para esmerilarla y ' +
          'singularla.',
        SecondaryLabel: 'Desportillado de borde',
        ToolLabel: 'DICE-15 Esmerilado trasero y corte',
        NextTheseDieGoToBond: 'Estos die van a BOND-16 después, donde un capilar los presiona con cierta ' +
          'fuerza. Un die más débil no solo se rompe aquí — pregúntale al soldador ' +
          'cómo se ve el riesgo de craterización en silicio delgado.',
        TitleDieLostAtSingulation: 'Die perdidos en la singulación',
        LabelResidualDamage: 'daño residual',
        AxisYDamageM: 'daño (µm)',
        AxisXGrit: 'grano (#)',
        TitleDamageAndDieLostAgainst: 'Daño y die perdidos frente a grano de la muela',
        NoteCoarseToFine: 'de grueso a fino',
        LabelDieSaved: 'Die salvados',
        LabelWeibullM: 'm de Weibull',
        LabelStrength: 'Resistencia',
        HypDamageDepthScalesWithAbrasive: 'La profundidad del daño escala con el tamaño del abrasivo, y la ' +
          'resistencia va como el daño a una potencia negativa en vez de linealmente. ' +
          'Si eso es correcto, ir más fino debería comprar una cantidad ' +
          'desproporcionada de resistencia — y como la rotura es Weibull y no un ' +
          'umbral duro, el beneficio debería aparecer como menos die perdidos y no ' +
          'como un límite que se mueve.',
        AskedIsAFinerFinishingWheel: '¿Vale una muela de acabado más fina lo que cuesta en rendimiento?',
        SumGritAgainstDamageDepthAnd: 'Grano frente a profundidad de daño, y la estadística de Weibull de lo que ' +
          'sobrevive.',
        NextNarrowerStreetsMeanMoreDie: 'Calles más estrechas significan más die por oblea, y solo uno de esos tres ' +
          'métodos las sobrevive. Dado el número de die de esta receta, ¿cuánto vale ' +
          'una calle de 30 µm?',
        LabelReachesTheDie: 'alcanza el die',
        AxisYChipM: 'esquirla (µm)',
        AxisXFeedRateMmS: 'avance (mm/s)',
        TitleChipOutAgainstFeedRate: 'Desportillado frente a velocidad de avance',
        HypABladeCuttingFasterRemoves: 'Una hoja que corta más rápido quita más material por revolución y desgarra ' +
          'en vez de esmerilar, así que el desportillado debería crecer con el ' +
          'avance. Hay un límite duro y no blando: la esquirla o se queda dentro de ' +
          'la calle o alcanza el die, y pasado ese punto el die está muerto por bien ' +
          'que se viera el corte.',
        AskedWhatDoesCuttingFasterCost: '¿Qué cuesta cortar más rápido, y dónde está el límite?',
        SumFeedRateAgainstChipOut: 'Velocidad de avance frente a desportillado, y la calle que tiene que ' +
          'contenerlo.',
        NextWpr08RemovesSawDamage: 'WPR-08 quita el daño de sierra con exactamente esta física, en una oblea ' +
          'con 775 µm de margen. Aquí no hay ninguno. ¿Qué dice eso sobre cuál de las ' +
          'dos herramientas puede permitirse una muela gruesa?',
        LabelBackSide: 'dorso',
        AxisXThicknessM: 'espesor (µm)',
        TitleBreakingStrengthAgainstTargetThickness: 'Resistencia a rotura frente a espesor objetivo',
        LabelBackStrength: 'Resistencia del dorso',
        HypTheWheelLeavesCracksOf: 'La muela deja grietas de una profundidad fijada por su grano, y esa ' +
          'profundidad no encoge cuando encoge la oblea. Así que al adelgazar la ' +
          'oblea, la capa dañada se vuelve una FRACCIÓN mayor de ella, y el die se ' +
          'debilita más rápido de lo que el espesor solo predeciría. Si es así, el ' +
          'límite no es un espesor en absoluto: es una razón daño-espesor, y quitar ' +
          'el daño debería mover el límite.',
        AskedHowThinCanThisWafer: '¿Cuán delgada puede esmerilarse esta oblea antes de que los die empiecen a ' +
          'romperse?',
        SumGrindingDamageAgainstThicknessAnd: 'Daño de esmerilado frente a espesor, y por qué el alivio de tensión cambia ' +
          'la respuesta.',
        fault: {
          weakTape: {
            Evidence: [
              'Fuerza de pelado por debajo del límite de 1,9 N.',
              'Desconchado alto, kerf y hoja ambos normales.',
              'El vacío del chuck está bien, lo que descarta la otra manera de que una ' +
                'oblea se mueva.',
            ],
            Truth: 'El lote de cinta adhiere poco, así que los die se mueven al pasar la hoja ' +
              'y se desconchan en el borde de salida. La sierra está bien; el consumible ' +
              'no, y es la reparación más barata de la máquina.',
            Complaint: 'Los die se desplazan durante el corte en el lote de cinta nuevo.',
            Label: 'Cinta de montaje con poca adherencia',
          },
          chuckLeak: {
            Evidence: [
              'Vacío del chuck por debajo del límite de 70 kPa.',
              'Desconchado muy por encima del límite con ancho de kerf normal — una hoja ' +
                'gastada movería ambos.',
              'Los metros de hoja están dentro de límite.',
            ],
            Truth: 'El chuck tiene fuga, así que la oblea no queda plana y se mueve ' +
              'microscópicamente bajo la hoja. Todo lo que la hoja toca se desconcha. ' +
              'Parece un problema de hoja y las lecturas de hoja están limpias.',
            Complaint: 'El desconchado ha saltado en una sierra y los die cuestan de recoger.',
            Label: 'Chuck perdiendo vacío',
          },
          wornBlade: {
            Evidence: [
              'Metros de hoja pasados del límite de 450 m.',
              'Ancho de kerf por encima del límite de 45 µm.',
              'La resistencia del die es normal — este fallo está en los bordes, no bajo ' +
                'la superficie.',
            ],
            Truth: 'La hoja ha pasado su límite de corte. Una hoja gastada corta un kerf más ' +
              'ancho y desconcha más los bordes — y a diferencia del fallo de la muela, ' +
              'este aparece en la oblea inmediatamente si alguien mide el kerf.',
            Complaint: 'El ancho de calle ha subido y dos productos están cerca de perder su ' +
              'anillo de sellado.',
            Label: 'Hoja de corte gastada',
          },
          glazedWheel: {
            Evidence: [
              'Horas de muela pasadas del límite de reavivado.',
              'La flexión a tres puntos muestra resistencia muy por debajo del suelo de ' +
                '300 MPa.',
              'Kerf y desconchado son ambos normales, que es lo que hace creer a corte ' +
                'que es inocente.',
            ],
            Truth: 'La muela se ha vitrificado y ara en vez de cortar, así que deja el triple ' +
              'de daño subsuperficial. El CONTEO de die está intacto — cada die está ahí ' +
              'y se ve bien — y la distribución de resistencia debajo se ha derrumbado. ' +
              'La prensa de PKG-17 aplica 183 MPa y lo descubre.',
            Complaint: 'Ensamblaje está perdiendo die en la prensa de moldeo dos estaciones abajo. ' +
              'Corte dice que su conteo de die es perfecto y se niega a asumirlo.',
            Label: 'Muela de rectificado vitrificada',
          },
          diceHealthy: {
            Evidence: [
              'Cada lectura del banco es nominal.',
              'Kerf y desconchado están dentro de límites.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lote.',
            Complaint: 'Lote de rutina. Conteo de die y visual, ambos normales.',
            Label: 'Nada mal',
          },
        },
        NoteLostMinWafer: '{{a}} perdidos, {{b}} min/oblea',
        NoteKerfMIntoAM: 'kerf de {{a}} µm en una calle de {{b}} µm',
        NoteAtMpaFromThePackage: 'a {{a}} MPa desde el encapsulado',
        NoteWeibullM: 'Weibull m = {{a}}',
        SectionWhyThisToolIsDangerous: 'Por qué esta máquina es peligrosa',
        NoteNothingYouCanMeasureHere: 'Nada de lo que puedas medir aquí te dice si el die sobrevivirá. El daño de ' +
          'rectificado es invisible, no cuesta ningún die en esta máquina, y decide ' +
          'cuántos encapsulados se agrietan en una prensa de moldeo dos máquinas más ' +
          'adelante.',
        NoteThisLotIsStillAn:
          'Este lote sigue siendo un lingote. WPR-08 tiene que cortarlo antes de que ' +
          'esta máquina pueda tocarlo.',
        LabelDieThickness: 'Espesor del die',
        LabelGrindingDamage: 'Daño de rectificado',
        LabelBackSideStrength: 'Resistencia de cara trasera',
        LabelEdgeStrength: 'Resistencia de borde',
        NoteSetByChippingNotBy: 'fijada por el desconchado, no por la muela',
        LabelSurvivesTheMouldPress: 'Sobrevive a la prensa de moldeo',
        LabelChipOut: 'Desconchado',
        LabelGoodDie: 'Die buenos',
        AssistantLead: 'Asistente de indagación DICE-15',
        AssistantBlurb:
          'Pregunta hasta qué grosor se puede adelgazar, si una muela más fina se ' +
          'paga sola, o qué cuesta cortar más rápido. Cada respuesta es un desbaste y ' +
          'corte ejecutado con esta receta.',
        reading: {
          chipping: {
            Label: 'Inspección de bordes, 50 die (1.5 h)',
          },
          kerf: {
            Label: 'Ancho de kerf en oblea monitor (1.0 h)',
          },
          strength: {
            Label: 'Flexión a tres puntos en 20 die — destructivo (2.0 h)',
          },
          tapePeel: {
            Label: 'Fuerza de pelado de la cinta',
          },
          chuckVacuum: {
            Label: 'Vacío del chuck',
          },
          bladeCuts: {
            Label: 'Metros cortados por la hoja',
          },
          wheelHours: {
            Label: 'Horas de muela desde el reavivado',
          },
        },
        aspect: {
          tape: {
            Action: 'Pasar a un lote de cinta nuevo y re-laminar',
            Label: 'Cinta de montaje',
          },
          chuck: {
            Action: 'Localizar y sellar la fuga, re-verificar la sujeción en toda la placa',
            Label: 'Vacío del chuck',
          },
          blade: {
            Action: 'Cambiar la hoja y re-cualificar el kerf en oblea monitor',
            Label: 'Hoja de corte',
          },
          wheel: {
            Action: 'Reavivar la muela, o cambiarla si está pasada',
            Label: 'Muela de rectificado',
          },
        },
      },
      bonding: {
        failure: {
          'attach void': 'vacío de pegado',
          intermetallic: 'intermetálico',
          'cratered pad': 'pad craterizado',
          'lifted ball': 'bola levantada',
          none: 'ninguno',
        },
        ActionAttachBondAndPass: 'Pega, suelda y pasa los die',
        UnitPctWelded: '% soldado',
        NoteNoDieOnTheBonder: 'No hay die en el soldador. Singula una oblea en DICE-15 y trae los die aquí.',
        SecondaryLabel: 'Vacíos en el pegado de die',
        ToolLabel: 'BOND-16 Pegado de die y soldadura de alambre',
        NextFt18AcceleratesExactlyThis: 'FT-18 acelera exactamente este tipo de difusión para encontrar las piezas ' +
          'que habrían fallado en el campo. Pregúntale cuántas horas de burn-in vale ' +
          'este intermetálico.',
        LabelImcThickness: 'espesor de IMC',
        AxisXCureTemperatureC: 'temperatura de curado (°C)',
        TitleIntermetallicAgainstCureTemperature: 'Intermetálico frente a temperatura de curado',
        LabelFromTheBond: 'De la soldadura',
        LabelImcTotal: 'IMC total',
        HypGoldAluminiumIntermetallicGrowsBy: 'El intermetálico oro-aluminio crece por difusión, así que su espesor ' +
          'debería depender del tiempo y la temperatura y no de cómo se hizo la ' +
          'soldadura. La soldadura misma dura milisegundos y el curado del epoxi dura ' +
          'horas — así que si la difusión es el mecanismo, casi todo el crecimiento ' +
          'debería ocurrir durante el CURADO, un paso que nadie considera parte de la ' +
          'soldadura.',
        AskedHowMuchIntermetallicIsThis: '¿Cuánto intermetálico está creciendo esta receta, y dónde crece?',
        SumTimeTemperatureGrowthOfIntermetallic: 'Crecimiento tiempo-temperatura del intermetálico, y por qué el curado es ' +
          'parte de la soldadura.',
        NextPrb14HasTheSame: 'PRB-14 tiene la misma aritmética con die en una oblea en vez de alambres ' +
          'en un die. Ambas son exponenciales en un número que eligió otra persona — ' +
          '¿en cuál de las dos puede un ingeniero de proceso hacer algo realmente?',
        AxisXWiresPerDie: 'alambres por die',
        TitleDieYieldAgainstWireCount: 'Rendimiento de die frente a número de alambres',
        LabelPerWireYield: 'Rendimiento por alambre',
        HypADieIsGoodOnly: 'Un die es bueno solo si cada alambre en él es bueno, así que el ' +
          'rendimiento de die debería ser el rendimiento por alambre elevado al ' +
          'número de alambres. Eso es una exponencial en el número de pines, lo que ' +
          'significa que una calidad de soldadura perfectamente adecuada con pocos ' +
          'pines se vuelve inutilizable con muchos — sin que nada del soldador ' +
          'cambie.',
        AskedWhyDoesAHigherPin: '¿Por qué un mayor número de pines rinde peor con una receta sin cambios?',
        SumPerWireYieldRaisedTo: 'El rendimiento por alambre elevado al número de alambres, y lo que eso ' +
          'exige de cada soldadura.',
        NextThatDieYieldIsOne: 'Ese rendimiento de die es un alambre elevado al número de alambres. ' +
          'Pregúntame qué le pasa en una pieza con seiscientos alambres en vez de ' +
          'esta.',
        TitleDieYieldAcrossTheWindow: 'Rendimiento de die a lo largo de la ventana',
        LabelCrateringRisk100: 'riesgo de craterización ×100',
        LabelBondedArea: 'área soldada',
        AxisXUltrasonicPowerMw: 'potencia ultrasónica (mW)',
        TitleTwoFailureModesOneKnob: 'Dos modos de fallo, una perilla',
        NotePerBond: 'por soldadura',
        LabelCratering: 'Craterización',
        LabelBondedAreaL113: 'Área soldada',
        HypUltrasonicEnergyIsWhatScrubs: 'La energía ultrasónica es lo que restriega el óxido del pad y suelda la ' +
          'bola a él, así que demasiado poca debería dejar una bola sin soldar que se ' +
          'levanta. La misma energía va a alguna parte cuando la soldadura ya está ' +
          'completa: al silicio bajo el pad. Si es así, los dos modos de fallo están ' +
          'en extremos opuestos de una misma perilla, y la ventana está donde ambos ' +
          'son simultáneamente pequeños, no donde alguno se minimiza.',
        AskedWhereIsTheBondingWindow: '¿Dónde está la ventana de soldadura, y qué falla a cada lado de ella?',
        SumLiftedBallsAtOneEnd: 'Bolas levantadas en un extremo, pads craterizados en el otro, y la ventana ' +
          'entre ambos.',
        fault: {
          starvedDispense: {
            Evidence: [
              'Volumen de dispensado al 45% contra un suelo del 90%.',
              'Fracción de huecos en rayos X por encima del límite del 10%.',
              'Las uniones de hilo son perfectas — este fallo está bajo el die, no encima.',
            ],
            Truth: 'La bomba de dispensado entrega el 45% de lo nominal. No hay epoxi ' +
              'suficiente para mojar toda la huella del die, así que el attach queda con ' +
              'huecos — que es un problema de camino térmico con el que la pieza no ' +
              'fallará hasta que esté corriendo caliente en el campo.',
            Complaint: 'Los rayos X muestran huecos bajo el die en un lote que unió perfectamente.',
            Label: 'Dispensado de adhesivo insuficiente',
          },
          dirtyPads: {
            Evidence: [
              'Contaminación de pads al 55% contra un límite del 15%.',
              'Potencia entregada, geometría de bola y conteo de uniones, todos normales.',
              'Un solo producto — un fallo de máquina estaría en todos.',
            ],
            Truth: 'No hay nada mal con el bonder. Los pads llegan oxidados — una limpieza por ' +
              'plasma saltada aguas arriba — y el óxido de aluminio es lo que la energía ' +
              'ultrasónica tiene que atravesar. Cambiar un transductor sano cuesta cinco ' +
              'horas y no arregla nada.',
            Complaint: 'No-adhesión en la primera unión, en un solo producto, y el bonder pasa ' +
              'todas las comprobaciones.',
            Label: 'Pads llegando contaminados',
          },
          wornCapillary: {
            Evidence: [
              'Uniones desde el cambio pasadas del límite de 500.000.',
              'Diámetro de bola por encima del límite de 62 µm.',
              'La potencia ultrasónica entregada es normal.',
            ],
            Truth: 'El capilar ha pasado su conteo de uniones. Una punta gastada imprime una ' +
              'bola más ancha e irregular, que es un riesgo de corto a paso fino — y ' +
              'agarra mal, así que la soldadura también es peor. Además es la reparación ' +
              'más barata del back end, y por eso vale la pena distinguirlo del ' +
              'transductor.',
            Complaint: 'Las bolas salen sobredimensionadas e irregulares, y dos están ' +
              'cortocircuitando con un vecino.',
            Label: 'Capilar gastado',
          },
          tiredTransducer: {
            Evidence: [
              'Potencia entregada al 62% contra un suelo del 90%.',
              'La tracción de hilo muestra área soldada por debajo del límite del 70%.',
              'Los pads están limpios y el capilar dentro de su conteo.',
            ],
            Truth: 'El transductor entrega el 62% de la potencia comandada. La consigna está ' +
              'bien y la energía que llega a la bola no — por eso la receta parece ' +
              'inocente. El área soldada cae, y una tracción de hilo es lo único que lo ' +
              'muestra.',
            Complaint: 'Los fallos de no-adhesión han subido en un bonder. La receta no se ha ' +
              'tocado y la consigna de potencia lee correcta.',
            Label: 'Transductor entregando menos de lo que reporta',
          },
          bondHealthy: {
            Evidence: [
              'Cada lectura del banco es nominal.',
              'El área soldada está por encima del suelo del 70%.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lote.',
            Complaint: 'Lote de rutina. El rendimiento de unión está donde debe.',
            Label: 'Nada mal',
          },
        },
        NoteSPerDie: '{{a}} s por die',
        NoteOverWires: 'sobre {{a}} hilos',
        SectionTheWindowHasAFloor: 'La ventana tiene suelo y techo',
        NoteUltrasonicEnergyHasToBreak: 'La energía ultrasónica tiene que romper el óxido de aluminio antes de que ' +
          'ningún metal pueda soldar, así que muy poca deja la bola levantada. La ' +
          'misma energía entra en el silicio una vez hecha la unión, así que ' +
          'demasiada crateriza el pad. Sube la potencia y vigila los dos extremos.',
        LabelWeldedArea: 'Área soldada',
        NoteOfTheBallFootprint: 'de la huella de la bola',
        LabelBallShear: 'Cizalla de bola',
        LabelCrateringRisk: 'Riesgo de cráter',
        NotePerWire: 'por hilo',
        LabelIntermetallic: 'Intermetálico',
        NoteVoidingPast15M: 'huecos pasadas 1,5 µm',
        LabelDieYield: 'Rendimiento por die',
        LabelFailureMode: 'Modo de fallo',
        reading: {
          attachVoid: {
            Label: 'Rayos X del die attach, 10 unidades (1.5 h)',
          },
          ballShear: {
            Label: 'Cizalla de bola a rotura, 20 uniones (1.0 h)',
          },
          pullStrength: {
            Label: 'Tracción de hilo a rotura, 30 hilos (2.0 h)',
          },
          dispenseVolume: {
            Label: 'Volumen de epoxi dispensado',
          },
          padClean: {
            Label: 'Contaminación de pads de entrada',
          },
          capillaryBonds: {
            Label: 'Uniones desde el cambio de capilar',
          },
          ultrasonicDelivered: {
            Label: 'Potencia ultrasónica entregada',
          },
        },
        aspect: {
          epoxy: {
            Action: 'Recalibrar la bomba de dispensado y purgar la jeringa',
            Label: 'Dispensado de epoxi',
          },
          pads: {
            Action:
              'No es una reparación aquí — los pads están contaminados y la limpieza por ' +
              'plasma de arriba es la dueña',
            Label: 'Condición de pads de entrada',
          },
          capillary: {
            Action: 'Cambiar el capilar y re-cualificar la geometría de la bola',
            Label: 'Capilar',
          },
          transducer: {
            Action: 'Cambiar el transductor y recalibrar la potencia entregada contra referencia',
            Label: 'Transductor ultrasónico',
          },
        },
        AssistantBlurb:
          'Hazme una pregunta y diseñaré un experimento, lo ejecutaré contra este ' +
          'solucionador e informaré de lo que realmente salió — y luego te devolveré ' +
          'una pregunta más afilada.',
        AssistantLead:
          'Fuerza, potencia ultrasónica y tiempo forman una ventana, no un ajuste — y ' +
          'la ventana se mueve con cada micra de hilo. Pregúntame dónde están sus ' +
          'bordes.',
      },
      packaging: {
        failure: {
          'wire sweep': 'barrido de alambres',
          'mould void': 'vacío de moldeo',
          popcorn: 'popcorn',
          warpage: 'alabeo',
          none: 'ninguno',
        },
        ActionMouldCureReflow: 'Moldea, cura, refunde y pasa los encapsulados',
        NoteNoDieOnThePress: 'No hay die en la prensa. Suelda un lote en BOND-16 y tráelo aquí.',
        SecondaryLabel: 'Vapor interno en reflow',
        ToolLabel: 'PKG-17 Moldeo y curado',
        NextEveryPackageThatLeavesHere: 'Cada encapsulado que sale de aquí va a FT-18 y luego a un cliente. ¿Cuál ' +
          'de los fallos de este panel encontraría el burn-in, y cuál solo aparecería ' +
          'en una placa?',
        AxisXCureMin: 'curado (min)',
        TitlePackageYieldAgainstPostMould: 'Rendimiento del encapsulado frente a curado post-moldeo',
        HypTransferMouldingFillsACavity: 'El moldeo por transferencia llena una cavidad con un compuesto viscoso ' +
          'alrededor de alambres que pueden ser empujados. El curado que sigue lo ' +
          'reticula. Si el curado importa mecánicamente y no cosméticamente, el ' +
          'alabeo y el rendimiento deberían seguir moviéndose después de que la pieza ' +
          'parezca terminada — y un estilo sin alambres debería esquivar todo un modo ' +
          'de fallo.',
        AskedWhatIsTheCureFor: '¿Para qué es el curado, y qué cambia el estilo de encapsulado?',
        SumTransferMouldingVoidsCureAnd: 'Moldeo por transferencia, vacíos, curado, y lo que cambia el estilo de ' +
          'encapsulado.',
        NextTheStressFreeTemperatureIs: 'La temperatura libre de tensión es la de moldeo. ¿Qué le haría moldear 30 ' +
          '°C más frío a ambas curvas — y qué costaría en curado y en flujo?',
        LabelAtRoomTemperature: 'a temperatura ambiente',
        LabelAtReflow: 'en reflow',
        AxisXMouldCapM: 'tapa de moldeo (µm)',
        TitleWarpageAgainstMouldCapThickness: 'Alabeo frente a espesor de la tapa de moldeo',
        LabelMouldVoids: 'Vacíos de moldeo',
        NoteTheOneThatMatters: 'el que importa',
        LabelWarpAtReflow: 'Alabeo en reflow',
        LabelWarpAtRoom: 'Alabeo a ambiente',
        HypMouldCompoundSiliconAndLaminate: 'El compuesto de moldeo, el silicio y el laminado tienen coeficientes de ' +
          'expansión distintos y se unieron a la temperatura de moldeo. Esa ' +
          'temperatura es la única a la que el encapsulado está libre de tensión; en ' +
          'cualquier otra se curva. Si es así, el alabeo debería depender de la RAZÓN ' +
          'DE ESPESORES de las capas y no de ninguna por sí sola, y debería haber un ' +
          'punto de equilibrio.',
        AskedWhatMakesThePackageWarp: '¿Qué hace que el encapsulado se alabee, y cuál alabeo es el que importa?',
        SumABimetallicStripMadeOf: 'Una lámina bimetálica hecha de tres materiales, y el alabeo que decide las ' +
          'bolas.',
        NextReflowPeakIsTheOther: 'El pico de reflow es la otra mitad de esto. Si la casa de ensamblaje corre ' +
          '5 °C más caliente de lo que dice el perfil, ¿cuál de estos números se ' +
          'mueve — y alguien se enteraría antes de que las piezas estuvieran en ' +
          'placas?',
        AxisYFractionOfSaturation: 'fracción de saturación',
        LabelSteamPressure: 'presión de vapor',
        AxisXHoursOnTheFloor: 'horas en el piso',
        TitleSteamPressureAgainstFloorLife: 'Presión de vapor frente a vida en piso',
        LabelSteamPressureL116: 'Presión de vapor',
        LabelMoisture: 'Humedad',
        HypMouldCompoundAbsorbsWaterFrom: 'El compuesto de moldeo absorbe agua del aire, lenta y reversiblemente. En ' +
          'el reflow la pieza pasa de 250 °C en segundos y esa agua se vuelve vapor ' +
          'dentro de una cavidad sellada. Si ese es el mecanismo, el fallo debería ' +
          'depender de cuánto tiempo ESTUVO la pieza en el piso y no de nada que se ' +
          'le haya hecho — un defecto creado por el almacenamiento.',
        AskedWhyDoesAPackageThat: '¿Por qué un encapsulado que pasó todo falla cuando se refunde?',
        SumAbsorbedMoistureSteamPressureAt: 'Humedad absorbida, presión de vapor en la interfaz del die, y el reloj de ' +
          'vida en piso.',
        fault: {
          wetParts: {
            Evidence: [
              'Tiempo fuera de bolsa muy pasado del límite de 48 h.',
              'El corte tras reflujo muestra delaminación por encima del límite del 2%.',
              'El perfil del horno está en objetivo — esto no es un fallo de ' +
                'sobre-temperatura.',
            ],
            Truth: 'El empaque seco se abrió y las piezas estuvieron fuera una semana. La ' +
              'humedad absorbida se convierte en vapor en el reflujo y revienta el ' +
              'encapsulado desde dentro — la clásica grieta de palomita. Nada en PKG-17 ' +
              'está roto; la disciplina de manejo sí, y la única cura es un horneado de ' +
              '24 horas.',
            Complaint: 'Delaminación y encapsulados agrietados tras el reflujo, en un lote que ' +
              'moldeó y probó limpio.',
            Label: 'Piezas con humedad absorbida',
          },
          staleCompound: {
            Evidence: [
              'Edad del compuesto al 85% de vida útil contra un límite del 30%.',
              'Longitud de flujo corta.',
              'Presión de transferencia nominal — la prensa hace su trabajo.',
            ],
            Truth: 'El compuesto ha avanzado dentro de la bolsa y gelifica antes de lo debido. ' +
              'Produce exactamente el mismo disparo corto que una prensa débil — y las ' +
              'lecturas de la prensa son perfectamente normales, que es lo único que los ' +
              'separa en el banco.',
            Complaint: 'El mismo relleno incompleto, y la prensa se recalibró la semana pasada.',
            Label: 'Compuesto de moldeo pasado de vida útil',
          },
          lowTransfer: {
            Evidence: [
              'Presión de transferencia al 45% contra un suelo del 90%.',
              'Longitud de flujo por debajo del límite de 55 mm.',
              'La edad del compuesto está bien dentro de vida útil, lo que descarta al ' +
                'parecido.',
            ],
            Truth: 'El pistón de transferencia entrega el 45% de la presión nominal. El ' +
              'compuesto no llega al final de la cavidad antes de gelificar, así que las ' +
              'esquinas lejanas quedan sin llenar. Es un problema de prensa y el ' +
              'compuesto está en fecha.',
            Complaint: 'Relleno incompleto en las esquinas lejanas del cuerpo grande.',
            Label: 'Presión de transferencia baja',
          },
          pkgHealthy: {
            Evidence: [
              'Cada lectura del banco es nominal.',
              'Longitud de flujo y supervivencia al reflujo, ambas buenas.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr el lote.',
            Complaint: 'Lote de rutina. Alabeo y visual, ambos normales.',
            Label: 'Nada mal',
          },
        },
        NoteHAtRh: '{{a}} h al {{b}}% HR',
        NoteMmBody: 'cuerpo de {{a}} mm',
        SectionWarpageIsGeometryNotProcess: 'El alabeo es geometría, no proceso',
        NoteThePackageIsFlatAt: 'El encapsulado es plano a la temperatura de moldeo y a ninguna otra. Todo ' +
          'lo que viene después es una diferencia de temperatura actuando sobre un ' +
          'desajuste de expansión, y crece con el CUADRADO del tamaño del cuerpo — ' +
          'así que se arregla con un material o un espesor, nunca en la prensa.',
        LabelWarpageAtRoom: 'Alabeo a temperatura ambiente',
        LabelWarpageAtReflow: 'Alabeo en reflujo',
        Note100MToSeatThe: '< 100 µm para asentar las bolas',
        LabelMoistureAbsorbed: 'Humedad absorbida',
        LabelSteamAtTheInterface: 'Vapor en la interfaz',
        NoteInterfaceHolds26Bar: 'la interfaz aguanta 26 bar',
        LabelWireSweep: 'Barrido de hilos',
        NoteWiresTouchPast04: 'los hilos se tocan pasado 0,4',
        LabelPackageYield: 'Rendimiento de encapsulado',
        AssistantLead: 'Asistente de indagación PKG-17',
        AssistantBlurb:
          'Pregunta por qué un chip bueno falló en el reflujo, qué hace que el ' +
          'encapsulado se combe, o para qué sirve el post-curado. Cada respuesta es ' +
          'un moldeo y reflujo ejecutado con esta receta.',
        reading: {
          popcorn: {
            Limit: 'sin grieta, < 2 bar',
            Label: 'Reflujo de 20 unidades y corte (4.0 h)',
          },
          mouldVoid: {
            Label: 'Corte de 12 unidades por huecos — destructivo (2.5 h)',
          },
          ovenPeak: {
            Label: 'Desviación del pico de reflujo',
          },
          moisture: {
            Label: 'Exposición fuera del dry-pack',
          },
          compoundAge: {
            Label: 'Edad del compuesto contra vida útil',
          },
          transferPressure: {
            Label: 'Presión de transferencia',
          },
        },
        aspect: {
          oven: {
            Action: 'Re-perfilar el horno contra una placa instrumentada',
            Label: 'Perfil del horno de reflujo',
          },
          drypack: {
            Action: 'Hornear el lote a 125 °C y restaurar la disciplina de dry-pack',
            Label: 'Manejo de dry-pack',
          },
          compound: {
            Action: 'Desechar las pastillas abiertas y cargar un lote nuevo, en fecha',
            Label: 'Compuesto de moldeo',
          },
          press: {
            Action: 'Recalibrar la presión del pistón de transferencia y revisar los sellos',
            Label: 'Prensa de moldeo',
          },
        },
      },
      finaltest: {
        ValueNo: 'no',
        ValueYes: 'sí',
        ValueDays: '{{a}} días',
        ActionBurnInTestAndBin: 'Burn-in, prueba y clasifica',
        UnitPoints: 'puntos',
        NoteNoLotOnTheHandler: 'No hay lote en el handler. Encapsula un lote en PKG-17 y tráelo aquí.',
        NoteThisPopulationIsNot: 'esta población no está fallando temprano',
        NoteInfantMortalityScreening: 'mortalidad infantil — el cribado la elimina',
        LabelEscapes: 'Escapes',
        SecondaryLabel: 'Deriva de fallos en burn-in',
        ToolLabel: 'FT-18 Prueba final y burn-in',
        LabelLotHours: 'horas de lote',
        TitleAndWhatItCostsThe: 'Y lo que le cuesta al piso',
        AxisYPartsEscaping: 'piezas que escapan',
        AxisXTestSecondsPerPart: 'segundos de prueba por pieza',
        TitleEscapesAgainstTestTime: 'Escapes frente a tiempo de prueba',
        LabelRoomOnlyEscapes: 'Escapes solo a temperatura ambiente',
        LabelLotTime: 'Tiempo de lote',
        HypMoreTestTimeMeansMore: 'Más tiempo de prueba significa más patrones ejecutados y más de la pieza ' +
          'ejercitada, así que los escapes deberían caer — con rendimientos ' +
          'decrecientes, ya que los fallos fáciles se encuentran primero. El tiempo ' +
          'de prueba se multiplica por el tamaño del lote, así que el segundo ' +
          'marginal es caro de un modo fácil de subestimar por pieza.',
        AskedHowMuchTestTimeDoes: '¿Cuánto tiempo de prueba necesita esta pieza, y qué compra más?',
        SumTestTimeAgainstEscapesAnd: 'Tiempo de prueba frente a escapes, y las esquinas de temperatura que más ' +
          'cuestan.',
        NextBond16GrowsIntermetallicThat: 'BOND-16 crece intermetálico que sigue creciendo a temperatura de ' +
          'operación. ¿Es ese un mecanismo que el burn-in atraparía, o uno que ' +
          'aceleraría hasta dañar una pieza que estaba bien?',
        LabelLotTimeL241: 'tiempo de lote',
        AxisYLotHours: 'horas de lote',
        TitleWhatItCosts: 'Lo que cuesta',
        AxisYFractionScreened: 'fracción cribada',
        AxisXBurnInHours: 'horas de burn-in',
        TitleScreenedFractionAndLotTime: 'Fracción cribada y tiempo de lote frente a burn-in',
        LabelJustified: 'Justificado',
        LabelScreened: 'Cribado',
        LabelFieldHours: 'Horas de campo',
        LabelAcceleration: 'Aceleración',
        HypBurnInWorksByRunning: 'El burn-in funciona corriendo la pieza caliente para que los mecanismos de ' +
          'fallo avancen más rápido que en servicio. Solo criba algo si la población ' +
          'tiene mortalidad infantil que cribar — una forma de Weibull menor que uno. ' +
          'En una población sana, cada hora de burn-in consume vida útil de piezas ' +
          'que iban a estar bien.',
        AskedIsBurnInEarningThe: '¿El burn-in se está ganando las horas que toma en esta población?',
        SumInfantMortalityTheAccelerationFactor: 'Mortalidad infantil, el factor de aceleración, y cuándo el burn-in solo ' +
          'consume vida.',
        NextPrb14AlreadyAppliedA: 'PRB-14 ya aplicó una banda de guarda a los die dentro de estas piezas. Dos ' +
          'bandas de guarda en serie desechan más que cualquiera sola — ¿está la ' +
          'segunda encontrando algo que la primera no encontró?',
        LabelGoodPartsScrapped: 'piezas buenas desechadas',
        LabelBadPartsShipped: 'piezas malas enviadas',
        AxisXGuardBandFractionOfSpec: 'banda de guarda (fracción del ancho de spec)',
        TitleTwoErrorsOneKnob: 'Dos errores, una perilla',
        HypTheTesterMeasuresWithAn: 'El tester mide con una incertidumbre propia, así que una pieza cerca del ' +
          'límite puede leer a cualquiera de sus lados. Meter el límite hacia adentro ' +
          'atrapa más piezas malas marginales y necesariamente rechaza también más ' +
          'buenas marginales. Si eso es correcto, no hay ajuste con cero de ambos — ' +
          'solo una elección sobre qué error prefiere cometer el negocio.',
        AskedWhatDoesMovingTheTest: '¿Qué compra y qué cuesta realmente mover el límite de prueba hacia adentro?',
        SumEscapesAgainstOverkillAndWhy: 'Escapes frente a sobre-rechazo, y por qué ninguno puede ser cero.',
        fault: {
          infantMortality: {
            Evidence: [
              'Forma de Weibull 0,35 contra un suelo de 0,45 — peor que la población para ' +
                'la que se dimensionó el burn-in.',
              'Fallo en burn-in muy por encima de límite con acuerdo de re-prueba perfecto.',
              'Gauge, contactor y handler, todos nominales.',
            ],
            Truth: 'No hay nada mal con el tester. La forma de Weibull de entrada ha caído a ' +
              '0,35 — una cola débil, que es lo que la mortalidad infantil ES — y el ' +
              'burn-in está haciendo exactamente su trabajo al encontrarla. El problema ' +
              'está en el front end, y el burn-in es la única razón de que no esté en el ' +
              'cliente.',
            Complaint: 'El fallo en burn-in se ha triplicado. Ingeniería de prueba quiere ' +
              'recalibrar el tester.',
            Label: 'Población de entrada con mortalidad infantil',
          },
          coldHandler: {
            Evidence: [
              'Desviación de remojo del handler −18 °C contra una ventana de ±3 °C.',
              'El acuerdo de re-prueba es normal, porque el tester está bien.',
              'Devoluciones de campo en vez de pérdida de rendimiento — un escape, no ' +
                'overkill.',
            ],
            Truth: 'El handler está 18 °C por debajo de la temperatura que el registro afirma. ' +
              'Las piezas se prueban en caliente estando frías, así que los límites ' +
              'calientes no se ejercitan de verdad — la prueba aprueba unidades que ' +
              'debería suspender, y eso es un ESCAPE, que es la dirección cara de ' +
              'equivocarse.',
            Complaint: 'Las pruebas en caliente pasan demasiado fácil, y han empezado a llegar ' +
              'devoluciones de campo de un cliente.',
            Label: 'Handler sin llegar a temperatura de remojo',
          },
          wornContactor: {
            Evidence: [
              'Inserciones pasadas del límite de 300.000.',
              'Desacuerdo de re-prueba por encima de límite.',
              'El sigma del gauge está dentro de límite, así que la cadena de medición en ' +
                'sí está sana.',
            ],
            Truth: 'El contactor ha pasado su límite de inserciones y añade resistencia. ' +
              'Produce el mismo desacuerdo de re-prueba que un tester ruidoso — y el ' +
              'conteo de inserciones lo nombra, mientras la calibración del gauge vuelve ' +
              'limpia.',
            Complaint: 'Fallos de continuidad en un sitio de un handler de cuatro.',
            Label: 'Contactor gastado',
          },
          noisyTester: {
            Evidence: [
              'Sigma del gauge 16% contra un límite del 6%.',
              'Desacuerdo de re-prueba muy por encima del 0,5% — las mismas unidades caen ' +
                'en bins distintos dos veces.',
              'Inserciones del contactor y temperatura del handler, ambas nominales.',
            ],
            Truth: 'El sigma del tester es el 16% del ancho de especificación contra un límite ' +
              'del 6%. Unidades buenas cerca de un límite están siendo suspendidas — la ' +
              'pérdida es OVERKILL, no defectos, y es invisible salvo que alguien ' +
              'repruebe los rechazos. Se están tirando dos puntos de rendimiento cada ' +
              'turno.',
            Complaint: 'El rendimiento de bin 1 ha caído dos puntos y el cliente no ha devuelto ni ' +
              'un solo fallo.',
            Label: 'Cadena de medición del tester ruidosa',
          },
          ftHealthy: {
            Evidence: [
              'Cada lectura del banco es nominal.',
              'El acuerdo de re-prueba está dentro de límite.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es enviar el lote.',
            Complaint: 'Lote de rutina. El reparto de bins coincide con el pronóstico.',
            Label: 'Nada mal',
          },
        },
        NotePartsAtS: '{{a}} piezas a {{b}} s',
        NoteGoodPartsScrapped: '{{a}} piezas buenas desechadas',
        NotePartsEachOneAReturn: '{{a}} piezas — cada una una devolución',
        NoteFromHOfBurnIn: 'de {{a}} h de burn-in',
        NoteCAgainstCUse: '{{a}} C frente a {{b}} C de uso',
        SectionTheGuardBandIsThe: 'La banda de guarda es toda la decisión',
        NoteATesterMeasuresWithError: 'Un tester mide con error, así que algunas piezas malas leen bien y algunas ' +
          'buenas leen mal. Mover el punto de corte hacia dentro compra menos escapes ' +
          'y los paga en overkill, uno a uno. No hay ajuste que evite ambos, y dónde ' +
          'te sientas en ese canje es un cálculo sobre lo que te cuesta una ' +
          'devolución.',
        LabelAccelerationFactor: 'Factor de aceleración',
        LabelEquivalentFieldLife: 'Vida de campo equivalente',
        LabelBurnInJustified: 'Burn-in justificado',
        LabelOverkill: 'Overkill',
        LabelTestFloorTime: 'Tiempo de sala de prueba',
        reading: {
          burnInFallout: {
            Label: 'Fallos de burn-in en 500 unidades (12 h)',
          },
          retest: {
            Label: 'Re-probar 200 unidades y comparar bins (1.0 h)',
          },
          weibull: {
            Limit: '> 0.45 (cualificado: 0.55)',
            Label: 'Forma de Weibull de entrada',
          },
          handlerTemp: {
            Label: 'Desviación de remojo del handler',
          },
          contactorInsertions: {
            Label: 'Inserciones del contactor',
          },
          gaugeSigma: {
            Label: 'Sigma del gauge del tester',
          },
        },
        aspect: {
          population: {
            Action:
              'No es una reparación aquí — los die tienen mortalidad infantil y el ' +
              'front-end es el dueño',
            Label: 'Población de entrada',
          },
          handler: {
            Action: 'Recalibrar los termopares de remojo del handler',
            Label: 'Temperatura del handler',
          },
          contactor: {
            Action: 'Cambiar el contactor y re-verificar continuidad con unidad dorada',
            Label: 'Contactor',
          },
          gauge: {
            Action: 'Recalibrar el tester y verificar contra una unidad dorada',
            Label: 'Cadena de medición del tester',
          },
        },
        AssistantBlurb:
          'Hazme una pregunta y diseñaré un experimento, lo ejecutaré contra este ' +
          'solucionador e informaré de lo que realmente salió — y luego te devolveré ' +
          'una pregunta más afilada.',
        AssistantLead:
          'Cuánta prueba es suficiente es un número que se calcula, no una ' +
          'preferencia. Pregúntame cuánto cuesta un escape frente a una hora de ' +
          'burn-in.',
      },
      common: {
        StartProcess: 'Iniciar proceso',
        HoldProcess: 'Pausar proceso',
        LoadWafer: 'Cargar oblea nueva',
        Measuring: 'Midiendo…',
        SessionRecord: 'SimFab — registro de sesión',
        ExportRecord: 'Exportar registro de sesión',
      },
      scope: {
        Pending:
          'Esta estación está colocada y se puede entrar en ella, pero aún no está ' +
          'simulada. Hoy hay seis módulos que ejecutan toda su física — grabado, ' +
          'deposición, litografía, térmico, metrología e instalaciones — y la ' +
          'arquitectura probada en ellos es la base sobre la que se construirá el ' +
          'resto.',
        NothingFaked:
          'Mientras tanto no se finge nada. Abajo está el alcance que {{tag}} tiene ' +
          'que cubrir: es la misma página a partir de la cual se construyó cada uno ' +
          'de los seis módulos activos.',
        WhereItSits: '{{zone}} — dónde encaja esto en el flujo',
        Takes: 'Recibe',
        Makes: 'Entrega',
        Macro: 'Macroscópico — protocolos y procesos',
        Micro: 'Microscópico — la ciencia de debajo',
        Engines: 'Solucionadores previstos',
        Competency: 'Competencia a la que sirve esta estación',
        BackToFloor: 'Volver a la planta',
      },
      deposition: {
        MdChartTitle: 'Átomos en la película frente al tiempo',
        MdChartX: 'tiempo simulado (ps)',
        MdChartY: 'átomos en la película',
        MdChartSeries: 'En la película',
        MdChartAllVapor: 'todos los átomos del vapor',
        MdStatus: '{{a}} de {{b}} átomos de precursor en la película · oblea {{c}} K · {{d}} ps',
        MdTemp: 'Temperatura de la oblea',
        MdReset: 'Restablecer',
        MdPause: 'Pausar',
        MdRun: 'Ejecutar',
        MdFailed: 'El motor de dinámica molecular no se pudo cargar.',
        MdLoading: 'Cargando el motor de dinámica molecular…',
        MdNote: 'Una simulación de dinámica molecular en vivo, no una animación: átomos de ' +
          'vapor sobre una losa fija de oblea, fuerzas de Lennard-Jones, ' +
          'un termostato sobre todo lo que se mueve y un suave flujo descendente que ' +
          'representa el flujo de suministro. Nada le dice a un átomo que se adhiera: ' +
          'una oblea fría captura el vapor en una película creciente y una oblea ' +
          'caliente lo vuelve a evaporar. Arrastre la temperatura unos cientos de ' +
          'kelvin y observe cómo la condensación y la desorción se intercambian.',
        RxStatus: '{{a}} de {{b}} Si del precursor incorporados a la oblea · {{c}} H en la superficie · oblea {{d}} K · {{e}} ps',
        RxGas: 'En el gas:',
        RxGasEmpty: 'no queda nada en el gas',
        RxChartTitle: 'Silicio incorporado e hidrógeno superficial frente al tiempo',
        RxChartY: 'átomos',
        RxChartGrown: 'Si incorporado a la oblea',
        RxChartSurfaceH: 'H en la superficie',
        RxChartAllSi: 'todo el Si del precursor',
        RxChartAllSites: 'todos los enlaces libres',
        RxPrecursor: 'Precursor',
        RxSilane: 'Silano (SiH₄, CVD térmico)',
        RxSilyl: 'Radicales sililo (SiH₃, CVD por plasma)',
        RxSurface: 'Superficie de la oblea',
        RxBare: 'Silicio desnudo',
        RxHydrogen: 'Terminada en hidrógeno',
        SectionRx: 'La otra mitad — un precursor que reacciona: enlaces que se forman y se rompen (dinámica molecular reactiva)',
        RxNote: 'El mismo motor con un campo de fuerzas reactivo: cada par de átomos lleva un orden de enlace vivo y se dibuja una varilla allí donde supera el umbral en este paso. Una molécula de silano llega a la oblea, uno de sus enlaces Si-H se rompe contra un enlace libre, se forma un enlace Si-Si y el silicio pasa a formar parte de la película: quimisorción, observada, sin que nada la guíe. El precursor es silano sobre silicio y no el trimetilaluminio de esta receta: el campo de fuerzas incluye silicio y no aluminio, y el silano sobre silicio es la química CVD contra la que el gráfico de la ventana compara la ALD, así que es lo correcto para observar aquí, no un sustituto. Dos selectores son química: el silano necesita que la superficie rompa un enlace antes de adherirse, los radicales sililo de un plasma no - por eso la CVD por plasma funciona cientos de kelvin más fría - y una oblea terminada en hidrógeno, el estado en que la deja una limpieza final con HF, no acepta nada hasta que el hidrógeno se va como H₂.',
        RxTimeNote: 'LEA EL MECANISMO Y EL ORDEN, NO UNA VELOCIDAD. Un evento que tarda microsegundos en un reactor a 900 K no puede esperarse aquí, así que el termostato mantiene la cámara lo bastante caliente para que el mismo mecanismo ocurra en picosegundos, y los parámetros del silicio son representativos y no una transcripción de un conjunto publicado. Lo que sobrevive a esa compresión es lo que importa: los radicales se adhieren antes que las moléculas, el silicio desnudo acepta precursor antes que el pasivado, y el hidrógeno tiene que irse antes de que el crecimiento continúe. Lo que no sobrevive es una velocidad de crecimiento en nanómetros por minuto: para eso está el solucionador del reactor en la herramienta EPI.',
        SectionMd: 'Bajo la ventana — un precursor que condensa en vez de reaccionar (dinámica molecular)',
        MdWindowNote:
          'LEA ESTO COMO EL EXTREMO FRÍO DE LA VENTANA, NO COMO LA FORMA EN QUE CRECE LA PELÍCULA DE ' +
          'ARRIBA. El modelo de superficie de más arriba hace competir cuatro procesos, y la ' +
          'CONDENSACIÓN es uno de ellos: por debajo de la ventana el precursor deja de reaccionar con ' +
          'la superficie y simplemente fisisorbe en multicapas, que es crecimiento que no se detiene ' +
          'cuando los sitios se llenan. Ese es el fallo que esto muestra, y vale la pena verlo, porque ' +
          'una dosis que condensa parece una velocidad de crecimiento maravillosa hasta el momento en ' +
          'que se mide el mapa de espesor. Lo que NO muestra es la semirreacción de la que está hecho ' +
          'el tramo plano de la ventana. La ALD crece por QUIMISORCIÓN — un intercambio de ligandos ' +
          'que forma y rompe enlaces de verdad y después ya no tiene adónde ir — y los enlaces son ' +
          'justamente lo que un potencial de Lennard-Jones no tiene. Así que los átomos de aquí son un ' +
          'vapor condensable genérico y no el trimetilaluminio de esta receta: un pozo de fisisorción ' +
          'es honesto sobre la adherencia y mudo sobre la química, y fingir lo contrario sería ' +
          'exactamente aquello que este panel dedica tres instrumentos a demostrar que la ALD no es.',
        presetLabel: {
          highAspect: 'ALTA RELACIÓN DE ASPECTO',
          tooHot: 'SOBRE LA VENTANA',
          tooCold: 'BAJO LA VENTANA',
          shortPurge: 'PURGA CORTA',
          starvedDose: 'DOSIS CORTA',
          baseline: 'BASELINE-AL2O3',
        },
        SecondaryLabel: 'No uniformidad',
        ToolLabel: 'ALD-01 · Deposición de Al2O3',
        fault: {
          wetHouseGas: {
            Evidence: [
              'El crecimiento por ciclo es alto con una gran fracción de CVD parásito — ' +
                'la firma de un fallo de purga, que es lo que lo hace tan convincente.',
              'La comprobación de velocidad de bombeo de la línea está LIMPIA. Esa es la ' +
                'lectura que lo convierte de una reparación en una escalada.',
              'La saturación sigue al 100% y la oblea instrumentada está en consigna, así ' +
                'que ni el suministro ni la temperatura están implicados.',
              'El horno se queja al mismo tiempo, de algo que suena sin relación. No es ' +
                'sin relación.',
            ],
            Truth: 'El secador de gas seco de la planta falló húmedo el fin de semana y la ' +
              'cabecera lleva cerca de un 2% de agua — arrastre, no la deriva a nivel de ' +
              'ppm de un secador saturado. Llega a toda máquina que respire nitrógeno u ' +
              'oxígeno de planta, y lo que hace al llegar depende por completo de para ' +
              'qué usaba el gas esa máquina. Fíjate en lo separados que están los dos ' +
              'umbrales: el ALD se arruina con ppm de una cifra, porque el agua es su ' +
              'co-reactivo, mientras que un horno creciendo óxido en vapor no nota nada ' +
              'por debajo de en torno a un uno por ciento. Hace falta un fallo grosero ' +
              'para que ambas máquinas se quejen a la vez, y exactamente por eso los dos ' +
              'grupos casi nunca conectan sus problemas. En esta máquina es peor que en ' +
              'ningún otro sitio del edificio, porque el agua es lo que este proceso usa ' +
              'como co-reactivo. El gas de purga húmedo pone agua en la superficie ' +
              'durante la dosis de TMA, que es la única cosa que la purga existe para ' +
              'impedir — así que la película ve exactamente lo que vería con una purga ' +
              'demasiado corta, y ninguna cantidad de purga lo arregla.',
            Complaint: 'El crecimiento corre alto y la película se ataca de forma desigual — igual ' +
              'que el problema de la línea. La línea se despejó el mes pasado y la ' +
              'comprobación de bombeo salió bien. THRM-04 también está reportando óxido ' +
              'grueso.',
            Label: 'Agua en el gas de purga de planta',
          },
          heaterReadsLow: {
            Evidence: [
              'El crecimiento por ciclo está muy por encima de lo nominal con una ' +
                'fracción parásita muy grande.',
              'La oblea instrumentada lee bien POR ENCIMA de la consigna — el signo ' +
                'opuesto al otro fallo de calefactor, y la razón de que la misma reparación ' +
                'arregle ambos.',
              'La longitud de purga apenas cambia nada aquí, lo que descarta la línea de ' +
                'escape.',
            ],
            Truth: 'El termopar ha derivado al otro lado: el controlador empuja el calefactor ' +
              'para sostener una lectura de 200 C mientras la oblea está más cerca de 350 ' +
              'C. Eso está por encima de la ventana ALD, donde el TMA se descompone solo ' +
              'sin esperar al co-reactivo. El resultado es CVD vestido con una receta ' +
              'ALD: rápido, grueso, y conforme solo por suerte.',
            Complaint: 'El crecimiento está altísimo y la cobertura de escalón falló en el lote de ' +
              'alta relación de aspecto.',
            Label: 'Termopar del calefactor leyendo bajo',
          },
          heaterReadsHigh: {
            Evidence: [
              'El crecimiento por ciclo es alto y la fracción parásita es alta — idéntico ' +
                'en forma a una línea restringida.',
              'La oblea instrumentada es la única lectura que los separa, y lee unos 85 C ' +
                'por debajo de la consigna.',
              'La saturación sigue al 100%: esto no es un problema de suministro.',
            ],
            Truth: 'El termopar ha derivado, así que el controlador cree estar a 200 C ' +
              'mientras la oblea está cerca de 115 C. Eso está por debajo de la ventana ' +
              'ALD, donde el TMA condensa sobre la superficie en vez de quimisorberse en ' +
              'una monocapa. El precursor condensado no es autolimitado, así que el ' +
              'crecimiento por ciclo SUBE — por eso una oblea fría parece un fallo de ' +
              'purga y no una ampolla fría.',
            Complaint: 'Igual que el problema de la línea del trimestre pasado: crecimiento alto, ' +
              'película velada, ataque HF desigual.',
            Label: 'Termopar del calefactor leyendo alto',
          },
          restrictedExhaust: {
            Evidence: [
              'El crecimiento por ciclo está POR ENCIMA de lo nominal, con una fracción ' +
                'parásita de CVD bien por encima de cero.',
              'La saturación sigue al 100% — la dosis en sí está sana, lo que descarta un ' +
                'fallo de suministro.',
              'Una oblea instrumentada lee en consigna, que es lo que lo separa de un ' +
                'fallo de calefactor — la otra manera de conseguir crecimiento alto y CVD ' +
                'alto en esta máquina.',
              'Alargar la purga en la receta recupera la tasa, lo que es un diagnóstico ' +
                'por sí mismo.',
            ],
            Truth: 'Los depósitos acumulados en la línea han recortado la velocidad efectiva ' +
              'de bombeo a un tercio, así que la cámara ya no se despeja durante la ' +
              'purga. Aún hay TMA presente cuando llega el agua, y parte del crecimiento ' +
              'se vuelve CVD: más rápido, no autolimitado y no uniforme.',
            Complaint: 'El crecimiento por ciclo está corriendo ALTO y la película se ataca de ' +
              'forma desigual en HF.',
            Label: 'Línea de escape restringida',
          },
          coldAmpoule: {
            Evidence: [
              'La saturación ha caído por debajo del 95% — la dosis ya no termina. Esta ' +
                'es la lectura que importa; ningún otro fallo de esta máquina la toca.',
              'La no uniformidad está muy fuera de especificación con la media apenas ' +
                'movida, que es la firma de caerse de la saturación y no la de una oblea ' +
                'caliente o fría.',
              'Una dosis MÁS LARGA recupera la película. Un fallo de temperatura no ' +
                'respondería a eso.',
              'La temperatura de la ampolla lee muy por debajo de su consigna.',
            ],
            Truth: 'El calefactor de la ampolla ha derivado a la baja, así que la presión de ' +
              'vapor del TMA — y con ella la presión parcial de precursor en la cámara — ' +
              'está por debajo de la mitad de lo nominal. La dosis ya no satura. Eso es ' +
              'lo que lo hace serio: mientras la dosis satura, a la película no le ' +
              'importa cómo se repartió el precursor, y en cuanto deja de saturar, a la ' +
              'película no le importa nada más. La uniformidad se va antes que la media.',
            Complaint: 'Los mapas de oblea llevan tres lotes en diana de tiro al blanco y la media ' +
              'ha bajado un poco. La receta no se ha tocado.',
            Label: 'Ampolla de TMA fría',
          },
          healthy: {
            Evidence: [
              'El crecimiento por ciclo está en el valor cualificado y la dosis satura.',
              'Cada lectura del banco está dentro de su límite.',
            ],
            Truth: 'No hay nada mal. La decisión correcta es correr la oblea.',
            Complaint: 'La máquina pasó la cualificación esta mañana. Nada reportado.',
            Label: 'Máquina cualificada',
          },
        },
        reading: {
          flowcheck: {
            Label: 'Comprobación de flujo de la ducha (60 min)',
          },
          houseutilities: {
            Limit: 'cabecera estanca, PCW a temperatura, gas seco < 50 ppm',
            Label: 'Comprobación de servicios con instalaciones (2 h)',
          },
          foreline: {
            Label: 'Comprobación de velocidad de bombeo (40 min)',
          },
          parasitic: {
            Label: 'Ataque HF sobre un monitor (45 min)',
          },
          instrumented: {
            Label: 'Oblea instrumentada (90 min)',
          },
          ampoule: {
            Label: 'Comprobación de temperatura de la ampolla (20 min)',
          },
          saturation: {
            Label: 'Curva de saturación de dosis (25 min)',
          },
          setpoint: {
            Limit: 'según receta',
            Label: 'Consigna del calefactor',
          },
          step: {
            Label: 'Cobertura de escalón a 10:1',
          },
          edge: {
            Label: 'Borde menos centro',
          },
          nu: {
            Label: 'No uniformidad',
          },
          gpc: {
            Label: 'Crecimiento por ciclo',
          },
        },
        aspect: {
          house: {
            Action: 'Parar el trabajo en la máquina y avisar a instalaciones',
            Label: 'Nada en esta máquina — escalar a instalaciones',
          },
          showerhead: {
            Action: 'Desmontar y limpiar la ducha, cambiar la junta',
            Label: 'Ducha de gases',
          },
          heater: {
            Action: 'Recalibrar el calefactor contra una oblea instrumentada',
            Label: 'Calefactor del sustrato',
          },
          purge: {
            Action: 'Despejar la restricción de la línea y re-verificar la velocidad de bombeo',
            Label: 'Ruta de purga / escape',
          },
          precursor: {
            Action:
              'Rellenar y recalentar la ampolla, verificar el suministro contra el ' +
              'caudalímetro',
            Label: 'Suministro de TMA',
          },
        },
        knob: {
          doseMs: 'Dosis de TMA',
          purgeMs: 'Purga tras el TMA',
          coReactantMs: 'Dosis de H2O',
          coPurgeMs: 'Purga tras el H2O',
          pressureMtorr: 'Presión de cámara',
          carrierSccm: 'Caudal portador',
          substrateTempC: 'Temperatura del sustrato',
          cycles: 'Ciclos',
          aspectRatio: 'Relación de aspecto de la estructura',
        },
        preset: {
          baseline:
            'Receta cualificada dentro de la ventana ALD: dosis saturada, purga ' +
            'suficiente.',
          starvedDose:
            'Dosis de TMA reducida a 15 ms. Por debajo de la saturación, así que el ' +
            'crecimiento por ciclo cae y - peor aún - ahora depende de cuánto precursor ' +
            'llegó a cada parte de la oblea, de modo que la uniformidad se va con él.',
          shortPurge:
            'Purga reducida a 200 ms. Todavía queda precursor en la cámara cuando llega ' +
            'el agua, así que parte del crecimiento pasa a ser CVD parásito: más ' +
            'rápido, pero ni autolimitado ni uniforme.',
          tooCold:
            'Sustrato a 100 C. Demasiado frío para que la reacción de superficie se ' +
            'complete.',
          tooHot:
            'Sustrato a 360 C. El precursor se descompone por su cuenta; el crecimiento ' +
            'deja de ser ALD.',
          highAspect:
            'La misma receta sobre una estructura 60:1. El fondo de la zanja necesita ' +
            'mucha más exposición que la boca, y esta dosis no se la da.',
        },
        SectionProcessState: 'Estado del proceso',
        SectionRunControl: 'Control de ejecución',
        RunControlNote:
          'El ALD se cuenta en ciclos, no en segundos. No hay tasa que fijar — tú ' +
          'eliges cuántas veces repetir el ciclo, y la superficie decide cuánto se ' +
          'lleva cada vez.',
        StartCycling: 'Empezar a ciclar',
        Hold: 'Pausar',
        RunToCompletion: 'Ejecutar hasta el final',
        LoadBareWafer: 'Cargar oblea desnuda',
        SectionWaferMap: 'Mapa de oblea — espesor depositado',
        ModeThickness: 'Espesor',
        ModeDeviation: 'Desviación respecto a la media',
        SectionRadialProfile: 'Perfil radial',
        SectionSaturation: 'Curva de saturación — ¿es autolimitada esta dosis?',
        SaturationNote:
          'Crecimiento por ciclo frente al tiempo de dosis, con todo lo demás fijo. ' +
          'Un proceso ALD de verdad sube y luego se queda PLANO: una vez ocupados ' +
          'todos los sitios de superficie, más precursor no hace nada. Si esta curva ' +
          'sigue subiendo, el proceso no es autolimitado y no es ALD.',
        MeasureSaturation: 'Medir la curva de saturación',
        SectionWindow: 'Ventana ALD — la banda de temperatura donde esto funciona',
        WindowNote:
          'Por debajo de la ventana el precursor condensa en multicapas; por encima, ' +
          'se descompone solo sin esperar al co-reactivo. Ambos hacen crecer la ' +
          'película más rápido y ninguno es autolimitado. La ventana es el tramo ' +
          'plano que queda en medio.',
        MeasureWindow: 'Medir la ventana',
        SectionRecipe: 'Receta — {{code}}',
        SectionStoredRecipes: 'Recetas almacenadas',
        TileGpc: 'Crecimiento por ciclo',
        TileThickness: 'Espesor',
        TileNonUniformity: 'No uniformidad',
        TileSaturation: 'Saturación',
        TileParasitic: 'CVD parásito',
        TileStepCoverage: 'Cobertura de escalón',
        TileCycles: 'Ciclos',
        TileCycleTime: 'Tiempo de ciclo',
        TileRunTime: 'Tiempo de ejecución',
        SelfLimiting: 'autolimitado',
        DoseStarved: 'dosis insuficiente',
        TrueAld: 'ALD de verdad',
        NotSelfLimiting: 'no autolimitado',
        AtAspect: 'a {{ratio}}:1',
        PurgeResidence: 'la purga es {{factor}}x la residencia',
        CaptionNonUniformity: '{{percent}}% de no uniformidad',
        CaptionNoFilm: 'crecimiento por ciclo — todavía no hay película depositada',
        ChartProfileThickness: 'Espesor depositado a lo largo del radio',
        ChartProfileGpc: 'Crecimiento por ciclo a lo largo del radio',
        ChartProfileX: 'r / R  (0 = centro)',
        ChartSeriesFilm: 'película',
        NotMeasured: 'Todavía sin medir',
        ChartSaturationTitle: 'Crecimiento por ciclo frente a dosis — plano significa autolimitado',
        ChartSaturationX: 'dosis de TMA (ms)',
        ChartSeriesGpc: 'crecimiento por ciclo',
        AnnYourDose: 'tu dosis',
        ChartWindowTitle: 'Crecimiento por ciclo frente a temperatura',
        ChartWindowX: 'temperatura del sustrato (°C)',
        AnnYourSetpoint: 'tu consigna',
        ChartParasiticTitle: 'Cuánto de ese crecimiento NO es autolimitado',
        ChartParasiticY: '% parásito',
        ChartSeriesParasitic: 'CVD parásito',
        AnnAldLimit: '5% — límite práctico del ALD',
        SectionOnChuck: 'En el portaobleas',
        SendToMet: 'Enviar la oblea a MET-05',
        SectionAtoms: 'Escala atómica — un ciclo, molécula a molécula',
        AtomsNote:
          'No es dinámica molecular: la MD real vive en femtosegundos y esto es un ' +
          'ciclo entero. Es una red de sitios superficiales con reglas de reacción ' +
          'programadas, esclavizada a las mismas ecuaciones de velocidad de las que ' +
          'se dibujan los gráficos de arriba, y gobernada por la receta viva de este ' +
          'panel. Acorta una purga, o sal de la ventana, y mira qué hace la química.',
        AtomsRun: 'Reanudar',
        AtomsPause: 'Pausar',
        AtomsReset: 'Reiniciar',
        AtomsPhasePurge: 'purga',
        AtomsLegendOh: 'sitio OH — acepta TMA',
        AtomsLegendCh3: 'terminado en CH₃ — lleno',
        AtomsLegendCh4: 'CH₄ saliendo',
        AtomsLegendCvd: 'partícula de CVD en fase gas',
        AtomsCycle: 'ciclo {{n}}',
        AtomsCoverage: 'cobertura',
        AtomsFilm: 'película',
        AtomsCvdCount: 'eventos CVD',
        AssistantBlurb:
          'Hazme una pregunta y diseñaré un experimento, lo ejecutaré contra este ' +
          'solucionador e informaré de lo que realmente salió — y luego te devolveré ' +
          'una pregunta más afilada.',
        AssistantLead:
          'La pregunta que importa aquí no es a qué velocidad crece la película, sino ' +
          'si el crecimiento sigue parándose cuando la superficie se llena. Pídeme ' +
          'que lo compruebe.',
      },
      metrology: {
        D3Note: 'La caja de arriba envía la onda contra una FILA de átomos y su pantalla es ' +
          'una línea. Una muestra real es una lámina, periódica en dos direcciones, y ' +
          'su pantalla es un plano de manchas, que es lo que un instrumento de ' +
          'difracción entrega de verdad a un técnico. Léelo como la red vuelta del ' +
          'revés: manchas muy separadas significan átomos muy juntos, y estirar la ' +
          'malla en una dirección acerca las manchas en esa dirección y en ninguna ' +
          'otra. Baja la energía y el círculo discontinuo encoge hasta contener solo ' +
          'la cruz central; por debajo de 1,5 eV la longitud de onda es mayor que la ' +
          'malla y la lámina deja pasar la onda como un solo haz sin dividir, casi sin ' +
          'nada en él que diga que los átomos estaban ahí. Casi, porque un paquete tan corto no tiene una sola ' +
          'energía: su cola rápida sigue por encima del umbral y levanta un primer anillo tenue, una quinta ' +
          'parte de brillante, hasta que el mando está bien por debajo. Las manchas son la ' +
          'transformada de Fourier de la onda que el solucionador propagó, no un ' +
          'dibujo: las cruces y el círculo son la predicción, y se pusieron en la ' +
          'pantalla después. La malla es de 1 nm, más ancha que una red superficial ' +
          'real, para que ambos anillos se abran dentro del rango del mando; a la ' +
          'geometría le da igual la escala. Los núcleos son bultos repulsivos suaves, ' +
          'hay una sola capa de ellos y nada dispersa de forma inelástica.',
        D3Energy: 'Energía del electrón',
        D3Rect: 'Estirada a lo largo de z',
        D3Square: 'Malla cuadrada',
        D3ScreenCaption: 'La pantalla, muy lejos: hacia dónde va la onda que pasó la lámina. Las ' +
          'cruces son la red recíproca de la malla, 2π entre el espaciado; el círculo ' +
          'discontinuo es el número de onda del propio electrón. Una mancha solo ' +
          'puede encenderse en una cruz dentro del círculo.',
        D3Idle: 'Pulsa Ejecutar: el paquete se propaga en un hilo de trabajo, de cinco a ' +
          'diez segundos por ejecución.',
        D3Gain: 'la onda está dibujada ×{{m}} más brillante de lo que es',
        D3Status: '{{fs}} de {{run}} fs · {{fwd}} % más allá de la lámina · Σ|ψ|² = {{norm}}, ' +
          'márgenes incluidos',
        D3NoOrders: 'no existe ningún haz difractado: la longitud de onda es mayor que la malla',
        D3FirstOrder: 'una mancha de primer orden tiene el {{p}} %, a {{deg}}° del haz',
        D3Straight: 'En la mancha directa',
        D3NoNextRing: 'todos los anillos al alcance de este mando están abiertos',
        D3NextRing: 'el siguiente anillo de manchas se abre a {{e}} eV',
        D3Beams: 'Haces que la lámina puede formar',
        D3MeshRect: 'la malla es de {{a}} nm por {{b}} nm',
        D3MeshSquare: 'la malla es cuadrada, de {{a}} nm',
        D3Wavelength: 'Longitud de onda frente a la malla',
        D3Circle: 'un haz debe caber dentro de k = {{k}} nm⁻¹',
        D3ScreenX: 'vector de onda lateral k_y (nm⁻¹)',
        D3About: 'Qué muestra esto y qué deja fuera el modelo',
        SectionDiffraction3d: 'Una lámina de átomos y las manchas que deja — difracción de electrones en ' +
          'tres dimensiones (Schrödinger, paso de Cayley dividido)',
        UnitNmReported: 'nm reportados',
        LabelFitResidual: 'Residuo del ajuste',
        SecondaryLabel: 'R&R del instrumento',
        ToolLabel: 'METR-05 · Reflectómetro espectroscópico',
        fault: {
          wrongIndex: {
            Evidence: [
              'El RESIDUO está quintuplicado con ruido de detector normal. Esa ' +
                'combinación es todo el diagnóstico: datos ruidosos suben el residuo Y el ' +
                'R&R, un modelo equivocado sube solo el residuo.',
              'El gauge R&R es normal, así que la medida es repetible — está ajustando ' +
                'repetiblemente el material equivocado.',
              'Un elipsómetro, que resuelve n y espesor juntos, devuelve un índice bien ' +
                'por encima del valor del óxido.',
            ],
            Truth: 'La máquina de deposición ha derivado hacia rico en nitrógeno, así que la ' +
              'película es un oxinitruro con índice cercano a 1,54 mientras la receta de ' +
              'medida sigue asumiendo 1,46 de óxido puro. Al ajuste se le pide reproducir ' +
              'franjas del material equivocado, y compensa de la única manera que puede — ' +
              'reportando una película más gruesa. Este es el único fallo donde la ' +
              'máquina de metrología es inocente y está reportando un cambio real aguas ' +
              'arriba.',
            Complaint: 'El espesor de óxido lee 4% alto y los ajustes se ven algo peores de lo ' +
              'usual.',
            Label: 'La película no es lo que la receta cree',
          },
          badStandard: {
            Evidence: [
              'Gauge R&R y residuo normales — otra vez nada parece una mala medida.',
              'El error es un número CONSTANTE de nanómetros, no un porcentaje. En la ' +
                'comprobación a dos puntos son los mismos 3 nm en el patrón fino y en el ' +
                'grueso, que es exactamente lo que un error de longitud de onda no haría.',
              'Re-certificar el patrón lo encuentra más grueso que su papeleo.',
            ],
            Truth: 'El patrón certificado contra el que se pone a cero la máquina llevaba ' +
              'tiempo en un cajón y ha crecido óxido nativo, así que su espesor verdadero ' +
              'ya no es el de su certificado. La máquina se puso a cero contra un número ' +
              'equivocado y ahora suma ese error a todo lo que mide.',
            Complaint: 'Todo lee unos 3 nm grueso, igual que el problema de la red del año pasado.',
            Label: 'El patrón de calibración ha crecido óxido nativo',
          },
          gratingDrift: {
            Evidence: [
              'El gauge R&R es normal y el residuo es normal. Nada aquí parece una mala ' +
                'medida.',
              'El error es PROPORCIONAL al espesor — 3% en cada película, gruesa o fina. ' +
                'Una comprobación a dos puntos contra patrones certificados de dos ' +
                'espesores muestra el error creciendo con la película, que es lo que lo ' +
                'separa de un patrón malo.',
              'La máquina es precisa y está equivocada, que es el modo de fallo que ' +
                'ninguna carta de control atrapará jamás.',
            ],
            Truth: 'La red del monocromador se ha deslizado. Cada longitud de onda se reporta ' +
              'un 3% alta, así que las franjas parecen caer en longitudes mayores de las ' +
              'reales, y el ajuste devuelve un espesor un 3% grueso. La medida es ' +
              'perfectamente repetible y el residuo exactamente nominal, porque no hay ' +
              'nada mal en los DATOS — solo en el número escrito al lado. Dos semanas de ' +
              'un grupo de proceso persiguiendo una máquina que nunca estuvo rota.',
            Complaint: 'La deposición lleva dos semanas leyendo 3% gruesa. La máquina de ' +
              'deposición se ha recalibrado dos veces y el grupo de grabado ha empezado a ' +
              'compensarlo.',
            Label: 'Monocromador fuera de calibración',
          },
          dimLamp: {
            Evidence: [
              'El gauge R&R está por encima del 30%, así que la mayor parte de la ' +
                'variación graficada es el propio gauge.',
              'La MEDIA de las repeticiones sigue en el valor certificado — la exactitud ' +
                'está intacta. Solo se fue la precisión.',
              'El residuo sube en proporción al ruido, como debe cuando los datos son ' +
                'ruidosos y no cuando el modelo está mal.',
            ],
            Truth: 'La lámpara ha envejecido y la óptica de colección está sucia, así que el ' +
              'ruido del detector es cinco veces el nominal. Al ajuste se le pide ' +
              'encontrar un espesor en un espectro más ruidoso y deambula. No hay nada ' +
              'mal en la máquina de deposición ni en la RESPUESTA — la máquina aún ' +
              'dispersa alrededor del valor correcto. Lo que se ha ido es la capacidad de ' +
              'distinguir un desplazamiento real del proceso de la dispersión de medida, ' +
              'que es lo único para lo que esta máquina existe.',
            Complaint: 'Las medidas están por todas partes. La misma oblea medida cinco veces da ' +
              'cinco números distintos. El ingeniero cree que la máquina de deposición se ' +
              'ha vuelto inestable.',
            Label: 'Lámpara envejecida, ruido del detector arriba',
          },
          healthy: {
            Evidence: [
              'El espesor reportado está en el valor certificado y el gauge R&R está bajo ' +
                'el 10%.',
            ],
            Truth: 'No hay nada mal. La máquina dice la verdad y la dice de forma repetible.',
            Complaint: 'Comprobación diaria del patrón superada. Nada reportado.',
            Label: 'Gauge cualificado',
          },
        },
        reading: {
          lampcheck: {
            Label: 'Comprobación de lámpara y corriente oscura (1 h)',
          },
          ellipsometer: {
            Label: 'Contraste en el elipsómetro, resolviendo n (4 h)',
          },
          certcheck: {
            Label: 'Re-certificar el patrón fuera (5 días)',
          },
          twopoint: {
            Limit: 'ambos dentro de +/- 1 nm',
            Label: 'Comprobación a dos puntos, patrones de 20 y 400 nm (3 h)',
          },
          model: {
            Limit: 'según receta',
            Label: 'Índice de película asumido',
          },
          repeat: {
            Label: 'Repetibilidad, 1 sigma',
          },
          residual: {
            Label: 'Residuo del ajuste (RMS)',
          },
          grr: {
            Label: 'Gauge R&R',
          },
          reported: {
            Label: 'Patrón certificado, lectura',
          },
        },
        aspect: {
          filmmodel: {
            Action: 'Re-medir n y k de esta película, actualizar el modelo de la receta',
            Label: 'Modelo óptico de la película',
          },
          standard: {
            Action: 'Retirar el patrón, re-certificarlo, re-cero de la máquina',
            Label: 'Patrón de calibración',
          },
          grating: {
            Action: 'Recalibrar la red contra una fuente de líneas de emisión',
            Label: 'Escala de longitud de onda del monocromador',
          },
          lamp: {
            Action: 'Cambiar la lámpara, limpiar la óptica de colección',
            Label: 'Lámpara y detector',
          },
        },
        SectionMeasurement: 'Medición',
        SectionSpectrum: 'Espectro de reflectancia y el ajuste',
        SpectrumNote:
          'El equipo no mide espesor. Mide luz reflejada frente a longitud de onda y ' +
          'luego ajusta una pila de películas a las franjas. El número que informa es ' +
          'aquello a lo que convergió ese ajuste.',
        SectionTruth: 'Lo que hay realmente en la oblea',
        TruthNote: 'La película en sí. Un estudiante normalmente no lo sabría.',
        SectionAssumed: 'Lo que la receta del equipo supone',
        AssumedNote:
          'Pon estas dos en películas distintas y el equipo seguirá informando un ' +
          'espesor, con dos decimales, y estará muy equivocado. Una medición es un ' +
          'modelo, y un modelo puede equivocarse con total confianza.',
        SectionGauge: 'Capacidad del instrumento',
        GaugeNote:
          'Mide muchas veces la misma oblea sin cambios. La dispersión es el equipo, ' +
          'no el proceso. Lo que importa es esa dispersión comparada con la variación ' +
          'que el proceso realmente tiene: un instrumento nunca es bueno o malo por ' +
          'sí solo, solo bueno o malo para un trabajo.',
        RunGauge: 'Ejecutar un estudio de capacidad (25 repeticiones)',
        SectionControl: 'Gráfico de control — 60 lotes',
        ControlNote:
          'Cada punto es el valor real del proceso más el ruido de medición. Las ' +
          'reglas se disparan con ambos, y el gráfico no puede decirte a cuál ' +
          'reaccionó. Decidir eso es el trabajo.',
        RunStable: 'Ejecutar un proceso estable',
        RunDrift: 'Ejecutar con una deriva lenta',
        RunNoisy: 'Ejecutar con un instrumento ruidoso',
        ResetGauge: 'Restablecer el instrumento',
        SectionAbsorption: 'De dónde sale el índice — el sustrato absorbiendo luz (bandas de enlace fuerte)',
        AbsNote: 'El ajuste de arriba necesita un índice de refracción para todo lo que hay en la pila, y un índice no es una constante del material: es lo que hacen los electrones del material cuando llega luz de esa energía. Por debajo de la banda prohibida ningún fotón puede elevar un electrón al otro lado, el cristal es transparente y n apenas se mueve; por encima el cristal absorbe, k sube desde cero y n oscila con él. Este es ese espectro calculado a partir de las mismas bandas de enlace fuerte que dibuja CZ-07: cada salto vertical de una banda llena a una vacía, en toda la zona, ponderado por su intensidad. El inicio del silicio está en su banda prohibida DIRECTA, cerca de 3 eV, muy por encima de su banda fundamental de 1,1 eV, porque un fotón por sí solo no puede aportar el momento que exige un salto indirecto: así que la oblea es transparente en el extremo rojo del reflectómetro y solo absorbe con fuerza en el azul. El germanio y el arseniuro de galio empiezan bajo y absorben todo el barrido. La forma y el inicio son el cálculo; la escala es relativa, porque el modelo no incluye correcciones excitónicas ni de campo local.',
        AbsChartTitle: 'Absorción frente a la energía del fotón',
        AbsChartX: 'energía del fotón (eV)',
        AbsChartY: 'ε₂, relativa',
        AbsOnset: 'inicio {{ev}} eV (banda directa)',
        AbsGap: 'banda {{ev}} eV, indirecta',
        AbsOnsetGap: 'inicio {{ev}} eV, banda {{gap}} eV',
        AbsShort: '{{formula}} absorbe desde {{onset}} eV · banda prohibida {{gap}} eV, {{kind}}',
        AbsHelp: 'El inicio en nanómetros y qué marcan las líneas ámbar',
        AbsStatus: '{{formula}} empieza a absorber a {{onset}} eV ({{nm}} nm) · banda fundamental {{gap}} eV, {{kind}} · reglas ámbar: el barrido de 800-400 nm del reflectómetro',
        AbsIndirect: 'indirecta',
        AbsDirect: 'directa',
        AbsCrystal: 'Cristal',
        AbsFailed: 'No se pudo cargar el motor de estructura de bandas.',
        AbsLoading: 'Cargando el motor de estructura de bandas…',
        SectionDiffraction: 'Lo que un instrumento puede resolver — una onda electrónica ante un blanco (Schrödinger, 2D)',
        PhononAcoustic: 'acústica',
        PhononOptical: 'óptica',
        PhononChartTitle: 'Dispersión de fonones',
        PhononChartX: 'vector de onda a lo largo de Γ–X–W–K–Γ–L',
        PhononChartY: 'frecuencia (cm⁻¹)',
        PhononMeasured: 'medido {{cm}} cm⁻¹',
        PhononRamanTitle: 'Línea Raman de primer orden',
        PhononRamanX: 'desplazamiento Raman (cm⁻¹)',
        PhononRamanY: 'intensidad, pico = 1',
        PhononRamanSeries: 'calculada: el singlete [001]',
        PhononFailed: 'El motor de cristal no se pudo cargar.',
        PhononShort: '{{material}}: línea Raman {{line}} cm⁻¹ (medida {{measured}})',
        PhononHelp: 'Todos los números de este cálculo',
        PhononStatus: '{{material}}: línea Raman (el singlete [001]) en {{line}} cm⁻¹ (medida {{measured}}) · doblete en el plano {{doublet}} cm⁻¹ · sonido a lo largo de [100] {{sound}} km/s (medido {{soundMeasured}}) · {{ms}} ms',
        PhononImaginary: 'inestable: un modo imaginario',
        PhononLoading: 'Cargando el motor de cristal…',
        PhononStrain: 'Deformación biaxial',
        PhononCrystal: 'Cristal',
        SectionPhonons: 'Lo que lee un equipo Raman — los fonones del cristal y la línea que se mueve con la deformación (dinámica de red Stillinger–Weber)',
        PhononsNote: 'Un espectrómetro Raman mide la luz que vuelve de la oblea desplazada exactamente un cuanto de vibración de la red, y una fábrica tiene uno porque ese cuanto se mueve con la deformación: comprima la red y los enlaces se endurecen, así que la línea sube; estírela y la línea baja. Nada de lo que sigue es una curva recordada. Se construye una supercelda de 3×3×3 del cristal elegido con condiciones periódicas sobre el mismo campo de fuerzas Stillinger–Weber con el que corre la red de IMP-10, se desplaza un átomo de cada subred una centésima de ångström a lo largo de cada eje, y se lee la fuerza que cae sobre todos los demás átomos: doce evaluaciones de fuerza son todo el hessiano que necesita una base de dos átomos. La matriz dinámica montada con ellas da las seis ramas a lo largo de Γ–X–W–K–Γ–L, la densidad de estados a su lado y, en Γ, la línea Raman. Espere el número del campo de fuerzas honesto, no el medido: SW sitúa la línea del silicio cerca de 593 cm⁻¹ frente a los 520 medidos, porque se ajustó al punto de fusión y a la energía de cohesión, no a los fonones, igual que la vista de bandas en CZ-07 muestra su brecha calculada y la medida. Con deformación biaxial la línea triplemente degenerada se desdobla en un singlete polarizado a lo largo de [001] y un doblete en el plano, y un equipo que mira de frente una oblea (001) solo ve el singlete; el doblete se dibuja discontinuo porque esa geometría lo prohíbe. SW acierta la dirección —hacia abajo en tensión, hacia arriba en compresión—, pero no el tamaño ni el orden: los potenciales de deformación medidos dan al singlete del silicio unos −7 a −8 cm⁻¹ por cada uno por ciento, más que a su doblete, mientras que SW da −4,4 con el doblete moviéndose más; para el germanio SW da −1,4 frente a −4,1 medidos. Se omiten: la anchura anarmónica y la temperatura (las líneas se dibujan con una anchura fija de 3 cm⁻¹) y el desorden isotópico de masa.',
        Mode3dLoading: 'Construyendo los modos del cristal…',
        Mode3dStatusStill: '{{name}} en Γ · 0 cm⁻¹ · todo el cristal deslizándose como un bloque: sin fuerza restauradora, no hay frecuencia',
        Mode3dWaveInfinite: 'infinita',
        Mode3dShort: '{{name}} · {{cm}} cm⁻¹ · longitud de onda {{wave}}',
        Mode3dHelp: 'Más sobre este modo y este bloque',
        Mode3dStatus: '{{name}} · {{cm}} cm⁻¹ ({{thz}} THz) · longitud de onda {{wave}} · un ciclo dura {{period}} fs, dibujado {{slow}} veces más lento',
        Mode3dTogether: 'se mueven juntas',
        Mode3dAgainst: 'se mueven una contra la otra',
        Mode3dMixed: 'van parcialmente en fase',
        Mode3dSublattices: 'Las dos subredes {{how}} ({{phase}})',
        PhononRamanForbidden: 'doblete en el plano, prohibido mirando de frente a (001)',
        PhononDosTitle: 'Densidad de estados',
        PhononDosX: 'modos por cm⁻¹',
        Mode3dPartial: 'Esta onda es más larga que el bloque, así que solo se muestra parte de una longitud de onda.',
        Mode3dBlock: '{{atoms}} átomos, {{cells}} celdas',
        Mode3dLegend: 'El ámbar y el azul son las dos subredes entrelazadas de la estructura de diamante.',
        Mode3dAmplitude: 'Amplitud dibujada',
        Mode3dAmplitudeValue: '{{a}} Å (el movimiento térmico real a 300 K es de unos {{thermal}} Å)',
        Mode3dPause: 'Pausa',
        Mode3dPlay: 'Reproducir',
        Mode3dChartMark: 'el modo dibujado abajo',
        Mode3dSegment: 'Vector de onda a lo largo de',
        Mode3dFraction: 'Posición en ese tramo',
        Mode3dBranch: 'Rama',
        DiffNote: 'Una onda no puede resolver un detalle más pequeño que su propia longitud de onda, y ese único hecho fija el límite de cada instrumento de esta planta: la luz del reflectómetro, de cientos de nanómetros, ve el espesor de una película y nada de su textura; los electrones de 1 keV del SEM, con una longitud de onda de 0,04 nm, ven una línea de 20 nm. Esto es ese hecho a una energía que una malla puede mostrar. Un paquete de ondas electrónico gaussiano encuentra dos rendijas o una fila de átomos, y lo que sale por el otro lado se recoge en una pantalla por ángulo. Un blanco periódico de paso p envía una longitud de onda λ a órdenes en sen θ = mλ/p - las reglas del gráfico - y no existe ningún orden cuando λ es mayor que p. Baje la energía hasta que lo sea y los órdenes desaparecen: la onda sale como un solo haz y la muestra no tiene estructura. Nada está dibujado a mano; el paquete encuentra un potencial y el margen de la caja, un absorbente, cuenta lo que llega. La probabilidad que rebota en el blanco también se cuenta, así que la contabilidad suma uno.',
        DiffChartTitle: 'Lo que llega a la pantalla, por ángulo',
        DiffChartX: 'ángulo respecto a la dirección de avance (°)',
        DiffChartY: 'probabilidad por grado, pico = 1',
        DiffChartSeries: 'En la pantalla',
        DiffOrder: 'm = {{m}}',
        DiffShort: 'λ = {{lambda}} nm · {{forward}}% hacia delante, {{back}}% hacia atrás',
        DiffHelp: 'El espaciado, los órdenes y el reloj',
        DiffStatus: 'λ = {{lambda}} nm frente a un paso de {{spacing}} nm · órdenes en {{orders}} · {{forward}}% adelante, {{back}}% atrás · {{fs}} fs',
        DiffNoOrders: 'ninguno - la longitud de onda es mayor que el paso',
        DiffEnergy: 'Energía del electrón',
        DiffTarget: 'Blanco',
        DiffSlits: 'Dos rendijas, a 1,5 nm',
        DiffAtoms: 'Una fila de átomos, a 0,5 nm',
        DiffRun: 'Ejecutar',
        DiffPause: 'Pausa',
        DiffReset: 'Reiniciar',
        DiffFailed: 'No se pudo cargar el solucionador de ondas.',
        DiffLoading: 'Cargando el solucionador de ondas…',
        SectionShift: 'Turno',
        TileToolReports: 'El equipo informa',
        TileActuallyIs: 'En realidad es',
        TileError: 'Error',
        TileFitResidual: 'Residuo del ajuste',
        TileRepeatability: 'Repetibilidad',
        TileGaugeRR: 'R&R del instrumento',
        TileCpk: 'Cpk',
        TileChartAlarms: 'Alarmas del gráfico',
        TileGaugeNoise: 'Ruido del instrumento',
        NoteAssuming: 'suponiendo {{material}}',
        NoteWrongModel: 'modelo supuesto incorrecto',
        NoteModelCorrect: 'el modelo es correcto',
        NoteResidualRaised: 'apenas se eleva — no te avisará',
        NoteResidualFloor: 'en el suelo de ruido del detector',
        NotMeasured: 'sin medir',
        NoteToolSigma: '1σ del equipo',
        Capable: 'capaz',
        NotCapable: 'NO capaz',
        RunAStudy: 'ejecuta un estudio',
        NoteAllFalse: 'el proceso es estable — todas falsas',
        NoteInLots: 'en 60 lotes',
        NoteMeasurementSigma: 'σ de medición por lote',
        SectionLot: 'Lote en la platina',
        StageEmpty:
          'La platina está vacía. Ejecuta una deposición en DEP-02 y manda la oblea ' +
          'aquí, luego mídela — el número que informa este equipo es la primera vez ' +
          'que alguien averigua qué hizo realmente esa receta.',
        TravelCard: 'Hoja de ruta',
        TileReported: 'Espesor informado',
        TileOffTarget: 'Desviación del objetivo',
        TileNonUniformity: 'No uniformidad',
        NoteModelMatch: 'lo bien que encajó el modelo',
        TileAssumedIndex: 'Índice supuesto',
        NoteFromRecipe: 'de la receta de medición',
        TileVerdict: 'Veredicto',
        VerdictShips: 'sale',
        VerdictHold: 'retener',
        NineSites:
          'Nueve puntos, del centro al borde. Este equipo no hizo la película y no ' +
          'puede repararla — si el número está mal, la respuesta está aguas arriba, y ' +
          'la comprobación del instrumento en el informe de turno es cómo descartas ' +
          'primero este equipo.',
        ChartSpectrumTitle: 'Espectro medido frente al modelo ajustado',
        ChartWavelength: 'longitud de onda (nm)',
        ChartReflectance: 'reflectancia',
        SeriesMeasured: 'medido',
        SeriesFitted: 'modelo ajustado',
        ChartGaugeTitle: '25 mediciones de una misma oblea sin cambios — σ {{sigma}} nm',
        ChartGaugeEmpty: 'Aún sin medir',
        ChartReadingNumber: 'número de lectura',
        ChartReportedThickness: 'espesor informado (nm)',
        SeriesReadings: 'lecturas',
        AnnTrueValue: 'valor real',
        ChartSpcTitle: 'Gráfico de control — {{alarms}} alarmas, Cpk {{cpk}}',
        ChartSpcEmpty: 'Aún sin ejecutar',
        ChartLot: 'lote',
        ChartThickness: 'espesor (nm)',
        SeriesTrue: 'real (oculto para el operador)',
        AnnCentre: 'centro',
        AnnSpec: 'especificación',
        AssistantBlurb:
          'Hazme una pregunta y diseñaré un experimento, lo ejecutaré contra este ' +
          'solucionador e informaré de lo que realmente salió — y luego te devolveré ' +
          'una pregunta más afilada.',
        AssistantLead:
          'Este equipo solo mira. Pregúntame si lo que te está diciendo es real — o ' +
          'si es el instrumento, el modelo o la suerte.',
      },
    },

    house: {

      gowning: {
        AssistantBlurb:
          'Pregunta por qué una bahía que marca dentro de clase puede seguir contaminando obleas, o cuánto vale una prenda en partículas. Cada respuesta es una ejecución contra esta bahía tal como está.',
        AssistantLead: 'Asistente de indagación GOWN-00',
      },

      crystal: {
        AssistantBlurb:
          'Pregunta por la segregación a lo largo del lingote, a qué velocidad se puede tirar este cristal, o de dónde viene el oxígeno. Cada respuesta es un crecimiento ejecutado en este horno.',
        AssistantLead: 'Asistente de indagación CZ-07',
      },

      waferprep: {
        AssistantBlurb:
          'Pregunta cuánto silicio se lleva la sierra, qué profundidad tiene el daño, o qué hace que una oblea se combe. Cada respuesta es una ejecución contra esta línea.',
        AssistantLead: 'Asistente de indagación WPR-08',
      },

      clean: {
        AssistantBlurb:
          'Pregunta si un baño más largo ayuda, qué cuesta la cola después del HF, o para qué sirve el segundo baño. Cada respuesta es una limpieza ejecutada en este banco.',
        AssistantLead: 'Asistente de indagación CLN-09',
      },

      implant: {
        AssistantBlurb:
          'Pregunta por qué una unión salió más profunda de lo que dicen las tablas, cuándo la red se vuelve amorfa, o qué fija realmente la energía. Cada respuesta es una implantación ejecutada en esta línea de haz.',
        AssistantLead: 'Asistente de indagación IMP-10',
      },

      patterning: {
        AssistantBlurb:
          'Pregunta por qué los espacios se alternan, qué fija realmente el paso final, o dónde se queda sin sitio la ruta. Cada respuesta es la ruta de ocho pasos ejecutada con esta receta.',
        AssistantLead: 'Asistente de indagación PAT-21',
      },

      plating: {
        AssistantBlurb:
          'Pregunta por qué una zanja quedó con hueco, por qué el borde deposita más grueso, o cuánto tiempo hay que platear. Cada respuesta es un llenado ejecutado en este baño.',
        AssistantLead: 'Asistente de indagación ECD-12',
      },

      inspection: {
        AssistantBlurb:
          'Pregunta qué compra un umbral más bajo, qué no puede ver esta receta en absoluto, o si basta con un barrido parcial. Cada respuesta es un barrido ejecutado en esta herramienta.',
        AssistantLead: 'Asistente de indagación INSP-13',
      },

      probe: {
        AssistantBlurb:
          'Pregunta por qué un chip más grande rinde peor, qué modelo de rendimiento necesita esta línea, o si la pérdida es de la oblea o de la tarjeta de puntas. Cada respuesta es un sorteo ejecutado sobre este lote.',
        AssistantLead: 'Asistente de indagación PRB-14',
      },

      dicing: {
        AssistantBlurb:
          'Pregunta hasta qué grosor se puede adelgazar, si una muela más fina se paga sola, o qué cuesta cortar más rápido. Cada respuesta es un desbaste y corte ejecutado con esta receta.',
        AssistantLead: 'Asistente de indagación DICE-15',
      },

      bonding: {
        AssistantBlurb:
          'Pregunta dónde está la ventana de soldadura, por qué el número de hilos cambia el rendimiento, o qué le hace el curado al intermetálico. Cada respuesta es una soldadura ejecutada en esta máquina.',
        AssistantLead: 'Asistente de indagación BOND-16',
      },

      packaging: {
        AssistantBlurb:
          'Pregunta por qué un chip bueno falló en el reflujo, qué hace que el encapsulado se combe, o para qué sirve el post-curado. Cada respuesta es un moldeo y reflujo ejecutado con esta receta.',
        AssistantLead: 'Asistente de indagación PKG-17',
      },

      finaltest: {
        AssistantBlurb:
          'Pregunta qué cuesta una banda de guarda, si el burn-in justifica sus horas, o cuánto tiempo de prueba es suficiente. Cada respuesta es un lote ejecutado en este probador.',
        AssistantLead: 'Asistente de indagación FT-18',
      },

      gas: {
        AssistantBlurb:
          'Pregunta cuántos ciclos de purga hacen falta de verdad, si el detector avisa a tiempo, o qué llega a la chimenea. Cada respuesta es una evaluación ejecutada en este panel.',
        AssistantLead: 'Asistente de indagación GAS-19',
      },

      system: {
        vacuum: {
          Label: 'Vacío de proceso general',
          Action: 'Escalar a instalaciones: buscar fugas en el colector de vacío compartido',
        },
        pcw: {
          Label: 'Agua de refrigeración de proceso',
          Action: 'Escalar a instalaciones: restablecer la temperatura del agua enfriada',
        },
        gas: {
          Label: 'Colector general de gas seco',
          Action: 'Escalar a instalaciones: cambiar el secador del colector, purgar y recertificar',
        },
        upw: {
          Label: 'Planta de agua ultrapura',
          Action: 'Escalar a instalaciones: regenerar el lecho mixto pulidor y recalificar el circuito',
        },
        hvac: {
          Label: 'Climatización de la sala limpia',
          Action: 'Escalar a instalaciones: devolver las manejadoras de recirculación al caudal de diseño',
        },
        mau: {
          Label: 'Unidad de aire de reposición',
          Action: 'Escalar a instalaciones: cambiar los prefiltros del aire de reposición y reequilibrar la cascada',
        },
        pumps: {
          Label: 'Bombas secas de herramienta',
          Action: 'Escalar a instalaciones: cambiar la bomba seca bajo la herramienta y recalificar su línea previa',
        },
        chem: {
          Label: 'Distribución de productos químicos',
          Action: 'Escalar a instalaciones: aislar la línea con fuga, sustituir el tubo interior y recertificar la contención',
        },
      },
      fault: {
        headerLeak: {
          Label: 'Fuga en el colector de vacío de proceso compartido',
          Truth:
            'Una brida del colector de vacío de proceso compartido se movió durante los trabajos ' +
            'en la subplanta el fin de semana pasado y está fugando. Cada cámara de ese colector ' +
            'lo ve como su propia fuga — porque desde dentro de la cámara lo es.',
        },
        warmPcw: {
          Label: 'Agua de refrigeración de proceso llegando caliente',
          Truth:
            'La planta de agua enfriada está entregando agua de suministro unos 22 C por encima ' +
            'de lo nominal. Nada en ningún equipo está roto; simplemente, todo equipo que cede ' +
            'calor a ese circuito ha dejado de poder mantener su propia consigna.',
        },
        wetHeader: {
          Label: 'Humedad en el colector general de gas seco',
          Truth:
            'El secador del gas seco general falló en húmedo durante el fin de semana y el ' +
            'colector lleva alrededor de un 2% de agua — arrastre, no la deriva a nivel de ppm ' +
            'que da un secador saturado. Llega a todo equipo que respira nitrógeno u oxígeno ' +
            'generales, y lo que hace al llegar depende por completo de para qué usaba ese equipo ' +
            'el gas. Fíjate en lo lejos que están los dos umbrales: al ALD lo arruinan ppm de una ' +
            'cifra, porque el agua es su co-reactivo, mientras que un horno que crece óxido en ' +
            'vapor no nota nada por debajo de un uno por ciento aproximadamente. Hace falta un ' +
            'fallo grosero para que ambos equipos se quejen a la vez, y por eso justamente los dos ' +
            'grupos rara vez conectan sus problemas.',
        },
        spentBed: {
          Label: 'Lecho mixto pulidor agotado',
          Truth:
            'El lecho mixto pulidor del circuito de agua ultrapura está al 91% de su ' +
            'capacidad de intercambio y el sodio está rompiendo: el circuito que la semana ' +
            'pasada leía 18,1 MΩ·cm hoy lee 11. Nadie lo vio venir porque no había nada que ' +
            'ver — un lecho de intercambio iónico mantiene el medidor en 18,2 durante casi ' +
            'toda su vida y luego falla en los últimos puntos porcentuales, y por eso se ' +
            'regenera por caudal acumulado y no por la lectura. El enjuague es lo ÚLTIMO que ' +
            've la oblea en el banco húmedo, así que el banco aparenta haber fallado en sus ' +
            'dos químicas a la vez — una firma que ningún baño de esa herramienta puede ' +
            'producir.',
        },
        mauFiltersLoaded: {
          Label: 'Prefiltros del aire de reposición colmatados',
          Truth:
            'Los prefiltros de la unidad de aire de reposición se han colmatado tras una temporada ' +
            'de polen y polvo de obra, y la unidad entrega alrededor del 55% del aire exterior de ' +
            'diseño. Los recirculadores están bien, así que cada bahía sigue renovando su aire a ' +
            'pleno caudal; pero el aire de reposición es contra lo que se equilibran los ' +
            'extractores, y perdido ese equilibrio toda la sala limpia ha caído por debajo del ' +
            'pasillo. Todas las bahías leen negativo a la vez y aspiran aire del pasillo por cada ' +
            'rendija, y nada que una bahía haga con su propia extracción devuelve una cascada que ' +
            'el edificio ha perdido. Cambiar los prefiltros abajo lleva cuatro horas.',
        },
        dryPumpFailed: {
          Label: 'Bomba seca bajo PRB-14 averiada',
          Truth:
            'La bomba seca bajo PRB-14 ha fundido los rodamientos y se ha gripado: la línea previa ' +
            'que mantenía a 50 mTorr está a 600 Torr, apenas por debajo de la atmósfera. La ' +
            'probadora sujeta cada oblea al chuck por vacío, y con una quinta parte de la sujeción ' +
            'debida la oblea queda suelta: plana donde el sello central del chuck aún agarra, ' +
            'levantada en el borde donde no. La tarjeta aterriza floja sobre los dados levantados y ' +
            'fallan por contacto, que se ve exactamente como una tarjeta fuera de plano; pero la ' +
            'tarjeta está bien, las puntas están bien, y ninguna otra herramienta de la planta se ' +
            'ha movido, porque ninguna otra comparte esta bomba. Cambiar la bomba abajo lleva tres ' +
            'horas.',
        },
        chemLineLeak: {
          Label: 'Fuga en la línea de química de electrodeposición bajo ECD-12',
          Truth:
            'El tubo interior de la línea de química de electrodeposición bajo ECD-12 se ha rajado ' +
            'dentro de su camisa de contención. La camisa hizo su trabajo: la química bajó a la ' +
            'bandeja bajo el armario, el sensor de la bandeja disparó a los cuatro litros y el ' +
            'enclavamiento de fuga paró la bomba de suministro, que es exactamente para lo que ' +
            'está, porque química de electrodeposición en el suelo del subfab es un derrame de ' +
            'ácido. Arriba, el baño no se ha repuesto en todo el turno, y cada oblea le saca cobre, ' +
            'así que la celda trabaja con un tercio del cobre que su receta supone. Ninguna otra ' +
            'herramienta se ha movido: cada línea es propia. Aislar la línea, sustituir el tubo ' +
            'interior y recertificar la contención lleva cinco horas.',
        },
        ahuBeltSlip: {
          Label: 'Manejadoras de recirculación entregando de menos',
          Truth:
            'Dos de las cuatro manejadoras de recirculación tienen las correas patinando y ' +
            'las bahías reciben alrededor del 55% del caudal de diseño. Cada filtro del ' +
            'techo está bien, cada ventilador de allá arriba está bien: el aire simplemente ' +
            'no llega. La sala lee más sucia con el mismo personal, y el número que lo ' +
            'delata es la RECUPERACIÓN: cuánto tarda la bahía en tragarse un alboroto lo ' +
            'fijan los cambios de aire y nada más, así que una recuperación lenta con ' +
            'filtros limpios es el edificio, no la sala. Cambiar los filtros de techo aquí ' +
            'arriba cuesta doce horas y no recupera nada.',
        },
      },
    },

    footer: {
      Built: 'Compilado el {{date}}.',
    },

    menu: {
      Menu: 'Menú',
      Settings: 'Ajustes',
      SessionNote: 'Tu turno — diario, lote y ajustes de las máquinas — se guarda en este equipo y sobrevive a una recarga.',
      SessionClear: 'Empezar una sesión nueva',
      Entrance: 'Volver a la entrada',
      EntranceNote: 'Vuelve a la pantalla de entrada. Tu diario, lote y ajustes de las máquinas se conservan.',
      Model: 'Modelo de IA',
      Auto: 'Automático',
      AutoNote: 'por región',
      LocalNote:
        'No hay ningún modelo alojado configurado, así que las preguntas se resuelven en este dispositivo.',
      Lighting: 'Iluminación',
      LightBright: 'Blanco',
      LightBrightNote: 'brillante y frío: la fábrica en marcha',
      LightBlue: 'Azul',
      LightBlueNote: 'la bahía automatizada fuera de horario',
      LightYellow: 'Ámbar',
      LightYellowNote: 'la luz de seguridad: sin UV que exponga la resina',
      Graphics: 'Gráficos',
      QualityAuto: 'Automático',
      QualityAutoNote: 'predeterminado',
      QualityHigh: 'Más luces',
      QualityHighNote: 'más realista',
      QualityLow: 'Menos luces',
      QualityLowNote: 'menos realista',
      Loading: 'Cargando',
      HallBayChase: 'Fábrica A',
      HallBayChaseNote: 'distribución de bahías y galerías: caras de equipo en un muro, la galería detrás',
      HallBallroom: 'Fábrica B',
      HallBallroomNote: 'distribución de sala abierta: planta abierta, todos los equipos a la vista',
      Sound: 'Sonido',
      SoundOn: 'Activado',
      SoundOnNote: 'la sala tal como suena',
      SoundOff: 'Silenciado',
      SoundOffNote: 'sin sonido — la tecla M alterna',
      DesignFab: 'Diseñar una fábrica',
      DesignFabNote: 'construye tu propia planta con las veinticinco estaciones',
      YourFabs: 'Fábricas que construiste',
      CustomFabNote: '{{n}} estaciones, distribución {{hall}}',
      Fabs: 'Mis fábricas',
      FabsNote: 'Tus propias salas: abre una, guarda esta o diseña una nueva',
      OpenFab: 'Abrir…',
      OpenFabNote: 'las fábricas guardadas en tu cuenta',
      SaveFab: 'Guardar',
      SaveFabNote: 'conserva la sala en la que estás, con su nombre',
      SaveNothing: 'La Fábrica A y la B vienen de serie; diseña una para tener algo que guardar',
      SaveFabAs: 'Guardar como…',
      SaveFabAsNote: 'una copia con otro nombre, y pasas a estar en la copia',
      SaveAsName: 'Nombre de la copia',
      SaveAsGo: 'Guardar la copia',
      NoSavedFabs: 'Esta cuenta aún no tiene fábricas guardadas.',
      LibrarySignedOut: 'Inicia sesión (botón de cuenta, arriba a la derecha) para guardar fábricas en tu cuenta. Como invitado también sirve.',
      LibraryDenied: 'Esta cuenta no puede leer esa biblioteca.',
      LibraryOffline: 'La biblioteca de fábricas no responde; revisa la red e inténtalo de nuevo.',
      LibraryLoading: 'Buscando tus fábricas…',
      FabSaved: 'Guardada {{name}}.',
      DeleteFab: 'Eliminar de mis fábricas',
      DeleteFabConfirm: '¿Eliminar “{{name}}” de tu cuenta? La sala en la que estás no cambia.',
      SavedOn: 'guardada el {{when}}',
    },

    routing: {
      Via: 'Respondido por {{label}} ({{model}})',
      OnDevice: 'Respondido en este dispositivo por coincidencia de patrones',
      Unreachable: 'Respondido en este dispositivo — el modelo alojado no estaba accesible',
      Assumed: 'se asume {{region}}',
      RegionUs: 'EE. UU.',
      RegionIntl: 'internacional',
    },

    shift: {
      Debrief: 'Balance del turno',
      SessionTallyRuns:
        'Esta sesión: {{runs}} ejecuciones en {{tools}} estaciones · {{scrapped}} fuera de especificación.',
      Performance: 'Rendimiento en la estación',
      KpiDowntime: 'Parada de equipo',
      KpiDowntimeNote: 'este turno',
      KpiOutOfSpec: 'Fuera de especificación',
      KpiOutOfSpecNote: 'de {{runs}} ejecuciones',
      CallsHeading: 'Lo que pronosticaste',
      CallsNote: 'Dijiste esto antes de que el equipo respondiera. Nada aquí lo calificó de acierto o error: compáralos tú.',
      CallsSaid: 'Dijiste',
      CallsMeasured: 'Medido',
      CallsExplained: 'Al preguntarte por qué',
      Hours: '{{hours}} h',
      CostTotal: 'Total',
      NothingYet: 'Todavía no hay nada medido en esta estación.',
      CostEscalation: 'Escalación — {{label}}',
      CostRepair: 'Reparación — {{label}}',
      CostRepairBoth: 'Reparación — {{wrong}}, y después {{actual}}',
      SessionTally:
        'Esta sesión: {{shifts}} turnos · {{correct}} de {{called}} acertados · ' +
        '{{hours}} h de parada · {{scrapped}} ejecuciones fuera de especificación.',

      Title: 'INFORME DE TURNO — {{tool}}',
      AsFoundInSpec:
        'Tal como se encontró, este equipo está EN ESPECIFICACIÓN — {{primary}}, {{secondary}}. ' +
        'Sacar el lote es una decisión defendible.',
      AsFoundOutOfSpec:
        'Tal como se encontró, este equipo está FUERA DE ESPECIFICACIÓN — {{primary}}, ' +
        '{{secondary}}. Cada oblea que saques es chatarra.',
      AtTheTool: 'EN EL EQUIPO — gratis',
      Investigate: 'INVESTIGAR — cuesta tiempo de equipo',
      TakeIt: 'tomarla',
      SpentSoFar:
        '{{hours}} h gastadas hasta ahora. Cada hora aquí es una hora en que el equipo no hace ' +
        'obleas — que es exactamente el intercambio que hace un turno real.',
      InvestigateNote:
        'Cada una de estas saca de producción al equipo, o a un técnico. Toma las que te harían ' +
        'cambiar de opinión; tomarlas todas también es una decisión.',
      CallIt: 'DECÍDELO',
      CallItNote:
        'Nombra la única cosa que repararías. La respuesta queda oculta hasta que te comprometas, ' +
        'y una decisión equivocada cuesta el tiempo de parada dos veces.',
      Correct: 'Correcto — {{label}}.',
      Wrong: 'Incorrecto — dijiste {{label}}.',
      RepairRight: '{{action}}. {{hours}} h de parada, y el equipo vuelve bien.',
      RepairWrong:
        '{{action}} cuesta {{hours}} h y el equipo vuelve igual de averiado. Después ' +
        '{{realAction}} cuesta otras {{realHours}} h. {{total}} h en total.',
      TheRealRepair: 'la reparación real',
      ThisShift:
        'Este turno: {{investigating}} h investigando, {{repairing}} h reparando, {{scrapped}} ' +
        'obleas desechadas mientras decidías.',
      WhatEachRepair: 'QUÉ HABRÍA RECUPERADO CADA REPARACIÓN',
      WhatEachRepairNote:
        'Cada fila es el equipo ejecutado de nuevo con esa única cosa devuelta a nominal y todo lo ' +
        'demás tal como se encontró. Esto es el método, no un marcador.',
      Recovered: '{{pct}}% recuperado — {{primary}}, {{secondary}}',
      Decidable:
        'La mejor reparación superó a la siguiente por {{points}} puntos. Esta se podía decidir ' +
        'solo con el contrafactual.',
      TooClose:
        'Las dos mejores reparaciones quedaron a solo {{points}} puntos, porque esas partes del ' +
        'equipo se afectan entre sí. Probar reparaciones no habría resuelto esta — solo una ' +
        'medición podía.',
      StartShift: 'Empezar un turno (fallo aleatorio)',
      RevealCause: 'Revelar la causa',
      HideAnswer: 'Ocultar la respuesta',
      Instructor: 'INSTRUCTOR — activar un escenario',
    },

    journal: {
      OrderComplete: 'Orden de trabajo completada',
      ByStation: 'Rendimiento por estación',
      ColTool: 'Equipo',
      ColRuns: 'Ejecuciones',
      ColOutOfSpec: 'Fuera de esp.',
      ColDowntime: 'Parada',
      ColCalls: 'Diagnósticos',
      CallsRight: '{{correct}} de {{calls}} acertados',
      NotApplicable: '—',
      Unattributed: '(sin equipo registrado)',

      Title: '# SimFab — registro de sesión',
      NoActivity: '_Sin actividad registrada._',
      ShiftStart: 'inicio de turno',
      TookReading: 'tomó lectura',
      RanWafer: 'procesó una oblea',
      InSpec: 'dentro de especificación',
      OutOfSpec: '**FUERA DE ESPECIFICACIÓN**',
      Changed: 'cambió',
      Asked: 'preguntó',
      Maintenance: 'realizó mantenimiento',
      CalledIt: 'dictaminó',
      Correct: 'CORRECTO',
      Incorrect: 'INCORRECTO',
      ReadingsBefore: 'lecturas tomadas antes del dictamen',
      WafersRun: 'obleas procesadas',
      QuestionsAsked: 'preguntas formuladas',
      ActualCause: 'la causa real era',
      NoFault: 'ningún fallo en absoluto',
    },

    author: {
      Title: 'Diseñar una fábrica',
      Subtitle:
        'Veintidós estaciones para elegir. Lo que dejas fuera es la decisión — y la ruta de producto te dirá lo que costó.',
      Ask: 'Pide un cambio…',
      Send: 'Enviar',
      Working: 'Trabajando…',
      NewFloor: 'Planta nueva',
      SavedFloors: 'Fábricas que construiste',
      NoSaved: 'Todavía no has construido nada. Empieza por una fábrica que existe, o nombra las herramientas que quieras.',
      Build: 'Constrúyela y entra',
      Rebuild: 'Reconstruir y entrar',
      Standing: 'Estás dentro de esta',
      Save: 'Guardar',
      Delete: 'Eliminar',
      DeleteActive: 'Estás dentro de esta. Ve primero a Fab A o Fab B.',
      DeleteConfirm: '¿Eliminar «{{name}}»? Se olvida el plano de planta. Las herramientas no: son las mismas veinticinco.',
      Cancel: 'Consérvala',
      Undo: 'Deshacer',
      SortShort: 'Orden de flujo',
      allAlias: 'todo|todas|todas las herramientas|todas las estaciones|todo lo que hay',
      renameStop: 'y|luego|pero',
      NotOnFloor: 'No está en esta planta: {{tags}}',
      LineNotOnFloor: 'Esta es la línea de referencia: la ruta de producto completa sobre las estaciones de la Fab A. La planta en la que estás no tiene {{tags}}, así que aquí un lote tendría que salir del edificio en cada una de esas operaciones. Los números de abajo son de la línea tal como se diseñó, no de esta planta.',
      NamePlaceholder: 'Nombra esta fábrica',
      HallLabel: 'Nave',
      OnTheFloor: 'En la planta, en el orden en que una oblea las encuentra',
      Palette: 'Fuera de esta planta',
      AddTool: 'Poner en la planta',
      RemoveTool: 'Quitar de la planta',
      MoveEarlier: 'Antes en el flujo',
      MoveLater: 'Después en el flujo',
      RequiredTool: 'La esclusa es la entrada. Toda planta tiene una.',
      HallSize: '{{length}} m de pasillo, {{bays}} bahías',
      Suggestions: 'Prueba a pedir',
      Local: 'reconocido en este dispositivo',
      CoverageTitle: 'Frente a la ruta de producto',
      CoverageBar: '{{covered}} de {{steps}} operaciones',
      MaskCount: '{{masksCovered}} de {{masks}} pasos con máscara',
      SegmentRuns: 'se ejecuta de principio a fin',
      SegmentPart: '{{covered}} de {{steps}}',
      Empty: 'En esta planta no hay más que la esclusa.',
      ListSeparator: ', ',
      hall: {
        bayChase: 'bahía y pasillo técnico',
        ballroom: 'salón abierto',
      },
      hallNote: {
        bayChase: 'los frentes de las herramientas en una pared, los bastidores en el pasillo técnico detrás',
        ballroom: 'una sola planta abierta, todos los bastidores a la vista',
      },
      hallAlias: {
        bayChase: 'bahía y pasillo|bay and chase|con pared|mamparo|bahías cerradas|amurallada',
        ballroom: 'salón|salón abierto|ballroom|planta abierta|sin pared|sin paredes|diáfana',
      },
      zone: {
        entry: 'entrada',
        substrate: 'sustrato',
        frontend: 'front end',
        metrology: 'metrología',
        interconnect: 'interconexión',
        sort: 'clasificación de obleas',
        assembly: 'ensamblaje y prueba',
        quantum: 'dispositivos cuánticos',
        support: 'soporte',
      },
      zoneAlias: {
        entry: 'acceso|por dónde se entra',
        substrate: 'suministro de obleas|de lingote a oblea|línea de sustrato',
        frontend: 'feol|capas del dispositivo|transistor',
        metrology: 'medición en línea',
        interconnect: 'beol|back end of line|capas de metal|cableado|damasceno',
        sort: 'planta de sondas|prueba de obleas',
        assembly: 'back end|línea de encapsulado|planta osat|línea de ensamblaje',
        quantum: 'cúbit|cúbits|chip cuántico|chips cuánticos|computación cuántica|línea cuántica',
        support: 'infraestructura|servicios de planta',
      },
      segment: {
        substrate: 'el módulo de sustrato',
        gate: 'el módulo de puerta',
        metal: 'el bucle de metal',
        backEnd: 'ensamblaje y prueba',
      },
      archetype: {
        idm: {
          Name: 'Fabricante integrado de dispositivos',
          Purpose: 'Arena por una puerta, piezas clasificadas por la otra. Las veinticinco estaciones.',
        },
        foundry: {
          Name: 'Fundición pura',
          Purpose: 'Compra obleas pulidas, entrega obleas terminadas. Sin tirador, y nunca ve un encapsulado.',
        },
        substrate: {
          Name: 'Proveedor de sustratos',
          Purpose: 'De donde sale la oblea con la que empieza una fábrica. No es una fábrica de obleas en absoluto.',
        },
        osat: {
          Name: 'Casa de ensamblaje y prueba',
          Purpose: 'Entra una oblea terminada, sale una pieza clasificada. Ni una litografía en toda la planta.',
        },
        power: {
          Name: 'Línea de potencia discreta',
          Purpose: 'Epi gruesa, implantes profundos, metal de aluminio — así que sin bucle damasceno, a propósito.',
        },
        teaching: {
          Name: 'Planta docente',
          Purpose: 'La planta más pequeña que aún fabrica una capa patronada. Nueve estaciones, un bucle.',
        },
      },
      archetypeAlias: {
        idm: 'idm|dispositivo integrado|todo|fábrica completa|fábrica entera',
        foundry: 'fundición|pure play|foundry|fábrica por contrato',
        substrate: 'proveedor de sustratos|proveedor de obleas|casa de lingotes|fabricante de obleas',
        osat: 'osat|casa de ensamblaje|casa de pruebas|casa de encapsulado',
        power: 'línea de potencia|discreto|mosfet|igbt|dispositivo de potencia',
        teaching: 'docente|aula|planta de formación|fábrica mínima|la más pequeña|fábrica simple',
      },
      alias: {
        gowning: 'bata|vestuario|esclusa|sala de cambio|entrada a sala limpia',
        crystal: 'tirador|czochralski|lingote|boule|crecedor de cristal',
        waferprep: 'preparación de obleas|corte|lapeado|oblea pulida|preparación de sustrato|sierra de hilo',
        epi: 'epi|epitaxia|epitaxial|reactor de epi',
        clean: 'banco húmedo|limpieza húmeda|limpieza rca|banco de limpieza|prelimpieza',
        implant: 'implante|implantador|implantador iónico|dopaje|dopante',
        etch: 'grabador|grabado|grabado seco|icp|grabador de plasma',
        deposition: 'cvd|pecvd|ald|depósito|deposición de película|herramienta de deposición',
        litho: 'lito|litografía|escáner|stepper|fotolitografía|herramienta de exposición|recubrir y revelar',
        patterning: 'patronado múltiple|multipatronado|saqp|espaciador|autoalineado|doble patronado|cuádruple patronado|división de paso',
        thermal: 'horno|recocido|templado|rtp|oxidación|difusión|herramienta térmica',
        metrology: 'metrología|elipsómetro|escaterometría|metrología en línea',
        facilities: 'instalaciones|sistemas de planta|panel de servicios|cara de instalaciones|panel de válvulas',
        cmp: 'cmp|pulidora|planarizadora|planarización|pulido mecánico|químico mecánico',
        plating: 'electrodeposición|galvanizado|ecd|relleno de cobre|deposición electroquímica',
        inspection: 'inspección|inspección de defectos|barrido de partículas|escáner de defectos',
        probe: 'sondeador|clasificación de obleas|tarjeta de sondas|prueba paramétrica|clasificación eléctrica',
        dicing: 'troceado|cortadora|singulación|rectificado y corte|sierra de corte|backgrind',
        bonding: 'fijación de dado|soldadura de hilo|wirebond|soldadora|unión',
        packaging: 'moldeo|encapsulación|molde|paquete|encapsulado',
        finaltest: 'prueba final|burn in|burn-in|clasificación en bins|probador|ate',
        gas: 'gas a granel|armario de gases|granja de gases|búnker de gases|gas especial|suministro de nitrógeno',
        euv: 'euv|escáner euv|ultravioleta extremo|13,5 nm|fuente de estaño|litografía euv',
        ebeam: 'cd-sem|sem|haz de electrones|microscopio electrónico|inspección por haz de electrones',
        quantum: 'cuántico|cuántica|prueba de cúbits|criostato|refrigerador de dilución|transmón|cúbit de espín',
      },
      action: {
        add: {
          Summary: 'Poner una estación, o toda una etapa del flujo, en la planta',
          Example: 'añade una pulidora CMP',
        },
        remove: {
          Summary: 'Quitar una estación de la planta',
          Example: 'quita el tirador de cristal',
        },
        archetype: {
          Summary: 'Empezar de nuevo desde una fábrica que existe',
          Example: 'constrúyeme una fundición pura',
        },
        hall: {
          Summary: 'Bahías con pared, o una sola planta abierta',
          Example: 'hazlo un salón abierto',
        },
        rename: {
          Summary: 'Ponerle nombre a esta fábrica',
          Example: 'llámala la línea piloto',
        },
        sort: {
          Summary: 'Devolver el pasillo al orden en que una oblea encuentra las herramientas',
          Example: 'ponla en orden de flujo',
        },
        clear: {
          Summary: 'Vaciar la planta y empezar otra vez',
          Example: 'vacía la planta',
        },
        assess: {
          Summary: '¿Qué puede fabricar esta planta y qué le falta?',
          Example: '¿qué me falta?',
        },
        catalogue: {
          Summary: '¿Qué estaciones hay para elegir?',
          Example: '¿qué herramientas puedo tener?',
        },
      },
      trigger: {
        add: 'añade|añadir|agrega|agregar|pon|poner|instala|incluye|quiero|necesito|dame|mete',
        remove: 'quita|quitar|elimina|eliminar|borra|saca|sacar|sin|deshazte|vende',
        archetype: 'empieza por|empieza con|empieza de nuevo|constrúyeme|construyeme|hazme|basado en|como una|plantilla|preajuste|móntalo como',
        hall: 'salón|bahía y pasillo|planta abierta|con pared|mamparo|distribución|tipo de nave',
        rename: 'llámala|llamala|llámalo|ponle nombre|renombra|nómbrala',
        sort: 'orden de flujo|en orden|reordena|reorganiza|ordena|orden de proceso',
        clear: 'vacía la|vaciar la|empieza otra vez|déjala vacía|nada en la planta',
        assess: 'qué puede|que puede|puede esta|qué me falta|que me falta|qué falta|cobertura|cuán completa|evalúa|servirá|funcionará',
        catalogue: 'qué herramientas|que herramientas|cuáles herramientas|qué estaciones|que estaciones|lista las|qué hay disponible|qué puedo elegir|mis opciones',
      },
      renameLead: 'llámala|llamala|llámalo|llamalo|ponle nombre|renómbrala a|nombra esta fábrica',
      reply: {
        Added: 'Ahora en la planta: {{tags}}.',
        Removed: 'Fuera de la planta: {{tags}}.',
        Refused: '{{tags}} se queda. Es la entrada.',
        AlreadyThere: 'Ya estaba: {{tags}}.',
        NotThere: 'No estaba en esta planta: {{tags}}.',
        Hall: 'Tipo de nave: {{hall}}.',
        Renamed: 'Se llama «{{name}}».',
        Sorted: 'El pasillo va ahora en el orden en que una oblea encuentra las herramientas.',
        Archetype: 'Empezando de nuevo desde: {{name}}.',
        NothingToDo: 'Eso lo puedo hacer — {{summary}} — pero nada en esa frase me dijo sobre qué hacerlo.',
        SameArchetype: 'Esta planta ya es {{name}}.',
        Unplaced: 'No pude convertir eso en un cambio de la planta. Esto es lo que sé hacer:',
        Complete: 'Cada operación de la ruta de producto tiene herramienta en esta planta — las {{steps}}, {{masks}} de ellas con máscara.',
        Coverage: 'Tiene herramienta para {{covered}} de las {{steps}} operaciones de la ruta de producto — {{percent}}%.',
        CoverageNone: 'Ninguna de las {{steps}} operaciones de la ruta de producto tiene herramienta en esta planta.',
        SegmentsRun: 'De principio a fin: {{segments}}.',
        SegmentsOpened: 'Eso cierra {{segments}}, que ahora se ejecuta de principio a fin.',
        SegmentsBroken: 'Eso rompe {{segments}}, que ya no se ejecuta de principio a fin.',
        BiggestGap: 'El mayor hueco es {{tag}} — {{process}} — con {{count}} operaciones y ningún sitio donde ejecutarlas.',
        BiggestGap_one: 'El mayor hueco es {{tag}} — {{process}} — con {{count}} operación y ningún sitio donde ejecutarla.',
        BiggestGap_other: 'El mayor hueco es {{tag}} — {{process}} — con {{count}} operaciones y ningún sitio donde ejecutarlas.',
        CatalogueHead: '{{n}} estaciones en el catálogo, {{chosen}} de ellas en esta planta. Una marca significa que está puesta.',
        CatalogueZone: '{{zone}}: {{tools}}',
        EmptyFloor: 'En esta planta no hay más que la esclusa. Pide una fábrica de la que partir, o nombra las herramientas que quieras.',
        Floor: '{{n}} estaciones: {{tools}}.',
        NoHouse: 'Sin {{tags}} en esta planta. Cada herramienta de aquí cuelga del vacío de proceso, el agua enfriada, el nitrógeno a granel y el extractor — dejarlos fuera es dejar fuera la mitad del edificio que de verdad falla.',
        Unused: 'La ruta de producto nunca visita {{tags}}. No es una crítica: están aquí por lo que esta medida no mide.',
      },
    },

  },
}
